// pdfContent.js — Page renderers matching reference PDF design
const safeParse = (v) => { if (typeof v === "number") return v; if (!v) return 0; return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; };

const G = [6, 150, 104];   // Yathartha Green
const DARK = [0, 0, 0];
const GREY = [0, 0, 0];
const SLATE = [0, 0, 0];
const LIGHT = [248, 250, 252];
const RED = [222, 59, 51];
const ORANGE = [244, 125, 49];
const BLUE = [26, 111, 188];
const PURPLE = [101, 58, 150];
const GREEN2 = [26, 125, 68];
const PURPLE_DARK = [91, 44, 111];
const RED_DARK = [142, 29, 29];
const ORANGE_DARK = [194, 94, 0];
const BLUE_DARK = [26, 82, 118];
const GREEN_DARK_ALT = [25, 111, 61];

export const addHeader = (doc, secNum, pageWidth) => {
    // Page is added by the caller in pdfGenerator.js
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, 297, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }
    doc.setFillColor(...DARK); doc.roundedRect(14, 8, 30, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(`SECTION ${secNum}`, 17, 14);
};

export const addPageNumberBadge = (doc, num, pageWidth = 210, pageHeight = 297) => {
    doc.setFillColor(30, 41, 59);
    doc.circle(pageWidth / 2, pageHeight - 5, 4, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(num, pageWidth / 2, pageHeight - 2.5, { align: 'center' });
};

export const addFooter = (doc, pageNum, name, pageWidth, pageHeight) => {
    doc.setTextColor(...GREY); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    const ft = ` www.myyathartha.com | Confidential - for ${(name || "neha").toLowerCase()} only | Financial awareness & education report.`;
    doc.text(doc.splitTextToSize(ft, pageWidth - 32), pageWidth / 2, pageHeight - 14, { align: 'center' });
    doc.setFillColor(...DARK); doc.circle(pageWidth / 2, pageHeight - 5, 4, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(pageNum, pageWidth / 2, pageHeight - 3.5, { align: 'center' });
};

export const addSimpleFooter = (doc, pageNum, pageWidth, pageHeight) => {
    doc.setFillColor(...DARK); doc.circle(pageWidth / 2, pageHeight - 5, 4, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(pageNum, pageWidth / 2, pageHeight - 3.5, { align: 'center' });
};

export const addGreenBanner = (doc, text, y, pageWidth) => {
    doc.setFillColor(...G); doc.roundedRect(14, y, pageWidth - 28, 9, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text(text, 18, y + 6);
    return y + 9;
};

// Page 8 - Dynamic Content based on User Input
export const renderAttentionPage2 = (doc, { name, savingsRateActual, savingsRateTarget, surplusLossCr, untrackedSurplus, personalLoanBalance, personalLoanEMI, loanRate, monthsLeft, interestSaved, pageWidth, pageHeight, m, calculations, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const ORANGE_BANNER = [194, 91, 31];
    const LIGHT = [243, 244, 246];

    // ========== DYNAMIC DATA CALCULATIONS ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};

    // Emergency Fund - Dynamic
    const currentEF = safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || 0;
    const monthlyExpenses = m?.totalEssentialExpenses || 0;
    const targetEF = monthlyExpenses > 0 ? monthlyExpenses * 6 : 0;
    const monthlyTransfer = Math.max(1000, Math.round(targetEF / 18 / 1000) * 1000); // Dynamic monthly amount
    const monthsToFund = Math.ceil((targetEF - currentEF) / monthlyTransfer);

    // Savings Rate - Dynamic
    const actualSavingsRate = safeParse(savingsRateActual) || safeParse(calculations?.diagnosis?.savingsRate) || 0;
    const targetSavingsRate = 20;
    const monthlyIncome = m?.totalMonthlyIncome || safeParse(personalInfo.monthlySalary) || 0;
    const annualIncomeVal = monthlyIncome * 12;

    // Calculate compounding penalty dynamically
    const yearsToRetire = Math.max(5, 60 - (parseInt(personalInfo.age) || 30));
    const monthlyRate = 0.01; // 12% annual
    const actualFV = monthlyIncome * (actualSavingsRate / 100) * ((Math.pow(1 + monthlyRate, yearsToRetire * 12) - 1) / monthlyRate) * (1 + monthlyRate);
    const targetFV = monthlyIncome * (targetSavingsRate / 100) * ((Math.pow(1 + monthlyRate, yearsToRetire * 12) - 1) / monthlyRate) * (1 + monthlyRate);
    const dynamicSurplusLossCr = ((targetFV - actualFV) / 10000000).toFixed(2);

    // Untracked surplus - Dynamic calculation
    const totalIncome = m?.totalMonthlyIncome || monthlyIncome;
    const totalExpenses = (m?.totalEssentialExpenses || 0) + (m?.totalLifestyleExpenses || 0) + (m?.totalEMI || 0);
    const trackedInvestments = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0) + (safeParse(sInv.ppf) || 0) + (safeParse(sInv.epf) || 0);
    const dynamicUntrackedSurplus = Math.max(0, Math.round(totalIncome - totalExpenses - trackedInvestments));

    // Personal Loan - Dynamic
    const actualLoanBalance = safeParse(sInc.personalLoanOutstanding) || safeParse(personalLoanBalance) || 0;
    const actualLoanEMI = safeParse(sInc.personalLoanEMI) || safeParse(personalLoanEMI) || 0;
    const actualLoanRate = safeParse(sInc.personalLoanRate) || safeParse(loanRate) || 14;

    // Calculate remaining months dynamically
    const dynamicMonthsLeft = actualLoanEMI > 0 ? Math.ceil(actualLoanBalance / actualLoanEMI) : 0;

    // Calculate interest saved with prepayment
    const extraPayment = Math.round(actualLoanEMI * 0.5); // 50% extra
    const monthsWithPrepay = actualLoanEMI > 0 ? Math.ceil(actualLoanBalance / (actualLoanEMI + extraPayment)) : 0;
    const dynamicInterestSaved = Math.round(actualLoanBalance * (actualLoanRate / 100) * (dynamicMonthsLeft - monthsWithPrepay) / 12);

    // --- HEADER (Page 10: SECTION 4 left, Logo right) ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    // Section badge removed
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    let y = 32;

    // --- TOP GREY BOXES - EMERGENCY FUND FOLLOWUP (DYNAMIC) ---
    const drawTopBox = (title, body) => {
        doc.setFillColor(...LIGHT); doc.roundedRect(14, y, pageWidth - 28, 16, 3, 3, 'F');
        doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(title, 18, y + 5.5);
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
        doc.text(doc.splitTextToSize(body, pageWidth - 36), 18, y + 9.5, { lineHeightFactor: 1.1 });
        y += 18;
    };
    drawTopBox("Where to Keep It:", "Not in a regular savings account (0.5–3% returns). Use a liquid mutual fund or a high–yield savings account. You get 5–7% returns AND can withdraw in 24 hours. Explore curated liquid fund options on Wealthcare by Yathartha at yathartha.in/wealthcare.");
    drawTopBox("Your Fix:", `Set up ₹${fmt(monthlyTransfer)}/month auto–transfer to a liquid fund. Name it '${name.split(' ')[0]}'s Safety Net'. In ${monthsToFund} months, this is fully funded. Then redirect ₹${fmt(monthlyTransfer)}/month to SIPs.`);

    // --- ISSUE 3 - SAVINGS RATE (DYNAMIC) ---
    y += 4;
    doc.setFillColor(...ORANGE_BANNER); doc.rect(14, y, pageWidth - 28, 7, 'F');
    try { doc.addImage('/icons/alert.png', 'PNG', 16, y + 1, 5, 5); } catch (e) { }
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(`ISSUE 3 [HIGH]: Savings Rate Below Benchmark`, 24, y + 5);

    y += 12;
    doc.setTextColor(30, 41, 59); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
    doc.text(`Current: ${actualSavingsRate}% -> Target: ${targetSavingsRate}%+ -> Achievable with Zero Lifestyle Cuts`, 14, y);

    const drawRoundIcon = (iy, iconType, rateText) => {
        doc.setFillColor(...ORANGE_BANNER); doc.circle(28, iy, 7.5, 'F');
        doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.4);
        let iconPath = null;
        if (iconType === 'penalty') iconPath = '/icons/invoice.png';
        else if (iconType === 'megaphone') iconPath = '/icons/speaker.png';

        if (iconPath) {
            try { try { doc.addImage(iconPath, 'PNG', 24, iy - 4, 8, 8); } catch (e) { } } catch (e) { }
        } else if (iconType === 'percent') {
            doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.text(`${rateText || targetSavingsRate}%`, 28, iy + 2, { align: 'center' });
        }
    };

    y += 10;
    const drawIssueRow = (title, body, iconType, rateText) => {
        drawRoundIcon(y + 3.8, iconType, rateText);
        doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(title, 40, y + 3);
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
        doc.text(doc.splitTextToSize(body, pageWidth - 55), 40, y + 7, { lineHeightFactor: 1.15 });
        y += 18;
    };
    drawIssueRow("The Compounding Penalty:", `The difference between saving ${actualSavingsRate}% and ${targetSavingsRate}% over ${yearsToRetire} years at 12% returns is approximately ₹${dynamicSurplusLossCr} Crore. That is the cost of not fixing this today.`, 'penalty');
    drawIssueRow(`Why the ${targetSavingsRate}% Rule Exists:`, `Financial research shows that saving ${targetSavingsRate}% of income consistently from your ${personalInfo.age || '30'}s virtually guarantees financial independence by 60, regardless of income level. It is not about the amount — it is about the percentage.`, 'percent', targetSavingsRate);
    drawIssueRow("The Great News:", `You do not need to cut any expense to reach ${targetSavingsRate}%. Your ₹${fmt(dynamicUntrackedSurplus)} untracked surplus contains the solution. This report will show you exactly where to redirect it.`, 'megaphone');

    // --- ISSUE 4 - PERSONAL LOAN (DYNAMIC) ---
    if (actualLoanBalance > 0 || actualLoanEMI > 0) {
        y += 2;
        doc.setFillColor(...ORANGE_BANNER); doc.rect(14, y, pageWidth - 28, 7, 'F');
        try { doc.addImage('/icons/alert.png', 'PNG', 16, y + 1, 5, 5); } catch (e) { }
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(`ISSUE 4 [MEDIUM]: Personal Loan at ${actualLoanRate}% is an Expensive Drag`, 24, y + 5);

        y += 12;
        doc.setTextColor(30, 41, 59); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
        doc.text(`Outstanding: ₹${fmt(actualLoanBalance)} | Rate: ${actualLoanRate}% p.a. | EMI: ₹${fmt(actualLoanEMI)}/month | Remaining: ${dynamicMonthsLeft} months`, 14, y);

        y += 5;
        const cardW = (pageWidth - 38) / 3;
        const cardH = 43;
        const colors = [[153, 51, 25], [194, 91, 31], [214, 121, 51]];
        const drawStepCard = (cx, cy, title, body, color) => {
            doc.setFillColor(...color); doc.rect(cx, cy, cardW, 8, 'F');
            doc.path([{ op: 'm', c: [cx + cardW / 2 - 4.5, cy + 8] }, { op: 'l', c: [cx + cardW / 2 + 4.5, cy + 8] }, { op: 'l', c: [cx + cardW / 2, cy + 13.5] }, { op: 'z' }]); doc.fill();
            doc.setFillColor(...LIGHT); doc.rect(cx, cy + 8, cardW, cardH - 8, 'F');
            doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
            doc.text(title, cx + cardW / 2, cy + 5.5, { align: 'center' });
            doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
            doc.text(doc.splitTextToSize(body, cardW - 8), cx + 4, cy + 17, { lineHeightFactor: 1.15 });
            doc.setDrawColor(...color); doc.setLineWidth(1.2); doc.line(cx, cy + cardH, cx + cardW, cy + cardH);
        };

        const estimatedInterest = Math.round(actualLoanBalance * (actualLoanRate / 100) * dynamicMonthsLeft / 12);
        drawStepCard(14, y, "THE HIDDEN COST:", `If you pay only the standard EMI, you will pay ₹${fmt(estimatedInterest)} in interest over ${dynamicMonthsLeft} months. More importantly, ${actualLoanRate}% is a guaranteed loss — your investments would need to earn more than ${actualLoanRate}% just to break even.`, colors[0]);
        const connY = y + 25.5;
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4); doc.line(14 + cardW + 1, connY, 14 + cardW + 5, connY);
        drawStepCard(14 + cardW + 6, y, "THE SMART MOVE:", `Pay ₹${fmt(extraPayment)} extra each month. Close the loan in ${monthsWithPrepay} months instead of ${dynamicMonthsLeft}. Save ₹${fmt(dynamicInterestSaved)} in interest. From Month ${monthsWithPrepay + 1}, free up ₹${fmt(actualLoanEMI + extraPayment)}/mo for investments.`, colors[1]);
        doc.line(14 + 2 * cardW + 7, connY, 14 + 2 * cardW + 11, connY);
        drawStepCard(14 + 2 * cardW + 12, y, "PRECAUTION:", `Before prepaying, check your loan agreement for prepayment charges (typically 2-4% of prepaid amount). For a ${actualLoanRate}% interest loan, prepayment almost always saves money even with charges.`, colors[2]);
    }

};

// Page 9 - Tax Optimization (Fully Dynamic)
export const renderAttentionPage3 = (doc, { name, taxSavings, taxSavingsMonthly, deductions, rentPaid, cityType, pageWidth, pageHeight, report = {}, calculations = {}, m = {} }) => {
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    const ORANGE_BANNER = [194, 91, 31];
    const DARK_TEXT = [30, 41, 59];

    // ========== DYNAMIC DATA EXTRACTION & CALCULATIONS ==========
    const sInc = report?.salaryIncome || {};
    const personalInfo = report?.personalInfo || {};

    // 1. Geography & Rent
    const displayCityRaw = personalInfo.city || personalInfo.cityType || personalInfo.address || 'your city';
    const cleanCityName = (val) => {
        if (!val) return "";
        const lower = val.toString().toLowerCase().trim();
        if (lower === "metro" || lower === "non_metro" || lower === "non-metro" || lower === "tier2" || lower === "tier3" || lower === "rural") {
            if (lower === "tier2") return "Tier 2";
            if (lower === "tier3") return "Tier 3";
            if (lower === "non_metro" || lower === "non-metro") return "Non-Metro";
            return val.toString().charAt(0).toUpperCase() + val.toString().slice(1);
        }
        return val.toString().replace(/(?:\s*\()?metro\)?/i, "").trim();
    };
    const displayCity = cleanCityName(displayCityRaw);
    const isMetro = (cityType || personalInfo.cityType || personalInfo.city || '').toLowerCase().includes('metro');
    const finalCityType = isMetro ? 'Metro' : 'Non-Metro';
    const monthlyRent = safeParse(rentPaid) || safeParse(sInc.rentPaid) || safeParse(sInc.houseRent) || 0;

    // 2. Deduction Breakdown (80C, 80D, etc.)
    const epfAnnual = (safeParse(sInc.epf) || 0) * 12;
    const ppfAnnual = (safeParse(sInc.ppf) || 0) * 12;
    const elssAnnual = (safeParse(sInc.elss) || safeParse(report?.salaryInvestment?.elss) || 0) * 12;
    const insuranceAnnual = (safeParse(sInc.lifeInsurance) || 0) * 12;
    const hlPrincipal = (safeParse(sInc.homeLoanPrincipal) || 0) * 12;
    const total80C = Math.min(150000, epfAnnual + ppfAnnual + elssAnnual + insuranceAnnual + hlPrincipal);

    const healthSelf = (safeParse(sInc.selfHealthInsurance) || 0) * 12;
    const healthParent = (safeParse(sInc.parentHealthInsurance) || 0) * 12;
    const total80D = Math.min(25000, healthSelf) + Math.min(50000, healthParent);

    // 3. HRA Logic
    const basicSalary = safeParse(sInc.basicSalary) || (m?.totalMonthlyIncome || 0) * 0.4;
    const hraReceived = safeParse(sInc.hra) || 0;
    const rentExemption = Math.max(0, monthlyRent - (basicSalary * 0.1));
    const hraLimit = isMetro ? (basicSalary * 0.5) : (basicSalary * 0.4);
    const hraExemption = Math.min(hraReceived, rentExemption, hraLimit);

    const hlInterest = Math.min(200000, (safeParse(sInc.homeLoanInterest) || 0) * 12);
    const finalDeductions = total80C + total80D + (hraExemption * 12) + hlInterest;

    // 4. Old vs New Regime Comparison
    const annualIncome = (m?.totalMonthlyIncome || safeParse(personalInfo.monthlySalary) || 0) * 12;
    const standardDeduction = 50000;

    // Simplified Tax Logic (Approximation for the report)
    const isOldBetter = finalDeductions > (annualIncome * 0.2) || finalDeductions > 250000;
    const recommendedRegime = isOldBetter ? "Old Tax Regime" : "New Tax Regime";
    const annualBenefit = isOldBetter ? Math.round(finalDeductions * 0.2) : 10000; // Baseline benefit
    const monthlyBenefit = Math.round(annualBenefit / 12);

    // --- HEADER ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8); } catch (e) { } } catch (e) { }
    doc.setFillColor(...DARK_TEXT); doc.rect(pageWidth - 42, 8, 28, 7, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 6", pageWidth - 39, 13);

    let y = 35;

    // --- ISSUE 5 BANNER ---
    doc.setFillColor(...ORANGE_BANNER); doc.rect(14, y, pageWidth - 28, 7, 'F');
    try { doc.addImage('/icons/alert.png', 'PNG', 16, y + 1, 5, 5); } catch (e) { }
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(`ISSUE 5 [OPTIMIZATION]: Tax Regime & HRA Not Fully Optimised`, 24, y + 5);

    y += 14;
    doc.setTextColor(...DARK_TEXT); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
    doc.text(`Annual Tax Saving Available: \u20B9${fmt(annualBenefit)} | Monthly Benefit: \u20B9${fmt(monthlyBenefit)}`, pageWidth / 2, y, { align: 'center' });

    y += 10;
    const drawCardWithIcon = (cy, title, body, iconType) => {
        const iconX = 40, boxX = 65, boxW = pageWidth - boxX - 14, boxH = 43;
        doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.7);
        doc.path([{ op: 'm', c: [boxX + 2, cy] }, { op: 'l', c: [boxX + boxW - 5, cy] }, { op: 'c', c: [boxX + boxW, cy, boxX + boxW, cy, boxX + boxW, cy + 5] }, { op: 'l', c: [boxX + boxW, cy + boxH - 5] }, { op: 'c', c: [boxX + boxW, cy + boxH, boxX + boxW, cy + boxH, boxX + boxW - 5, cy + boxH] }, { op: 'l', c: [boxX + 5, cy + boxH] }, { op: 'c', c: [boxX, cy + boxH, boxX, cy + boxH, boxX, cy + boxH - 5] }, { op: 'l', c: [boxX, cy + boxH / 2 + 10] }, { op: 'c', c: [boxX - 5, cy + boxH / 2 + 10, boxX - 10, cy + boxH / 2 + 5, boxX - 10, cy + boxH / 2] }, { op: 'c', c: [boxX - 10, cy + boxH / 2 - 5, boxX - 5, cy + boxH / 2 - 10, boxX, cy + boxH / 2 - 10] }, { op: 'l', c: [boxX, cy + 5] }, { op: 'c', c: [boxX, cy, boxX, cy, boxX + 5, cy] }, { op: 'z' }]); doc.stroke();
        doc.setFillColor(...ORANGE_BANNER); doc.circle(iconX, cy + boxH / 2, 16, 'F');
        let iconPath = iconType === 'thumbs' ? '/icons/like.png' : (iconType === 'fist' ? '/icons/victory.png' : '/icons/rocket.png');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 8, cy + boxH / 2 - 8, 16, 16); } catch (e) { } } catch (e) { }
        doc.setTextColor(...DARK_TEXT); doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.text(title, boxX + 8, cy + 8);
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.2); doc.text(doc.splitTextToSize(body, boxW - 16), boxX + 8, cy + 13, { lineHeightFactor: 1.15 });
    };

    const hraText = monthlyRent > 0
        ? `In ${displayCity}, your \u20B9${fmt(monthlyRent)}/month rent qualifies you for approx \u20B9${fmt(hraExemption)} monthly HRA exemption. This reduces your taxable income by \u20B9${fmt(hraExemption * 12)} annually. Ensure your HR is applying this correctly.`
        : `If you live in ${displayCity} and pay rent, you can claim significant HRA exemption. Since your rent is currently \u20B90, this benefit is currently unused. If you start paying rent, ensure receipts are submitted to HR for tax savings.`;

    drawCardWithIcon(y, "HRA Strategy:", hraText, "thumbs");

    y += 53;
    const regimeText = `Based on your deductions of \u20B9${fmt(finalDeductions)} (80C, 80D, HRA, Home Loan), the ${recommendedRegime} is ${isOldBetter ? 'superior' : 'competitive'} for your profile. This saves you approximately \u20B9${fmt(annualBenefit)} per year versus the other regime.`;
    drawCardWithIcon(y, "Old vs New Regime:", regimeText, "fist");

    y += 53;
    const actionMsg = isOldBetter
        ? `Email HR this week: 'I want to opt for Old Tax Regime for FY 2025-26. Please share investment declaration form.' Submit rent receipts, rental agreement, and all investment proofs before your company's cut-off date to secure your ₹${fmt(annualBenefit)} savings.`
        : `Email HR this week: 'I want to opt for New Tax Regime for FY 2025-26. It provides more post-tax cashflow for my profile.' You can still submit investments for your own records, but New Regime is simpler and better for you.`;
    drawCardWithIcon(y, "Action Plan:", actionMsg, "rocket");
};

export const renderCard = (doc, y, pageWidth, opts = {}) => {
    const { bg = LIGHT, border = G, h = 20, radius = 2, stroke = false } = opts;
    doc.setFillColor(...bg); doc.roundedRect(14, y, pageWidth - 28, h, radius, radius, stroke ? 'FD' : 'F');
    if (stroke) { doc.setDrawColor(...border); doc.setLineWidth(0.4); doc.roundedRect(14, y, pageWidth - 28, h, radius, radius, 'S'); }
    return y;
};

// PAGE: Executive Summary (page 3 in reference)
export const renderExecutiveSummaryPage = (doc, { name, personalInfo, m, metrics, pageWidth, pageHeight, calculations }) => {
    addHeader(doc, 1, pageWidth);

    doc.setTextColor(...G); doc.setFontSize(22); doc.setFont("Montserrat", "bold");
    doc.text("EXECUTIVE ", 14, 28);
    doc.setTextColor(...DARK); doc.text("SUMMARY", 64, 28);

    // Story Letter
    const storyY = 38;
    doc.setFillColor(248, 250, 252); doc.roundedRect(14, storyY, pageWidth - 28, 65, 3, 3, 'F');
    doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    const story = [
        `Dear ${name}, you just did something that most people your age never do — you stopped to look honestly at your financial life. That single act of courage and curiosity puts you miles ahead of the crowd.`,
        `This report is not a judgement. It is your personal financial mirror — showing you exactly where you stand, why it matters, what to do next, and the science behind every recommendation.`,
        `You have real financial foundations that took discipline. Be proud of each one. Your score today is ${calculations.grades?.overall || 0}/100. By the time you finish this report and take the first three actions, you will already feel more in control.`,
        `Let's build the future you deserve. Together.`
    ];
    let sy = storyY + 8;
    story.forEach(s => {
        const split = doc.splitTextToSize(s, pageWidth - 36);
        doc.text(split, 20, sy);
        sy += (split.length * 4.5) + 3;
    });

    // Strengths Title
    doc.setTextColor(...G); doc.setFontSize(12); doc.setFont("Montserrat", "bold");
    doc.text("What is Working Well For You", 14, 115);
    doc.setDrawColor(...G); doc.setLineWidth(0.5); doc.line(14, 118, 55, 118);

    const strengths = [
        { t: "Lifestyle Discipline", d: `Spending only ${(m.lifestyleRatio || 11).toFixed(0)}% on lifestyle — better than 85% of peers.` },
        { t: "Family Protection", d: "Insuring both yourself and your senior citizen parents. Smart protection." },
        { t: "Asset Creation", d: "Every Home Loan EMI creates real wealth in property while saving tax." },
        { t: "80C Strategy", d: "Excellent 96% utilization of Rs. 1.5L limit through EPF + PPF + Principal." }
    ];

    let gridY = 125;
    strengths.forEach((s, idx) => {
        const gx = 14 + (idx % 2) * (pageWidth / 2 - 10);
        const gy = gridY + Math.floor(idx / 2) * 25;
        doc.setFillColor(255, 255, 255); doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3);
        doc.roundedRect(gx, gy, pageWidth / 2 - 18, 20, 2, 2, 'FD');
        doc.setTextColor(...G); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(s.t, gx + 4, gy + 7);
        doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        doc.text(doc.splitTextToSize(s.d, pageWidth / 2 - 26), gx + 4, gy + 12);
    });

};

export const renderDiagnosisPage = (doc, { name, grades, m, metrics, pageWidth, pageHeight, report, calculations }) => {
    const fmt = v => v ? Math.round(v).toLocaleString('en-IN') : '0';

    // Top Section Branding
    doc.setFillColor(30, 41, 59); doc.roundedRect(14, 12, 28, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 4", 28, 16.5, { align: 'center' });

    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    // Page Title
    doc.setTextColor(6, 150, 104); doc.setFontSize(22); doc.setFont("Montserrat", "bold");
    doc.text("SIX-PARAMETER ", 14, 28);
    const titW = doc.getTextWidth("SIX-PARAMETER ");
    doc.setTextColor(30, 41, 59); doc.setFont("Montserrat", "normal");
    doc.text("COMPREHENSIVE DIAGNOSIS", 14 + titW, 28);

    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const intro = `${name}, we measure financial health across six universal parameters. Think of this as your annual financial health check-up — just like a doctor runs blood tests across multiple parameters to understand your overall health. Here is what each parameter reveals about you.`;
    doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, 34);

    // --- DIAGNOSIS TABLE ---
    const tY = 42;
    const colX = { p: 14, val: 78, bench: 110, diag: 145 };
    const rowH = 14;

    // Header Boxes
    const drH = (x, w, l) => {
        doc.setFillColor(6, 150, 104); doc.roundedRect(x, tY, w, 7, 2, 2, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(l, x + w / 2, tY + 4.5, { align: 'center' });
    };
    drH(colX.p, 62, "Parameter");
    drH(colX.val, 30, "Your Number");
    drH(colX.bench, 33, "Benchmark");
    drH(colX.diag, 51, "Diagnosis & Priority");

    const diag = calculations?.diagnosis || {};
    const isBusiness = calculations?.incomeType === 'business';

    // Priority Colors
    const C = {
        LOW: [6, 150, 104],    // Green
        MEDIUM: [245, 158, 11], // Orange
        HIGH: [245, 158, 11],   // Orange
        URGENT: [249, 115, 22], // Deep Orange
        CRITICAL: [225, 29, 72] // Red
    };

    let items = [];

    if (isBusiness) {
        items = [
            {
                l: "Gross Margin",
                v: diag.grossMargin?.value || "0%",
                s: `(Revenue: ₹${Math.round(metrics.turnover / 12 || 0).toLocaleString()}/mo)`,
                b: diag.grossMargin?.benchmark || "Healthy: >40%",
                d: diag.grossMargin?.status,
                p: diag.grossMargin?.priority,
                c: diag.grossMargin?.color === 'red' ? C.CRITICAL : (diag.grossMargin?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'save'
            },
            {
                l: "Net Profit Margin",
                v: diag.netMargin?.value || "0%",
                s: `(Profit: ₹${Math.round(metrics.monthlyProfit || 0).toLocaleString()}/mo)`,
                b: diag.netMargin?.benchmark || "Target: >15%",
                d: diag.netMargin?.status,
                p: diag.netMargin?.priority,
                c: diag.netMargin?.color === 'red' ? C.CRITICAL : (diag.netMargin?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'wallet'
            },
            {
                l: "Operating Cost",
                v: diag.costExit?.value || "0%",
                s: `Ratio to Sales`,
                b: diag.costExit?.benchmark || "Healthy: <15%",
                d: diag.costExit?.status,
                p: diag.costExit?.priority,
                c: diag.costExit?.color === 'red' ? C.CRITICAL : (diag.costExit?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'bag'
            },
            {
                l: "Debt-to-Revenue",
                v: diag.debt?.value || "0%",
                s: `Annual EMI / Sales`,
                b: diag.debt?.benchmark || "Limit: <10%",
                d: diag.debt?.status,
                p: diag.debt?.priority,
                c: diag.debt?.color === 'red' ? C.CRITICAL : (diag.debt?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'scale'
            },
            {
                l: "Business Reserve",
                v: diag.reserve?.value || "0%",
                s: `Cash Reserves`,
                b: diag.reserve?.benchmark || "Target: 5% Sale",
                d: diag.reserve?.status,
                p: diag.reserve?.priority,
                c: diag.reserve?.color === 'red' ? C.CRITICAL : (diag.reserve?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'cross'
            },
            {
                l: "Liquidity Runway",
                v: diag.runway?.value || "0 Mo",
                s: `Reserve / Outflow`,
                b: diag.runway?.benchmark || "Safe: 6 Months",
                d: diag.runway?.status,
                p: diag.runway?.priority,
                c: diag.runway?.color === 'red' ? C.CRITICAL : (diag.runway?.color === 'orange' ? C.MEDIUM : C.LOW),
                ic: 'heart'
            }
        ];
    } else {
        const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
        const isCouple = report?.personalInfo?.familyType === 'couple';

        if (isCouple) {
            const jointSalary = report?.jointSalary || {};
            const sectionB = jointSalary.sectionB || {};
            const sectionC = jointSalary.sectionC || {};
            const sectionD = jointSalary.sectionD || {};
            const sectionE = jointSalary.sectionE || {};
            const sectionF = jointSalary.sectionF || {};
            const sectionG = jointSalary.sectionG || {};

            const safeParse = (v) => {
                if (typeof v === "number") return v;
                if (!v) return 0;
                return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0;
            };

            const hSalary = safeParse(sectionB.husbandSalary) || 0;
            const wSalary = safeParse(sectionB.wifeSalary) || 0;
            const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
            const wOther = safeParse(sectionB.wifeOtherIncome) || 0;

            const hTotal = hSalary + hOther;
            const wTotal = wSalary + wOther;
            const combinedMonthly = hTotal + wTotal;
            const combinedAnnual = combinedMonthly * 12;

            const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
            const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
            const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;

            const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
            const transport = (safeParse(sectionC.transport) || 0) + cl;
            const dining = safeParse(sectionC.dining) || 0;
            const shopping = safeParse(sectionC.shopping) || 0;

            const insurance = (safeParse(sectionC.healthInsurance) || 0) +
                (safeParse(sectionE.husbandLifePremium) || 0) +
                (safeParse(sectionE.wifeLifePremium) || 0) +
                (safeParse(sectionE.husbandHealthPremium) || 0) +
                (safeParse(sectionE.wifeHealthPremium) || 0);

            const investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
                (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
                (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
                (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
                (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
                (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
                (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

            const srVal = combinedMonthly > 0 ? (investments / combinedMonthly) * 100 : 0;
            const calcEssential = groceriesUtilities + (safeParse(sectionC.transport) || 0) + insurance + hl;
            const eePct = combinedMonthly > 0 ? (calcEssential / combinedMonthly) * 100 : 0;
            const lifestyleSpending = dining + shopping;
            const lsPct = combinedMonthly > 0 ? (lifestyleSpending / combinedMonthly) * 100 : 0;
            const totalEMIs = hl + pl + cl;
            const dtiVal = combinedMonthly > 0 ? (totalEMIs / combinedMonthly) * 100 : 0;

            const emActual = safeParse(sectionG.emergencyFund) || 0;
            const jointExpenses = groceriesUtilities + (safeParse(sectionC.transport) || 0) + dining + shopping + insurance + hl + pl + cl;
            const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
            const emTarget = monthlyExpenses * 6;
            const efPctFunded = emTarget > 0 ? (emActual / emTarget) * 100 : 0;

            const lifeActual = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
            const lifeTarget = Math.max(25000000, combinedAnnual * 10);

            const fmtK = (val) => {
                if (!val) return "0";
                if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
                if (val >= 1000) return `${Math.round(val / 1000)}k`;
                return val.toString();
            };

            const fmtL = (val) => {
                if (!val) return "0";
                if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
                if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
                if (val >= 1000) return `${Math.round(val / 1000)}k`;
                return val.toString();
            };

            const getCouplePriority = (cat, val) => {
                if (cat === 'sr') return val >= 20 ? 'LOW' : (val >= 10 ? 'MEDIUM' : 'URGENT');
                if (cat === 'ee') return val <= 50 ? 'LOW' : (val <= 60 ? 'MEDIUM' : 'URGENT');
                if (cat === 'ls') return val <= 15 ? 'LOW' : (val <= 20 ? 'MEDIUM' : 'URGENT');
                if (cat === 'dti') return val <= 35 ? 'MEDIUM' : 'URGENT';
                if (cat === 'ef') return val >= 100 ? 'LOW' : (val >= 50 ? 'MEDIUM' : 'URGENT');
                if (cat === 'li') return val >= 100 ? 'LOW' : (val >= 50 ? 'MEDIUM' : 'CRITICAL');
                return 'LOW';
            };

            const getCoupleDiagnosis = (cat, val) => {
                if (cat === 'sr') return val >= 20 ? 'Excellent' : (val >= 10 ? 'Healthy' : 'Critical');
                if (cat === 'ee') return val <= 50 ? 'Healthy' : (val <= 60 ? 'Manageable' : 'Critical');
                if (cat === 'ls') return val <= 15 ? 'Excellent' : (val <= 20 ? 'Healthy' : 'Critical');
                if (cat === 'dti') return val === 0 ? 'Zero Debt' : (val <= 35 ? 'Manageable' : 'Critical');
                if (cat === 'ef') return val >= 100 ? 'Healthy' : (val >= 50 ? 'Manageable' : 'Critical');
                if (cat === 'li') return val >= 100 ? 'Healthy' : (val >= 50 ? 'Inadequate' : 'Critical');
                return 'Healthy';
            };

            const srP = getCouplePriority('sr', srVal);
            const eeP = getCouplePriority('ee', eePct);
            const lsP = getCouplePriority('ls', lsPct);
            const dtiP = getCouplePriority('dti', dtiVal);
            const efP = getCouplePriority('ef', efPctFunded);
            const liP = getCouplePriority('li', (lifeActual / (lifeTarget || 1)) * 100);

            const srD = getCoupleDiagnosis('sr', srVal);
            const eeD = getCoupleDiagnosis('ee', eePct);
            const lsD = getCoupleDiagnosis('ls', lsPct);
            const dtiD = getCoupleDiagnosis('dti', dtiVal);
            const efD = getCoupleDiagnosis('ef', efPctFunded);
            const liD = getCoupleDiagnosis('li', (lifeActual / (lifeTarget || 1)) * 100);

            items = [
                {
                    l: "Combined Savings Rate",
                    v: `${srVal.toFixed(1)}%`,
                    s: `(₹${fmtK(investments)} of ₹${fmtL(combinedMonthly)})`,
                    b: "Target:\n>20%",
                    d: srD,
                    p: srP,
                    c: C[srP] || C.LOW,
                    ic: 'save'
                },
                {
                    l: "Essential Expenses Ratio",
                    v: `${eePct.toFixed(0)}%`,
                    s: `(₹${fmtK(calcEssential)}/₹${fmtL(combinedMonthly)})`,
                    b: "Healthy:\n<50%",
                    d: eeD,
                    p: eeP,
                    c: C[eeP] || C.LOW,
                    ic: 'wallet'
                },
                {
                    l: "Lifestyle Spending",
                    v: `${lsPct.toFixed(0)}%`,
                    s: `(₹${fmtK(lifestyleSpending)}/₹${fmtL(combinedMonthly)})`,
                    b: "Healthy:\n10-15%",
                    d: lsD,
                    p: lsP,
                    c: C[lsP] || C.LOW,
                    ic: 'bag'
                },
                {
                    l: "Joint Debt-to-Income (DTI)",
                    v: `${dtiVal.toFixed(1)}%`,
                    s: `(₹${fmtK(totalEMIs)}/₹${fmtL(combinedMonthly)})`,
                    b: "Limit:\n<35%",
                    d: dtiD,
                    p: dtiP,
                    c: C[dtiP] || C.LOW,
                    ic: 'scale'
                },
                {
                    l: "Joint Emergency Fund",
                    v: `₹${fmt(emActual)}`,
                    s: "",
                    b: `₹${fmtL(emTarget)}\n(6 months)`,
                    d: efD,
                    p: efP,
                    c: C[efP] || C.LOW,
                    ic: 'cross'
                },
                {
                    l: "Joint Life Insurance",
                    v: `₹${fmt(lifeActual)}`,
                    s: "",
                    b: `₹${fmtL(lifeTarget)}`,
                    d: liD,
                    p: liP,
                    c: C[liP] || C.LOW,
                    ic: 'heart'
                }
            ];
        } else {
            const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
            const sExp = (isBiz ? report?.businessExpenses : report?.salaryExpenses) || {};
            const section3 = report?.section3 || {};

            const hl = safeParse(section3.homeLoanEMI) || (safeParse(sInc.homeLoanEMI) || 0);
            const rt = (safeParse(sInc.rentEMI) || 0);
            const lv = (safeParse(sInc.societyMaintenance) || 0) + (safeParse(sInc.propertyTax) || 0);
            const gr = (safeParse(sExp.groceries) || 0) + (safeParse(sExp.vegetables) || 0) + (safeParse(sExp.dairyProducts) || 0) + (safeParse(sExp.householdSupplies) || 0);
            const medical = (safeParse(sExp.doctorConsultations) || 0) + (safeParse(sExp.medicines) || 0) + (safeParse(sExp.diagnosticTests) || 0) + (safeParse(sExp.dentalCare) || 0) + (safeParse(sExp.eyeCare) || 0);
            const education = (safeParse(sExp.schoolFees) || 0) + (safeParse(sExp.booksStationery) || 0) + (safeParse(sExp.tuitionClasses) || 0) + (safeParse(sExp.extracurricular) || 0);
            const ut = (safeParse(sInc.electricity) || 0) + (safeParse(sInc.water) || 0) + (safeParse(sInc.gas) || 0) + (safeParse(sInc.internet) || 0) + (safeParse(sInc.broadband) || 0) + (safeParse(sInc.ottSubscriptions) || 0) + (safeParse(sInc.cable) || 0) + medical + education;
            const tr = (safeParse(sExp.fuelDiesel) || 0) + (safeParse(sExp.publicTransport) || 0) + (safeParse(sExp.taxiCabUber) || 0) + (safeParse(sExp.vehicleMaintenance) || 0) + (safeParse(sExp.parkingCharges) || 0) + (safeParse(sExp.tollCharges) || 0);

            const in_ = (safeParse(sInc.lifeInsurance) || 0) + (safeParse(sInc.healthInsurance) || 0) + (safeParse(sInc.criticalIllness) || 0) + (safeParse(sInc.motorInsurance) || 0) + (safeParse(sInc.homeInsurance) || 0);

            const calcEssential = (hl || rt) + lv + gr + ut + tr + in_;
            const totalInc = metrics?.totalMonthlyIncome || safeParse(sInc.monthlySalary) || 1;
            const calcEssentialPct = ((calcEssential / totalInc) * 100).toFixed(1) + "%";

            items = [
                {
                    l: "Savings Rate",
                    v: diag.sr?.value || "0%",
                    s: `(₹${Math.round(metrics.totalMonthlyInvestments || 0).toLocaleString()}/mo)`,
                    b: diag.sr?.benchmark || "",
                    d: diag.sr?.status,
                    p: diag.sr?.priority,
                    c: C[diag.sr?.priority] || C.LOW,
                    ic: 'save'
                },
                {
                    l: "Essential Expenses",
                    v: calcEssential > 0 ? calcEssentialPct : (diag.eir?.value || "0%"),
                    s: `(₹${Math.round(calcEssential > 0 ? calcEssential : (metrics.totalEssentialExpenses || 0)).toLocaleString()}/mo)`,
                    b: diag.eir?.benchmark || "",
                    d: diag.eir?.status,
                    p: diag.eir?.priority,
                    c: C[diag.eir?.priority] || C.LOW,
                    ic: 'wallet'
                },
                {
                    l: "Lifestyle Spending",
                    v: diag.lifestyle?.value || "0%",
                    s: `(₹${Math.round(metrics.totalLifestyleExpenses || 0).toLocaleString()}/mo)`,
                    b: diag.lifestyle?.benchmark || "",
                    d: diag.lifestyle?.status,
                    p: diag.lifestyle?.priority,
                    c: C[diag.lifestyle?.priority] || C.LOW,
                    ic: 'bag'
                },
                {
                    l: "Debt-to-Income",
                    v: diag.dti?.value || "0%",
                    s: `(₹${Math.round(metrics.totalEMI || 0).toLocaleString()} EMIs)`,
                    b: diag.dti?.benchmark || "",
                    d: diag.dti?.status,
                    p: diag.dti?.priority,
                    c: C[diag.dti?.priority] || C.LOW,
                    ic: 'scale'
                },
                {
                    l: "Emergency Fund",
                    v: diag.efc?.value || "0 Mo",
                    s: `(${metrics.emergencyFundRatio?.toFixed(0)}% Funded)`,
                    b: diag.efc?.benchmark || "",
                    d: diag.efc?.status,
                    p: diag.efc?.priority,
                    c: C[diag.efc?.priority] || C.LOW,
                    ic: 'cross'
                },
                {
                    l: "Life Insurance",
                    v: `₹${(metrics.currentLifeCover || 0).toLocaleString()}`,
                    s: `(${diag.ica?.value || "0.0x"} Coverage)`,
                    b: diag.ica?.benchmark || "",
                    d: diag.ica?.status,
                    p: diag.ica?.priority,
                    c: C[diag.ica?.priority] || C.LOW,
                    ic: 'heart'
                }
            ];
        }
    }

    let ry = tY + 9;
    items.forEach(item => {
        const mid = ry + rowH / 2;
        const ix = 20, iy = mid;
        doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.1); doc.line(76, ry, 76, ry + rowH);

        let iconPath = null;
        if (item.ic === 'save') iconPath = '/icons/rupee.png';
        else if (item.ic === 'wallet') iconPath = '/icons/expenses.png';
        else if (item.ic === 'bag') iconPath = '/icons/shopping-cart.png';
        else if (item.ic === 'scale') iconPath = '/icons/balance.png';
        else if (item.ic === 'cross') iconPath = '/icons/medical-support.png';
        else if (item.ic === 'heart') iconPath = '/icons/insurance.png';

        if (iconPath) {
            try { try { doc.addImage(iconPath, 'PNG', ix - 3.5, iy - 3.5, 7, 7); } catch (e) { } } catch (e) { }
        }

        doc.setTextColor(6, 150, 104); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(item.l || "", 28, mid + 1);

        doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.text(item.v || "", colX.val + 15, mid - 1, { align: 'center' });
        doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
        doc.text(item.s || "", colX.val + 15, mid + 2.5, { align: 'center' });
        const bLines = (item.b || "").split('\n');
        doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const bY = bLines.length > 2 ? mid - 2 : (bLines.length > 1 ? mid - 1 : mid + 1);
        doc.text(bLines, colX.bench + 16.5, bY, { align: 'center', lineHeightFactor: 1.15 });

        doc.setDrawColor(...(item.c || [0, 0, 0])); doc.setLineWidth(0.4);
        // Diagnosis (Hollow Box)
        doc.roundedRect(colX.diag + 1.5, mid - 5, 26, 10, 2, 2, 'S');
        doc.setTextColor(...(item.c || [0, 0, 0])); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(item.d || "", colX.diag + 14.5, mid + 1.2, { align: 'center' });

        // Priority (Solid Box)
        doc.setFillColor(...(item.c || [0, 0, 0]));
        doc.roundedRect(colX.diag + 29.5, mid - 5, 20, 10, 2, 2, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(item.p || "", colX.diag + 39.5, mid + 1.4, { align: 'center' });
        ry += rowH;
    });

    // --- REPORT CARD SECTION ---
    const rcY = ry + 6; // Reduced from 10
    doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.5); doc.line(14, rcY, pageWidth - 14, rcY);
    doc.setFillColor(255, 255, 255); doc.rect(14, rcY - 4, 85, 8, 'F');
    doc.setTextColor(6, 150, 104); doc.setFontSize(16); doc.setFont("Montserrat", "bold");
    doc.text("Your Financial Report Card", 14, rcY + 2);

    const mnX = pageWidth / 2, mnY = rcY + 16; // Reduced from 20
    doc.setFillColor(6, 150, 104); doc.circle(mnX, mnY, 14, 'F');
    try { try { doc.addImage('/icons/rupee (1).png', 'PNG', mnX - 5, mnY - 9, 10, 10); } catch (e) { } } catch (e) { }
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text("Financial\nArea", mnX, mnY + 8, { align: 'center' });

    const fmtK = (val) => {
        if (!val) return "0";
        if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    const fmtL = (val) => {
        if (!val) return "0";
        if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
        if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    const isCouple = report?.personalInfo?.familyType === 'couple';
    let coupleCards = [];

    if (isCouple) {
        const jointSalary = report?.jointSalary || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        const safeParse = (v) => {
            if (typeof v === "number") return v;
            if (!v) return 0;
            return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0;
        };

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const combinedMonthly = hSalary + wSalary + hOther + wOther;
        const combinedAnnual = combinedMonthly * 12;

        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;

        const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
        const transport = (safeParse(sectionC.transport) || 0) + cl;
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;
        const insurance = (safeParse(sectionC.healthInsurance) || 0) +
            (safeParse(sectionE.husbandLifePremium) || 0) +
            (safeParse(sectionE.wifeLifePremium) || 0) +
            (safeParse(sectionE.husbandHealthPremium) || 0) +
            (safeParse(sectionE.wifeHealthPremium) || 0);

        const investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
            (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
            (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
            (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
            (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
            (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
            (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

        const srVal = combinedMonthly > 0 ? (investments / combinedMonthly) * 100 : 0;
        const totalEMIs = hl + pl + cl;
        const dtiVal = combinedMonthly > 0 ? (totalEMIs / combinedMonthly) * 100 : 0;

        const emActual = safeParse(sectionG.emergencyFund) || 0;
        const jointExpenses = groceriesUtilities + (safeParse(sectionC.transport) || 0) + dining + shopping + insurance + hl + pl + cl;
        const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
        const efMonths = monthlyExpenses > 0 ? emActual / monthlyExpenses : 0;

        const lifeActual = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
        const icaMultiVal = combinedAnnual > 0 ? (lifeActual / combinedAnnual) : 0;

        const srGoalText = srVal >= 20 ? " fulfills benchmark." : " is below target.";
        const srColor = srVal >= 20 ? [6, 150, 104] : [222, 59, 51];

        coupleCards = [
            {
                t: "Savings Rate", g: grades.savings || "F", s: `${Math.round(diag.sr?.score || 0)}/100`, d: [
                    { text: "Savings Rate: " }, { text: `${srVal.toFixed(1)}%`, color: srColor },
                    { text: srGoalText },
                    { text: "\nGoal: Reach A-grade savings." }
                ]
            },
            {
                t: "Tax Efficiency", g: grades.tax || "F", s: `${Math.round(diag.tes?.score || 0)}/100`,
                d: `Using tax optimizations. Score based on 80C, 80D and HRA utilization. Current: ${metrics.tesPercent?.toFixed(0) || 0}%.`
            },
            {
                t: "Life Insurance", g: grades.insurance || "F", s: `${Math.round(diag.ica?.score || 0)}/100`,
                d: `Insurance cover is ${icaMultiVal.toFixed(1)}x of income. Ideal target is 10-15x to ensure family safety.`
            },
            {
                t: "Debt Management", g: grades.debt || "A", s: `${Math.round(diag.dti?.score || 0)}/100`,
                d: `Debt level is ${dtiVal.toFixed(0)}% DTI. Manageable range but room for optimization.`
            },
            {
                t: "Retirement Planning", g: grades.retirement || "F", s: `${Math.round(diag.rrs?.score || 0)}/100`,
                d: `Retirement progress tracking at ${metrics.rrsScore?.toFixed(0) || 0}%. Compounding will boost this significantly.`
            },
            {
                t: "Emergency Preparedness", g: grades.emergency || "F", s: `${Math.round(diag.efc?.score || 0)}/100`,
                d: `Currently provides ${efMonths.toFixed(1)} months of coverage. Target: 6 months for stability.`
            }
        ];
    }

    const cards = isCouple ? coupleCards : [
        {
            t: "Savings Rate", g: grades.savings || "C", s: `${Math.round(diag.sr?.score || 0)}/100`, d: [
                { text: "Savings Rate: " }, { text: `${metrics.savingsRate?.toFixed(1)}%`, color: metrics.savingsRate >= 25 ? [6, 150, 104] : [222, 59, 51] },
                { text: metrics.savingsRate >= 25 ? " fulfills benchmark." : " is below target." },
                { text: "\nGoal: Reach A-grade savings." }
            ]
        },
        {
            t: "Tax Efficiency", g: grades.tax || "B", s: `${Math.round(diag.tes?.score || 0)}/100`,
            d: `Using tax optimizations. Score based on 80C, 80D and HRA utilization. Current: ${metrics.tesPercent?.toFixed(0)}%.`
        },
        {
            t: "Life Insurance", g: grades.insurance || "D", s: `${Math.round(diag.ica?.score || 0)}/100`,
            d: `Insurance cover is ${metrics.icaMulti?.toFixed(1)}x of income. Ideal target is 10-15x to ensure family safety.`
        },
        {
            t: "Debt Management", g: grades.debt || "B", s: `${Math.round(diag.dti?.score || 0)}/100`,
            d: `Debt level is ${metrics.dtiRatio?.toFixed(0)}% DTI. Manageable range but room for optimization via prepayment.`
        },
        {
            t: "Retirement Planning", g: grades.retirement || "C", s: `${Math.round(diag.rrs?.score || 0)}/100`,
            d: `Retirement progress tracking at ${metrics.rrsScore?.toFixed(0)}%. Compounding will boost this significantly.`
        },
        {
            t: "Emergency Preparedness", g: grades.emergency || "D", s: `${Math.round(diag.efc?.score || 0)}/100`,
            d: `Currently provides ${metrics.efcMonths?.toFixed(1)} months of coverage. Target: 6 months for stability.`
        }
    ];

    addFooter(doc, "04", name, pageWidth, pageHeight);

    const cy = mnY + 24; // Reduced from 28
    cards.forEach((c, idx) => {
        const x = 14.5 + (idx * 30.2); // Recalibrated step for 210mm page width
        const cW = 27; // Slightly narrower cards to stay in margins

        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3); doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, cy, cW, 65, 5, 5, 'FD');
        doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.4);
        doc.moveTo(mnX, mnY + 14);
        doc.curveTo(mnX, cy - 10, x + cW / 2, cy - 10, x + cW / 2, cy); doc.stroke();
        doc.setFillColor(30, 41, 59); doc.circle(x + cW / 2, cy, 1.2, 'F');

        doc.setTextColor(6, 150, 104); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(doc.splitTextToSize(c.t, cW - 4.5), x + 3, cy + 7);

        const py = cy + 15, pw = 23, ph = 7;
        doc.setFillColor(30, 41, 59); doc.roundedRect(x + 2, py, pw, ph, 3.5, 3.5, 'F');
        doc.setFillColor(6, 150, 104); doc.circle(x + 5.5, py + 3.5, 4.5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text(c.g, x + 3.8, py + 4.8);
        doc.setFontSize(6); doc.text("Score", x + 11.5, py + 2.5);
        doc.setFontSize(8); doc.text(c.s, x + 11.5, py + 5.8);

        let by = cy + 26;
        doc.setFontSize(7.5); doc.setFont("Montserrat", "normal");
        if (Array.isArray(c.d)) {
            let tx = x + 1.5;
            c.d.forEach(seg => {
                const words = seg.text.split(' ');
                words.forEach((word, wIdx) => {
                    const wordW = doc.getTextWidth(word + (wIdx < words.length - 1 ? ' ' : ''));
                    if (word.includes('\n') || tx + wordW > x + cW - 1.5) {
                        by += 4.2; tx = x + 1.5;
                        if (word.includes('\n')) word = word.replace('\n', '');
                    }
                    doc.setTextColor(...(seg.color || [0, 0, 0]));
                    doc.setFont("Montserrat", seg.color ? "bold" : "normal");
                    doc.text(word + ' ', tx, by);
                    tx += wordW;
                });
            });
        } else {
            doc.setTextColor(0, 0, 0);
            doc.text(doc.splitTextToSize(c.d, cW - 5.5), x + 3, by);
        }
    });

    const sbY = cy + 68; // Reduced from 70
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, sbY, 45, 8, 4, 4, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.text(`OVERALL SCORE ${grades.overall || 0}/100`, 17, sbY + 5.8);
    doc.setFillColor(240, 244, 248); doc.rect(62, sbY, 15, 8, 'F');
    doc.setTextColor(0, 0, 0); doc.setFontSize(8.5); doc.text(`${grades.overall || 0}%`, 69.5, sbY + 5.8, { align: 'center' });

    const currentYear = new Date().getFullYear();
    const score = grades.overall || 0;
    let projYears = 5;
    if (score > 80) projYears = 1;
    else if (score > 60) projYears = 2;
    else if (score > 40) projYears = 3;
    else if (score > 20) projYears = 4;

    const targetYear = currentYear + projYears;

    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text(`SOLID FOUNDATION: A-GRADE FINANCIAL LIFE IS ${projYears}`, 82, sbY + 4);
    doc.text(`FOCUSED YEARS AWAY (Projected Target: ${targetYear})`, 82, sbY + 8);

    doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    const footerTxt = `${grades.overall || 46}/100 is an honest score, ${name} — a starting point. The chart above shows how close you are to health. The gap is not years of sacrifice, but a few smart decisions.`;
    doc.text(doc.splitTextToSize(footerTxt, pageWidth - 28), 14, sbY + 16);
};

export const renderStrengthsPage1 = (doc, { name, m, metrics, pageWidth, pageHeight, personalInfo = {}, calculations = {}, report = {} }) => {
    const isCouple = personalInfo?.familyType === 'couple' || report?.personalInfo?.familyType === 'couple';
    if (isCouple) {
        const jointSalary = report?.jointSalary || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionE = jointSalary.sectionE || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        const combinedMonthly = hTotal + wTotal;

        const isDual = hTotal > 0 && wTotal > 0;
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;
        const lifestyleSpending = dining + shopping;
        const lsPct = combinedMonthly > 0 ? (lifestyleSpending / combinedMonthly) * 100 : 0;

        const homeLoanEMI = safeParse(sectionC.homeLoanEMI) || safeParse(sectionF.homeLoanEMI) || 0;
        const homeLoanOutstanding = safeParse(sectionF.homeLoanOutstanding) || 0;
        const hasHomeLoan = homeLoanEMI > 0;

        let totalHomeDeduction = 0;
        if (hasHomeLoan) {
            const principalRatio = homeLoanOutstanding > 0 ? Math.min(0.55, Math.max(0.35, (homeLoanEMI * 12) / (homeLoanOutstanding + homeLoanEMI * 120))) : 0.5;
            const annualPrincipal = homeLoanEMI * 12 * principalRatio;
            const section24Interest = Math.min(400000, homeLoanEMI * 12 * (1 - principalRatio));
            const section80CPrincipal = Math.min(300000, annualPrincipal);
            totalHomeDeduction = section24Interest + section80CPrincipal;
        }

        const ccOS = safeParse(sectionF.creditCardOutstanding) || 0;
        const ccEMI = safeParse(sectionC.creditCardEMI) || safeParse(sectionF.creditCardEMI) || 0;
        const hasNoCCDebt = ccOS === 0 && ccEMI === 0;

        const hHealthCover = safeParse(sectionE.husbandHealthCover) || 0;
        const wHealthCover = safeParse(sectionE.wifeHealthCover) || 0;
        const combinedCover = hHealthCover + wHealthCover;

        // Top Section Branding
        doc.setFillColor(30, 41, 59); doc.roundedRect(14, 12, 28, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("SECTION 5", 28, 16.5, { align: 'center' });
        try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

        doc.setTextColor(6, 150, 104); doc.setFontSize(20); doc.setFont("Montserrat", "bold");
        const t1 = "WHAT IS WORKING"; doc.text(t1, 14, 32);
        const tw1 = doc.getTextWidth(t1);
        doc.setTextColor(30, 41, 59); doc.text("WELL FOR YOU", 14 + tw1 + 2, 32);

        doc.setTextColor(17, 24, 39); doc.setFontSize(9.5); doc.setFont("Montserrat", "normal");
        const namesSplit = (name || "Valued Client").split('&');
        const hFirstName = namesSplit[0] ? namesSplit[0].trim().split(' ')[0] : "Client";
        const wFirstName = namesSplit[1] ? namesSplit[1].trim().split(' ')[0] : "Spouse";
        const firstName = `${hFirstName} & ${wFirstName}`;
        const introStr = firstName + ", before we talk about improvements, we want you to truly appreciate what you have already built. These are not small wins — they are real financial foundations that took discipline and good decision-making. Be proud of each one.";
        doc.text(doc.splitTextToSize(introStr, pageWidth - 28), 14, 38);

        // Render Strengths 1, 2, 3, 4, 5 Cards on a single page
        let currentY = 52;
        const renderCard = (num, title, body, iconPath) => {
            // Card outline
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.4);
            doc.roundedRect(14, currentY, 182, 37, 4, 4, 'FD');

            // Icon Circle
            doc.setFillColor(209, 250, 229);
            const cx = 32;
            const cy = currentY + 18.5;
            doc.circle(cx, cy, 10, 'F');
            try { doc.addImage(iconPath, 'PNG', cx - 6, cy - 6, 12, 12); } catch (e) { }

            // Title
            doc.setTextColor(17, 24, 39);
            doc.setFont("Montserrat", "bold");
            doc.setFontSize(11);
            doc.text(`${num}. ${title}`, 50, currentY + 12);

            // Body
            doc.setTextColor(31, 41, 55);
            doc.setFont("Montserrat", "normal");
            doc.setFontSize(9.2);
            const splitLines = doc.splitTextToSize(body, 138);
            doc.text(splitLines, 50, currentY + 18, { lineHeightFactor: 1.2 });

            currentY += 42;
        };

        // Strength 1
        const s1Title = isDual ? "Dual Income Stability" : "Single Income Focus";
        const s1Body = isDual
            ? `Two earners mean one job loss does not break you. Both of you are actively contributing to the household income (Husband: ₹${hTotal.toLocaleString('en-IN')}/mo, Wife: ₹${wTotal.toLocaleString('en-IN')}/mo), providing a highly resilient financial safety net.`
            : `A steady income source of ₹${(hTotal + wTotal).toLocaleString('en-IN')}/month is the foundation of your plan. Focusing on career growth and building a robust emergency fund is key to backing up this single engine.`;
        renderCard(1, s1Title, s1Body, '/icons/partnerships.png');

        // Strength 2
        const s2Title = lsPct <= 20 ? `Low Lifestyle Spending (${Math.round(lsPct)}%)` : `Lifestyle Spending Control (${Math.round(lsPct)}%)`;
        const s2Body = lsPct <= 20
            ? `You enjoy life without overspending. Keeping combined lifestyle spending at ${lsPct.toFixed(1)}% of your income shows exceptional financial discipline, allowing you to route more towards wealth creation.`
            : `Your combined lifestyle spending is ${lsPct.toFixed(1)}% of income. Aligning this closer to the 10-15% benchmark will free up significant capital for your long-term goals.`;
        renderCard(2, s2Title, s2Body, '/icons/piggy-bank.png');

        // Strength 3
        const s3Title = hasHomeLoan ? "Home Loan as Wealth Tool" : "Zero Home Loan Liabilities";
        const s3Body = hasHomeLoan
            ? `₹${(homeLoanEMI / 1000).toFixed(0)}k EMI builds equity + joint tax deduction (₹${(totalHomeDeduction / 100000).toFixed(1)}L/year combined). Your home loan EMI is not just an expense; it is forced savings building real estate equity.`
            : `You enjoy the ultimate debt freedom with zero home loan liabilities. This gives you a clean slate and massive capacity to redirect your monthly surplus directly into high-growth wealth creators.`;
        renderCard(3, s3Title, s3Body, '/icons/home copy.png');

        // Strength 4
        const s4Title = hasNoCCDebt ? "No Credit Card Debt" : "Credit Card Action Plan";
        const s4Body = hasNoCCDebt
            ? "You carry no high-interest revolving debt. Excellent. By keeping your short-term liabilities at zero, you avoid paying 36-42% interest rates that destroy wealth."
            : `You have ₹${ccOS.toLocaleString('en-IN')} outstanding card dues. Resolving this high-interest debt quickly is a high priority to protect your monthly savings.`;
        renderCard(4, s4Title, s4Body, '/icons/rupees.png');

        // Strength 5
        const s5Title = combinedCover > 0 ? "Employer Health Insurance" : "Family Health Protection";
        const s5Body = combinedCover > 0
            ? `₹${(combinedCover / 100000).toFixed(0)}L combined base cover (needs family floater top-up). Having employer-sponsored health insurance protects your hard-earned savings from unexpected medical emergencies.`
            : "Active health insurance plan is running to shield family savings from unexpected hospitalisation costs. Securing a base cover is a smart protective decision.";
        renderCard(5, s5Title, s5Body, '/icons/insurance copy.png');

        return;
    }

    // Top Section Branding
    doc.setFillColor(30, 41, 59); doc.roundedRect(14, 12, 28, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 5", 28, 16.5, { align: 'center' });
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    // Page Title - moved up to avoid overlap with section badge
    doc.setTextColor(6, 150, 104); doc.setFontSize(20); doc.setFont("Montserrat", "bold");
    const t1 = "WHAT IS WORKING"; doc.text(t1, 14, 32);
    const tw1 = doc.getTextWidth(t1);
    doc.setTextColor(30, 41, 59); doc.text("WELL FOR YOU", 14 + tw1 + 2, 32);

    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const firstName = (name || "Valued Client").split(' ')[0];
    const introStr = firstName + ", before we talk about improvements, we want you to truly appreciate what you have already built. These are not small wins — they are real financial foundations that took discipline and good decision-making. Be proud of each one.";
    doc.text(doc.splitTextToSize(introStr, pageWidth - 28), 14, 38);

    // --- Dynamic Strength Selection ---
    const diag = calculations?.diagnosis || {};
    // Robust value extraction helpers
    const getSR = () => safeParse(metrics.savingsRate) || safeParse(diag.sr?.value) || safeParse(diag.savingsRate) || 0;
    const getLR = () => safeParse(metrics.lifestyleRatio) || safeParse(diag.lifestyle?.value) || 0;
    const getDTI = () => safeParse(metrics.dtiRatio) || safeParse(diag.dti?.value) || 0;
    const getTES = () => safeParse(metrics.tesPercent) || safeParse(diag.tes?.value) || 0;

    const strengths = [
        {
            id: 'lifestyle',
            score: diag.lifestyle?.score || 0,
            label: 'Lifestyle Spending Discipline',
            val: getLR(),
            unit: '%',
            type: 'lowIsGood',
            target: '10-15%',
            max: 40,
            title: 'Outstanding Lifestyle Spending Discipline',
            desc: (v) => v < 1 ? "Your lifestyle spending is near zero — exceptional financial discipline! Most professionals spend 20-30%. Almost all your income goes toward essentials and savings." :
                v <= 15 ? `Your lifestyle spending sits at just ${v.toFixed(1)}% of income — squarely within the healthy 10-15% range. This is actually rare and impressive.` :
                    `Your lifestyle spending is at ${v.toFixed(1)}% of income. Keeping this under control is key to your long-term wealth building strategy.`,
            bullets: [
                "Lifestyle inflation is the #1 destroyer of wealth among professionals.",
                "You have resisted it successfully.",
                "This discipline is worth more than any investment tip."
            ],
            science: "Studies show people who maintain lifestyle spending below 15% are 3x more likely to achieve financial independence before 60."
        },
        {
            id: 'savings',
            score: diag.sr?.score || 0,
            label: 'Savings Rate Efficiency',
            val: getSR(),
            unit: '%',
            type: 'highIsGood',
            target: '>25%',
            max: 50,
            title: 'Exceptional Wealth Building Momentum',
            desc: (v) => v >= 25 ? `You are saving ${v.toFixed(1)}% of your income! This puts you in the top 1% of disciplined earners. You are not just earning; you are building an empire.` :
                `You are saving ${v.toFixed(1)}% of your income. Every rupee you save today is working for your future self with compounding interest.`,
            bullets: [
                "The 'Rich' earn money. The 'Wealthy' keep it. Your savings rate is your primary wealth engine.",
                "High savings early on creates a massive 'compounding snowball' effect.",
                "You have prioritized your future self over current desires."
            ],
            science: "A 25% savings rate means for every 3 years worked, you can fund 1 year of retirement. You are accelerating your path to freedom."
        },
        {
            id: 'debt',
            score: diag.dti?.score || 0,
            label: 'Debt-Free Discipline',
            val: getDTI(),
            unit: '%',
            type: 'lowIsGood',
            target: '<35%',
            max: 50,
            title: 'Remarkable Debt Management Discipline',
            desc: (v) => v === 0 ? "You are completely debt-free (EMI-free)! This gives you the ultimate financial freedom and choice. You don't work for a bank; you work for yourself." :
                `Your debt-to-income ratio is at ${v.toFixed(1)}%. You have kept your liabilities low, ensuring they don't eat into your future wealth.`,
            bullets: [
                "Interest you pay is a tax on your future. Interest you earn is a gift to it.",
                "Being EMI-free (or low debt) significantly reduces your financial stress levels.",
                "You have avoided the 'Debt Trap' that many professionals fall into."
            ],
            science: "High debt obligations limit your ability to pivot in career or life. Low debt means high optionality and faster wealth accumulation."
        },
        {
            id: 'tax',
            score: diag.tes?.score || 0,
            label: 'Tax Strategy Optimization',
            val: getTES(),
            unit: '%',
            type: 'highIsGood',
            target: '>80%',
            max: 100,
            title: 'Smart Tax Optimization Strategy',
            desc: (v) => `You have optimized ${v.toFixed(1)}% of your available tax-saving limits. You are legally and intelligently keeping more of what you earn.`,
            bullets: [
                "Taxes are often the single largest expense in a professional's life.",
                "By optimizing 80C and 80D, you are effectively getting an instant return on investment.",
                "You have shown great awareness of the legal benefits available to you."
            ],
            science: "Saving ₹1.5L in 80C every year for 20 years (at 12% returns) creates a corpus of ~₹1.2 Crores. Tax efficiency is wealth building."
        }
    ];

    const prefOrder = { savings: 4, lifestyle: 3, tax: 2, debt: 1 };
    const bestHero = strengths
        .sort((a, b) => b.score - a.score || (prefOrder[b.id] - prefOrder[a.id]))[0];

    let y = 48;
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text(`STRENGTH 1: ${bestHero.title}`, 18, y + 5.5);

    y += 13;
    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    doc.text(doc.splitTextToSize(bestHero.desc(bestHero.val), pageWidth - 28), 14, y);

    // --- Dynamic Strength Gauge ---
    y += 12;
    const gx = 65, gy = y + 42, rOut = 40, rIn = 25, rRail = 49;
    const gaugeMaxVal = bestHero.max;
    const isLIGuard = bestHero.type === 'lowIsGood';
    const segs = [
        [0, 20, isLIGuard ? [22, 101, 52] : [180, 30, 10]],
        [20, 40, isLIGuard ? [22, 101, 52] : [202, 138, 4]],
        [40, 60, [202, 138, 4]],
        [60, 80, isLIGuard ? [180, 60, 20] : [22, 101, 52]],
        [80, 100, isLIGuard ? [180, 30, 10] : [22, 101, 52]]
    ];

    segs.forEach((seg, i) => {
        const sDeg = 180 + (i * 36) + 2.5, eDeg = 180 + ((i + 1) * 36) - 2.5;
        const pts = [];
        for (let a = sDeg; a <= eDeg; a += 4) pts.push({ op: pts.length === 0 ? 'm' : 'l', c: [gx + Math.cos(a * Math.PI / 180) * rOut, gy + Math.sin(a * Math.PI / 180) * rOut] });
        for (let a = eDeg; a >= sDeg; a -= 4) pts.push({ op: 'l', c: [gx + Math.cos(a * Math.PI / 180) * rIn, gy + Math.sin(a * Math.PI / 180) * rIn] });
        pts.push({ op: 'z' });
        const isActive = isLIGuard ? (bestHero.val <= (i + 1) * (gaugeMaxVal / 5)) : (bestHero.val >= (i) * (gaugeMaxVal / 5));
        doc.setFillColor(...(isActive ? seg[2] : [241, 245, 249])); doc.path(pts); doc.fill();
    });

    const bndPcts = [0, 0.2, 0.4, 0.6, 0.8, 1.0].map(p => Math.round(p * gaugeMaxVal));
    bndPcts.forEach((v, i) => {
        const lblDeg = 180 + (i * 36), lx = gx + Math.cos(lblDeg * Math.PI / 180) * rRail, ly = gy + Math.sin(lblDeg * Math.PI / 180) * rRail;
        doc.setFillColor(30, 41, 59); doc.circle(lx, ly, 3.5, 'F'); doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.text(`${v}${bestHero.unit}`, lx, ly + 1.3, { align: 'center' });
    });

    const rrLabelX = gx - 47; doc.setTextColor(30, 41, 59); doc.setFontSize(7); doc.setFont("Montserrat", "bold"); doc.text("Recommended\nrange", rrLabelX, gy - 17, { align: 'center' }); doc.setFontSize(8); doc.text(bestHero.target, rrLabelX, gy - 11, { align: 'center' });

    const cCol = bestHero.score >= 80 ? [22, 101, 52] : (bestHero.score >= 50 ? [202, 138, 4] : [180, 30, 10]);
    doc.setFillColor(0, 0, 0, 0.05); doc.circle(gx + 0.5, gy - 7.5, 9.5, 'F'); doc.setFillColor(255, 255, 255); doc.circle(gx, gy - 8, 9, 'F'); doc.setFillColor(...cCol); doc.circle(gx, gy - 8, 7.5, 'F'); doc.setTextColor(255, 255, 255); doc.setFontSize(10);
    doc.text(`${bestHero.val.toFixed(1)}${bestHero.unit}`, gx, gy - 5, { align: 'center' });

    const nRatio = Math.min(Math.max(bestHero.val / gaugeMaxVal, 0), 1), nAngle = Math.PI + (nRatio * Math.PI);
    doc.setFillColor(23, 23, 23); doc.path([{ op: 'm', c: [gx + Math.cos(nAngle - 0.1) * 4.5, gy + Math.sin(nAngle - 0.1) * 4.5] }, { op: 'l', c: [gx + Math.cos(nAngle) * 36, gy + Math.sin(nAngle) * 36] }, { op: 'l', c: [gx + Math.cos(nAngle + 0.1) * 4.5, gy + Math.sin(nAngle + 0.1) * 4.5] }, { op: 'z' }]); doc.fill();
    doc.circle(gx, gy, 4.5, 'F'); doc.setFillColor(30, 41, 59); doc.circle(gx - 0.5, gy - 0.5, 1.2, 'F');

    let bltY = gy - 26; doc.setTextColor(6, 150, 104); doc.setFontSize(10); doc.setFont("Montserrat", "bold"); doc.text("Why this matters:", gx + 56, bltY - 6);
    bestHero.bullets.forEach(b => { doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal"); const lines = doc.splitTextToSize("• " + b, 54); doc.text(lines, gx + 56, bltY); bltY += lines.length * 4.2 + 1; });

    y = gy + 15; doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.4); doc.roundedRect(14, y, pageWidth - 28, 14, 4, 4, 'S'); doc.setTextColor(6, 150, 104); doc.setFontSize(12); doc.text("The science:", 18, y + 9); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5); doc.text(doc.splitTextToSize(bestHero.science, pageWidth - 80), 55, y + 6);

    // --- Strength 2: Health Protection ---
    y += 26;
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("STRENGTH 2: Family Health Protection in Place", 18, y + 5.5);

    y += 13;
    // --- Read actual health data from user input ---
    const sInc = report?.salaryIncome || {};
    const hCoverRaw = safeParse(sInc.healthInsuranceCover) || metrics.healthInsuranceCover || 0;
    const hCoverL = hCoverRaw > 0 ? (hCoverRaw / 100000).toFixed(0) : '0';
    // Check if parent insurance is present
    const hasParentIns = personalInfo.parentHealthInsurance === 'yes'
        || (safeParse(sInc.parentHealthPremium) || safeParse(sInc.parentHealthInsurancePremium) || 0) > 0;
    const cW = 98, cH = 40, r = 13; // Card dimensions
    doc.setDrawColor(31, 41, 55, 0.1); doc.roundedRect(14, y, cW, cH, 5, 5, 'S');
    doc.setFillColor(209, 250, 229); doc.circle(32, y + 20, r, 'F');

    const ix = 32, iy = y + 20;
    try { try { doc.addImage('/icons/insurance copy.png', 'PNG', ix - 8, iy - 8, 16, 16); } catch (e) { } } catch (e) { }

    doc.setTextColor(30, 41, 59); doc.setFontSize(8.4); doc.setFont("Montserrat", "normal");
    const hCoverTxt = hCoverRaw > 0 ? ` (\u20b9${hCoverL}L cover)` : '';
    const parentTxt = hasParentIns ? ' AND for your elderly parents' : '';
    const hTxtAll = hCoverRaw > 0
        ? `You have health insurance for yourself${hCoverTxt}${parentTxt}. Many people your age only insure themselves and leave family unprotected.`
        : `Building health protection is a priority. Even a basic health cover protects your savings from one unexpected hospitalisation.`;
    const tx = 48, tw = 58;
    doc.text(doc.splitTextToSize(hTxtAll, tw), tx, y + 10, { lineHeightFactor: 1.15 });

    // Card 2: Family + Heart
    y += cH + 5;
    doc.setDrawColor(31, 41, 55, 0.1); doc.roundedRect(14, y, cW, cH, 5, 5, 'S');
    doc.setFillColor(209, 250, 229); doc.circle(32, y + 20, r, 'F');

    const fix = 32, fiy = y + 20;
    try { try { doc.addImage('/icons/family copy.png', 'PNG', fix - 8, fiy - 8, 16, 16); } catch (e) { } } catch (e) { }

    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("Why this matters:", tx, y + 10);
    doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(9);
    const mCity = personalInfo.city || personalInfo.cityType || "your city";
    // Metro cities have higher hospitalisation costs
    const isMetro = ['mumbai', 'delhi', 'bangalore', 'hyderabad', 'pune', 'chennai', 'kolkata'].includes((mCity || '').toLowerCase());
    const cityRange = isMetro ? '\u20b95-12 Lakhs' : '\u20b93-8 Lakhs';
    const whyTxt = hasParentIns
        ? `A single hospitalisation for a senior citizen in ${mCity} can cost ${cityRange}. Without insurance, that emergency can wipe out years of savings. You have protected your family intelligently.`
        : `Health insurance is the most critical financial protection. A single hospitalisation in ${mCity} can cost ${cityRange} — wiping out years of savings. Securing adequate cover is the immediate next step.`;
    doc.text(doc.splitTextToSize(whyTxt, tw), tx, y + 15, { lineHeightFactor: 1.15 });

    // Right Card: Large Family icon + 80D benefit
    const rcX = 14 + cW + 4, rcW = pageWidth - rcX - 14;
    doc.setDrawColor(31, 41, 55, 0.1); doc.roundedRect(rcX, y - cH - 5, rcW, cH * 2 + 5, 5, 5, 'S');
    const fxx = rcX + rcW / 2, fyy = y - cH + 16;
    doc.setFillColor(209, 250, 229); doc.circle(fxx, fyy, 19, 'F');

    try { try { doc.addImage('/icons/family copy.png', 'PNG', fxx - 12, fyy - 12, 24, 24); } catch (e) { } } catch (e) { }

    doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    doc.text("Your total 80D benefit:", fxx, y + 12, { align: 'center' });
    doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont("Montserrat", "bold");

    // Compute 80D directly from raw fields — self + family (limit ₹25,000) + parents (limit ₹50,000)
    const _sIncR = report?.salaryIncome || {};
    const _selfHealthMonthly = safeParse(_sIncR.healthInsurance) || safeParse(_sIncR.healthInsurancePremium) || 0;
    const _parentHealthMonthly = safeParse(_sIncR.parentHealthInsurance) || safeParse(_sIncR.parentHealthPremium) || safeParse(_sIncR.parentHealthInsurancePremium) || 0;
    const _selfAnnual = _selfHealthMonthly * 12;
    const _parentAnnual = _parentHealthMonthly * 12;
    // 80D limits: ₹25,000 self+family, ₹50,000 senior parents
    const _d80Self = Math.min(25000, _selfAnnual);
    const _d80Parent = Math.min(50000, _parentAnnual);
    const d80Val = _d80Self + _d80Parent
        || metrics.taxBenefits80D
        || metrics.tax?.benefit80D
        || 0;

    doc.text(`₹${d80Val.toLocaleString('en-IN')}/year`, fxx, y + 17, { align: 'center' });
    doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    const d80Txt = d80Val > 0
        ? `in tax deductions. Your health insurance saves you ₹${d80Val.toLocaleString('en-IN')} in taxes every year.`
        : "No 80D benefit claimed yet. Adding health insurance gives you both protection and tax savings.";
    doc.text(doc.splitTextToSize(d80Txt, rcW - 12), fxx, y + 22, { align: 'center', lineHeightFactor: 1.1 });
};

export const renderStrengthsPage2 = (doc, { name, m, metrics, pageWidth, pageHeight, personalInfo = {}, report = {} }) => {
    const isCouple = personalInfo?.familyType === 'couple' || report?.personalInfo?.familyType === 'couple';
    if (isCouple) {
        // Header
        doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
        try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

        // Render Titles
        doc.setTextColor(6, 95, 70); doc.setFontSize(14); doc.setFont("Montserrat", "bold"); // Dark Emerald (#065f46)
        doc.text("JOINT SAVINGS COMPOUNDING BLUEPRINT", 14, 25);

        // Underline
        doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.6); // Brand Green
        doc.line(14, 28, 196, 28);

        // Section badge on top-right
        doc.setFillColor(236, 253, 245); doc.roundedRect(175, 20.5, 21, 5.5, 1, 1, 'F');
        doc.setFont("Montserrat", "bold"); doc.setFontSize(7.5); doc.setTextColor(6, 150, 104);
        doc.text("SECTION 06", 185.5, 24.3, { align: 'center' });

        doc.setTextColor(71, 85, 105); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        const jointSalary = report?.jointSalary || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const combinedMonthly = hSalary + hOther + wSalary + wOther;

        const surplus = safeParse(m?.monthlySurplus) || safeParse(metrics?.monthlySurplus) || 0;
        const monthlySavings = surplus > 0 ? surplus : (combinedMonthly * 0.15 > 0 ? Math.round(combinedMonthly * 0.15 / 1000) * 1000 : 20000);

        const introTxt = `If you automate your estimated joint monthly surplus of ₹${monthlySavings.toLocaleString('en-IN')}/month, here is how compounding will work for you at a conservative 12% average annual return:`;
        doc.text(doc.splitTextToSize(introTxt, pageWidth - 28), 14, 34, { lineHeightFactor: 1.2 });

        // Draw Table Container
        const containerY = 42;
        const containerH = 42.5;
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        doc.roundedRect(14, containerY, 182, containerH, 5, 5, 'S');

        const colWidths = [40, 65, 77];
        const headers = ["Years", "Total Invested", "Estimated Wealth (12% Return)"];

        const tableY = containerY + 2;

        // Draw Headers as Rounded Badges (Pills)
        let currentX = 14;
        headers.forEach((header, idx) => {
            const colCenter = currentX + colWidths[idx] / 2;
            const w = Math.min(colWidths[idx] - 4, doc.getTextWidth(header.toUpperCase()) + 8);
            doc.setFillColor(6, 150, 104);
            doc.roundedRect(colCenter - w / 2, tableY, w, 7.5, 3.75, 3.75, 'F');
            doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(7.8);
            doc.text(header.toUpperCase(), colCenter, tableY + 4.8, { align: 'center' });
            currentX += colWidths[idx];
        });

        // Data Rows: 15, 20, 25 years
        const years = [15, 20, 25];
        const rRate = 0.01; // 1% monthly
        const rowH = 11;

        let ry = tableY + 7.5;
        years.forEach((t, rIdx) => {
            // Draw alternating background ONLY for Row 2 to prevent square corners from sticking out of rounded top/bottom container corners
            if (rIdx === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, ry, 182, rowH, 'F');
            }

            // Draw soft horizontal divider line ONLY between rows (the container bottom border serves as bottom-most row divider)
            if (rIdx < years.length - 1) {
                doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.35);
                doc.line(14, ry + rowH, 196, ry + rowH);
            }

            const mid = ry + rowH / 2;

            // Draw vertical dotted dividers inside row
            let cxDivider = 14;
            for (let i = 0; i < colWidths.length - 1; i++) {
                cxDivider += colWidths[i];
                doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
                let cy = ry;
                while (cy < ry + rowH) {
                    doc.line(cxDivider, cy, cxDivider, cy + 0.8);
                    cy += 2;
                }
            }

            const totalInvested = t * 12 * monthlySavings;
            const fv = monthlySavings * ((Math.pow(1 + rRate, t * 12) - 1) / rRate) * (1 + rRate);

            const fmtAmt = (val) => {
                if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
                return `₹${(val / 100000).toFixed(2)} Lakhs`;
            };

            doc.setFont("Montserrat", "normal"); doc.setFontSize(8.8);

            // Years cell (Left aligned)
            doc.setTextColor(30, 41, 59);
            doc.text(`${t} Years`, 14 + 4, mid + 1.2);

            // Total Invested cell (Center aligned)
            doc.text(fmtAmt(totalInvested), 14 + colWidths[0] + colWidths[1] / 2, mid + 1.2, { align: 'center' });

            // Estimated Wealth cell (Right aligned, Bold green)
            doc.setFont("Montserrat", "bold"); doc.setTextColor(6, 150, 104);
            doc.text(fmtAmt(fv), 196 - 4, mid + 1.2, { align: 'right' });

            ry += rowH;
        });

        doc.setTextColor(100, 116, 139); doc.setFont("Montserrat", "normal"); doc.setFontSize(7.5);
        const tableNote = "Note: Values calculated assuming monthly compounding at a constant 12% return rate. Actual returns will vary depending on your dynamic asset allocation choice.";
        doc.text(doc.splitTextToSize(tableNote, 182), 14, containerY + containerH + 4, { lineHeightFactor: 1.2 });

        // Teal box at bottom
        const boxY = containerY + containerH + 13;
        doc.setFillColor(240, 253, 250);
        doc.setDrawColor(204, 251, 241); doc.setLineWidth(0.4);
        doc.roundedRect(14, boxY, 182, 42, 3, 3, 'FD');

        doc.setTextColor(13, 148, 136); doc.setFont("Montserrat", "bold"); doc.setFontSize(10.5);
        doc.text("THE POWER OF JOINT WEALTH CREATION", 19, boxY + 10);

        doc.setTextColor(71, 85, 105); doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
        const insightTxt = "By planning together as a couple, you pool your resources, tax benefits, and borrowing power. This dual engine is the fastest way to compound wealth in India. Focus on closing high-interest gaps first, then automate your monthly surplus into long-term equity to secure your joint financial independence.";
        doc.text(doc.splitTextToSize(insightTxt, 172), 19, boxY + 16, { lineHeightFactor: 1.35 });

        return;
    }

    // Page Header / Background cleanup
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    // Section Badge
    doc.setFillColor(30, 41, 59); doc.roundedRect(14, 10, 32, 7, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(`SECTION 6`, 17, 15);

    // Title removed as per user request

    let y = 40;

    // ========== DYNAMIC DATA EXTRACTION FROM USER INPUT ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const sExp = report?.salaryExpenses || {};

    // --- Home Loan Data (Dynamic from user input) ---
    const homeLoanEMI = safeParse(sInc.homeLoanEMI) || 0;
    const homeLoanOutstanding = safeParse(sInc.homeLoanOutstanding) || 0;
    const hasHomeLoan = homeLoanEMI > 0;

    // Dynamic principal calculation based on standard amortization (~40-55% goes to principal in early years)
    const principalRatio = homeLoanOutstanding > 0 ? Math.min(0.55, Math.max(0.35, (homeLoanEMI * 12) / (homeLoanOutstanding + homeLoanEMI * 120))) : 0.5;
    const monthlyPrincipal = Math.round(homeLoanEMI * principalRatio);
    const annualPrincipal = monthlyPrincipal * 12;

    // City and loan details
    const userCity = personalInfo.city || personalInfo.cityType || "your city";
    const outstandingLakhs = (homeLoanOutstanding / 100000).toFixed(1);

    // Tax benefit calculations (dynamic based on actual loan)
    const section24Interest = Math.min(200000, homeLoanEMI * 12 * (1 - principalRatio)); // Max 2L for interest
    const section80CPrincipal = Math.min(150000, annualPrincipal); // Max 1.5L for principal
    const totalHomeDeductionLakhs = ((section24Interest + section80CPrincipal) / 100000).toFixed(1);

    // --- 80C Strategy Data (Dynamic from user input) ---
    const epfMonthly = safeParse(sInc.epf) || safeParse(sInc.vpf) || 0;
    const ppfMonthly = safeParse(sInc.ppf) || 0;
    const npsMonthly = safeParse(sInc.nps_80ccd2) || 0;
    const sukanyaMonthly = safeParse(sInc.sukanyaSamriddhi) || 0;
    const elssMonthly = safeParse(sInv.elss) || 0;
    const lifeInsuranceMonthly = safeParse(sInc.lifeInsurance) || 0;

    // Calculate total 80C contribution dynamically
    const epfAnnual = epfMonthly * 12;
    const ppfAnnual = ppfMonthly * 12;
    const npsAnnual = npsMonthly * 12;
    const sukanyaAnnual = sukanyaMonthly * 12;
    const elssAnnual = elssMonthly * 12;
    const insurance80CAnnual = lifeInsuranceMonthly * 12;

    // Total 80C including home loan principal
    const total80CAnnual = epfAnnual + ppfAnnual + elssAnnual + insurance80CAnnual + Math.min(annualPrincipal, 150000);
    const used80C = Math.min(150000, total80CAnnual);
    const pct80C = Math.min(100, Math.round((used80C / 150000) * 100));

    // Build dynamic list of 80C instruments user is actually using
    const active80CInstruments = [];
    if (epfMonthly > 0) active80CInstruments.push(`EPF (\u20b9${epfMonthly.toLocaleString('en-IN')}/mo)`);
    if (ppfMonthly > 0) active80CInstruments.push(`PPF (\u20b9${ppfMonthly.toLocaleString('en-IN')}/mo)`);
    if (npsMonthly > 0) active80CInstruments.push(`NPS (\u20b9${npsMonthly.toLocaleString('en-IN')}/mo)`);
    if (elssMonthly > 0) active80CInstruments.push(`ELSS (\u20b9${elssMonthly.toLocaleString('en-IN')}/mo)`);
    if (lifeInsuranceMonthly > 0) active80CInstruments.push(`Insurance (\u20b9${lifeInsuranceMonthly.toLocaleString('en-IN')}/mo)`);
    if (monthlyPrincipal > 0) active80CInstruments.push(`Home Principal (\u20b9${monthlyPrincipal.toLocaleString('en-IN')}/mo)`);

    // --- Investment Habits Data (Dynamic from user input) ---
    const sipMonthly = safeParse(sInv.mutualFunds) || 0;
    const stocksMonthly = safeParse(sInv.stocksEquity) || 0;
    const emergencyMonthly = safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || 0;
    const rdMonthly = safeParse(sInv.recurringDeposits) || 0;
    const fdMonthly = safeParse(sInv.fixedDeposits) || 0;
    const goldMonthly = safeParse(sInv.goldBonds) || 0;

    const userAge = parseInt(personalInfo.age) || 30;
    const yearsToRetire = Math.max(5, 60 - userAge);
    const monthlyIncome = metrics.totalMonthlyIncome || (safeParse(personalInfo.monthlySalary) || 0) + (safeParse(personalInfo.otherIncomeAmount) || 0);

    // Calculate total monthly investments
    const totalMonthlyInvestments = sipMonthly + stocksMonthly + emergencyMonthly + rdMonthly + fdMonthly + goldMonthly +
        epfMonthly + ppfMonthly + npsMonthly + sukanyaMonthly + elssMonthly;

    // Future value calculation using actual user SIP amount
    const monthlyRate = 0.01; // 12% annual = 1% monthly
    const months = yearsToRetire * 12;
    const futureValue = sipMonthly > 0
        ? sipMonthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate)
        : 0;
    const futureValueCrores = (futureValue / 10000000).toFixed(2);
    const doubleSIPValue = sipMonthly > 0
        ? (sipMonthly * 2) * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate)
        : 0;
    const doubleSIPCrores = (doubleSIPValue / 10000000).toFixed(2);

    // ========== STRENGTH 3: HOME LOAN OR DEBT-FREE ==========
    if (hasHomeLoan) {
        // --- User HAS Home Loan ---
        doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text(`STRENGTH 3: Home Loan Building Dual Wealth`, 18, y + 5.5);

        y += 16;
        const cw = (pageWidth - 28 - 16) / 3, ch = 52;
        const drawS3Card = (x, title, body, iconType, hasDivider) => {
            doc.setFillColor(248, 250, 252); doc.roundedRect(x, y + 8, cw, ch, 18, 18, 'F');
            const cx = x + cw / 2, cy = y + 8;
            doc.setFillColor(30, 41, 59);
            doc.path([{ op: 'm', c: [cx - 14, cy] }, { op: 'c', c: [cx - 14, cy - 18, cx + 14, cy - 18, cx + 14, cy] }, { op: 'z' }]); doc.fill();
            doc.setFillColor(255, 255, 255); doc.circle(cx, cy, 12.5, 'F');
            doc.setDrawColor(31, 41, 55); doc.setLineWidth(0.7);
            let iconPath = null;
            if (iconType === 'home-hand') iconPath = '/icons/home copy.png';
            else if (iconType === 'home-stats') iconPath = '/icons/market.png';
            else if (iconType === 'tax') iconPath = '/icons/insurance copy.png';
            if (iconPath) { try { try { doc.addImage(iconPath, 'PNG', cx - 9, cy - 9, 18, 18); } catch (e) { } } catch (e) { } }
            doc.setTextColor(31, 41, 55); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
            doc.text(title, cx, cy + 16, { align: 'center' });
            doc.setTextColor(30, 41, 59); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
            const lines = doc.splitTextToSize(body, cw - 12);
            doc.text(lines, cx, cy + 22, { align: 'center', lineHeightFactor: 1.15 });
            if (hasDivider) { doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.4); doc.line(x + cw + 4, cy + 12, x + cw + 4, cy + ch - 12); }
        };

        drawS3Card(14, "Wealth Machine",
            `Your home loan is not just an expense — it is a wealth-building tool. Every EMI reduces the \u20b9${outstandingLakhs}L principal and builds equity in a ${userCity} real estate asset.`,
            'home-hand', true);

        drawS3Card(14 + cw + 8, "The numbers:",
            `Of your \u20b9${homeLoanEMI.toLocaleString('en-IN')} EMI, approximately \u20b9${monthlyPrincipal.toLocaleString('en-IN')} currently goes toward principal. That is saving in the form of home equity.`,
            'home-stats', true);

        drawS3Card(14 + cw * 2 + 16, "Tax benefit:",
            `Section 24 (interest) + 80C (principal) = up to \u20b9${totalHomeDeductionLakhs}L in combined tax deductions annually from this one loan.`,
            'tax', false);
        y += ch + 6;
    } else {
        // --- User has NO Home Loan (Debt-Free Strength) ---
        doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text(`STRENGTH 3: Debt-Free Financial Foundation`, 18, y + 5.5);

        y += 16;
        const cw = (pageWidth - 28 - 16) / 3, ch = 52;
        const drawS3Card = (x, title, body, iconType, hasDivider) => {
            doc.setFillColor(248, 250, 252); doc.roundedRect(x, y + 8, cw, ch, 18, 18, 'F');
            const cx = x + cw / 2, cy = y + 8;
            doc.setFillColor(30, 41, 59);
            doc.path([{ op: 'm', c: [cx - 14, cy] }, { op: 'c', c: [cx - 14, cy - 18, cx + 14, cy - 18, cx + 14, cy] }, { op: 'z' }]); doc.fill();
            doc.setFillColor(255, 255, 255); doc.circle(cx, cy, 12.5, 'F');
            let iconPath = null;
            if (iconType === 'freedom') iconPath = '/icons/market.png';
            else if (iconType === 'invest') iconPath = '/icons/rupees.png';
            else if (iconType === 'flex') iconPath = '/icons/piggy-bank.png';
            if (iconPath) { try { try { doc.addImage(iconPath, 'PNG', cx - 9, cy - 9, 18, 18); } catch (e) { } } catch (e) { } }
            doc.setTextColor(31, 41, 55); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
            doc.text(title, cx, cy + 16, { align: 'center' });
            doc.setTextColor(30, 41, 59); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
            const lines = doc.splitTextToSize(body, cw - 12);
            doc.text(lines, cx, cy + 22, { align: 'center', lineHeightFactor: 1.15 });
            if (hasDivider) { doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.4); doc.line(x + cw + 4, cy + 12, x + cw + 4, cy + ch - 12); }
        };

        // --- Dynamic Data for Cards ---
        const personalLoanEMI = safeParse(sInc.personalLoanEMI) || 0;
        const otherLoanEMI = safeParse(sInc.otherLoanEMI) || 0;
        const totalNonHomeEMI = personalLoanEMI + otherLoanEMI;

        // True surplus calculation (Syncs with Page 3 Unassigned Surplus)
        const currentSurplus = metrics.monthlySurplus || 0;

        const activeInvs = [];
        if (sipMonthly > 0) activeInvs.push("SIPs");
        if (stocksMonthly > 0) activeInvs.push("Stocks");
        if (ppfMonthly > 0) activeInvs.push("PPF");
        if (npsMonthly > 0) activeInvs.push("NPS");
        const invStr = activeInvs.length > 0 ? activeInvs.join(", ") : "wealth creation";

        const surplusFV = currentSurplus > 0
            ? currentSurplus * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate)
            : 0;
        const surplusFVCrores = (surplusFV / 10000000).toFixed(2);

        // Zero Debt Freedom Card Logic
        let debtFreeText;
        if (totalNonHomeEMI === 0) {
            const potentialEMISaving = Math.round(monthlyIncome * 0.35);
            debtFreeText = `No home loan means zero interest burden of approx \u20b9${potentialEMISaving.toLocaleString('en-IN')} monthly. Every rupee of income works for you.`;
        } else {
            debtFreeText = `You have avoided home-debt! While you have \u20b9${totalNonHomeEMI.toLocaleString('en-IN')} in other EMIs, keeping home-debt zero gives you massive leverage to clear them fast.`;
        }

        drawS3Card(14, "Zero Debt Freedom", debtFreeText, 'freedom', true);

        drawS3Card(14 + cw + 8, "Investment Power",
            `Your \u20b9${currentSurplus.toLocaleString('en-IN')} monthly surplus, if invested in ${invStr}, can grow to approx \u20b9${surplusFVCrores}Cr by retirement.`,
            'invest', true);

        drawS3Card(14 + cw * 2 + 16, "Financial Agility",
            `Being home-debt free gives you flexibility to pivot quickly \u2014 from starting a business to family emergencies, you are in total control.`,
            'flex', false);
        y += ch + 6;
    }

    // ========== STRENGTH 4: 80C STRATEGY (FULLY DYNAMIC) ==========
    y += 16;
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text(`STRENGTH 4: Smart Multi-Instrument 80C Strategy`, 18, y + 5.5);

    y += 12;
    const boxH = active80CInstruments.length > 4 ? 70 : 65;
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, boxH, 5, 5, 'F');

    const ix = 28;
    try { try { doc.addImage('/icons/account-settings.png', 'PNG', ix - 6, y + 13 - 6, 12, 12); } catch (e) { } } catch (e) { }
    try { try { doc.addImage('/icons/partnerships.png', 'PNG', ix - 6, y + 37 - 6, 12, 12); } catch (e) { } } catch (e) { }
    try { try { doc.addImage('/icons/briefcase.png', 'PNG', ix - 6, y + 55 - 6, 12, 12); } catch (e) { } } catch (e) { }

    // Dynamic 80C description based on actual user instruments
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");

    let s4Text1;
    if (active80CInstruments.length > 0) {
        const instrumentsText = active80CInstruments.join(', ');
        s4Text1 = `You are using ${instrumentsText}. Combined 80C utilisation: \u20b9${used80C.toLocaleString('en-IN')} annually — ${pct80C}% of the \u20b91,50,000 maximum limit.`;
    } else {
        s4Text1 = `You have the opportunity to start 80C tax-saving investments. The \u20b91,50,000 annual limit can save you up to \u20b9${(150000 * 0.3).toLocaleString('en-IN')} in taxes every year.`;
    }
    doc.text(doc.splitTextToSize(s4Text1, pageWidth - 70), 45, y + 10, { lineHeightFactor: 1.15 });

    doc.setFont("Montserrat", "bold"); doc.text("Why this matters:", 45, y + 26);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    const s4Text2 = pct80C >= 80
        ? "Most salaried employees only use EPF and forget the rest. You have maximised your 80C potential with a smart multi-instrument strategy. This saves you thousands per year versus the average taxpayer."
        : "Most salaried employees only use EPF and forget the rest. You have started building a multi-instrument tax strategy. Increasing this to 100% utilisation will save you thousands more per year.";
    doc.text(doc.splitTextToSize(s4Text2, pageWidth - 70), 45, y + 30, { lineHeightFactor: 1.15 });

    doc.setFont("Montserrat", "bold"); doc.text("Bonus fact:", 45, y + 46);
    doc.setFont("Montserrat", "normal");

    let s4Text3;
    if (epfMonthly > 0) {
        const employerAnnual = (epfMonthly * 12).toLocaleString('en-IN');
        s4Text3 = `Your employer contributes \u20b9${epfMonthly.toLocaleString('en-IN')}/month to your EPF on top of your contribution. That is \u20b9${employerAnnual}/year of FREE money. Never leave a job without ensuring your EPF is transferred.`;
    } else if (active80CInstruments.length === 0) {
        s4Text3 = `Starting EPF contributions unlocks employer matching — FREE money added on top of your savings. It is one of the best returns available to any salaried employee.`;
    } else {
        s4Text3 = `The government gives you a \u20b91,50,000 gift every year through 80C deductions. Every rupee you invest in PPF, ELSS, or insurance reduces your taxable income directly.`;
    }
    doc.text(doc.splitTextToSize(s4Text3, pageWidth - 70), 45, y + 50, { lineHeightFactor: 1.15 });

    // ========== STRENGTH 5: INVESTING HABIT (FULLY DYNAMIC) ==========
    y += 12 + boxH + 8;
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, y, pageWidth - 28, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text(`STRENGTH 5: Investing Habit Already Running`, 18, y + 5.5);

    y += 14;
    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");

    // Build dynamic list of active investments
    const activeInvestments = [];
    if (sipMonthly > 0) activeInvestments.push(`Mutual Fund SIP (\u20b9${sipMonthly.toLocaleString('en-IN')}/month)`);
    if (stocksMonthly > 0) activeInvestments.push(`Stocks (\u20b9${stocksMonthly.toLocaleString('en-IN')}/month)`);
    if (emergencyMonthly > 0) activeInvestments.push(`Emergency Fund (\u20b9${emergencyMonthly.toLocaleString('en-IN')}/month)`);
    if (ppfMonthly > 0) activeInvestments.push(`PPF (\u20b9${ppfMonthly.toLocaleString('en-IN')}/month)`);
    if (npsMonthly > 0) activeInvestments.push(`NPS (\u20b9${npsMonthly.toLocaleString('en-IN')}/month)`);
    if (rdMonthly > 0) activeInvestments.push(`RD (\u20b9${rdMonthly.toLocaleString('en-IN')}/month)`);
    if (fdMonthly > 0) activeInvestments.push(`FD (\u20b9${fdMonthly.toLocaleString('en-IN')}/month)`);
    if (goldMonthly > 0) activeInvestments.push(`Gold (\u20b9${goldMonthly.toLocaleString('en-IN')}/month)`);
    if (elssMonthly > 0) activeInvestments.push(`ELSS (\u20b9${elssMonthly.toLocaleString('en-IN')}/month)`);

    let s5T1;
    if (activeInvestments.length >= 2) {
        const mainHabits = activeInvestments.slice(0, 2).join(' and ');
        s5T1 = `\u2022 You have already started important financial habits: ${mainHabits}. The amounts can grow, but the crucial habit of regular investing is established.`;
    } else if (activeInvestments.length === 1) {
        s5T1 = `\u2022 You have started one important financial habit: ${activeInvestments[0]}. This is the foundation — now consider adding one more investment avenue to diversify.`;
    } else {
        s5T1 = `\u2022 The most important step is starting. Even small, consistent investments compound dramatically over ${yearsToRetire} years. This is your moment to begin the investing habit that will define your financial future.`;
    }
    doc.text(doc.splitTextToSize(s5T1, pageWidth - 36), 18, y, { lineHeightFactor: 1.15 });

    y += 8;

    let s5T2;
    if (sipMonthly > 0 && futureValue > 0) {
        s5T2 = `\u2022 The compounding power: Your \u20b9${sipMonthly.toLocaleString('en-IN')}/month SIP, continued for ${yearsToRetire} years at 12% average returns, grows to approximately \u20b9${futureValueCrores} Crore. Simply doubling it to \u20b9${(sipMonthly * 2).toLocaleString('en-IN')} makes that \u20b9${doubleSIPCrores} Crore at retirement.`;
    } else if (totalMonthlyInvestments > 0) {
        // Calculate future value for total investments
        const totalFV = totalMonthlyInvestments * ((Math.pow(1.01, months) - 1) / 0.01) * 1.01;
        const totalCrores = (totalFV / 10000000).toFixed(2);
        s5T2 = `\u2022 The compounding power: Your total \u20b9${totalMonthlyInvestments.toLocaleString('en-IN')}/month investments, continued for ${yearsToRetire} years at 12% average returns, can grow to approximately \u20b9${totalCrores} Crore by retirement.`;
    } else {
        const starterAmount = Math.max(1000, Math.round((metrics?.monthlyIncome || 0) * 0.05 / 1000) * 1000);
        const starterFV = starterAmount * ((Math.pow(1.01, months) - 1) / 0.01) * 1.01;
        const starterCrores = (starterFV / 10000000).toFixed(2);
        s5T2 = `\u2022 Starting a SIP of \u20b9${starterAmount.toLocaleString('en-IN')}/month today, continued for ${yearsToRetire} years at 12% returns, grows to \u20b9${starterCrores} Crore. Every month you delay is compound interest you lose forever.`;
    }
    doc.text(doc.splitTextToSize(s5T2, pageWidth - 36), 18, y, { lineHeightFactor: 1.15 });
};

// Page 11 - Savings Plan (Fully Dynamic - Matches Screenshot)
export const renderSavingsPlanPage = (doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, personalLoanBalance, interestSaved, pageWidth, pageHeight, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');

    // ========== DYNAMIC DATA ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};

    const isCouple = personalInfo?.familyType === 'couple';

    const monthlySurplus = m?.monthlySurplus || metrics?.monthlySurplus || 0;
    const userIncome = metrics?.totalMonthlyIncome || metrics?.monthlyIncome || safeParse(personalInfo?.monthlySalary) || 0;

    const currentNPS = safeParse(sInc?.nps_80ccd2) || safeParse(sInv?.nps_80ccd2) || 0;
    const npsAmount = currentNPS > 0 ? currentNPS : Math.max(1000, Math.round(monthlySurplus * 0.05 / 500) * 500);
    const elssSIP = Math.max(1000, Math.round((monthlySurplus > 0 ? monthlySurplus * 0.1 : (userIncome * 0.05)) / 1000) * 1000);

    const pLB = safeParse(personalLoanBalance) || safeParse(sInc.personalLoanOutstanding) || safeParse(report?.landGold?.personalLoanOutstanding) || 0;
    const pLE = safeParse(personalLoanEMI) || safeParse(sInc.personalLoanEMI) || 0;
    const extraPayment = pLE > 0 ? Math.round(pLE * 0.5) : 0;
    const tLP = pLE + extraPayment;
    const moP = tLP > 0 && pLB > 0 ? Math.ceil(pLB / tLP) : 12;
    const orM = pLE > 0 && pLB > 0 ? Math.ceil(pLB / pLE) : 24;
    const iS = interestSaved || Math.round(pLB * 0.14 * (orM - moP) / 12);

    const sipMonthly = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
    const termInsOpt = calculations?.grades?.insurance !== 'A' ? 1200 : 0;

    // ===== CLEAN WHITE PAGE =====
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 6, 38, 8); } catch (e) { }

    // ===== SECTION TITLE =====
    let y = 22;
    doc.setTextColor(0, 0, 180); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text(isCouple ? "SECTION 7: JOINT SAVINGS ACTION PLAN \uD83D\uDCCB" : "SECTION 5: SAVINGS ACTION PLAN \uD83D\uDCCB", 14, y);
    y += 7;

    // ===== TABLE CONFIG =====
    const tLeft = 14;
    const tRight = pageWidth - 14;
    const tWidth = tRight - tLeft; // 182mm

    // Columns: Action Step | What To Do | Why This Works | Amount/Month
    const cW = [38, 68, 46, 30]; // sum = 182
    const cX = [tLeft, tLeft + cW[0], tLeft + cW[0] + cW[1], tLeft + cW[0] + cW[1] + cW[2]];

    const FS = 8.5;
    const LH = 4.4;
    const PAD = 3.5;

    const COL_STEP = [10, 20, 50];
    const COL_WHAT = [15, 25, 45];
    const COL_WHY = [4, 72, 48];
    const COL_AMT = [6, 140, 96];
    const COL_HEADER = [20, 30, 60];

    // Dynamic row height
    const measureRowH = (cells) => {
        doc.setFontSize(FS); doc.setFont("Montserrat", "normal");
        const maxLines = Math.max(...cells.map((txt, i) =>
            doc.splitTextToSize(String(txt || ""), cW[i] - PAD * 2).length
        ));
        return Math.max(12, maxLines * LH + 8);
    };

    // Draw a table row
    const drawRow = (rowY, rowH, cells, colors, isBold, isAlt) => {
        if (isAlt) { doc.setFillColor(247, 249, 251); doc.rect(tLeft, rowY, tWidth, rowH, 'F'); }
        cells.forEach((txt, i) => {
            if (!txt) return;
            doc.setFontSize(FS);
            doc.setFont("Montserrat", isBold[i] ? "bold" : "normal");
            doc.setTextColor(...colors[i]);
            const lines = doc.splitTextToSize(String(txt), cW[i] - PAD * 2);
            const textH = lines.length * LH;
            const topY = rowY + (rowH - textH) / 2 + LH * 0.85;
            lines.forEach((l, li) => doc.text(l, cX[i] + PAD, topY + li * LH));
        });
        doc.setDrawColor(205, 215, 220); doc.setLineWidth(0.25);
        doc.line(tLeft, rowY + rowH, tRight, rowY + rowH);
    };

    // Draw header row
    const drawHeader = (hy, labels) => {
        const hH = 10;
        doc.setFillColor(255, 255, 255); doc.rect(tLeft, hy, tWidth, hH, 'F');
        doc.setTextColor(...COL_HEADER); doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5);
        labels.forEach((h, i) => doc.text(h, cX[i] + PAD, hy + 7));
        doc.setDrawColor(...COL_HEADER); doc.setLineWidth(0.55);
        doc.rect(tLeft, hy, tWidth, hH, 'S');
        doc.line(tLeft, hy + hH, tRight, hy + hH);
        return hy + hH;
    };

    // Table frame + column dividers
    const drawFrame = (sy, ey) => {
        doc.setDrawColor(...COL_HEADER); doc.setLineWidth(0.5);
        doc.rect(tLeft, sy, tWidth, ey - sy, 'S');
        doc.setDrawColor(190, 205, 210); doc.setLineWidth(0.3);
        for (let i = 1; i < cX.length; i++) doc.line(cX[i], sy, cX[i], ey);
        doc.setDrawColor(...COL_HEADER); doc.setLineWidth(0.5);
        doc.rect(tLeft, sy, tWidth, ey - sy, 'S');
    };

    // ===== PHASE 2 BANNER =====
    doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("Phase 2: Months 2\u20136 \u2014 Building Momentum", 18, y + 6.2);
    y += 12;

    // ===== PHASE 2 ROWS =====
    const elssLabel = isCouple ? "Joint ELSS SIP" : "ELSS SIP";
    const npsLabel = isCouple ? "Joint NPS Tier 1" : "NPS Tier 1";
    const loanLabel = pLB > 0 ? "Extra Loan Repay" : "Emergency Fund";
    const termLabel = isCouple ? "Term Insurance (Joint)" : "Term Insurance";

    const p2rows = [
        [elssLabel,
            "Start ELSS SIP. 3-year lock-in builds discipline. 80C tax saving.",
            "12\u201315% returns + 80C benefit up to \u20B91.5L.",
            `\u20B9${fmt(elssSIP)}`],
        [npsLabel,
            currentNPS > 0
                ? `Continue NPS of \u20B9${fmt(currentNPS)}/mo. Builds pension corpus.`
                : "Open NPS Tier 1 at any bank or enps.nsdl.com.",
            "Extra \u20B950K deduction (80CCD-1B). 60% tax-free at 60.",
            `\u20B9${fmt(npsAmount)}`],
        [loanLabel,
            pLB > 0
                ? `Pay \u20B9${fmt(extraPayment)} extra/mo (total \u20B9${fmt(tLP)}). Closes in ${moP} vs ${orM} months.`
                : `Add \u20B9${fmt(Math.max(1000, Math.round(monthlySurplus * 0.1 / 1000) * 1000))}/mo to liquid fund until 6-month target met.`,
            pLB > 0
                ? `Saves \u20B9${fmt(iS)} interest. Frees \u20B9${fmt(pLE)}/mo in Phase 3.`
                : "5\u20137% returns. Shields from emergency debt.",
            pLB > 0 ? `+\u20B9${fmt(extraPayment)}` : `\u20B9${fmt(Math.max(1000, Math.round(monthlySurplus * 0.1 / 1000) * 1000))}`],
    ];
    if (termInsOpt > 0) {
        p2rows.push([termLabel,
            "Get pure term plan online for \u20B91Cr+ cover. Cheapest method.",
            "Maximum protection at lowest premium.",
            `~\u20B9${fmt(termInsOpt)}`]);
    }

    const p2colors = [COL_STEP, COL_WHAT, COL_WHY, COL_AMT];
    const p2bold = [true, false, false, true];
    const p2H = p2rows.map(r => measureRowH(r));

    const p2Start = y;
    let rY2 = drawHeader(y, ["Action Step", "What To Do", "Why This Works", "Amount/Month"]);
    p2rows.forEach((row, idx) => { drawRow(rY2, p2H[idx], row, p2colors, p2bold, idx % 2 !== 0); rY2 += p2H[idx]; });
    drawFrame(p2Start, rY2);
    y = rY2;

    // ===== PHASE 3 BANNER =====
    y += 9;
    doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
    doc.text("Phase 3: Month 12 Onwards \u2014 Growth Acceleration", 18, y + 6.2);
    y += 12;

    // ===== PHASE 3 ROWS =====
    const p3rows = [
        [pLB > 0 ? `Loan Closed (Mo.${moP})` : "Year 2 Momentum",
        pLB > 0
            ? `\u20B9${fmt(pLB)} loan closed. \u20B9${fmt(pLE)}/mo freed.`
            : "All foundations set. Full focus on wealth creation.",
        pLB > 0
            ? "Half \u2192 SIPs, half \u2192 emergency boost."
            : "Increase SIPs 10%/yr. Add index funds.",
        pLB > 0 ? `\u20B9${fmt(iS)} saved` : "Wealth grows"],
        ["Scale SIPs (Yr 2+)",
            "Increase SIPs 10%/yr via Step-up SIP. Market dips = buy more.",
            `Total SIP target: \u20B9${fmt(sipMonthly + elssSIP + npsAmount)}/mo.`,
            "12%+ CAGR"],
        ["Diversify (Yr 3+)",
            "Explore index ETFs or PMS once corpus crosses \u20B925L.",
            "Review with SEBI fee-only advisor annually.",
            "Best returns"],
    ];

    const p3colors = [COL_STEP, COL_WHAT, [15, 25, 45], COL_AMT];
    const p3bold = [true, false, false, true];
    const p3H = p3rows.map(r => measureRowH(r));

    const p3Start = y;
    let rY3 = drawHeader(y, ["Milestone", "What Happens", "Your New Monthly Plan", "Impact"]);
    p3rows.forEach((row, idx) => { drawRow(rY3, p3H[idx], row, p3colors, p3bold, idx % 2 !== 0); rY3 += p3H[idx]; });
    drawFrame(p3Start, rY3);
    y = rY3;

    // ===== KEY INSIGHT (simple green outline box) =====
    y += 9;
    if (y + 22 < pageHeight - 22) {
        const corpus5yr = Math.round((elssSIP + npsAmount + Math.max(1000, Math.round(monthlySurplus * 0.1 / 1000) * 1000)) * 12 * 5 * 1.12);
        doc.setFillColor(240, 253, 244); doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.5);
        doc.roundedRect(tLeft, y, tWidth, 20, 3, 3, 'FD');
        doc.setTextColor(4, 72, 48); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        doc.text("\uD83D\uDCA1 Key Insight:", tLeft + 5, y + 8.5);
        doc.setFont("Montserrat", "normal"); doc.setTextColor(10, 20, 50); doc.setFontSize(8.5);
        const insightTxt = `In 5 years, this plan builds a corpus of \u20B9${fmt(corpus5yr)} \u2014 without any lifestyle sacrifice.`;
        const iLines = doc.splitTextToSize(insightTxt, tWidth - 52);
        iLines.forEach((l, li) => doc.text(l, tLeft + 46, y + 8.5 + li * 4.5));
    }

    // Footer handled by generator
};

// Page 12 - Timeline & Milestones (Fully Dynamic)
export const renderTimelinePage = (doc, { name, calculations, metrics, taxSavings, personalLoanEMI, emergencyFundTarget, pageWidth, pageHeight, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const localG = [6, 150, 104], localSlate = [30, 41, 59], border = [6, 150, 104, 0.5];
    const score = calculations?.grades?.overall || 62;

    // ========== DYNAMIC DATA ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};

    // Dynamic calculations
    const actualLoanEMI = safeParse(personalLoanEMI) || safeParse(sInc.personalLoanEMI) || 0;
    const actualLoanBalance = safeParse(sInc.personalLoanOutstanding) || 0;
    const monthlyIncome = safeParse(personalInfo.monthlySalary) || metrics?.totalMonthlyIncome || 0;
    const monthlyExpenses = metrics?.totalEssentialExpenses || (metrics?.totalMonthlyIncome * 0.4) || 30000;
    const efTarget = safeParse(emergencyFundTarget) || (metrics?.targetEmergency) || (monthlyExpenses * 6);
    const efMonthly = safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || safeParse(metrics?.currentEmergency) || 0;
    const efGap = Math.max(0, efTarget - efMonthly);
    const efMonthsToFund = efGap > 0 && monthlyIncome > 0 ? Math.max(6, Math.ceil(efGap / (monthlyIncome * 0.1))) : 0;
    const monthlySIP = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0);
    const totalSIPTarget = monthlySIP + (actualLoanEMI > 0 ? actualLoanEMI : 0) + (monthlyIncome > 0 ? Math.round(monthlyIncome * 0.1 / 1000) * 1000 : 0);

    // --- Header ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    // Section badge removed
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    let y = 30;

    // --- Dynamic Logic ---
    const surplus = (metrics?.monthlySurplus || 0);
    const ef_months = efGap > 0 && monthlyIncome > 0 ? Math.ceil(efGap / (monthlyIncome * 0.1)) : 0; // Based on actual surplus redirect
    const total_sip_target = monthlySIP + (actualLoanEMI > 0 ? actualLoanEMI : 0) + (monthlyIncome > 0 ? Math.round(monthlyIncome * 0.1 / 1000) * 1000 : 0); // Freed EMI + New SIPs

    // --- Card 1: Month 18 (Dynamic Milestone) ---
    doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.45);
    doc.roundedRect(12, y, pageWidth - 24, 48, 4, 4, 'S');
    doc.setTextColor(...localSlate); doc.setFontSize(11.5); doc.setFont("Montserrat", "bold");
    doc.text(`Month ${ef_months}: Emergency Fund Fully Funded at ₹${(efTarget / 100000).toFixed(1)} Lakhs`, 18, y + 11);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(8.4); doc.setTextColor(30, 41, 59);
    const m18Desc = `When your emergency fund reaches ₹${(efTarget / 100000).toFixed(1)}L, stop adding to it (it self-grows with interest). Redirect the monthly surplus to long-term SIPs. You now have a 100% complete financial base.`;
    doc.text(doc.splitTextToSize(m18Desc, 115), 18, y + 19, { lineHeightFactor: 1.3 });

    // Icon 1 (Official PNG - Ultra Miniature)
    try {
        try { try { doc.addImage('/icons/insurance.png', 'PNG', pageWidth - 38, y + 17, 12, 14); } catch (e) { } } catch (e) { }
    } catch (e) { }

    y += 54;

    // --- Card 2: Year 2 (Dynamic Milestone) ---
    doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.45);
    doc.roundedRect(12, y, pageWidth - 24, 48, 4, 4, 'S');
    doc.setTextColor(...localSlate); doc.setFontSize(11.5); doc.setFont("Montserrat", "bold");
    doc.text(`Year 2 Onwards: SIP Target ₹${fmt(total_sip_target)}/month`, 18, y + 11);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(8.4); doc.setTextColor(30, 41, 59);
    const y2Desc = `By Month ${ef_months}, your combined monthly SIP should be ₹${fmt(total_sip_target)}+. This keeps you on track for your long-term retirement corpus. Increase SIP by 10% annually to double your wealth every 15-20 years.`;
    doc.text(doc.splitTextToSize(y2Desc, 115), 18, y + 19, { lineHeightFactor: 1.3 });

    // Icon 2 (Official PNG - Ultra Miniature)
    try {
        try { try { doc.addImage('/icons/profit.png', 'PNG', pageWidth - 38, y + 17, 12, 14); } catch (e) { } } catch (e) { }
    } catch (e) { }

    y += 50;

    // --- Timeline Table (Maximum Spacious Fit) ---
    const tX = 10, tW = pageWidth - 20;
    doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.6);
    doc.roundedRect(tX, y, tW, 135, 4, 4, 'S');

    // Headers re-aligned for better fit
    doc.setFillColor(6, 150, 104);
    doc.roundedRect(tX + 3, y + 2, 28, 8, 2, 2, 'F');   // Timeline
    doc.roundedRect(tX + 34, y + 2, 58, 8, 2, 2, 'F');  // Key Milestone
    doc.roundedRect(tX + 95, y + 2, 60, 8, 2, 2, 'F');  // Financial Position
    doc.roundedRect(tX + 158, y + 2, 28, 8, 2, 2, 'F'); // Score Target

    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("Timeline", tX + 17, y + 7.5, { align: 'center' });
    doc.text("Key Milestone", tX + 63, y + 7.5, { align: 'center' });
    doc.text("Financial Position", tX + 125, y + 7.5, { align: 'center' });
    doc.text("Score Target", tX + 172, y + 7.5, { align: 'center' });

    let ty = y + 18;
    const rows = [];

    // Milestone 1: Immediate Protection (Dynamic)
    const hasInsGap = (metrics?.lifeInsuranceGap || 0) > 0;
    const hasEFGap = efGap > 20000;

    rows.push({
        t: "Today",
        m: hasInsGap ? "Bridge Life Insurance\n+ Emergency Fund" : "Maintain Protection\n+ Emergency Fund",
        p: hasInsGap ? "Safety net and family\nprotection starts." : "Security base is strong.\nFocus on liquidity.",
        s: `${score} \u2192 ${score + 3}+`
    });

    // Milestone 2: Tax Optimization (Dynamic)
    const hasTaxGap = (metrics?.taxSavingsMonthly || 0) > 500;
    if (hasTaxGap) {
        rows.push({
            t: "Month 6",
            m: "ELSS + NPS running,\nTax planning complete",
            p: `\u20B9${fmt(taxSavings)} saved\nin taxes annually.`,
            s: `${score + 3} \u2192 ${score + 7}+`
        });
    } else {
        rows.push({
            t: "Month 6",
            m: `Expand Wealth Portfolio\nvia Broad Index Funds`,
            p: "Diversification beyond\ntax-saving starts.",
            s: `${score + 3} \u2192 ${score + 7}+`
        });
    }

    // Milestone 3: Loan Freedom or Advanced Saving (Dynamic)
    if (actualLoanEMI > 0) {
        const loanMonths = Math.min(24, Math.ceil(actualLoanBalance / (actualLoanEMI * 1.5)));
        rows.push({
            t: `Month ${loanMonths}`,
            m: "Personal loan closed\nentirely",
            p: `\u20B9${fmt(actualLoanEMI)}/m freed for\nWealth Scaling.`,
            s: `${score + 7} \u2192 ${score + 12}+`
        });
    } else if (hasEFGap) {
        rows.push({
            t: `Month ${Math.round(ef_months / 2)}`,
            m: "Halfway to full\nemergency cover",
            p: "Resistance to job\nloss / medical shocks.",
            s: `${score + 7} \u2192 ${score + 12}+`
        });
    }

    // Milestone 4: Safety Net Completion (Dynamic)
    if (hasEFGap) {
        rows.push({
            t: `Month ${ef_months}`,
            m: "Emergency Fund\nfully funded",
            p: `\u20B9${(efTarget / 100000).toFixed(1)}L safety net complete.`,
            s: `${score + 12} \u2192 ${score + 16}+`
        });
    } else {
        rows.push({
            t: "Year 1",
            m: "Solid Foundation\nVerified",
            p: "Liquidity benchmark\nfully maintained.",
            s: `${score + 12} \u2192 ${score + 16}+`
        });
    }

    // Milestone 5: Long-term Scaling (Dynamic)
    rows.push({
        t: "Year 3",
        m: `SIPs at \u20B9${fmt(total_sip_target)}+/m`,
        p: "Compounding phase\nin full swing.",
        s: `${score + 16} \u2192 ${score + 22}+`
    });

    rows.push({
        t: "Year 5",
        m: (safeParse(sInc.homeLoanEMI) || 0) > 0 ? "Home loan prepay /\nWealth scaling" : "Aggressive Portfolio\nscaling",
        p: "Wealth acceleration\nphase begins.",
        s: `${score + 22} \u2192 90+`
    });

    rows.forEach((r, idx) => {
        // Status indicator dot
        doc.setFillColor(6, 150, 104); doc.circle(tX + 5, ty + 0.5, 0.7, 'F');

        doc.setTextColor(6, 150, 104); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(r.t, tX + 17, ty + 1, { align: 'center' });

        doc.setTextColor(...localSlate); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        const mLines = doc.splitTextToSize(r.m, 55);
        const yOff = (mLines.length > 1) ? 0 : 1;
        mLines.forEach((line, i) => doc.text(line, tX + 36, ty + (i * 3.8) + (yOff ? -1 : 0)));

        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5); doc.setTextColor(30, 41, 59);
        const pLines = doc.splitTextToSize(r.p, 55);
        const pyOff = (pLines.length > 1) ? 0 : 1;
        pLines.forEach((line, i) => doc.text(line, tX + 98, ty + (i * 3.8) + (pyOff ? -1 : 0)));

        doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
        doc.text(r.s, tX + 172, ty + 1, { align: 'center' });

        if (idx < rows.length - 1) {
            doc.setDrawColor(203, 213, 225, 0.5); doc.setLineWidth(0.2); doc.setLineDash([0.5, 0.5], 0);
            doc.line(tX + 4, ty + 12.5, tX + tW - 4, ty + 12.5);
            doc.setLineDash([], 0);
        }
        ty += (rows.length > 5 ? 17.5 : 21);
    });

    addSimpleFooter(doc, "12", pageWidth, pageHeight);
};

export const renderJointTaxOptimisationPage = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');

    // ========== DYNAMIC DATA ==========
    const jointSalary = report?.jointSalary || {};
    const sectionA = jointSalary.sectionA || {};
    const sectionB = jointSalary.sectionB || {};
    const sectionC = jointSalary.sectionC || {};
    const sectionD = jointSalary.sectionD || {};
    const sectionE = jointSalary.sectionE || {};
    const sectionF = jointSalary.sectionF || {};

    // Names
    const hName = sectionA.husbandName || "Husband";
    const wName = sectionA.wifeName || "Wife";
    const hFirstName = hName.trim().split(' ')[0] || "Husband";
    const wFirstName = wName.trim().split(' ')[0] || "Wife";

    // Salary/Income
    const hSalary = safeParse(sectionB.husbandSalary) || 0;
    const wSalary = safeParse(sectionB.wifeSalary) || 0;
    const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
    const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
    const hTotal = hSalary + hOther;
    const wTotal = wSalary + wOther;
    const isHusbandHigher = hTotal >= wTotal;
    const higherEarnerName = isHusbandHigher ? hFirstName : wFirstName;

    const combinedIncomeAnnual = (hTotal + wTotal) * 12 || 1800000;
    const hasIncome = (hTotal + wTotal) > 0;

    // 2. Home Loan
    const homeLoanEMI = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
    const hasHomeLoan = homeLoanEMI > 0 || safeParse(sectionF.homeLoanOutstanding) > 0;

    // Estimate annual interest paid as ~65% of annual EMIs (very typical ratio for early-mid stage loans)
    const annualInterest = homeLoanEMI * 12 * 0.65;

    const currHL = hasHomeLoan ? Math.min(200000, annualInterest || 200000) : 0;
    const optHL = hasHomeLoan ? Math.min(400000, annualInterest || 400000) : 0;
    const extraHL = Math.max(0, optHL - currHL);

    // 1. HRA Exemption
    const rentMonthly = safeParse(sectionC.rent) || 0;
    const rentAmount = rentMonthly * 12;
    const hasHRAInput = rentMonthly > 0;

    const optHRA = hasHRAInput ? rentAmount : (hasHomeLoan ? 0 : 350000);
    const currHRA = hasHRAInput ? Math.round(optHRA * 0.628) : (hasHomeLoan ? 0 : 220000);
    const extraHRA = optHRA - currHRA;

    // 3. 80C
    const hEPF = safeParse(sectionD.husbandEPF) || 0;
    const wEPF = safeParse(sectionD.wifeEPF) || 0;
    const hPPF = safeParse(sectionD.husbandPPF) || 0;
    const wPPF = safeParse(sectionD.wifePPF) || 0;
    const hELSS = safeParse(sectionD.husbandELSS) || 0;
    const wELSS = safeParse(sectionD.wifeELSS) || 0;
    const hFD = safeParse(sectionD.husbandFD) || 0;
    const wFD = safeParse(sectionD.wifeFD) || 0;

    const h80C_annual = (hEPF + hPPF + hELSS + hFD) * 12;
    const w80C_annual = (wEPF + wPPF + wELSS + wFD) * 12;
    const has80CInput = (h80C_annual > 0 || w80C_annual > 0);

    const curr80C = has80CInput ? (Math.min(h80C_annual, 150000) + Math.min(w80C_annual, 150000)) : 120000;
    const opt80C = 300000;
    const extra80C = Math.max(0, opt80C - curr80C);

    // 4. 80D
    const hHealth = safeParse(sectionE.husbandHealthPremium) || 0;
    const wHealth = safeParse(sectionE.wifeHealthPremium) || 0;
    const jointHealth = safeParse(sectionC.healthInsurance) || 0;

    const totalHealthAnnual = (hHealth + wHealth + jointHealth) * 12;
    const hasHealthInput = totalHealthAnnual > 0;

    const curr80D = hasHealthInput ? Math.min(totalHealthAnnual, 50000) : 0;
    const opt80D = 50000;
    const extra80D = Math.max(0, opt80D - curr80D);

    // Totals
    const currTotal = currHRA + currHL + curr80C + curr80D;
    const optTotal = optHRA + optHL + opt80C + opt80D;
    const extraTotal = extraHRA + extraHL + extra80C + extra80D;

    // Tax Saved based on real income
    const currTaxSaved = hasIncome
        ? Math.round(currTotal * (combinedIncomeAnnual >= 1500000 ? 0.185 : 0.15))
        : 81000;
    const optTaxSaved = hasIncome
        ? Math.round(optTotal * (combinedIncomeAnnual >= 1500000 ? 0.2336 : 0.20))
        : 220000;
    const extraTaxSaved = optTaxSaved - currTaxSaved;

    // --- Header ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 6, 38, 8); } catch (e) { } } catch (e) { }

    // --- SECTION HEADER BANNER (Green Rectangle) ---
    let y = 22;
    doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 7: JOINT TAX OPTIMISATION STRATEGY", 18, y + 6.2);

    y += 15;
    doc.setTextColor(194, 94, 0); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text(`Combined Tax Saving Potential: \u20B9${fmt(extraTaxSaved)}/year`, 14, y);

    // --- TABLE CONTAINER ---
    const tLeft = 14;
    const tRight = pageWidth - 14;
    const tWidth = tRight - tLeft; // 182mm

    const cW = [58, 45, 51, 28];
    const cX = [tLeft, tLeft + cW[0], tLeft + cW[0] + cW[1], tLeft + cW[0] + cW[1] + cW[2]];

    const containerY = y + 6;
    const containerH = 82; // 16mm header + 4 rows * 11mm + 2 totals * 11mm = 82mm

    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    doc.roundedRect(tLeft, containerY, tWidth, containerH, 6, 6, 'S');

    // Draw header pills
    const drawHeaderPill = (colIdx, label) => {
        const labelWidth = doc.getTextWidth(label);
        const w = Math.min(cW[colIdx] - 2, labelWidth + 6);
        const x = cX[colIdx] + cW[colIdx] / 2;
        doc.setFillColor(6, 150, 104);
        doc.roundedRect(x - w / 2, containerY + 3.5, w, 8, 4, 4, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(label, x, containerY + 9, { align: 'center' });
    };

    drawHeaderPill(0, "Component");
    drawHeaderPill(1, "Individual Filing");
    drawHeaderPill(2, "Joint Optimised");
    drawHeaderPill(3, "Extra Saving");

    // Vertical dotted lines between columns
    const drawDottedLine = (x, y1, y2) => {
        let cy = y1;
        while (cy < y2) {
            doc.line(x, cy, x, cy + 0.8);
            cy += 2;
        }
    };
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
    drawDottedLine(cX[1], containerY + 16, containerY + 60);
    drawDottedLine(cX[2], containerY + 16, containerY + 60);
    drawDottedLine(cX[3], containerY + 16, containerY + 60);

    const rH = 11;
    let curY = containerY + 16;

    const rowsData = [
        {
            comp: "HRA Exemption",
            curr: currHRA > 0 ? `\u20B9${fmt(currHRA)} (combined)` : `\u20B90`,
            opt: optHRA > 0 ? `\u20B9${fmt(optHRA)} (claim via\nhigher earner)` : `\u20B90`,
            extra: `\u20B9${fmt(extraHRA)}`
        },
        {
            comp: "Home Loan Interest\n(Sec 24)",
            curr: hasHomeLoan ? `\u20B9${fmt(currHL)} (one person)` : `\u20B90`,
            opt: hasHomeLoan ? `\u20B9${fmt(Math.round(optHL / 2))} each =\n\u20B9${fmt(optHL)}` : `\u20B90`,
            extra: `\u20B9${fmt(extraHL)}`
        },
        {
            comp: "80C (Combined)",
            curr: `\u20B9${fmt(curr80C)}`,
            opt: `\u20B9${fmt(opt80C)} (\u20B91.5L each)`,
            extra: `\u20B9${fmt(extra80C)}`
        },
        {
            comp: "80D (Health\nInsurance)",
            curr: curr80D > 0 ? `\u20B9${fmt(curr80D)} (combined)` : `\u20B90 (no cover)`,
            opt: `\u20B9${fmt(opt80D)} (family floater)`,
            extra: `\u20B9${fmt(extra80D)}`
        }
    ];

    const drawTextCol = (text, x, midY, fontStyle, color, size = 8.5) => {
        doc.setFont("Montserrat", fontStyle); doc.setTextColor(...color); doc.setFontSize(size);
        const lines = text.split("\n");
        if (lines.length === 1) {
            doc.text(text, x, midY + 1.2);
        } else {
            const startY = midY - ((lines.length - 1) * 3.8) / 2 + 1.2;
            lines.forEach((line, lIdx) => {
                doc.text(line, x, startY + (lIdx * 3.8));
            });
        }
    };

    rowsData.forEach((row) => {
        const mid = curY + rH / 2;

        // Component Name (Col 1) - aligned to column boundary
        drawTextCol(row.comp, cX[0] + 3, mid, "bold", [15, 23, 42], 8.5);

        // Current Value (Col 2)
        drawTextCol(row.curr, cX[1] + 3, mid, "normal", [71, 85, 105]);

        // Optimised Value (Col 3)
        drawTextCol(row.opt, cX[2] + 3, mid, "bold", [6, 150, 104]);

        // Extra Saving (Col 4)
        drawTextCol(row.extra, cX[3] + 3, mid, "bold", [6, 150, 104]);

        curY += rH;
    });

    // Solid divider before TOTAL DEDUCTIONS
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    doc.line(tLeft, curY, tRight, curY);

    // TOTAL DEDUCTIONS Row
    const midTotal = curY + rH / 2;
    doc.setFont("Montserrat", "bold"); doc.setTextColor(15, 23, 42); doc.setFontSize(9);
    doc.text("TOTAL DEDUCTIONS", cX[0] + 3, midTotal + 1.2);
    doc.text(`\u20B9${fmt(currTotal)}`, cX[1] + 3, midTotal + 1.2);
    doc.text(`\u20B9${fmt(optTotal)}`, cX[2] + 3, midTotal + 1.2);
    doc.text(`\u20B9${fmt(extraTotal)}`, cX[3] + 3, midTotal + 1.2);

    curY += rH;

    // Solid divider before TAX SAVED
    doc.line(tLeft, curY, tRight, curY);

    // TAX SAVED Row (Green highlighted total row)
    const midTaxSaved = curY + rH / 2;
    doc.setFont("Montserrat", "bold"); doc.setTextColor(6, 150, 104); doc.setFontSize(9.5);
    doc.text("TAX SAVED", cX[0] + 3, midTaxSaved + 1.2);

    doc.setFont("Montserrat", "bold"); doc.setTextColor(71, 85, 105); doc.setFontSize(9.5);
    doc.text(`~\u20B9${fmt(currTaxSaved)}`, cX[1] + 3, midTaxSaved + 1.2);

    doc.setFont("Montserrat", "bold"); doc.setTextColor(6, 150, 104); doc.setFontSize(9.5);
    doc.text(`~\u20B9${fmt(optTaxSaved)}`, cX[2] + 3, midTaxSaved + 1.2);
    doc.text(`\u20B9${fmt(extraTaxSaved)}`, cX[3] + 3, midTaxSaved + 1.2);

    // --- Action note below container ---
    const actionY = containerY + containerH + 10;
    const fullActionText = `> Action: Consult a CA. File returns as "Individual" but optimise who claims which deduction. Higher earner (${higherEarnerName}) claims HRA and home loan.`;
    const actionLines = doc.splitTextToSize(fullActionText, pageWidth - 28);

    let currentActionY = actionY;
    actionLines.forEach((line, idx) => {
        if (idx === 0) {
            doc.setFont("Montserrat", "bold"); doc.setTextColor(0, 0, 0); doc.setFontSize(8.8);
            doc.text("> Action:", 14, currentActionY);
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
            const lineWithoutAction = line.replace("> Action:", "");
            doc.text(lineWithoutAction, 14 + doc.getTextWidth("> Action: "), currentActionY);
        } else {
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
            doc.text(line, 14, currentActionY);
        }
        currentActionY += 4.5;
    });

    // Sub-footer
    doc.setTextColor(115, 115, 115); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
    const subFt = ` www.myyathartha.com | Confidential - for ${hFirstName.toLowerCase()} & ${wFirstName.toLowerCase()} only | Financial awareness & education report.`;
    doc.text(doc.splitTextToSize(subFt, pageWidth - 28), pageWidth / 2, pageHeight - 18.5, { align: 'center', lineHeightFactor: 1.2 });

    addSimpleFooter(doc, "09", pageWidth, pageHeight);
};

export const renderSavingsOptimisationPage = (doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, emergencyFundTarget, pageWidth, pageHeight, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const localG = [6, 150, 104], localSlate = [30, 41, 59];

    // ========== DYNAMIC DATA ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};

    const isCouple = personalInfo?.familyType === 'couple';

    if (isCouple) {
        // Clean white page
        doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
        try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 6, 38, 8); } catch (e) { }

        // --- SECTION HEADER BANNER (Green Rectangle) ---
        let y = 22;
        doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 9, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text("SECTION 6: OPTIMISED JOINT SAVINGS & INVESTMENT PLAN \uD83D\uDCB0", 18, y + 6.2);

        const jointSalary = report?.jointSalary || {};
        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        // Names
        const hName = sectionA.husbandName || "Husband";
        const wName = sectionA.wifeName || "Wife";
        const hFirstName = hName.trim().split(' ')[0] || "Husband";
        const wFirstName = wName.trim().split(' ')[0] || "Wife";

        // Income
        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const combinedIncome = hSalary + wSalary + hOther + wOther || 216000;

        // Current values
        const currentEF = safeParse(sectionG.emergencyFund) || 0;
        const currentPPF = (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0);
        const currentMF = (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0);
        const currentELSS = (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0);
        const currentNPS = (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0);
        const currentEPF = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0);
        const currentPL = (safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0);
        const currentPLOS = safeParse(sectionF.personalLoanOutstanding) || 0;
        const currentIns = (safeParse(sectionE.husbandLifePremium) || 0) + (safeParse(sectionE.wifeLifePremium) || 0);

        // Recommendations
        const optimisedEF = Math.max(50000, currentEF || 50000);
        const optimisedPPF = Math.max(7000, currentPPF || 7000);
        const optimisedMF = Math.max(15000, currentMF || 15000);
        const optimisedELSS = Math.max(6000, currentELSS || 6000);
        const optimisedNPS = Math.max(4000, currentNPS || 4000);
        const optimisedEPF = currentEPF || 8000;
        const optimisedPL = currentPLOS > 0 ? Math.max(25000, Math.round(currentPL * 1.5 / 5000) * 5000) : (currentPL > 0 ? currentPL : 25000);
        const optimisedIns = Math.max(1800, currentIns || 1800);

        // --- TABLE CONTAINER STYLE (Checklist Theme) ---
        const tLeft = 14;
        const tRight = pageWidth - 14;
        const tWidth = tRight - tLeft; // 182mm

        // Checklist column widths (Instrument widened to 60mm to prevent any text overlaps)
        const cW = [60, 24, 24, 24, 50];
        const cX = [tLeft, tLeft + cW[0], tLeft + cW[0] + cW[1], tLeft + cW[0] + cW[1] + cW[2], tLeft + cW[0] + cW[1] + cW[2] + cW[3]];

        const containerY = 38;
        const containerH = 128; // Increased container height to 128mm to perfectly fit totals rows

        // Rounded container box around table
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        doc.roundedRect(tLeft, containerY, tWidth, containerH, 6, 6, 'S');

        // Draw header badges (rounded green pills)
        const drawHeaderPill = (colIdx, label) => {
            const w = Math.min(cW[colIdx] - 4, doc.getTextWidth(label) + 8);
            const x = cX[colIdx] + cW[colIdx] / 2;
            doc.setFillColor(6, 150, 104);
            doc.roundedRect(x - w / 2, containerY + 3.5, w, 8, 4, 4, 'F');
            doc.setTextColor(255, 255, 255); doc.setFontSize(8.2); doc.setFont("Montserrat", "bold");
            doc.text(label, x, containerY + 9, { align: 'center' });
        };

        drawHeaderPill(0, "Instrument");
        drawHeaderPill(1, "Current");
        drawHeaderPill(2, "Optimised");
        drawHeaderPill(3, "Change");
        drawHeaderPill(4, "Purpose");

        // Vertical dotted dividers between columns
        const drawDottedLine = (x, y1, y2) => {
            let cy = y1;
            while (cy < y2) {
                doc.line(x, cy, x, cy + 0.8);
                cy += 2;
            }
        };
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
        drawDottedLine(cX[1], containerY + 16, containerY + 104);
        drawDottedLine(cX[2], containerY + 16, containerY + 104);
        drawDottedLine(cX[3], containerY + 16, containerY + 104);
        drawDottedLine(cX[4], containerY + 16, containerY + 104);

        // Axis vertical line on the left side of the first column
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        doc.line(17, containerY + 21.5, 17, containerY + 98.5);

        let curY = containerY + 16;
        const rH = 11;

        const rowsData = [
            { i: "Joint Emergency Fund", c: currentEF, o: optimisedEF, p: "6-month safety net" },
            { i: `PPF (${hFirstName} + ${wFirstName})`, c: currentPPF, o: optimisedPPF, p: "Tax-free retirement" },
            { i: "Joint Mutual Fund SIP", c: currentMF, o: optimisedMF, p: "Wealth creation" },
            { i: "Joint ELSS SIP", c: currentELSS, o: optimisedELSS, p: "80C tax saving" },
            { i: "Joint NPS", c: currentNPS, o: optimisedNPS, p: "Extra 80CCD(1B)" },
            { i: "EPF (Combined)", c: currentEPF, o: optimisedEPF, p: "Automatic retirement" },
            { i: "Personal Loan Prepay", c: currentPL, o: optimisedPL, p: "Close 15% debt early" },
            { i: "Term Insurance (Joint)", c: currentIns, o: optimisedIns, p: "Family protection" }
        ];

        rowsData.forEach((row) => {
            const mid = curY + rH / 2;

            // Draw axis connector
            doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
            doc.line(17, mid, 20, mid);
            // Green dot at connector end
            doc.setFillColor(6, 150, 104); doc.circle(20, mid, 0.65, 'F');
            // Checkbox outline (Yathartha Green)
            doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.45);
            doc.roundedRect(22, mid - 2.5, 5, 5, 0.5, 0.5, 'S');

            // Col 1: Instrument (Bold Dark Navy)
            doc.setFont("Montserrat", "bold"); doc.setTextColor(15, 23, 42); doc.setFontSize(8.5);
            doc.text(row.i, 29, mid + 1.2);

            // Col 2: Current (Normal Slate)
            doc.setFont("Montserrat", "normal"); doc.setTextColor(71, 85, 105); doc.setFontSize(8.5);
            doc.text(`₹${fmt(row.c)}`, cX[1] + 3, mid + 1.2);

            // Col 3: Optimised (Bold Green)
            doc.setFont("Montserrat", "bold"); doc.setTextColor(6, 150, 104); doc.setFontSize(8.5);
            doc.text(`₹${fmt(row.o)}`, cX[2] + 3, mid + 1.2);

            // Col 4: Change (Conditional Color)
            const changeVal = row.o - row.c;
            if (changeVal > 0) {
                doc.setTextColor(6, 150, 104); doc.setFont("Montserrat", "bold");
            } else if (changeVal < 0) {
                doc.setTextColor(220, 38, 38); doc.setFont("Montserrat", "bold");
            } else {
                doc.setTextColor(100, 116, 139); doc.setFont("Montserrat", "normal");
            }
            const changeTxt = changeVal === 0 ? "No change" : (changeVal > 0 ? `+₹${fmt(changeVal)}` : `-₹${fmt(Math.abs(changeVal))}`);
            doc.text(changeTxt, cX[3] + 3, mid + 1.2);

            // Col 5: Purpose (Normal Slate)
            doc.setFont("Montserrat", "normal"); doc.setTextColor(71, 85, 105); doc.setFontSize(8);
            doc.text(row.p, cX[4] + 3, mid + 1.2);

            curY += rH;
        });

        // Totals calculation
        const totalCurrent = rowsData.reduce((acc, r) => acc + r.c, 0);
        const totalOptimised = rowsData.reduce((acc, r) => acc + r.o, 0);
        const currentRate = (combinedIncome > 0) ? ((totalCurrent / combinedIncome) * 100).toFixed(1) : "0.0";
        const optimisedRate = (combinedIncome > 0) ? Math.round((totalOptimised / combinedIncome) * 100) : 0;

        // Draw solid divider line for Totals section
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        doc.line(tLeft, curY, tRight, curY);

        // CURRENT SAVINGS row (clean white, divided)
        const midCurrent = curY + rH / 2;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(15, 23, 42); doc.setFontSize(9);
        doc.text("CURRENT SAVINGS", cX[0] + 3, midCurrent + 1.2);
        doc.text(`₹${fmt(totalCurrent)}`, cX[1] + 3, midCurrent + 1.2);
        doc.text(`${currentRate}% savings rate`, cX[4] + 3, midCurrent + 1.2);
        curY += rH;

        // Draw solid divider line between totals
        doc.line(tLeft, curY, tRight, curY);

        // OPTIMISED SAVINGS row (clean white with Green highlights)
        const midOptimised = curY + rH / 2;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(6, 150, 104); doc.setFontSize(9.5);
        doc.text("OPTIMISED SAVINGS", cX[0] + 3, midOptimised + 1.2);
        doc.text(`₹${fmt(totalOptimised)}`, cX[2] + 3, midOptimised + 1.2);
        doc.text(`${optimisedRate}% savings rate 🎉`, cX[4] + 3, midOptimised + 1.2);

        // Sub-footer (Custom high-fidelity position)
        doc.setTextColor(115, 115, 115); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
        const subFt = ` www.myyathartha.com | Confidential - for ${hFirstName.toLowerCase()} & ${wFirstName.toLowerCase()} only | Financial awareness & education report.`;
        doc.text(doc.splitTextToSize(subFt, pageWidth - 28), pageWidth / 2, pageHeight - 18.5, { align: 'center', lineHeightFactor: 1.2 });

        // Page number circle (Fixed at bottom)
        doc.setFillColor(30, 41, 59); doc.circle(pageWidth / 2, pageHeight - 7, 5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text("08", pageWidth / 2, pageHeight - 5, { align: 'center' });

        return;
    }

    // Dynamic values from user input
    const epfMonthly = safeParse(sInc.epf) || safeParse(sInv.epf) || metrics?.epfMonthly || 0;
    const sipMonthly = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
    const efMonthly = safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || metrics?.emergencyFundMonthly || 0;
    const ppfMonthly = safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics?.ppfMonthly || 0;
    const monthlySurplus = metrics?.monthlySurplus || 0;
    const actualLoanEMI = safeParse(personalLoanEMI) || safeParse(sInc.personalLoanEMI) || 0;

    // --- Header ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8); } catch (e) { } } catch (e) { }
    // Section badge removed

    // --- Main Banner ---
    let y = 30;
    doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("OPTIMISED SAVINGS & INVESTMENT PLAN", 18, y + 6.2);

    y += 18;
    doc.setTextColor(...localSlate); doc.setFontSize(8.8); doc.setFont("Montserrat", "normal");
    const userIncome = metrics?.monthlyIncome || safeParse(personalInfo.monthlySalary) || 0;
    const incomeStr = userIncome > 0 ? `\u20b9${fmt(userIncome)} salary` : "current salary";
    const introText = `Imagine waking up six months from now with your savings rate nearly tripled \u2014 without giving up a single dinner out or a single shopping trip. That is not a fantasy, ${name}. That is this plan. The same ${incomeStr}. The same lifestyle you love. But every rupee of your untracked surplus \u2014 the money that quietly disappears each month \u2014 now has a destination and a purpose.`;
    doc.text(doc.splitTextToSize(introText, pageWidth - 28), 14, y, { lineHeightFactor: 1.25 });

    y += 32;
    // --- High-Fidelity Card-Per-Column Architecture (Dynamic Polish) ---
    const hx = [14, 56, 82, 107, 131, 166];
    const hw = [39, 23, 22, 21, 32, 30];
    const hdrs = ["Savings Instrument", "Current/mo", "Optimised/mo", "Change", "Purpose", "Expected Return"];
    const colY = y + 2;

    // --- Dynamic Content Prep (Deep System Binding) ---
    const epfM = metrics?.epfMonthly || 0;
    const sipM = metrics?.sipMonthly || 0;
    const efM = metrics?.emergencyFundMonthly || 0;
    const ppfM = metrics?.ppfMonthly || 0;
    const mSurplus = metrics?.monthlySurplus || 0;

    // Intelligent Recommendations Logic
    // Redirect surplus: Priority to EF gap, then Debt, then Tax, then Wealth
    let remainingSurplus = mSurplus;

    // 1. Priority: Emergency Fund (Allocate 40% of surplus until funded)
    const targetEF = safeParse(emergencyFundTarget) || 0;
    const efGap = Math.max(0, targetEF - efM);
    const recEF_Add = efGap > 0 ? Math.min(remainingSurplus * 0.4, efGap) : 0;
    const recEF = efM + recEF_Add;
    remainingSurplus -= recEF_Add;

    // 2. Priority: Debt Prepayment (Allocate 30% of remaining surplus)
    const recPrepay_Add = actualLoanEMI > 0 ? Math.min(remainingSurplus * 0.5, actualLoanEMI * 0.5) : 0;
    const recPrepay = recPrepay_Add;
    remainingSurplus -= recPrepay_Add;

    // 3. Priority: Tax Saving (ELSS/NPS)
    const elssNeeded = Math.max(0, (taxSavingsMonthly || 0));
    const recELSS = Math.min(remainingSurplus * 0.6, elssNeeded);
    remainingSurplus -= recELSS;

    const recNPS = Math.min(remainingSurplus * 0.2, 4166); // 50k / 12 for 80CCD
    remainingSurplus -= recNPS;

    // 4. Priority: Remaining to Wealth (SIPs)
    const recSIP = sipM + remainingSurplus;

    const rows = [
        { i: "Emergency Fund", c: efM, o: recEF, p: "Building 6-month safety net", r: "5-7%\n(liquid fund)" },
        { i: "PPF", c: ppfM, o: ppfM, p: "Safe tax-free retirement", r: "7.1%\n(guaranteed)" },
        { i: "Mutual Fund SIP", c: sipM, o: recSIP, p: "Long term wealth creation", r: "10-14%\n(equity)" },
        { i: "ELSS SIP", c: 0, o: recELSS, p: "Tax saving + equity growth", r: "10-14%\n(equity)" },
        { i: "NPS Tier 1", c: 0, o: recNPS, p: "Retirement + 80CCD tax dedn", r: "9-11%\n(balanced)" },
        { i: "EPF (Auto)", c: epfM, o: epfM, p: "Automatic retirement savings", r: "8.1%\n(guaranteed)" },
        { i: "Personal Loan Prepay", c: 0, o: recPrepay, p: "Reduce debt & interest", r: "14% saved\n(guaranteed!)" },
        { i: "Term Insurance", c: 0, o: (calculations?.grades?.insurance !== 'A' ? 1000 : 0), p: "Pure family protection", r: "Priceless" },
    ].filter(r => r.c > 0 || r.o > 0); // Only show relevant rows

    // Headers Row (Perfectly Clean)
    doc.setTextColor(6, 150, 104); doc.setFontSize(8.4); doc.setFont("Montserrat", "bold");
    hdrs.forEach((h, i) => {
        const cx = hx[i] + (hw[i] / 2);
        doc.text(doc.splitTextToSize(h, hw[i]), cx, y - 2, { align: 'center', lineHeightFactor: 1.15 });
    });

    // Draw data BEFORE boxes for proper layering
    y += 8;
    rows.forEach((r, idx) => {
        const ry = y + (idx * 11);
        const change = r.o - r.c;
        const changeTxt = change === 0 ? "No change" : (change > 0 ? `+₹${fmt(change)}` : `₹${fmt(change)}`);

        // Instrument (Left Aligned)
        doc.setTextColor(30, 41, 59); doc.setFont("Montserrat", "bold"); doc.setFontSize(8.8);
        doc.text(r.i, hx[0] + 3, ry + 1);

        // Data (Centred)
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.4);
        doc.setTextColor(115, 115, 115); doc.text(`₹${fmt(r.c)}`, hx[1] + (hw[1] / 2), ry + 1, { align: 'center' });
        doc.setTextColor(6, 150, 104); doc.setFont("Montserrat", "bold"); doc.text(`₹${fmt(r.o)}`, hx[2] + (hw[2] / 2), ry + 1, { align: 'center' });
        doc.setTextColor(85, 85, 85); doc.setFont("Montserrat", "normal"); doc.setFontSize(8.1); doc.text(changeTxt, hx[3] + (hw[3] / 2), ry + 1, { align: 'center' });

        // Purpose & Return (Multi-line spacing)
        doc.text(doc.splitTextToSize(r.p, hw[4] - 4), hx[4] + 2, ry + 1, { lineHeightFactor: 1.15 });
        doc.setFontSize(7.6); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        const retLines = doc.splitTextToSize(r.r, hw[5] - 2);
        retLines.forEach((line, li) => {
            const ly = ry + 1 + (li * 3.5) - (retLines.length > 1 ? 1.5 : 0);
            doc.text(line, hx[5] + 2, ly);
        });

        // Shared Dotted Dividers
        if (idx < rows.length - 1) {
            doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.15); doc.setLineDash([0.5, 1], 0);
            doc.line(14, ry + 5, pageWidth - 14, ry + 5);
            doc.setLineDash([], 0);
        }
    });

    // Draw Final Column Boxes (100% Brand Green borders)
    hdrs.forEach((h, i) => {
        doc.setDrawColor(6, 150, 104);
        doc.setLineWidth(i === 2 ? 0.65 : 0.45);
        doc.roundedRect(hx[i], colY, hw[i], 90, 4, 4, 'S');
    });

    y = colY + 98;
    doc.setFillColor(6, 150, 104); doc.rect(14, y, pageWidth - 28, 8.5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(10.8); doc.setFont("Montserrat", "bold");
    doc.text("THE TRANSFORMATION: Same Income. Triple the Savings.", pageWidth / 2, y + 5.8, { align: 'center' });

    y += 18;
    doc.setTextColor(31, 41, 55); doc.setFontSize(11.5); doc.text("The transformation summary:", 16, y);

    y += 8;
    // --- Transformation Cards (High-Fidelity Colors) ---
    const totalCurrent = rows.reduce((acc, r) => acc + r.c, 0);
    const totalOptimised = rows.reduce((acc, r) => acc + r.o, 0);
    const currentRate = Math.round((totalCurrent / (userIncome || 1)) * 100);
    const optimisedRate = Math.round((totalOptimised / (userIncome || 1)) * 100);

    const cw = (pageWidth - 40) / 3;
    const drawTCard = (tx, title, oldV, newV, desc) => {
        doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.45);
        doc.roundedRect(tx, y, cw, 52, 6, 6, 'S');

        // Label Badge (Dark Slate) - Widened to 38mm for better fit
        const badgeW = 38;
        const badgeX = tx + (cw - badgeW) / 2;
        doc.setFillColor(30, 41, 59); doc.roundedRect(badgeX, y - 4, badgeW, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.2); doc.setFont("Montserrat", "bold");
        doc.text(title, tx + cw / 2, y + 0.2, { align: 'center' });

        // Old Value (Vivid Red)
        doc.setTextColor(220, 38, 38); doc.setFontSize(16); doc.setFont("Montserrat", "bold");
        doc.text(oldV, tx + cw / 2, y + 15, { align: 'center' });

        // Arrow
        doc.setTextColor(30, 41, 59); doc.setFontSize(14); doc.text("\u2193", tx + cw / 2, y + 23, { align: 'center' });

        // New Value (Official Green)
        doc.setTextColor(6, 150, 104); doc.setFontSize(18);
        doc.text(newV, tx + cw / 2, y + 36, { align: 'center' });

        doc.setTextColor(30, 41, 59); doc.setFontSize(7.8); doc.setFont("Montserrat", "normal");
        doc.text(doc.splitTextToSize(desc, cw - 6), tx + cw / 2, y + 43, { align: 'center', lineHeightFactor: 1.1 });
    };

    const oldM = rows.reduce((acc, current) => acc + current.c, 0);
    const newM = rows.reduce((acc, current) => acc + current.o, 0);
    const mIncome = (metrics?.totalMonthlyIncome || metrics?.monthlyIncome || safeParse(personalInfo.monthlySalary) || 0);
    const oldR = mIncome > 0 ? ((oldM / mIncome) * 100).toFixed(1) : "0.0";
    const newR = mIncome > 0 ? ((newM / mIncome) * 100).toFixed(1) : "0.0";

    // Dynamic multiplier
    const multiplier = oldM > 0 ? (newM / oldM).toFixed(1) : (newM / (mIncome * 0.1 || 1)).toFixed(1);
    const multiplierText = oldM > 0 ? `${multiplier}\u00D7 improvement` : "Dramatic transformation";

    // Dynamic surplus source text
    const totalExp = (metrics?.totalEssentialExpenses || 0) + (metrics?.totalLifestyleExpenses || 0);
    const surplusSource = totalExp === 0 ? "untracked surplus" : "optimised surplus";

    drawTCard(14, "Savings rate:", `${oldR}%`, `${newR}%`, `(${multiplierText} without cutting a single lifestyle expense)`);
    drawTCard(14 + cw + 6, "Monthly savings:", `\u20B9${fmt(oldM)}`, `\u20B9${fmt(newM)}`, `(+\u20B9${fmt(newM - oldM)}/month redirected from ${surplusSource})`);
    drawTCard(14 + cw * 2 + 12, "Annual savings:", `\u20B9${fmt(oldM * 12)}`, `\u20B9${fmt(newM * 12)}`, `(+\u20B9${fmt((newM - oldM) * 12)}/year working for your future)`);

    // Sub-footer (Custom high-fidelity position)
    doc.setTextColor(115, 115, 115); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
    const subFt = ` www.myyathartha.com | Confidential - for ${(name || "suresh").toLowerCase()} only | Financial awareness & education report.`;
    doc.text(doc.splitTextToSize(subFt, pageWidth - 28), pageWidth / 2, pageHeight - 18.5, { align: 'center', lineHeightFactor: 1.2 });

    // Page number circle (Fixed at bottom)
    doc.setFillColor(30, 41, 59); doc.circle(pageWidth / 2, pageHeight - 7, 5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text("13", pageWidth / 2, pageHeight - 5, { align: 'center' });
};

// PAGE: Goals & Corpus Projection
export const renderGoalsPage = (doc, { name, metrics, m, personalInfo, pageWidth, pageHeight }) => {
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    addHeader(doc, 9, pageWidth);
    addGreenBanner(doc, "GOAL-BASED INVESTMENT PLAN", 20, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    doc.text(doc.splitTextToSize(`Investments without goals are like driving without a destination. Every rupee you invest should be tied to a specific goal with a specific timeline. Here is ${name}'s personalised, goal-based investment portfolio:`, pageWidth - 28), 14, 36);

    const years = 60 - (parseInt(personalInfo?.age) || 32);
    const userIncome = metrics?.monthlyIncome || safeParse(personalInfo.monthlySalary) || 0;
    const monthlyIncome = userIncome; // Alias for use in goals array
    const monthlyExpenses = m?.totalEssentialExpenses || (m?.totalMonthlyIncome * 0.4) || 30000; // Robust fallback
    const epfMonthly = metrics?.epfMonthly || (userIncome > 0 ? Math.round(userIncome * 0.12 / 1000) * 1000 : 0);
    const ppfMonthly = metrics?.ppfMonthly || (userIncome > 0 ? Math.round(userIncome * 0.08 / 500) * 500 : 0);
    const npsMonthly = metrics?.npsMonthly || (userIncome > 0 ? Math.round(userIncome * 0.05 / 500) * 500 : 0);
    const sipMonthly = metrics?.sipMonthly || (userIncome > 0 ? Math.round(userIncome * 0.1 / 1000) * 1000 : 0);
    const epfCorpus = Math.round((epfMonthly * 2) * 12 * ((Math.pow(1.081, years) - 1) / 0.081));
    const ppfCorpus = Math.round(ppfMonthly * 12 * ((Math.pow(1.071, years) - 1) / 0.071));
    const npsCorpus = Math.round(npsMonthly * 12 * ((Math.pow(1.10, years) - 1) / 0.10));
    const sipCorpus = Math.round(sipMonthly * 12 * ((Math.pow(1.12, years) - 1) / 0.12));
    const totalCorpus = epfCorpus + ppfCorpus + npsCorpus + sipCorpus;
    const targetEF = m?.targetEmergency || m?.emergencyFundTarget || (monthlyExpenses * 6);

    const goals = [
        { goal: "Emergency\nSafety Net", inst: "Liquid Mutual\nFund / Flexi RD", sip: Math.max(1000, Math.round(monthlyExpenses * 0.1 / 1000) * 1000), tl: "18 months", corpus: `Rs.${(targetEF / 100000).toFixed(1)}L`, why: "Instant withdrawal. 5-7% returns. Better than idle cash." },
        { goal: "Tax Saving\n+ Growth", inst: "ELSS Mutual\nFund", sip: Math.max(1000, Math.round((monthlyIncome || 0) * 0.05 / 1000) * 1000), tl: "3+ years", corpus: "Part of\nretirement", why: "3-year lock-in builds habit. 80C benefit + equity returns." },
        { goal: "Retirement\n(Safe, Tax-Free)", inst: "PPF", sip: Math.max(500, Math.round((monthlyIncome || 0) * 0.08 / 500) * 500), tl: `${years} years`, corpus: `Rs.${(ppfCorpus / 100000).toFixed(0)}+L`, why: "Sovereign guarantee. 7.1% p.a. Completely tax-free at maturity." },
        { goal: "Retirement\n(Extra Deduction)", inst: "NPS Tier 1", sip: Math.max(500, Math.round((monthlyIncome || 0) * 0.05 / 500) * 500), tl: `${years} years`, corpus: `Rs.${(npsCorpus / 100000).toFixed(0)}+L`, why: "Extra Rs.50,000 80CCD deduction. 60% lump sum tax-free at 60." },
        { goal: "Retirement\n(Growth Engine)", inst: "Existing SIP", sip: Math.max(1000, Math.round((monthlyIncome || 0) * 0.1 / 1000) * 1000), tl: `${years} years`, corpus: `Rs.${(sipCorpus / 10000000).toFixed(1)}+Cr`, why: "Equity compounding over long horizon. Core wealth creator." },
        { goal: "Retirement\n(Auto)", inst: "EPF", sip: Math.max(1000, Math.round((monthlyIncome || 0) * 0.12 / 1000) * 1000), tl: "Till retirement", corpus: `Rs.${(epfCorpus / 10000000).toFixed(1)}+Cr`, why: "Employer match doubles it. 8.1% guaranteed. Tax-free at 60." },
    ];

    // Table header
    const ty2 = 52;
    const gcols = [14, 44, 80, 102, 128, 162];
    const ghdrs = ["Your Goal", "Instrument", "Monthly\nInvest", "Timeline", "Target\nCorpus", "Why This\nInstrument"];
    doc.setFillColor(...G); doc.roundedRect(14, ty2, pageWidth - 28, 10, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    ghdrs.forEach((h, i) => doc.text(h.replace('\n', ' '), gcols[i] + 1, ty2 + 6));

    let gy = ty2 + 11;
    goals.forEach((g, idx) => {
        const rh = 14;
        const bg = idx % 2 === 0 ? [255, 255, 255] : LIGHT;
        doc.setFillColor(...bg); doc.rect(14, gy, pageWidth - 28, rh, 'F');
        doc.setTextColor(...SLATE); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        g.goal.split('\n').forEach((l, li) => doc.text(l, gcols[0] + 1, gy + 5 + li * 4.5));
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
        g.inst.split('\n').forEach((l, li) => doc.text(l, gcols[1] + 1, gy + 5 + li * 4.5));
        doc.text(`Rs.${g.sip.toLocaleString('en-IN')}`, gcols[2] + 1, gy + 7);
        doc.text(g.tl, gcols[3] + 1, gy + 7);
        doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        g.corpus.split('\n').forEach((l, li) => doc.text(l, gcols[4] + 1, gy + 5 + li * 4.5));
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8);
        doc.text(doc.splitTextToSize(g.why, 36), gcols[5] + 1, gy + 5);
        gy += rh + 1;
    });
    // Total row
    doc.setFillColor(...G); doc.roundedRect(14, gy, pageWidth - 28, 9, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5);
    doc.text("Total Investment", gcols[0] + 1, gy + 6);
    doc.text("All above", gcols[1] + 1, gy + 6);
    doc.text(`Rs.${(3600 + 3500 + 3000 + 3000 + 2000 + 10000 + 5000 + 900).toLocaleString('en-IN')}/mo`, gcols[2] + 1, gy + 6);
    doc.setTextColor(255, 255, 0); doc.text(`Rs.${(totalCorpus / 10000000).toFixed(2)} Cr target`, gcols[4] + 1, gy + 6);
    doc.setTextColor(255, 255, 255); doc.text("Diversified &\noptimised", gcols[5] + 1, gy + 5);
    gy += 12;

    // Corpus projection
    doc.setTextColor(...G); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text("Retirement Corpus Projection", 14, gy + 8);
    gy += 12;
    doc.setTextColor(...SLATE); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    doc.text(`Your Retirement Projection at Age 60 (${years} years from now):`, 14, gy);
    gy += 7;

    const projections = [
        { label: "EPF corpus", calc: `(Rs.${((metrics?.epfMonthly || 0) * 2).toLocaleString('en-IN')}+employer) × ${years}y × 8.1% p.a.`, val: `Rs.${(epfCorpus / 10000000).toFixed(2)} Crore` },
        { label: "PPF corpus", calc: `Rs.${fmt(ppfMonthly)}/month × ${years}y × 7.1% p.a.`, val: `Rs.${(ppfCorpus / 100000).toFixed(0)} Lakhs` },
        { label: "NPS corpus", calc: `Rs.${fmt(npsMonthly)}/month × ${years}y × 10% p.a.`, val: `Rs.${(npsCorpus / 100000).toFixed(0)} Lakhs` },
        { label: "SIP corpus", calc: `Rs.${fmt(sipMonthly)}/month combined × ${years}y × 12% p.a.`, val: `Rs.${(sipCorpus / 10000000).toFixed(2)} Crore` },
    ];
    projections.forEach(p => {
        doc.setFillColor(...LIGHT); doc.roundedRect(14, gy, pageWidth - 28, 9, 1, 1, 'F');
        doc.setTextColor(...G); doc.setFont("Montserrat", "bold"); doc.setFontSize(8);
        doc.text(`• ${p.label}`, 17, gy + 6);
        doc.setTextColor(...SLATE); doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
        doc.text(`(${p.calc}):`, 52, gy + 6);
        doc.setTextColor(...G); doc.setFont("Montserrat", "bold"); doc.setFontSize(8);
        doc.text(`≈ ${p.val}`, pageWidth - 14, gy + 6, { align: 'right' });
        gy += 11;
    });
    doc.setDrawColor(...G); doc.setLineWidth(0.5); doc.line(14, gy, pageWidth - 14, gy);
    gy += 5;
    doc.setTextColor(...G); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
    doc.text(`TOTAL PROJECTED CORPUS: Rs.${(totalCorpus / 10000000).toFixed(2)} Crore`, 14, gy);
    gy += 6;
    doc.setTextColor(...SLATE); doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    const target = monthlyExpenses > 0 ? Math.round(monthlyExpenses * 12 * 25 / 10000000 * 10) / 10 : 0;
    doc.text(`Target needed: Rs.${target} Crore. You are projected to ${totalCorpus / 10000000 > target ? 'EXCEED' : 'approach'} target by Rs.${Math.abs(Math.round((totalCorpus / 10000000 - target) * 100) / 10)} Lakhs!`, 14, gy);

    // Footer handled by generator
};

// PAGE: Final Word (last page of reference)
// Page 16 - Goal-Based Investment Plan (Fully Dynamic)
export const renderGoalBasedInvestmentPlan = (doc, { name, metrics, calculations, personalInfo, pageWidth, pageHeight, report = {} }) => {
    const BRAND_GREEN = [6, 150, 104];
    const NAVY_DARK = [15, 23, 42];
    const SLATE = [30, 41, 59];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    addHeader(doc, "09", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("GOAL-BASED INVESTMENT PLAN", 18, y + 5.5);

    y += 12;
    doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    const intro = `Investments without goals are like driving without a destination. Every rupee you invest should be tied to a specific goal with a specific timeline. Here is ${name}'s personalised, goal-based investment portfolio:`;
    doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, y, { lineHeightFactor: 1.2 });

    y += 12;
    // --- GOALS TABLE ---
    const colX = { goal: 14, inst: 48, monthly: 78, timeline: 104, target: 128, why: 156 };
    const rowH = 16.5;

    // Header Bar
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 10, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("Your Goal", 17, y + 6);
    doc.text("Instrument", colX.inst + 12, y + 6, { align: 'center' });
    doc.text("Monthly\nInvest", colX.monthly + 10, y + 4, { align: 'center', lineHeightFactor: 0.9 });
    doc.text("Timeline", colX.timeline + 11, y + 6, { align: 'center' });
    doc.text("Target\nCorpus", colX.target + 12, y + 4, { align: 'center', lineHeightFactor: 0.9 });
    doc.text("Why This\nInstrument", colX.why + 16, y + 4, { align: 'center', lineHeightFactor: 0.9 });

    // Dynamic Logic (Thorough Extraction)
    const sInv = report?.salaryInvestment || {};
    const sInc = report?.salaryIncome || {};
    const pInfo = report?.personalInfo || personalInfo || {};

    const currentAge = parseInt(pInfo.age) || 32;
    const yearsToRetire = Math.max(1, 60 - currentAge);
    const monthlyIncomeVal = safeParse(pInfo.monthlySalary) || metrics?.monthlyIncome || 0;

    // Direct binding from form inputs
    const valEPF = safeParse(sInc.epf) || safeParse(sInv.epf) || metrics.epfMonthly || 0;
    const rawPPF = safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics.ppfMonthly || 0;
    const valPPF = rawPPF === 0 ? 1000 : rawPPF;
    const rawNPS = safeParse(sInc?.nps_80ccd2) || safeParse(sInv?.nps_80ccd2) || 0;
    const valNPS = rawNPS === 0 ? 1000 : rawNPS;
    const valSIP = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics.sipMonthly || 0;
    const valELSS = safeParse(sInv.elss) || Math.max(0, (metrics.totalMonthlyInvestments || 0) - (valEPF + valPPF + valNPS + valSIP));
    const valEmerg = safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || metrics.emergencyFundMonthly || 0;

    const totalInvested = valEPF + valPPF + valNPS + valSIP + valELSS + valEmerg;

    // Future Value Helper
    const calculateFV = (P, r, n) => {
        if (P <= 0) return 0;
        const monthlyRate = r / 12 / 100;
        const months = n * 12;
        return P * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    };

    const corpusEPF = calculateFV(valEPF * 2, 8.1, yearsToRetire);
    const corpusPPF = calculateFV(valPPF, 7.1, yearsToRetire);
    const corpusNPS = calculateFV(valNPS, 10, yearsToRetire);
    const corpusSIP = calculateFV(valSIP + valELSS, 12, yearsToRetire);
    const totalProjected = corpusEPF + corpusPPF + corpusNPS + corpusSIP;

    // Target Needed calculation
    const targetNeeded = Math.round((monthlyIncomeVal * 12 * 20) / 10000000) || 3.5;
    const finalTargetCr = Math.max(3.5, targetNeeded);

    const isNewRegime = report?.taxOptimization?.recommendedRegime === 'New' || metrics?.taxRegime === 'New';

    const goalData = [
        { g: "Emergency\nSafety Net", i: "Liquid Mutual\nFund / Flexi RD", m: `\u20B9${fmt(valEmerg)}`, t: "18 months", c: `\u20B9${((metrics.targetEmergency || 270000) / 100000).toFixed(1)} \nLakhs`, w: `Instant withdrawal logic for ${name}. 5-7% returns. Safe & better than idle cash.` },
        { g: "Tax Saving\n+ Growth", i: "ELSS Mutual\nFund", m: `\u20B9${fmt(valELSS)}`, t: "3+ years", c: "Part of\nretirement", w: isNewRegime ? "Pure equity growth engine with shortest lock-in (3yr) for wealth creation." : "Dual benefit: 80C tax deduction + highest equity returns in tax category." },
        { g: "Retirement\n(Safe, Tax-Free)", i: "PPF", m: `\u20B9${fmt(valPPF)}`, t: `${yearsToRetire} years`, c: `\u20B9${(corpusPPF / 100000).toFixed(0)}+\nLakhs`, w: `Sovereign guarantee. 7.1% p.a. tax-free corpus for ${name} at age 60.` },
        { g: "Retirement\n(Extra Deduction)", i: "NPS Tier 1", m: `\u20B9${fmt(valNPS)}`, t: `${yearsToRetire} years`, c: `\u20B9${(corpusNPS / 100000).toFixed(0)}+\nLakhs`, w: `Extra \u20B950,000 80CCD deduction for ${name}. 60% lump sum tax-free at 60.` },
        { g: "Retirement\n(Growth Engine)", i: "Existing SIP", m: `\u20B9${fmt(valSIP)}`, t: `${yearsToRetire} years`, c: `\u20B9${(corpusSIP / 10000000).toFixed(1)}+\nCrore`, w: `Equity compounding for ${name} over ${yearsToRetire} year horizon. Core wealth creator.` },
        { g: "Retirement\n(Auto)", i: "EPF", m: `\u20B9${fmt(valEPF)}`, t: "Till retirement", c: `\u20B9${(corpusEPF / 10000000).toFixed(1)}+\nCrore`, w: `Employer match doubles it. 8.1% guaranteed & tax-free for ${name}'s retirement.` }
    ];

    let ry = y + 10;
    goalData.forEach((r, idx) => {
        const mid = ry + rowH / 2;
        if (idx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(14, ry, pageWidth - 28, rowH, 'F');
        }

        doc.setTextColor(...NAVY_DARK); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(r.g, 17, mid - 1, { lineHeightFactor: 1 });

        // Instrument Pill
        doc.setFillColor(209, 250, 229); doc.roundedRect(colX.inst + 2, ry + 3.5, 24, rowH - 7, 2.5, 2.5, 'F');
        doc.setTextColor(5, 150, 105); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(doc.splitTextToSize(r.i, 20), colX.inst + 14, mid - 1.5, { align: 'center', lineHeightFactor: 1 });

        doc.setTextColor(31, 41, 55); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(r.m, colX.monthly + 10, mid + 0.5, { align: 'center' });

        doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        doc.text(r.t, colX.timeline + 11, mid + 0.5, { align: 'center' });

        doc.setTextColor(31, 41, 55); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(doc.splitTextToSize(r.c, 24), colX.target + 12, mid - 1.5, { align: 'center', lineHeightFactor: 0.9 });

        doc.setTextColor(107, 114, 128); doc.setFontSize(7.5); doc.setFont("Montserrat", "normal");
        doc.text(doc.splitTextToSize(r.w, 38), colX.why, mid - 3, { lineHeightFactor: 1.05 });

        ry += rowH;
    });

    // Total Bar
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, ry, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");

    // 1. Total Investment (Col 1)
    doc.text("Total Investment", 31, ry + 6, { align: 'center' });
    doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.3); doc.line(48, ry + 1.5, 48, ry + 7.5);

    // 2. All above (Col 2)
    doc.text("All above", 63, ry + 6, { align: 'center' });
    doc.line(78, ry + 1.5, 78, ry + 7.5);

    // 3. Amount (Col 3 & 4 combined)
    doc.text(`\u20B9${fmt(totalInvested)}/mo`, 103, ry + 6, { align: 'center' });
    doc.line(128, ry + 1.5, 128, ry + 7.5);

    // 4. Target (Col 5)
    doc.text(`\u20B9${(totalProjected / 10000000).toFixed(1)} Cr\ntarget`, 142, ry + 4, { align: 'center', lineHeightFactor: 0.85 });
    doc.line(156, ry + 1.5, 156, ry + 7.5);

    // 5. Why (Col 6)
    doc.text("Diversified &\noptimised", 176, ry + 4, { align: 'center', lineHeightFactor: 0.85 });

    y = ry + 16;
    // --- Retirement Projection Area ---
    doc.setTextColor(...BRAND_GREEN); doc.setFontSize(12); doc.setFont("Montserrat", "bold");
    doc.text("Retirement Corpus Projection", 14, y);
    y += 7;
    doc.setTextColor(107, 114, 128); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    doc.text(`Your Retirement Projection at Age 60 (${yearsToRetire} years from now):`, 14, y);

    y += 12;
    const formatCrLakh = v => v >= 10000000 ? `\u2248 \u20B9${(v / 10000000).toFixed(2)} Crore` : `\u2248 \u20B9${(v / 100000).toFixed(1)} Lakhs`;

    const projData = [
        { l: "EPF corpus", f: `(\u20B9${fmt(valEPF)}+\u20B9${fmt(valEPF)} employer \u00D7 ${yearsToRetire} years \u00D7 8.1% p.a.):`, v: formatCrLakh(corpusEPF) },
        { l: "PPF corpus", f: `(\u20B9${fmt(valPPF)}/month \u00D7 ${yearsToRetire} years \u00D7 7.1% p.a.):`, v: formatCrLakh(corpusPPF) },
        { l: "NPS corpus", f: `(\u20B9${fmt(valNPS)}/month \u00D7 ${yearsToRetire} years \u00D7 10% p.a.):`, v: formatCrLakh(corpusNPS) },
        { l: "SIP corpus", f: `(\u20B9${fmt(valSIP + valELSS)}/month combined \u00D7 ${yearsToRetire} years \u00D7 12% p.a.):`, v: formatCrLakh(corpusSIP) }
    ];

    projData.forEach((p, idx) => {
        const py = y + (idx * 11);
        doc.setFillColor(...BRAND_GREEN); doc.circle(30, py - 3, 0.6, 'F');
        doc.setTextColor(...NAVY_DARK); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(p.l, 35, py);

        // Formula Capsule
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        doc.roundedRect(72, py - 4.5, 95, 7, 3.5, 3.5, 'S');
        doc.setTextColor(107, 114, 128); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        doc.text(p.f, 75, py);

        // Connector Red Dot
        doc.setFillColor(222, 59, 51); doc.circle(172, py - 1, 0.8, 'F');
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.3);
        doc.line(167, py - 1, 172, py - 1);

        // Result Capsule
        doc.setFillColor(255, 255, 255); doc.setDrawColor(52, 211, 153); doc.setLineWidth(0.8);
        doc.roundedRect(175, py - 4.5, 28, 7, 3.5, 3.5, 'S');
        doc.setTextColor(...NAVY_DARK); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text(p.v, 189, py, { align: 'center' });
    });

    y += 48;
    // --- Total Summary ---
    const totalProjStr = (totalProjected / 10000000).toFixed(2);
    doc.setTextColor(...BRAND_GREEN); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text(`TOTAL PROJECTED CORPUS: \u20B9${totalProjStr} Crore`, 14, y);
    const totW = doc.getTextWidth(`TOTAL PROJECTED CORPUS: \u20B9${totalProjStr} Crore`);
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    // Manual dash for line
    let curX = 14 + totW + 4;
    while (curX < pageWidth - 14) {
        doc.line(curX, y - 1, curX + 1.5, y - 1);
        curX += 3;
    }

    y += 6.5;
    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const diff = totalProjected - (finalTargetCr * 10000000);
    const exceedStr = diff >= 0 ? `EXCEED target by \u20B9${(diff / 100000).toFixed(0)} Lakhs` : `fall SHORT by \u20B9${(Math.abs(diff) / 100000).toFixed(0)} Lakhs`;
    doc.text(`Target needed: \u20B9${finalTargetCr} Crore. You are projected to ${exceedStr}!`, 14, y);

    y += 12;
    // --- Precaution Box ---
    doc.setDrawColor(222, 59, 51); doc.setLineWidth(0.4);
    doc.roundedRect(14, y, pageWidth - 28, 14, 2, 2, 'S');
    doc.setTextColor(222, 59, 51); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("PRECAUTION: ", 18, y + 6);
    const ptw = doc.getTextWidth("PRECAUTION: ");
    doc.setTextColor(115, 115, 115); doc.setFont("Montserrat", "normal"); doc.setFontSize(8);
    const pText = "Market-linked returns (SIP, ELSS, NPS equity component) are not guaranteed. Actual returns depend on market performance. The projections above use conservative estimates. Diversify and review annually. Past performance does not guarantee future results.";
    doc.text(doc.splitTextToSize(pText, pageWidth - 42 - ptw), 18 + ptw, y + 6, { lineHeightFactor: 1.15 });

    // --- Footer ---
    doc.setTextColor(156, 163, 175); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    const footText = ` www.myyathartha.com | Confidential - for ${(name || "neha").toLowerCase()} only | Financial awareness & education report.`;
    doc.text(doc.splitTextToSize(footText, pageWidth - 42), pageWidth / 2, pageHeight - 14, { align: 'center', lineHeightFactor: 1.1 });
    addSimpleFooter(doc, "16", pageWidth, pageHeight);
};

export const renderJointDebtAndGoalsPage = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report = {}, pageNum = "10" }) => {
    const fmt = v => Math.round(v || 0).toLocaleString('en-IN');

    // Color Palette matching Section 7 and Joint Tax Optimisation Page
    const COL_BLUE = [0, 0, 180];
    const COL_HEADER = [20, 30, 60];
    const COL_GRID = [205, 215, 220];
    const COL_TEXT = [15, 23, 42];
    const COL_SLATE = [30, 41, 59];
    const COL_GREEN = [6, 140, 96];
    const COL_RED = [220, 38, 38];
    const BRAND_GREEN = [6, 150, 104];

    const tLeft = 14;
    const tRight = pageWidth - 14;
    const tWidth = tRight - tLeft; // 182mm
    const FS = 8.5;
    const LH = 4.4;
    const PAD = 2.5;

    // Helper to format values as Lakhs/Crores/etc.
    const fmtLakh = v => {
        if (!v) return "₹0";
        if (v >= 10000000) return `₹${(v / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
        if (v >= 100000) return `₹${(v / 100000).toFixed(1).replace(/\.0$/, '')}L`;
        return `₹${v.toLocaleString('en-IN')}`;
    };

    // Helper for safe parsing and fallback defaults
    const getValOrDefault = (val, def) => (val !== undefined && val !== null && val !== "") ? safeParse(val) : def;

    // ========== DYNAMIC DATA ==========
    const jointSalary = report?.jointSalary || {};
    const sectionA = jointSalary.sectionA || {};
    const sectionB = jointSalary.sectionB || {};
    const sectionC = jointSalary.sectionC || {};
    const sectionD = jointSalary.sectionD || {};
    const sectionE = jointSalary.sectionE || {};
    const sectionF = jointSalary.sectionF || {};
    const sectionG = jointSalary.sectionG || {};
    const sectionI = jointSalary.sectionI || {};

    // Names & Ages
    const hAge = parseInt(sectionA.husbandAge) || 34;
    const wAge = parseInt(sectionA.wifeAge) || 32;

    // 1. Debt Details
    const plOS = getValOrDefault(sectionF.personalLoanOutstanding, 350000);
    const plEMI = getValOrDefault(sectionF.personalLoanEMI || sectionC.personalLoanEMI, 12000);
    const plRate = getValOrDefault(sectionF.personalLoanRate, 15);

    const hlOS = getValOrDefault(sectionF.homeLoanOutstanding, 4500000);
    const hlEMI = getValOrDefault(sectionF.homeLoanEMI || sectionC.homeLoanEMI, 32000);
    const hlRate = getValOrDefault(sectionF.homeLoanInterestRate || sectionF.homeLoanRate, 8.5);

    // Debt prepayment calculations
    const prepayAmount = plOS > 0 ? Math.round(plOS * 0.0715 / 5000) * 5000 : 25000;
    const monthsToClose = plEMI + prepayAmount > 0 ? Math.ceil(plOS / (plEMI + prepayAmount)) : 10;

    // 2. Expenses for Emergency Fund Target
    const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
    const clEMI = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;
    const transport = (safeParse(sectionC.transport) || 0) + clEMI;
    const dining = safeParse(sectionC.dining) || 0;
    const shopping = safeParse(sectionC.shopping) || 0;
    const insurance = (safeParse(sectionC.healthInsurance) || 0) +
        (safeParse(sectionE.husbandLifePremium) || 0) +
        (safeParse(sectionE.wifeLifePremium) || 0) +
        (safeParse(sectionE.husbandHealthPremium) || 0) +
        (safeParse(sectionE.wifeHealthPremium) || 0);

    const combinedMonthly = (safeParse(sectionB.husbandSalary) || 0) + (safeParse(sectionB.husbandOtherIncome) || 0) +
        (safeParse(sectionB.wifeSalary) || 0) + (safeParse(sectionB.wifeOtherIncome) || 0);
    const jointExpenses = groceriesUtilities + transport + dining + shopping + insurance + hlEMI + plEMI;
    const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
    const targetEF = getValOrDefault(sectionG.emergencyFundTarget || calculations?.metrics?.targetEmergency, monthlyExpenses * 6 || 600000);

    // 3. Goal Details
    const efTargetVal = targetEF;
    const efMonthly = Math.round(efTargetVal / 12);

    const retMonthly = (safeParse(sectionD.husbandEPF) + safeParse(sectionD.wifeEPF) + safeParse(sectionD.husbandPPF) + safeParse(sectionD.wifePPF) + safeParse(sectionD.husbandNPS) + safeParse(sectionD.wifeNPS) + safeParse(sectionD.husbandMutualFund) + safeParse(sectionD.wifeMutualFund) + safeParse(sectionD.husbandELSS) + safeParse(sectionD.wifeELSS)) || 34000;
    const targetRetCorpus = getValOrDefault(sectionI.retirementCorpus || calculations?.retirement?.target, 62000000);
    const retTimeline = `${60 - hAge}-${60 - wAge} years`;

    const eduTarget = getValOrDefault(sectionI.childEducation, 12000000);
    const eduHorizon = getValOrDefault(sectionI.childeducationHorizon || sectionI.childEducationHorizon, 15);
    const eduMonthly = Math.round(eduTarget * 0.00083 / 1000) * 1000 || 10000;

    const vacationTarget = getValOrDefault(sectionI.vacation, 1200000);
    const vacationHorizon = getValOrDefault(sectionI.vacationHorizon, 5);
    const vacMonthly = Math.round(vacationTarget * 0.0125 / 1000) * 1000 || 15000;

    const totalMonthlyInvestment = efMonthly + retMonthly + eduMonthly + vacMonthly;

    // Clean white page
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 6, 38, 8); } catch (e) { }

    let y = 22;

    // ===== TITLE SECTION 8 =====
    doc.setTextColor(...COL_BLUE); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 8: JOINT DEBT MANAGEMENT PLAN 📋", 14, y);
    y += 6;

    // Table drawing & styling helper functions
    const drawHeaderPills = (containerY, labels, widths, coords) => {
        labels.forEach((label, colIdx) => {
            const labelWidth = doc.getTextWidth(label);
            const w = Math.min(widths[colIdx] - 2, labelWidth + 6);
            const x = coords[colIdx] + widths[colIdx] / 2;
            doc.setFillColor(...BRAND_GREEN);
            doc.roundedRect(x - w / 2, containerY + 3.5, w, 8, 4, 4, 'F');
            doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
            doc.text(label, x, containerY + 9, { align: 'center' });
        });
    };

    const drawRow = (rowY, cells, colors, isBold, widths, coords, alignments = []) => {
        const rH = 11;
        cells.forEach((txt, i) => {
            if (!txt) return;
            doc.setFontSize(FS);
            doc.setFont("Montserrat", isBold[i] ? "bold" : "normal");
            doc.setTextColor(...colors[i]);
            const lines = doc.splitTextToSize(String(txt), widths[i] - PAD * 2);
            const textH = lines.length * LH;
            const topY = rowY + (rH - textH) / 2 + LH * 0.85;

            const align = alignments[i] || 'left';
            if (align === 'center') {
                const x = coords[i] + widths[i] / 2;
                lines.forEach((l, li) => doc.text(l, x, topY + li * LH, { align: 'center' }));
            } else {
                lines.forEach((l, li) => doc.text(l, coords[i] + PAD, topY + li * LH));
            }
        });
    };

    const drawDottedLine = (x, y1, y2) => {
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
        let cy = y1;
        while (cy < y2) {
            doc.line(x, cy, x, cy + 0.8);
            cy += 2;
        }
    };

    // Columns: Loan | Outstanding | EMI | Rate | Type | Strategy
    const cW8 = [40, 22, 20, 14, 28, 58]; // sum = 182
    const cX8 = [tLeft, tLeft + cW8[0], tLeft + cW8[0] + cW8[1], tLeft + cW8[0] + cW8[1] + cW8[2], tLeft + cW8[0] + cW8[1] + cW8[2] + cW8[3], tLeft + cW8[0] + cW8[1] + cW8[2] + cW8[3] + cW8[4]];

    const containerY8 = y;
    const containerH8 = 38; // 16mm header + 2 rows * 11mm = 38mm

    // Draw container card with rounded corners
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    doc.roundedRect(tLeft, containerY8, tWidth, containerH8, 6, 6, 'S');

    // Draw header pills
    drawHeaderPills(containerY8, ["Loan", "Outstanding", "EMI", "Rate", "Type", "Strategy"], cW8, cX8);

    const p8rows = [
        ["Joint Personal Loan", fmtLakh(plOS), `₹${fmt(plEMI)}`, `${plRate}%`, plOS > 0 ? "Expensive" : "Zero Debt", plOS > 0 ? `Prepay ₹${Math.round(prepayAmount / 1000)}k/m, close in ${monthsToClose}m` : "Maintain debt-free status"],
        ["Joint Home Loan", fmtLakh(hlOS), `₹${fmt(hlEMI)}`, `${hlRate}%`, hlOS > 0 ? "Productive" : "Zero Debt", hlOS > 0 ? "Regular EMI + tax benefit" : "Maintain debt-free status"]
    ];

    const p8colors = [COL_HEADER, COL_TEXT, COL_TEXT, COL_SLATE, COL_SLATE, COL_GREEN];
    const p8bold = [true, false, false, false, false, true];

    // Draw row 1 and divider
    drawRow(containerY8 + 16, p8rows[0], p8colors, p8bold, cW8, cX8);
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
    doc.line(tLeft, containerY8 + 27, tRight, containerY8 + 27);

    // Draw row 2
    drawRow(containerY8 + 27, p8rows[1], p8colors, p8bold, cW8, cX8);

    // Draw vertical dotted line dividers
    for (let i = 1; i < cX8.length; i++) {
        drawDottedLine(cX8[i], containerY8 + 16, containerY8 + containerH8);
    }

    y = containerY8 + containerH8 + 5;

    // Debt Avalanche text below table
    const postTaxHlRate = (hlRate * 0.65).toFixed(1);
    const avalancheText = plOS > 0
        ? `Debt Avalanche: Attack the ${plRate}% personal loan first. Home loan at ${hlRate}% (${postTaxHlRate}% post-tax) is fine.`
        : `Debt Avalanche: Maintain zero high-cost debt. Home loan at ${hlRate}% is productive debt.`;
    doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5); doc.setTextColor(...COL_RED);
    doc.text(avalancheText, 14, y);
    y += 9;

    // ===== TITLE SECTION 9 =====
    doc.setTextColor(...COL_BLUE); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 9: JOINT GOAL-BASED INVESTMENT PLAN 🎯", 14, y);
    y += 6;

    // Columns: Goal | Instrument | Monthly Investment | Timeline | Target Corpus
    const cW9 = [40, 50, 42, 24, 26]; // sum = 182
    const cX9 = [tLeft, tLeft + cW9[0], tLeft + cW9[0] + cW9[1], tLeft + cW9[0] + cW9[1] + cW9[2], tLeft + cW9[0] + cW9[1] + cW9[2] + cW9[3]];

    const containerY9 = y;
    const containerH9 = 71; // 16mm header + 5 rows * 11mm = 71mm

    // Draw container card with rounded corners
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    doc.roundedRect(tLeft, containerY9, tWidth, containerH9, 6, 6, 'S');

    // Draw header pills
    drawHeaderPills(containerY9, ["Goal", "Instrument", "Monthly Investment", "Timeline", "Target Corpus"], cW9, cX9);

    const p9rows = [
        ["Emergency Fund", "Joint Liquid Fund", `₹${fmt(efMonthly)}`, "12 months", fmtLakh(efTargetVal)],
        ["Retirement (Age 60)", "EPF + PPF + NPS + Joint SIP", `₹${fmt(retMonthly)} (combined)`, retTimeline, fmtLakh(targetRetCorpus)],
        ["Child Education (future)", "Joint ELSS + Balanced Fund", `₹${fmt(eduMonthly)}`, `${eduHorizon} years`, fmtLakh(eduTarget)],
        ["Vacation / Down payment", "Joint RD / Arbitrage Fund", `₹${fmt(vacMonthly)}`, `${vacationHorizon} years`, fmtLakh(vacationTarget)],
        ["TOTAL MONTHLY", "", `₹${fmt(totalMonthlyInvestment)}`, "", ""]
    ];

    const p9colors = [COL_HEADER, COL_SLATE, COL_GREEN, COL_SLATE, COL_HEADER];
    const p9bold = [true, false, true, false, true];
    const p9align = ['left', 'left', 'left', 'left', 'center'];

    // Draw row 1 to 5 and horizontal dividers
    for (let rIdx = 0; rIdx < 5; rIdx++) {
        const rowY = containerY9 + 16 + (rIdx * 11);
        drawRow(rowY, p9rows[rIdx], p9colors, p9bold, cW9, cX9, p9align);
        if (rIdx < 4) {
            doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
            doc.line(tLeft, rowY + 11, tRight, rowY + 11);
        }
    }

    // Draw vertical dotted line dividers
    for (let i = 1; i < cX9.length; i++) {
        drawDottedLine(cX9[i], containerY9 + 16, containerY9 + containerH9);
    }

    y = containerY9 + containerH9 + 8;

    // ===== KEY INSIGHT (matching Section 7 styling exactly) =====
    if (y + 18 < pageHeight - 14) {
        const corpus5yr = Math.round(totalMonthlyInvestment * 12 * 5 * 1.12);
        doc.setFillColor(240, 253, 244); doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.5);
        doc.roundedRect(tLeft, y, tWidth, 16, 3, 3, 'FD');
        doc.setTextColor(4, 72, 48); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        doc.text("💡 Key Insight:", tLeft + 5, y + 10);
        doc.setFont("Montserrat", "normal"); doc.setTextColor(...COL_TEXT); doc.setFontSize(8.5);
        const insightTxt = `In 5 years, this plan builds a corpus of ₹${fmt(corpus5yr)} — without any lifestyle sacrifice.`;
        doc.text(insightTxt, tLeft + 36, y + 10);
    }

    addSimpleFooter(doc, pageNum, pageWidth, pageHeight);
};

// Issue Page Helper - EXACT DESIGN MATCH FROM SCREENSHOT
export const renderAttentionPage = (doc, { name, lifeInsuranceActualL, lifeInsuranceTargetCr, lifeInsuranceTarget, lifeInsuranceActual, personalLoanBalance, currentEmergency, emergencyTarget, m, pageWidth, pageHeight, age, gender, isSmoker, annualIncome, calculations, report = {} }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const RED = [222, 59, 51];
    const GREEN = [6, 150, 104];
    const GRAY = [30, 41, 59];
    const DARK_GRAY = [30, 41, 59];
    const LIGHT_GRAY = [243, 244, 246];

    // ========== DYNAMIC DATA CALCULATIONS ==========
    const sInc = report?.salaryIncome || {};
    const userAge = parseInt(age) || parseInt(report?.personalInfo?.age) || 30;
    const userGender = gender || report?.personalInfo?.gender || 'professional';
    const isUserSmoker = isSmoker || report?.personalInfo?.isSmoker || false;

    // Life Insurance - Dynamic calculations
    const currentCover = safeParse(lifeInsuranceActual) || safeParse(sInc.lifeInsuranceCover) || 0;
    const currentCoverLakhs = (currentCover / 100000).toFixed(0);
    const annualIncomeVal = safeParse(annualIncome) || (calculations?.metrics?.totalMonthlyIncome || 0) * 12;
    const targetCover = lifeInsuranceTarget || (annualIncomeVal * 10); // 10x annual income
    const targetCoverCr = (targetCover / 10000000).toFixed(2);
    const targetCoverLakhs = Math.round(targetCover / 100000);
    const gapLakhs = Math.max(0, targetCoverLakhs - parseInt(currentCoverLakhs));

    // Home loan outstanding for reality check
    const homeLoanOS = safeParse(sInc.homeLoanOutstanding) || personalLoanBalance || 0;
    const homeLoanOSLakhs = (homeLoanOS / 100000).toFixed(0);

    // Term insurance cost estimate (dynamic based on age/gender/smoker)
    const baseRate = isUserSmoker ? 1200 : 800;
    const ageMultiplier = userAge > 35 ? 1.5 : (userAge > 30 ? 1.2 : 1.0);
    const estimatedMonthlyCost = Math.round(baseRate * ageMultiplier);
    const estimatedAnnualCost = estimatedMonthlyCost * 12;

    // Emergency Fund - Dynamic calculations
    const currentEF = safeParse(currentEmergency) || safeParse(report?.salaryInvestment?.emergencyFund) ||
        safeParse(report?.salaryInvestment?.liquidFund) || 0;
    const monthlyExpenses = m?.totalEssentialExpenses || (m?.totalMonthlyIncome * 0.4) || 30000;
    const targetEF = emergencyTarget || m?.targetEmergency || m?.emergencyFundTarget || (monthlyExpenses * 6);
    const targetEFLakhs = (targetEF / 100000).toFixed(1);
    const currentEFLakhs = (currentEF / 100000).toFixed(1);
    const gapEF = Math.max(0, targetEF - currentEF);
    const gapEFLakhs = (gapEF / 100000).toFixed(1);

    // Percentage built
    const efPercent = targetEF > 0 ? Math.round((currentEF / targetEF) * 100) : 0;

    // ========== HEADER (Logo Left, Section Badge Right) ==========
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Yathartha Logo
    try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8); } catch (e) { }

    // ========== TITLE ==========
    let y = 42;
    doc.setTextColor(85, 85, 85); doc.setFontSize(24); doc.setFont("Montserrat", "bold");
    doc.text("AREAS THAT NEED YOUR ", 14, y);
    const tw = doc.getTextWidth("AREAS THAT NEED YOUR ");
    doc.setTextColor(...RED); doc.text("ATTENTION", 14 + tw, y);

    // Warning Triangle Icon
    const tx = 14 + tw + doc.getTextWidth("ATTENTION") + 6, ty = y - 4;
    doc.setFillColor(...RED);
    doc.path([{ op: 'm', c: [tx, ty - 6] }, { op: 'l', c: [tx - 7, ty + 5] }, { op: 'l', c: [tx + 7, ty + 5] }, { op: 'z' }]); doc.fill();
    // Alert icon inside triangle
    try { doc.addImage('/icons/alert.png', 'PNG', tx - 3.5, ty - 3.5, 7, 7); } catch (e) { }

    // ========== INTRO PARAGRAPH ==========
    y += 12;
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const introText = `${name.split(' ')[0]}, what follows is not a list of failures — it is a map of opportunities. Every single item here is 100% fixable. In fact, the most successful financial transformations always start with someone brave enough to look honestly at these gaps. You are that person. Read each one not with worry, but with the quiet confidence of someone who now knows exactly what to do next.`;
    doc.text(doc.splitTextToSize(introText, pageWidth - 28), 14, y, { lineHeightFactor: 1.25 });

    // ========== ISSUE 1: LIFE INSURANCE [CRITICAL] ==========
    y += 22;
    // Red banner with alert icon
    doc.setFillColor(...RED); doc.rect(14, y, pageWidth - 28, 9, 'F');
    try { doc.addImage('/icons/alert.png', 'PNG', 17, y + 1.5, 6, 6); } catch (e) { }
    // Banner text
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("ISSUE 1 [CRITICAL]: Life Insurance Cover is Dangerously Inadequate", 28, y + 6);

    // Stats line
    y += 14;
    doc.setTextColor(85, 85, 85); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text(`Current: \u20b9${currentCoverLakhs} Lakhs  |  Required: \u20b9${targetCoverLakhs >= 100 ? (targetCoverLakhs / 100).toFixed(1) + ' Crore+' : targetCoverLakhs + ' Lakhs+'}  |  Gap: \u20b9${gapLakhs} Lakhs`, 14, y);

    // ========== 4 BOXES WITH ICONS - SYMMETRICAL LAYOUT ==========
    y += 12;
    const iconSpace = 24;
    const sideMargin = 14;
    const gapBetweenBoxes = 10;
    const boxW = (pageWidth - (sideMargin * 2) - (iconSpace * 2) - gapBetweenBoxes) / 2;
    const boxH = 46;
    const iconRadius = 10;
    const lift = 4;
    const leftBoxX = sideMargin + iconSpace + lift;
    const rightBoxX = pageWidth - sideMargin - iconSpace - boxW - lift;

    const drawCircleIcon = (cx, cy, iconType) => {
        doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.4);
        doc.circle(cx, cy, iconRadius, 'S');
        doc.setFillColor(255, 255, 255); doc.circle(cx, cy, iconRadius - 0.5, 'F');
        let iconPath = null;
        if (iconType === 'search') iconPath = '/icons/loupe.png';
        else if (iconType === 'cost') iconPath = '/icons/settings.png';
        else if (iconType === 'bulb') iconPath = '/icons/lightbulb.png';
        else if (iconType === 'target') iconPath = '/icons/target.png';
        if (iconPath) {
            try { doc.addImage(iconPath, 'PNG', cx - 5, cy - 5, 10, 10); } catch (e) { }
        }
    };

    // Box 1: The Reality Check (Left, Top)
    const midY1 = y + boxH / 2;
    doc.setDrawColor(...RED); doc.setLineWidth(0.6);
    doc.roundedRect(leftBoxX, y, boxW, boxH, 3, 3, 'S');
    doc.setTextColor(...RED); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Reality Check:", leftBoxX + 4, y + 6);
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const realityText = `If the unthinkable happened tomorrow, your family would receive \u20b9${currentCoverLakhs} Lakhs. Your debt obligations are approximately \u20b9${homeLoanOSLakhs} Lakhs. Your family would not just lose you — they would inherit a debt that takes years to repay.`;
    doc.text(doc.splitTextToSize(realityText, boxW - 8), leftBoxX + 4, y + 11, { lineHeightFactor: 1.1 });

    // Left icon for Box 1
    const leftIconX = sideMargin + iconRadius;
    drawCircleIcon(leftIconX, midY1, 'search');
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.4);
    doc.line(leftIconX + iconRadius, midY1, leftBoxX, midY1);

    // Box 2: The Cost Reality (Right, Top)
    doc.setDrawColor(...GREEN); doc.setLineWidth(0.6);
    doc.roundedRect(rightBoxX, y, boxW, boxH, 3, 3, 'S');
    doc.setTextColor(...GREEN); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Cost Reality:", rightBoxX + 4, y + 6);
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const genderStr = (userGender && userGender.toLowerCase() === 'female') ? 'woman' : 'man';
    const costText = `A ${userAge}-year-old non-smoking ${genderStr} can get \u20b91 Crore pure term cover for \u20b9${estimatedMonthlyCost}-${Math.round(estimatedMonthlyCost * 1.3)}/mo (\u20b9${estimatedAnnualCost.toLocaleString('en-IN')}/year). This is less than 1% of your annual income. It is the single most impactful \u20b9${estimatedMonthlyCost} you will ever spend.`;
    doc.text(doc.splitTextToSize(costText, boxW - 8), rightBoxX + 4, y + 11, { lineHeightFactor: 1.1 });

    // Right icon for Box 2
    const rightIconX = pageWidth - sideMargin - iconRadius;
    drawCircleIcon(rightIconX, midY1, 'cost');
    doc.line(rightBoxX + boxW, midY1, rightIconX - iconRadius, midY1);

    y += boxH + 8;

    // Box 3: The Financial Science (Left, Bottom)
    const midY2 = y + boxH / 2;
    doc.setDrawColor(...RED); doc.setLineWidth(0.6);
    doc.roundedRect(leftBoxX, y, boxW, boxH, 3, 3, 'S');
    doc.setTextColor(...RED); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Financial Science:", leftBoxX + 4, y + 6);
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const scienceText = `The international thumb rule is 10-15× annual income. Your annual income is \u20b9${(annualIncomeVal / 100000).toFixed(1)} Lakhs, so ideal cover = \u20b9${(annualIncomeVal * 10 / 10000000).toFixed(2)}-${(annualIncomeVal * 15 / 10000000).toFixed(2)} Cr. This ensures your family can maintain their lifestyle.`;
    doc.text(doc.splitTextToSize(scienceText, boxW - 8), leftBoxX + 4, y + 11, { lineHeightFactor: 1.1 });

    // Left icon for Box 3
    drawCircleIcon(leftIconX, midY2, 'bulb');
    doc.line(leftIconX + iconRadius, midY2, leftBoxX, midY2);

    // Box 4: Your Fix — Do This Week (Right, Bottom)
    doc.setDrawColor(...GREEN); doc.setLineWidth(0.6);
    doc.roundedRect(rightBoxX, y, boxW, boxH, 3, 3, 'S');
    doc.setTextColor(...GREEN); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("Your Fix — Do This Week:", rightBoxX + 4, y + 6);
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const fixText = `Consult Wealthcare by Yathartha. Search 'term insurance 1 crore'. Buy a 30-year term plan. Choose 'non-smoker' category. Do medical tests honestly. Set nominee as your closest dependant.`;
    doc.text(doc.splitTextToSize(fixText, boxW - 8), rightBoxX + 4, y + 11, { lineHeightFactor: 1.1 });

    // Right icon for Box 4
    drawCircleIcon(rightIconX, midY2, 'target');
    doc.line(rightBoxX + boxW, midY2, rightIconX - iconRadius, midY2);

    // ========== DID YOU KNOW? SECTION ==========
    y += boxH + 6;

    // Green bordered box - adjusted width for question mark
    const dykBoxW = pageWidth - 54;
    doc.setDrawColor(...GREEN); doc.setLineWidth(0.8);
    doc.roundedRect(14, y, dykBoxW, 20, 2, 2, 'S');

    // DID YOU KNOW? text (Green)
    doc.setTextColor(...GREEN); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
    doc.text("DID YOU", 18, y + 7);
    doc.text("KNOW?", 18, y + 14);

    // Description text - constrained width
    doc.setTextColor(85, 85, 85); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    const dykText = "Term insurance is the ONLY type of life insurance that makes pure financial sense for wealth protection. ULIP, endowment, and money-back plans mix insurance with investment and deliver poor returns on both. Always buy term — it is pure protection at minimum cost.";
    doc.text(doc.splitTextToSize(dykText, dykBoxW - 50), 52, y + 4.5, { lineHeightFactor: 1.15 });

    // Big question mark - positioned outside box
    doc.setTextColor(...GREEN); doc.setFontSize(36); doc.setFont("Montserrat", "normal");
    doc.text("?", pageWidth - 28, y + 14);

    // ========== ISSUE 2: EMERGENCY FUND [URGENT] ==========
    y += 26;
    // Red banner with alert icon
    doc.setFillColor(...RED); doc.rect(14, y, pageWidth - 28, 8, 'F');
    try { doc.addImage('/icons/alert.png', 'PNG', 17, y + 1.5, 5, 5); } catch (e) { }
    // Banner text
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(`ISSUE 2 [URGENT]: Emergency Fund is Only ${efPercent}% Built`, 28, y + 5.5);

    // Stats line
    y += 12;
    doc.setTextColor(85, 85, 85); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text(`Current: \u20b9${currentEFLakhs} Lakhs  |  Target: \u20b9${targetEFLakhs} Lakhs  |  Gap: \u20b9${gapEFLakhs} Lakhs`, 14, y);

    // Scenario Box - smaller height
    y += 10;
    doc.setFillColor(...LIGHT_GRAY); doc.roundedRect(14, y, pageWidth - 28, 16, 2, 2, 'F');
    doc.setTextColor(85, 85, 85); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("The Scenario You Must Avoid:", 18, y + 5);
    doc.setTextColor(102, 102, 102); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const weeksOfExpenses = Math.max(1, Math.floor(currentEF / (monthlyExpenses || 1)));
    const scenarioText = `Imagine your company downsizes next month. You have \u20b9${currentEFLakhs} Lakhs — less than ${weeksOfExpenses} week${weeksOfExpenses > 1 ? 's' : ''} of expenses. Without an emergency fund, you would be forced to break your SIP, take a personal loan at 14%+ interest, or borrow from family.`;
    doc.text(doc.splitTextToSize(scenarioText, pageWidth - 32), 18, y + 9, { lineHeightFactor: 1.1 });
};

export const renderIssueHeader = (doc, num, title, sev, color, pageWidth) => {
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, 297, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8); } catch (e) { } } catch (e) { }
    doc.setFillColor(...DARK); doc.roundedRect(pageWidth - 32, 8, 28, 9, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text("SECTION 4", pageWidth - 30, 14);

    doc.setTextColor(...GREY); doc.setFontSize(18); doc.setFont("Montserrat", "bold");
    doc.text("AREAS THAT NEED YOUR ", 14, 25);
    doc.setTextColor(...RED);
    const attTxt = "ATTENTION !";
    doc.text(attTxt, 108, 25);
    const attW = doc.getTextWidth(attTxt);
    // Draw Triangle and add alert icon
    doc.setFillColor(...RED);
    doc.path([{ op: 'm', c: [108 + attW + 4, 26] }, { op: 'l', c: [108 + attW + 10, 16] }, { op: 'l', c: [108 + attW + 16, 26] }, { op: 'z' }]); doc.fill();
    try { doc.addImage('/icons/alert.png', 'PNG', 108 + attW + 8.5, 18.5, 3, 3); } catch (e) { }

    doc.setDrawColor(...G); doc.setLineWidth(0.5); doc.line(14, 28, 70, 28);

    doc.setFillColor(...color); doc.roundedRect(14, 46, pageWidth - 28, 10, 1, 1, 'F');
    try { doc.addImage('/icons/alert.png', 'PNG', 17, 48, 6, 6); } catch (e) { }
    doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
    doc.text(`ISSUE ${num} [${sev}]: ${title}`, 26, 52.5);
    return 60;
};

export const renderIssue1Insurance = (doc, { lifeInsuranceActualL, lifeInsuranceTargetCr, lifeInsuranceTarget, lifeInsuranceActual, personalLoanBalance, name, pageWidth, age, gender, annualIncome }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const y = renderIssueHeader(doc, 1, "Life Insurance Cover is Dangerously Inadequate", "CRITICAL", RED, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text(`Current: Rs. ${lifeInsuranceActualL} Lakhs | Required: Rs. ${lifeInsuranceTargetCr} Crore+ | Gap: Rs. ${fmt((lifeInsuranceTarget - lifeInsuranceActual) / 100000)} Lakhs`, 14, y + 6);

    const bW = (pageWidth - 36) / 2;
    const bH = 36;
    const drawBox = (x, y, title, text, color) => {
        doc.setDrawColor(...color); doc.setLineWidth(0.4); doc.roundedRect(x, y, bW, bH, 2, 2, 'S');
        doc.setTextColor(...color); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold"); doc.text(title, x + 4, y + 6);
        doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        doc.text(doc.splitTextToSize(text, bW - 8), x + 4, y + 12);
    };

    drawBox(14, y + 12, "The Reality Check:", `If the unthinkable happened tomorrow, your family would receive Rs. ${lifeInsuranceActualL} Lakhs. Your debt obligations (including Home/Personal loans) are approximately Rs. ${fmt(personalLoanBalance)}. Your family would inherit a burden that would take them years to repay.`, RED);
    drawBox(pageWidth / 2 + 4, y + 12, "The Cost Reality:", `A ${age || '30'}-year-old non-smoking ${gender || 'professional'} can get Rs. 1 Crore pure term cover for approx. Rs. 700-1100/month. This is less than 1.5% of your monthly income. It is the single most impactful protection you will ever buy.`, G);
    drawBox(14, y + 54, "The Financial Science:", `The international benchmark is 10-15x annual income. Your annual income is approx. Rs. ${fmt(annualIncome)}, so ideal cover is Rs. ${lifeInsuranceTargetCr} Cr+. This ensures your family can maintain their lifestyle for 10+ years.`, RED);
    drawBox(pageWidth / 2 + 4, y + 54, "Your Fix - Do This Week:", "Consult Wealthcare by Yathartha. Search 'term insurance 1 crore'. Buy a 30-year term plan. Choose 'non-smoker' category. Do medical tests honestly. Set nominee.", G);

    doc.setDrawColor(...GREY); doc.setLineWidth(0.3);
    [30, pageWidth - 30].forEach(ix => {
        doc.circle(ix, y + 30, 9, 'S'); doc.circle(ix, y + 70, 9, 'S');
    });

    doc.setDrawColor(...G); doc.setLineWidth(0.5); doc.line(14, y + 105, pageWidth - 14, y + 105);
    doc.setTextColor(...G); doc.setFontSize(9); doc.text("DID YOU KNOW?", 14, y + 115);
    doc.setTextColor(...GREY); doc.setFontSize(8.5);
    doc.text(doc.splitTextToSize("Term insurance is the ONLY type of life insurance that makes pure financial sense for wealth protection. Always buy term — it is pure protection.", pageWidth - 70), 50, y + 113);
    addFooter(doc, "06", name, pageWidth, 297);
};

export const renderIssue2Emergency = (doc, { currentEmergency, emergencyTarget, m, name, pageWidth }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const y = renderIssueHeader(doc, 2, "Emergency Fund is Only 10% Built", "URGENT", ORANGE, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text(`Current: Rs. ${fmt(currentEmergency)} | Target: Rs. ${fmt(emergencyTarget)} | Gap: Rs. ${fmt(emergencyTarget - currentEmergency)}`, 14, y + 6);

    const boxY = y + 12;
    doc.setFillColor(...LIGHT); doc.roundedRect(14, boxY, pageWidth - 28, 25, 2, 2, 'F');
    doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Scenario You Must Avoid:", 18, boxY + 7);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    doc.text(doc.splitTextToSize(`Imagine your company downsizes next month. You have Rs. ${fmt(currentEmergency)} — less than 3 weeks of expenses. Without an emergency fund, you would be forced to break your SIP, take a personal loan at 14%+ interest, or borrow from family — each of which sets your financial plan back by years.`, pageWidth - 40), 18, boxY + 12);

    const box2Y = boxY + 30;
    doc.setFillColor(...LIGHT); doc.roundedRect(14, box2Y, pageWidth - 28, 15, 2, 2, 'F');
    doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Right Target:", 18, box2Y + 7);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    const userMonthlyExp = m?.totalEssentialExpenses || 0;
    doc.text(`6 months of all essential expenses. Your essential monthly outflow = Rs. ${fmt(userMonthlyExp)}. So target = Rs. ${fmt(emergencyTarget)}.`, 18, box2Y + 12);

    addFooter(doc, "08", name, pageWidth, 297);
};

export const renderIssue3Savings = (doc, { savingsRateActual, surplusLossCr, name, pageWidth }) => {
    const y = renderIssueHeader(doc, 3, "Savings Rate is Below Universal Benchmarks", "HIGH", BLUE, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text(`Current: ${savingsRateActual}% | Target: 20% | Gap: ${(20 - safeParse(savingsRateActual)).toFixed(1)}%`, 14, y + 6);

    const boxY = y + 12;
    doc.setFillColor(...LIGHT); doc.roundedRect(14, boxY, pageWidth - 28, 25, 2, 2, 'F');
    doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Cost of Status Quo:", 18, boxY + 7);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    doc.text(doc.splitTextToSize(`Every 1% gap in your savings rate today costs you approximately Rs. 14 Lakhs in future wealth over 25 years. Your current gap is ${(20 - safeParse(savingsRateActual)).toFixed(1)}%. This gap is currently disappearing into 'untracked surplus' — money that is spent but provides no lasting lifestyle value.`, pageWidth - 40), 18, boxY + 12);

    const box2Y = boxY + 30;
    doc.setFillColor(236, 253, 245); doc.roundedRect(14, box2Y, pageWidth - 28, 15, 2, 2, 'F');
    doc.setTextColor(...G); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text(`The Wealth Opportunity: Rs. ${surplusLossCr || '2.8'} Crore extra wealth created by age 60.`, 18, box2Y + 9);

    addFooter(doc, "08", name, pageWidth, 297);
};

export const renderIssue4Debt = (doc, { personalLoanBalance, personalLoanEMI, name, pageWidth }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const y = renderIssueHeader(doc, 4, "High-Interest Personal Loan is Draining Surplus", "MEDIUM", GREEN2, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text(`Outstanding: Rs. ${fmt(personalLoanBalance)} | Interest Rate: 14%+ | EMI: Rs. ${fmt(personalLoanEMI)}`, 14, y + 6);

    const boxY = y + 12;
    doc.setFillColor(...LIGHT); doc.roundedRect(14, boxY, pageWidth - 28, 25, 2, 2, 'F');
    doc.setTextColor(...SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("The Strategic Fix:", 18, boxY + 7);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    doc.text(doc.splitTextToSize(`Paying just Rs. 5,000 extra per month towards your principal will close this loan 12 months earlier and save you Rs. 18,000 in pure interest. In the financial world, prepaying a 14% loan is mathematically identical to getting a guaranteed 14% return on your investment — tax-free!`, pageWidth - 40), 18, boxY + 12);

    addFooter(doc, "09", name, pageWidth, 297);
};

export const renderIssue5Tax = (doc, { taxSavings, taxSavingsMonthly, name, pageWidth }) => {
    const fmt = v => (v || 0).toLocaleString('en-IN');
    const y = renderIssueHeader(doc, 5, "Tax Optimisation Opportunities", "OPTIMISATION", PURPLE, pageWidth);
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text(`Potential Tax Savings: Rs. ${fmt(taxSavings)}/year | Monthly Benefit: Rs. ${fmt(taxSavingsMonthly)}`, 14, y + 6);

    const boxY = y + 12;
    doc.setFillColor(...LIGHT); doc.roundedRect(14, boxY, pageWidth - 28, 25, 2, 2, 'F');
    doc.text("Tax Saving Opportunities:", 18, boxY + 7);
    doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
    const taxMsg = `By optimising your investments and deductions under Section 80C, 80D, and HRA, you can save Rs. ${fmt(taxSavingsMonthly)} per month. Review your tax declarations with HR.`;
    doc.text(doc.splitTextToSize(taxMsg, pageWidth - 40), 18, boxY + 12);
};

// Page 10 - Financial Roadmap (Fully Dynamic)
export const renderRoadmapPage = (doc, { name, calculations, pageWidth, pageHeight, report = {} }) => {
    const fmt = v => Math.round(v || 0).toLocaleString('en-IN');
    const formatCover = (val) => {
        if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
        return `${Math.round(val / 100000)}L`;
    };
    const formatLakhs = (val) => {
        if (val >= 10000000) return `${(val / 10000000).toFixed(2)}Cr`;
        return `${Math.round(val / 100000)}L`;
    };

    const localG = [6, 150, 104];
    const indigoBlue = [26, 37, 110];
    const brownOrange = [180, 83, 9];
    const localSlate = [30, 41, 59];

    // ========== DYNAMIC DATA CALCULATIONS ==========
    const personalInfo = report?.personalInfo || {};
    const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
    const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
    const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};
    const section3 = report?.section3 || {};
    const section4 = report?.section4 || {};
    const m = calculations?.metrics || {};

    const isCouple = personalInfo.familyType === 'couple';
    const jointSalary = report?.jointSalary || {};
    const sectionA = jointSalary.sectionA || {};
    const sectionB = jointSalary.sectionB || {};
    const sectionC = jointSalary.sectionC || {};
    const sectionD = jointSalary.sectionD || {};
    const sectionE = jointSalary.sectionE || {};
    const sectionF = jointSalary.sectionF || {};
    const sectionG = jointSalary.sectionG || {};

    // First names
    const userName = name || "Client";
    const userFirstName = userName.split(' ')[0];
    const hName = sectionA?.husbandName || "Husband";
    const wName = sectionA?.wifeName || "Wife";
    const hFirstName = hName.split(' ')[0];
    const wFirstName = wName.split(' ')[0];

    // Income
    const monthlyIncome = m.totalMonthlyIncome || safeParse(personalInfo.monthlySalary) || 0;
    const annualIncome = monthlyIncome * 12;

    // 1. Term Insurance Gap
    let row1Action = "";
    if (isCouple) {
        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hTargetCover = hSalary * 12 * 10;
        const wTargetCover = wSalary * 12 * 10;
        const hCurrentCover = safeParse(sectionE.husbandLifeCover) || 0;
        const wCurrentCover = safeParse(sectionE.wifeLifeCover) || 0;
        const hGap = Math.max(0, hTargetCover - hCurrentCover);
        const wGap = Math.max(0, wTargetCover - wCurrentCover);

        if (hGap > 0 && wGap > 0) {
            row1Action = `${hFirstName} buys ₹${formatCover(hTargetCover)} term insurance, ${wFirstName} buys ₹${formatCover(wTargetCover)}`;
        } else if (hGap > 0) {
            row1Action = `${hFirstName} buys ₹${formatCover(hTargetCover)} term insurance`;
        } else if (wGap > 0) {
            row1Action = `${wFirstName} buys ₹${formatCover(wTargetCover)} term insurance`;
        } else {
            row1Action = "Term insurance is already optimized for both";
        }
    } else {
        const uTargetCover = annualIncome * 10;
        const uCurrentCover = safeParse(report.salaryIncome?.lifeInsuranceCover) || safeParse(report.section4?.lifeInsuranceCover) || 0;
        const uGap = Math.max(0, uTargetCover - uCurrentCover);
        if (uGap > 0) {
            row1Action = `${userFirstName} buys ₹${formatCover(uTargetCover)} term insurance`;
        } else {
            row1Action = "Term insurance is already optimized";
        }
    }

    // 2. Emergency Fund Transfer
    const fallbackExp = monthlyIncome * 0.4;
    const targetEF = m.emergencyFundTarget || m.targetEmergency || Math.round((m.totalEssentialExpenses || fallbackExp || 30000) * 6);
    const currentEF = isCouple
        ? (safeParse(sectionG.emergencyFund) || 0)
        : (safeParse(report.salaryInvestment?.emergencyFund) || safeParse(report.salaryInvestment?.liquidFund) || 0);
    const efGap = Math.max(0, targetEF - currentEF);
    const efMonthlyTransfer = efGap > 0 ? Math.max(1000, Math.round(efGap / 18 / 1000) * 1000) : 0;

    let row2Action = "";
    if (isCouple) {
        row2Action = efMonthlyTransfer > 0
            ? `Open joint liquid fund, start ₹${fmt(efMonthlyTransfer)}/month emergency transfer`
            : "Emergency fund is already fully funded";
    } else {
        row2Action = efMonthlyTransfer > 0
            ? `Open liquid fund, start ₹${fmt(efMonthlyTransfer)}/month emergency transfer`
            : "Emergency fund is already fully funded";
    }

    // 3. Tax Savings Regime
    const taxSaving = m.tax?.taxSavings || calculations?.taxSavings || 0;
    const recommendedRegime = (m.tax?.recommendedRegime === 'New' || calculations?.tax?.recommendedRegime === 'New') ? 'New' : 'Old';
    const row3Action = isCouple
        ? `Declare recommended Tax Regimes to HR`
        : `Declare recommended ${recommendedRegime} Tax Regime to HR`;
    const row3Impact = taxSaving > 0
        ? `Save ₹${fmt(taxSaving)}/year`
        : "Regime optimized";

    // 4. PPF Targets
    let row4Action = "";
    if (isCouple) {
        const hPPF = safeParse(sectionD.husbandPPF) || 0;
        const wPPF = safeParse(sectionD.wifePPF) || 0;
        const hPPF_target = hPPF > 0 ? hPPF : 3500;
        const wPPF_target = wPPF > 0 ? wPPF : 3500;
        row4Action = `Increase joint PPF to ₹${fmt(hPPF_target + wPPF_target)}/month (₹${fmt(hPPF_target)} for ${hFirstName}, ₹${fmt(wPPF_target)} for ${wFirstName})`;
    } else {
        const uPPF = safeParse(report.salaryInvestment?.ppf) || safeParse(report.salaryIncome?.ppf) || 0;
        const uPPF_target = uPPF > 0 ? uPPF : 5000;
        row4Action = `Increase PPF to ₹${fmt(uPPF_target)}/month`;
    }

    // 5. Personal Loan prepayments
    const personalLoanEMI = isCouple
        ? (safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0)
        : (safeParse(report.salaryIncome?.personalLoanEMI) || m.personalLoanEMI || 0);
    const personalLoanBalance = isCouple
        ? (safeParse(sectionF.personalLoanOutstanding) || 0)
        : (safeParse(report.salaryIncome?.personalLoanOutstanding) || m.personalLoanBalance || 0);
    const extraPayment = Math.round(personalLoanEMI * 0.5);
    const monthsToClose = personalLoanEMI > 0 ? Math.ceil(personalLoanBalance / (personalLoanEMI + extraPayment)) : 0;
    const interestSaved = Math.round(personalLoanBalance * 0.14 * (monthsToClose / 24)); // approx interest saved

    let row5Action = "";
    let row5Impact = "";
    if (personalLoanEMI > 0) {
        row5Action = `Pay ₹${fmt(extraPayment)} extra on personal loan monthly`;
        row5Impact = `Close in ${monthsToClose} months, save ₹${fmt(interestSaved)}`;
    } else {
        row5Action = "Direct untracked surplus into systematic goal-based mutual fund SIPs";
        row5Impact = "Accelerate your ₹10Cr wealth compound trajectory";
    }

    // 6. ELSS SIP
    let row6Action = "";
    if (isCouple) {
        row6Action = "Start joint ELSS SIP of ₹6k/month (₹3k each)";
    } else {
        row6Action = "Start ELSS SIP of ₹5k/month";
    }

    // 7. NPS
    let row7Action = "";
    let row7Impact = "";
    if (isCouple) {
        row7Action = "Start joint NPS: ₹4k/month (₹2k each)";
        row7Impact = "Extra ₹50k deduction each";
    } else {
        row7Action = "Start NPS: ₹4.2k/month to utilise Section 80CCD(1B)";
        row7Impact = "Extra ₹50k tax deduction";
    }

    // 8. Redirect savings
    let row8Action = "";
    let row8Impact = "";
    const redirectAmount = personalLoanEMI + extraPayment;
    const largeCap = Math.round(redirectAmount * 0.4 / 1000) * 1000;
    const midCap = Math.round(redirectAmount * 0.3 / 1000) * 1000;
    const debt = redirectAmount - largeCap - midCap;

    if (personalLoanEMI > 0) {
        row8Action = isCouple
            ? `Personal loan closed. Redirect ₹${fmt(redirectAmount)}/month to joint SIPs`
            : `Personal loan closed. Redirect ₹${fmt(redirectAmount)}/month to SIPs`;
        row8Impact = `₹${fmt(largeCap)} to large cap, ₹${fmt(midCap)} to mid-cap, ₹${fmt(debt)} to debt`;
    } else {
        const fallbackSurplus = Math.round(monthlyIncome * 0.3 / 1000) * 1000;
        const surplusAmt = m.monthlySurplus || fallbackSurplus;
        row8Action = isCouple
            ? `Redirect savings surplus of ₹${fmt(surplusAmt)}/month to joint SIPs`
            : `Redirect savings surplus of ₹${fmt(surplusAmt)}/month to SIPs`;
        row8Impact = `₹${fmt(Math.round(surplusAmt * 0.4))} to large cap, ₹${fmt(Math.round(surplusAmt * 0.3))} to mid-cap, ₹${fmt(Math.round(surplusAmt * 0.3))} to debt`;
    }

    // 9. Emergency fund fully funded
    const row9Action = `Emergency fund fully funded at ₹${formatLakhs(targetEF)} (Month 12)`;
    const row9Impact = efMonthlyTransfer > 0
        ? `Full safety. Redirect ₹${fmt(efMonthlyTransfer)}/month to SIPs`
        : "Full safety cushion achieved";

    // 10. Target SIP
    const targetSIP = Math.round(monthlyIncome * 0.4 / 5000) * 5000;
    const retirementTarget = annualIncome * 15;
    const retirementCorpus = Math.round(retirementTarget * 1.2);
    const retirementCorpusCr = (retirementCorpus / 10000000).toFixed(2);

    const row10Action = isCouple
        ? `Target joint SIP: ₹${fmt(targetSIP)}/month by Year 2`
        : `Target individual SIP: ₹${fmt(targetSIP)}/month by Year 2`;
    const row10Impact = `₹${retirementCorpusCr}Cr retirement corpus at 60`;

    // Clear background
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Yathartha Logo
    try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { }

    // Table height calculator
    const calculateTableHeight = (rows, colWidths) => {
        let h = 8.5; // header height
        rows.forEach(row => {
            const actionLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
            const impactLines = doc.splitTextToSize(row[row.length - 1], colWidths[colWidths.length - 1] - 8);
            const maxLines = Math.max(actionLines.length, impactLines.length);
            h += Math.max(10, maxLines * 4.8 + 4.5);
        });
        return h;
    };

    // Table divider line helper (Checklist Theme match with internal padding)
    const drawTable = (headers, rows, colWidths, startY) => {
        let y = startY;
        const tLeft = 18;
        const tRight = 192;
        const tWidth = 174;

        // Calculate column X coordinates
        const cX = [tLeft];
        let tempX = tLeft;
        for (let i = 0; i < colWidths.length - 1; i++) {
            tempX += colWidths[i];
            cX.push(tempX);
        }

        // Draw Headers as Rounded Badges (Pills)
        let currentX = tLeft;
        headers.forEach((header, idx) => {
            if (idx > 0 && header) {
                const colCenter = currentX + colWidths[idx] / 2;
                const w = Math.min(colWidths[idx] - 4, doc.getTextWidth(header.toUpperCase()) + 8);
                doc.setFillColor(6, 150, 104);
                doc.roundedRect(colCenter - w / 2, y, w, 7.5, 3.75, 3.75, 'F');
                doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(7.8);
                doc.text(header.toUpperCase(), colCenter, y + 4.8, { align: 'center' });
            }
            currentX += colWidths[idx];
        });
        y += 9.5;

        // Pre-calculate midpoints for the left vertical axis line
        let firstMidY = 0;
        let lastMidY = 0;
        const rowHeights = rows.map((row) => {
            const actionLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
            const impactLines = doc.splitTextToSize(row[row.length - 1], colWidths[colWidths.length - 1] - 8);
            const maxLines = Math.max(actionLines.length, impactLines.length);
            return Math.max(10, maxLines * 4.8 + 4.5);
        });

        if (rowHeights.length > 0) {
            firstMidY = y + rowHeights[0] / 2;
            let tempY = y;
            for (let i = 0; i < rowHeights.length - 1; i++) {
                tempY += rowHeights[i];
            }
            lastMidY = tempY + rowHeights[rowHeights.length - 1] / 2;

            // Draw left vertical axis line
            doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
            const col0W = colWidths[0];
            const axisX = tLeft + (col0W === 14 ? 2 : 1.5);
            doc.line(axisX, firstMidY, axisX, lastMidY);
        }

        // Draw Rows
        rows.forEach((row, rIdx) => {
            const rowH = rowHeights[rIdx];

            // Draw alternating background
            if (rIdx % 2 === 0) {
                doc.setFillColor(255, 255, 255);
            } else {
                doc.setFillColor(248, 250, 252);
            }
            doc.rect(tLeft, y, tWidth, rowH, 'F');

            // Draw soft horizontal divider line at bottom of row
            doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.35);
            doc.line(tLeft, y + rowH, tRight, y + rowH);

            const mid = y + rowH / 2;

            // Draw vertical dotted dividers inside row
            let cxDivider = tLeft;
            colWidths.forEach((w, colIdx) => {
                cxDivider += w;
                if (colIdx < colWidths.length - 1) {
                    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
                    let cy = y;
                    while (cy < y + rowH) {
                        doc.line(cxDivider, cy, cxDivider, cy + 0.8);
                        cy += 2;
                    }
                }
            });

            // Draw cells
            let cx = tLeft;
            row.forEach((cell, idx) => {
                doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5); doc.setTextColor(31, 41, 55);

                if (idx === 0) {
                    const col0W = colWidths[0];
                    const axisX = tLeft + (col0W === 14 ? 2 : 1.5);
                    const circleCenter = tLeft + col0W / 2;

                    // Draw horizontal connector from axis to circular badge
                    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
                    doc.line(axisX, mid, circleCenter - 2.8, mid);

                    // Small green dot at connector end on the axis
                    doc.setFillColor(6, 150, 104); doc.circle(axisX, mid, 0.6, 'F');

                    // Circular badge with number (Centered at circleCenter)
                    doc.setFillColor(6, 150, 104);
                    doc.circle(circleCenter, mid, 2.8, 'F');
                    doc.setFont("Montserrat", "bold"); doc.setTextColor(255, 255, 255); doc.setFontSize(8);
                    doc.text(cell.toString(), circleCenter, mid + 1.0, { align: 'center' });
                } else if (idx === 1) {
                    const actionLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
                    doc.setFont("Montserrat", "bold"); doc.setTextColor(15, 23, 42); // Bold Dark Navy for Actions
                    doc.text(actionLines, cx + 4, y + 5);
                } else if (idx === 2 && row.length === 4) { // By When
                    doc.setFont("Montserrat", "normal"); doc.setTextColor(71, 85, 105);
                    doc.text(cell, cx + 4, mid + 1.2);
                } else { // Impact
                    const impactLines = doc.splitTextToSize(row[row.length - 1], colWidths[colWidths.length - 1] - 8);
                    doc.setFont("Montserrat", "normal"); doc.setTextColor(71, 85, 105);
                    doc.text(impactLines, cx + 4, y + 5);
                }
                cx += colWidths[idx];
            });
            y += rowH;
        });

        return y;
    };

    // Render Titles
    doc.setTextColor(6, 95, 70); doc.setFontSize(14); doc.setFont("Montserrat", "bold"); // Dark Emerald (#065f46)
    const sectionTitle = isCouple ? "JOINT PERSONAL FINANCIAL ROADMAP" : "PERSONAL FINANCIAL ROADMAP";
    doc.text(sectionTitle, 14, 25);

    // Render section badge
    doc.setFillColor(236, 253, 245); doc.roundedRect(175, 20.5, 21, 5.5, 1, 1, 'F');
    doc.setFont("Montserrat", "bold"); doc.setFontSize(7.5); doc.setTextColor(6, 150, 104);
    doc.text("SECTION 05", 185.5, 24.3, { align: 'center' });

    // Draw thin underline for section title
    doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.6); // Brand Green
    doc.line(14, 28, 196, 28);

    let currentY = 33;

    // Phase 1 Card Container
    const phase1Headers = isCouple ? ["", "Action", "By When", "Joint Impact"] : ["", "Action", "By When", "Impact"];
    const phase1Cols = [14, 76, 30, 54];
    const phase1Rows = [
        ["1", row1Action, "This week", "Critical vulnerability closed"],
        ["2", row2Action, "This week", "Safety net begins"],
        ["3", row3Action, "This week", row3Impact],
        ["4", row4Action, "This month", isCouple ? "Max 80C for both" : "Max 80C limit"]
    ];

    const p1TableH = calculateTableHeight(phase1Rows, phase1Cols);
    const p1CardH = 4 + 5.5 + 3 + p1TableH + 4; // top-pad + header + middle + table + bottom-pad

    doc.setFillColor(255, 255, 255); doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
    doc.roundedRect(14, currentY, 182, p1CardH, 3, 3, 'FD'); // draw card

    // Inside card: Phase Badge
    doc.setFillColor(236, 253, 245); doc.setDrawColor(167, 243, 208); doc.setLineWidth(0.3);
    doc.roundedRect(18, currentY + 4, 18, 5.5, 2, 2, 'FD');
    doc.setFont("Montserrat", "bold"); doc.setFontSize(8); doc.setTextColor(6, 95, 70);
    doc.text("PHASE 1", 27, currentY + 4 + 3.8, { align: 'center' });

    doc.setTextColor(30, 41, 59); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text("This Month – Foundation Sprint", 39, currentY + 4 + 3.9);

    drawTable(phase1Headers, phase1Rows, phase1Cols, currentY + 4 + 5.5 + 3);
    currentY += p1CardH + 7; // spacing for next card

    // Phase 2 Card Container
    const phase2Headers = isCouple ? ["", "Action", "Joint Impact"] : ["", "Action", "Impact"];
    const phase2Cols = [14, 96, 64];
    const phase2Rows = [
        ["5", row5Action, row5Impact],
        ["6", row6Action, "Tax saving + equity growth"],
        ["7", row7Action, row7Impact]
    ];

    const p2TableH = calculateTableHeight(phase2Rows, phase2Cols);
    const p2CardH = 4 + 5.5 + 3 + p2TableH + 4;

    doc.setFillColor(255, 255, 255); doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
    doc.roundedRect(14, currentY, 182, p2CardH, 3, 3, 'FD'); // draw card

    doc.setFillColor(236, 253, 245); doc.setDrawColor(167, 243, 208); doc.setLineWidth(0.3);
    doc.roundedRect(18, currentY + 4, 18, 5.5, 2, 2, 'FD');
    doc.setFont("Montserrat", "bold"); doc.setFontSize(8); doc.setTextColor(6, 95, 70);
    doc.text("PHASE 2", 27, currentY + 4 + 3.8, { align: 'center' });

    doc.setTextColor(30, 41, 59); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text("Months 2-6 – Debt Fire & Momentum", 39, currentY + 4 + 3.9);

    drawTable(phase2Headers, phase2Rows, phase2Cols, currentY + 4 + 5.5 + 3);
    currentY += p2CardH + 7; // spacing for next card

    // Phase 3 Card Container
    const phase3Headers = isCouple ? ["", "Action", "Joint Impact"] : ["", "Action", "Impact"];
    const phase3Cols = [14, 96, 64];
    const phase3Rows = [
        ["8", row8Action, row8Impact],
        ["9", row9Action, row9Impact],
        ["10", row10Action, row10Impact]
    ];

    const p3TableH = calculateTableHeight(phase3Rows, phase3Cols);
    const p3CardH = 4 + 5.5 + 3 + p3TableH + 4;

    doc.setFillColor(255, 255, 255); doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
    doc.roundedRect(14, currentY, 182, p3CardH, 3, 3, 'FD'); // draw card

    doc.setFillColor(236, 253, 245); doc.setDrawColor(167, 243, 208); doc.setLineWidth(0.3);
    doc.roundedRect(18, currentY + 4, 18, 5.5, 2, 2, 'FD');
    doc.setFont("Montserrat", "bold"); doc.setFontSize(8); doc.setTextColor(6, 95, 70);
    doc.text("PHASE 3", 27, currentY + 4 + 3.8, { align: 'center' });

    doc.setTextColor(30, 41, 59); doc.setFontSize(10.5); doc.setFont("Montserrat", "bold");
    doc.text("Month 12 & Beyond – Wealth Acceleration", 39, currentY + 4 + 3.9);

    drawTable(phase3Headers, phase3Rows, phase3Cols, currentY + 4 + 5.5 + 3);
};

// Page 17 - Master Action Checklist (Fully Dynamic)
export const renderChecklistPage = (doc, { name, metrics, calculations, pageWidth, pageHeight, lifeInsuranceTargetCr, taxSavingsMonthly, personalLoanEMI, report = {} }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    // ========== DYNAMIC DATA ==========
    const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
    const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
    const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};
    const section3 = report?.section3 || {};
    const section4 = report?.section4 || {};
    const personalInfo = report?.personalInfo || {};

    // Dynamic values
    const actualLoanEMI = safeParse(section3.personalLoanEMI) || safeParse(personalLoanEMI) || safeParse(sInc.personalLoanEMI) || 0;
    const actualLoanBalance = safeParse(sInc.personalLoanOutstanding) || 0;
    const monthlyIncome = safeParse(personalInfo.monthlySalary) || metrics?.totalMonthlyIncome || 0;
    const rawEF = safeParse(section4.emergencyFundAmount) || safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || metrics?.emergencyFundMonthly || 0;
    const efMonthly = rawEF === 0 && monthlyIncome > 0 ? Math.max(1000, Math.round(monthlyIncome * 0.1 / 1000) * 1000) : (rawEF === 0 ? 5000 : rawEF);
    const rawSIP = safeParse(section3.sipAmount) || safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
    const sipMonthly = rawSIP === 0 && monthlyIncome > 0 ? Math.max(1000, Math.round(monthlyIncome * 0.1 / 1000) * 1000) : (rawSIP === 0 ? 5000 : rawSIP);
    const rawPPF = safeParse(section3.ppfAmount) || safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics?.ppfMonthly || 0;
    const ppfMonthly = rawPPF === 0 ? 1000 : rawPPF;
    const rawNPS = safeParse(sInv.nps_80ccd1b) || safeParse(sInc.nps_80ccd1b) || metrics?.npsMonthly || 0;
    const npsMonthly = rawNPS === 0 ? 1000 : rawNPS;

    addHeader(doc, "10", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("MASTER ACTION CHECKLIST", 18, y + 5.5);

    y += 12;
    doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    const intro = `This page, ${name}, is where your financial story officially begins. Every tick you make here is a real, irreversible step toward the life you are building. Print it. Put it where you can see it. Come back to it in 6 months and feel the quiet pride of someone who actually did what most people only talk about.`;
    doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, y, { lineHeightFactor: 1.2 });

    y += 20;
    // --- OUTER CONTAINER BORDER (Rounded Top-Left) ---
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.4);
    // Large rounded background container for the whole table
    doc.roundedRect(14, y, pageWidth - 28, 160, 6, 6, 'S');

    // --- TABLE HEADERS ---
    const colX = { axis: 14, box: 24, action: 35, when: 92, impact: 128, status: 182 };

    const drawHeaderPill = (x, w, label) => {
        doc.setFillColor(6, 150, 104);
        doc.roundedRect(x - w / 2, y - 5, w, 9, 4.5, 4.5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
        doc.text(label, x, y + 0.8, { align: 'center' });
    };

    drawHeaderPill(50, 50, "Action");
    drawHeaderPill(98, 35, "By When");
    drawHeaderPill(140, 42, "Impact");
    drawHeaderPill(182, 30, "Status");

    y += 12;

    // Vertical Column Dotted Lines
    const drawDottedLine = (x, y1, y2) => {
        let cy = y1;
        while (cy < y2) {
            doc.line(x, cy, x, cy + 0.8);
            cy += 2;
        }
    };
    doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3);
    drawDottedLine(81, y - 4, y + 150);
    drawDottedLine(118, y - 4, y + 150);
    drawDottedLine(166, y - 4, y + 150);

    const checkItems = [
        { a: `Buy \u20B9${lifeInsuranceTargetCr || '1'} Crore Term\nLife Insurance`, w: "This Week", i: "Critical \u2014 closes\n#1 vulnerability", s: "Not Started" },
        { a: `Set up \u20B9${fmt(efMonthly)} Emergency\nFund auto-transfer`, w: "This Week", i: "Urgent \u2014 builds\nsafety net", s: "Not Started" },
        { a: "Declare Old Tax\nRegime to HR", w: "This Week", i: `Saves \u20B9${fmt((taxSavingsMonthly || 6045) * 12)}/year\nimmediately`, s: "Not Started" },
        { a: `Increase PPF to\n\u20B9${fmt(ppfMonthly)}/month`, w: "This Month", i: "Maxes 80C +\nretirement corpus", s: "Not Started" },
        { a: `Start ELSS SIP\n\u20B9${fmt(sipMonthly)}/month`, w: "Month 2", i: "Tax saving + long\n-term wealth", s: "Not Started" },
        { a: `Open NPS and invest\n\u20B9${fmt(npsMonthly)}/month`, w: "Month 3", i: "Extra \u20B950,000\ndeduction + pension", s: "Not Started" },
        { a: actualLoanEMI > 0 ? `Start \u20B9${fmt(Math.round(actualLoanEMI * 0.5))} extra personal\nloan prepayment` : 'No personal loan\nto prepay', w: actualLoanEMI > 0 ? "Month 1" : "N/A", i: actualLoanEMI > 0 ? `Save \u20B9${fmt(Math.round(actualLoanBalance * 0.14 * 0.5))}\ninterest` : "Continue investing\naggressively", s: actualLoanEMI > 0 ? "Not Started" : "N/A" },
        { a: "Submit rent receipts\nto HR (HRA)", w: "Before HR\ndeadline", i: "Optimize HRA\nexemption", s: "Not Started" },
        { a: actualLoanEMI > 0 ? "Close personal\nloan entirely" : "Maintain zero\ndebt status", w: actualLoanEMI > 0 ? "Month 12" : "Ongoing", i: actualLoanEMI > 0 ? `Free \u20B9${fmt(actualLoanEMI + Math.round(actualLoanEMI * 0.5))}/m\ncash flow` : "No loan burden\nmaximize savings", s: actualLoanEMI > 0 ? "Not Started" : "On Track" },
        { a: `Boost SIPs to \u20B9${fmt(sipMonthly + 5000)}+\npost loan closure`, w: "Month 13", i: "Accelerate\nwealth creation", s: "Not Started" },
        { a: `Emergency fund\nreaches \u20B9${((metrics?.targetEmergency || monthlyIncome * 6 * 0.4) / 100000).toFixed(1)} Lakhs`, w: "Month 18", i: "Safety net\nfully funded", s: "Not Started" },
        { a: "Redirect emergency\nsavings to SIPs", w: "Month 19", i: `Final SIP target:\n\u20B9${fmt(sipMonthly + efMonthly)}/month`, s: "Not Started" },
        { a: "Review portfolio with\nSEBI-RIA advisor", w: "Annual", i: "Rebalance and\nrecalibrate", s: "Annually" },
        { a: "Increase SIPs by 10%\neach April (Step-Up)", w: "Every April", i: "Doubles corpus\nover 20 years", s: "Every Year" }
    ];

    let rowY = y;
    checkItems.forEach((c, idx) => {
        const mid = rowY + 5;

        // Detailed L-Connector match (Image based)
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3);
        doc.line(colX.axis, mid - 1, colX.box - 2, mid - 1); // Horizontal connector from axis
        doc.setFillColor(6, 150, 104); doc.circle(colX.box - 2, mid - 1, 0.7, 'F'); // Dot at end of connector

        // Checkbox precision
        doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.4);
        doc.roundedRect(colX.box, mid - 4.5, 7, 7, 0.5, 0.5, 'S');

        // Column Data
        doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text(c.a, colX.action, mid - 1.5, { lineHeightFactor: 1 });

        doc.setTextColor(31, 41, 55); doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
        doc.text(c.w, colX.when, mid);

        doc.setTextColor(115, 115, 115); doc.setFontSize(8.5);
        doc.text(c.i, colX.impact, mid - 1.5, { lineHeightFactor: 1.1 });

        doc.setTextColor(115, 115, 115); doc.setFontSize(9);
        doc.text(c.s, colX.status, mid, { align: 'center' });

        rowY += 10.8;
    });
};

export const renderRoadmapChecklist = (doc, { name, taxSavingsMonthly, lifeInsuranceTargetCr, personalLoanEMI, pageWidth, metrics, m }) => {
    const ORANGE = [230, 126, 34];
    const BLUE = [41, 128, 185];
    const fmt = v => (v || 0).toLocaleString('en-IN');

    const checklist = [
        {
            week: "THIS WEEK", color: [192, 57, 43], items: [
                `Buy Rs. ${lifeInsuranceTargetCr}Cr term insurance online — 30 mins`,
                `Set up Rs. ${fmt(Math.max(1000, Math.round(((m?.totalEssentialExpenses || metrics?.totalEssentialExpenses || 0) * 0.1 / 1000) * 1000)))}/mo auto-transfer to 'Safety Net' liquid fund`
            ]
        },
        {
            week: "THIS MONTH", color: ORANGE, items: [
                `Start ELSS SIP of Rs. ${fmt(Math.max(1000, Math.round(((m?.totalMonthlyIncome || metrics?.totalMonthlyIncome || 0) * 0.05 / 1000) * 1000)))}/month (Wealthcare by Yathartha)`,
                `Increase PPF to Rs. ${fmt(Math.max(500, Math.round(((m?.totalMonthlyIncome || metrics?.totalMonthlyIncome || 0) * 0.08 / 500) * 500)))}/month`,
                `Start NPS Rs. ${fmt(Math.max(500, Math.round(((m?.totalMonthlyIncome || metrics?.totalMonthlyIncome || 0) * 0.05 / 500) * 500)))}/month (extra Rs. 50k deduction)`,
                `Pay Rs. ${fmt(personalLoanEMI)} extra on personal loan each month`
            ]
        },
        {
            week: "MONTH 6", color: BLUE, items: [
                "Review and increase all SIPs by 10% (step-up strategy)",
                "Check EPF balance and ensure employer match is correct"
            ]
        }
    ];

    let cy = 36;
    checklist.forEach(section => {
        doc.setFillColor(...section.color); doc.roundedRect(14, cy, pageWidth - 28, 7, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(section.week, 18, cy + 5);
        cy += 10;
        section.items.forEach(item => {
            doc.setFillColor(248, 250, 252); doc.roundedRect(14, cy, pageWidth - 28, 8, 1, 1, 'F');
            doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
            doc.text(`☐  ${item}`, 18, cy + 5.5);
        });
    });
};

export const renderDebtManagementPlan = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report }) => {
    const BRAND_GREEN = [6, 150, 104];
    const NAVY_DARK = [15, 23, 42];
    const SLATE = [30, 41, 59];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    // Dynamic Variables (Enhanced)
    const sInc = report?.salaryIncome || {};
    const plBal = safeParse(sInc.personalLoanOutstanding) || calculations?.personalLoan?.balance || metrics.personalLoanBalance || 0;
    const hlBal = safeParse(sInc.homeLoanOutstanding) || calculations?.homeLoan?.balance || 0;
    const plEMI = safeParse(sInc.personalLoanEMI) || metrics.personalLoanEMI || 0;
    const hlEMI = safeParse(sInc.homeLoanEMI) || metrics.homeLoanEMI || 0;
    const plRate = safeParse(sInc.personalLoanRate) || 14;
    const hlRate = safeParse(sInc.homeLoanInterestRate) || 8.5;
    const mSurplus = metrics?.monthlySurplus || 0;

    addHeader(doc, "08", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 9, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    doc.text("DEBT MANAGEMENT PLAN", 18, y + 6.2);

    y += 24;
    const cx = pageWidth / 2;
    const cy = y + 44;
    const r = 33;

    doc.setFont("Montserrat", "bold");
    doc.setTextColor(...BRAND_GREEN); doc.setFontSize(18);
    doc.text("Personal Loan", cx - 55.5, y + 8.5, { align: 'center' });
    doc.setTextColor(...NAVY_DARK);
    doc.text("Home Loan", cx + 55.5, y + 8.5, { align: 'center' });

    doc.setLineWidth(0.7);
    // Personal Lead-in Line (Green)
    doc.setDrawColor(...BRAND_GREEN);
    const pStartDot = cx - 85, pJunction = cx - 25;
    doc.circle(pStartDot, y + 1.8, 1, 'F');
    doc.line(pStartDot, y + 1.8, pJunction, y + 1.8);
    doc.circle(pJunction, y + 1.8, 1, 'F');
    doc.line(pJunction, y + 1.8, pJunction, cy - 12);

    // Home Lead-in Line (Navy)
    doc.setDrawColor(...NAVY_DARK);
    const hStartDot = cx + 85, hJunction = cx + 25;
    doc.circle(hStartDot, y + 1.8, 1, 'F');
    doc.line(hStartDot, y + 1.8, hJunction, y + 1.8);
    doc.circle(hJunction, y + 1.8, 1, 'F');
    doc.line(hJunction, y + 1.8, hJunction, cy - 12);

    // Overlapping Circles
    doc.setFillColor(...NAVY_DARK); doc.circle(cx - 28, cy, r, 'F');
    doc.setFillColor(...BRAND_GREEN); doc.circle(cx + 28, cy, r, 'F');

    // Infographic Icons
    doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(42);
    doc.text("\u20B9", cx - 28, cy + 6.5, { align: 'center' }); // Rupee
    try { try { doc.addImage('/icons/home copy.png', 'PNG', cx + 28 - 9, cy - 9, 18, 18); } catch (e) { } } catch (e) { }

    // Statistics Grid
    const lx = 18, rxS = pageWidth - 18;
    const statRow = (label, val, rowY, isR = false, vSize = 12.5) => {
        const x = isR ? rxS : lx;
        doc.setTextColor(...SLATE); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
        doc.text(label, x, rowY, { align: isR ? 'right' : 'left' });
        doc.setTextColor(...NAVY_DARK); doc.setFontSize(vSize); doc.setFont("Montserrat", "bold");
        doc.text(val, x, rowY + 6, { align: isR ? 'right' : 'left' });
    };

    statRow("Outstanding", `\u20B9${fmt(plBal)}`, cy - 18);
    statRow("Outstanding", `\u20B9${fmt(hlBal)}`, cy - 18, true);
    statRow("Monthly EMI", `\u20B9${fmt(plEMI)}`, cy + 12);
    statRow("Monthly EMI", `\u20B9${fmt(hlEMI)}`, cy + 12, true);
    statRow("Interest Rate", `${plRate}% p.a.`, cy + 28);
    statRow("Interest Rate", `${hlRate}% p.a.`, cy + 28, true);
    statRow("Type", "Unsecured Debt", cy + 44);
    statRow("Type", "Secured (Asset)", cy + 44, true);
    statRow("Strategy", plBal > 0 ? "Snowball Closure" : "Maintain Debt-Free", cy + 60);
    statRow("Strategy", hlBal > 0 ? "Tenure Reduction" : "Maintain Ownership", cy + 60, true);

    y = cy + r + 50;
    // Phase Strategy Boxes
    doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.3);
    doc.roundedRect(14, y, pageWidth - 28, 85, 4.5, 4.5, 'S');

    const hPos = [27, 51, 98, 146, 178];
    const hText = ["Phase", "Month", "Action", "Cost", "Result"];
    const hWidths = [22, 18, 68, 25, 32];
    hText.forEach((t, i) => {
        doc.setFillColor(...BRAND_GREEN); doc.roundedRect(hPos[i] - hWidths[i] / 2, y - 5, hWidths[i], 11, 5, 5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text(t, hPos[i], y + 2, { align: 'center' });
    });

    const extraPay = Math.round(plEMI > 0 ? plEMI * 0.5 : mSurplus * 0.4);
    const monthsToClosePl = plEMI > 0 ? Math.ceil(plBal / (plEMI + extraPay)) : 0;

    const phaseRowData = [
        ["Phase 01", plBal > 0 ? `1-${monthsToClosePl}` : "1-6", plBal > 0 ? `Pay \u20B9${fmt(plEMI + extraPay)}/m on personal loan` : "Maintain zero high-cost debt status", plBal > 0 ? `\u20B9${fmt(plEMI + extraPay)}/m` : "No new cost", plBal > 0 ? `Loan closed in ${monthsToClosePl} months.` : "Savings rate protected."],
        ["Phase 02", plBal > 0 ? `${monthsToClosePl + 1}-18` : "7-12", "Redirect freed EMI to Emergency Fund", "No new cost", "Safety net fully funded."],
        ["Phase 03", "19+", "Redirect entire surplus to Wealth SIPs", "No new cost", `Building \u20B9${fmt(mSurplus)}+ wealth monthly.`],
        ["Phase 04", "Yr 5+", hlBal > 0 ? `Partial home loan prepayment (₹${fmt(hlEMI * 6)})` : "Aggressive stock portfolio shift", "Lump sum", hlBal > 0 ? "Save 8-12L in future interest." : "Maximize long-term equity returns."]
    ];

    phaseRowData.forEach((row, idx) => {
        const ry = y + 15 + (idx * 17.5);
        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(row[0], hPos[0], ry, { align: 'center' });
        doc.setTextColor(...SLATE); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
        doc.text(row[1], hPos[1], ry, { align: 'center' });
        doc.setTextColor(...NAVY_DARK); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        doc.text(doc.splitTextToSize(row[2], 65), 70, ry - 1, { lineHeightFactor: 1.15 });
        doc.setTextColor(...SLATE); doc.setFont("Montserrat", "normal"); doc.setFontSize(9);
        doc.text(row[3], hPos[3], ry, { align: 'center' });
        doc.text(doc.splitTextToSize(row[4], 34), 162, ry - 1, { lineHeightFactor: 1.15 });
        if (idx < 3) {
            doc.setDrawColor(241, 245, 249);
            doc.line(18, ry + 10.0, pageWidth - 18, ry + 10.0);
        }
    });

    addSimpleFooter(doc, "15", pageWidth, pageHeight);
};

export const renderGlossaryPage = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report }) => {
    const BRAND_GREEN = [6, 150, 104];
    const NAVY_DARK = [15, 23, 42];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    const personalInfo = report?.personalInfo || {};
    const isCouple = personalInfo.familyType === 'couple';

    addHeader(doc, "11", pageWidth);

    if (isCouple) {
        // ========== COUPLE GLOSSARY TABLE LAYOUT ==========
        let y = 28;
        // 1. Title Bar
        doc.setFillColor(...BRAND_GREEN); doc.roundedRect(14, y, pageWidth - 28, 10, 1.5, 1.5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text("SECTION 11: JOINT FINANCIAL GLOSSARY (10 New Family Terms)", 18, y + 6.5);

        y += 18;
        // 2. Intro Text
        doc.setTextColor(30, 41, 59); doc.setFontSize(9.2); doc.setFont("Montserrat", "normal");
        const jointSalary = report?.jointSalary || {};
        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};

        const hN = (sectionA.husbandName || "Rohan").trim().split(' ')[0];
        const wN = (sectionA.wifeName || "Priya").trim().split(' ')[0];
        const hFirstName = hN.charAt(0).toUpperCase() + hN.slice(1);
        const wFirstName = wN.charAt(0).toUpperCase() + wN.slice(1);

        const coupleNames = `${hFirstName} & ${wFirstName}`;
        const intro = `${coupleNames}, financial jargon can make simple concepts seem confusing. Below are 10 joint financial terms explained in plain, simple language with real examples from your own lives to help you build joint wealth.`;
        doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, y, { lineHeightFactor: 1.25 });

        y += 18;

        // 3. Dynamic Calculation Values
        const sectionD = jointSalary.sectionD || {};
        const sectionI = jointSalary.sectionI || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotalInc = hSalary + hOther;
        const wTotalInc = wSalary + wOther;
        const combinedMonthly = hTotalInc + wTotalInc;

        // Surplus
        const monthlySurplus = metrics?.monthlySurplus || 0;

        // DTI
        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;
        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const totalEMI = pl + cl + hl;
        const dti = combinedMonthly > 0 ? Math.round((totalEMI / combinedMonthly) * 100) : 0;

        const formatCover = (val) => {
            if (!val) return "0";
            if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
            if (val >= 100000) return `${(val / 100000).toFixed(1).replace(/\.0$/, '')}L`;
            if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
            return `${Math.round(val)}`;
        };

        // 1. DINK
        const surplusText = monthlySurplus > 0
            ? `Combined surplus is ₹${fmt(monthlySurplus)}/month. Prime position to build joint wealth.`
            : `Combined income of ₹${fmt(combinedMonthly)}/month. Optimize surplus to build wealth.`;

        // 2. Family Floater
        const hHealthCover = safeParse(sectionE.husbandHealthCover) || 0;
        const wHealthCover = safeParse(sectionE.wifeHealthCover) || 0;
        const combinedHealthCover = hHealthCover + wHealthCover;
        const healthText = combinedHealthCover > 0
            ? `Combine ${hFirstName}'s cover of ₹${formatCover(hHealthCover)} & ${wFirstName}'s of ₹${formatCover(wHealthCover)} into a single family floater.`
            : `No health cover found. Buy a Family Floater of at least ₹10L immediately to protect savings.`;

        // 3. Nomination
        const nominationText = `Always keep spouse as nominee (${hFirstName} nominates ${wFirstName} & vice-versa in all investments).`;

        // 4. Joint Tenancy
        const hlOS = safeParse(sectionF.homeLoanOutstanding) || 0;
        const jointTenancyText = hlOS > 0
            ? `Your ₹${(hlOS / 100000).toFixed(1).replace(/\.0$/, '')}L home loan & property must be co-owned in equal share.`
            : `Co-own all future properties & major investments between ${hFirstName} & ${wFirstName}.`;

        // 5. Survivor Benefit
        const hCov = hTotalInc * 12 * 10;
        const wCov = wTotalInc * 12 * 10;
        const hTargCover = hCov > 0 ? Math.round(hCov / 2500000) * 2500000 : 0;
        const wTargCover = wCov > 0 ? Math.round(wCov / 2500000) * 2500000 : 0;
        const hTargetVal = Math.max(10000000, hTargCover);
        const wTargetVal = Math.max(10000000, wTargCover);
        const husbandLifeCover = safeParse(sectionE.husbandLifeCover) || 0;
        const wifeLifeCover = safeParse(sectionE.wifeLifeCover) || 0;
        const survivorBenefitText = (husbandLifeCover > 0 || wifeLifeCover > 0)
            ? `Covers: ${hFirstName} (₹${formatCover(husbandLifeCover)}) & ${wFirstName} (₹${formatCover(wifeLifeCover)}). Keep spouse as sole beneficiary.`
            : `Buy term cover of ₹${formatCover(hTargetVal)} for ${hFirstName} & ₹${formatCover(wTargetVal)} for ${wFirstName} to secure spouse.`;

        // 6. Household DTI
        const dtiText = totalEMI > 0
            ? `Combined EMIs are ₹${fmt(totalEMI)}/month. Combined DTI ratio is ${dti}% (target: below 35%).`
            : `Your combined DTI is 0% (debt-free). Maintain this clean slate for wealth creation.`;

        // 7. Shared Goals
        const targetRetCorpus = safeParse(sectionI.retirementCorpus) || calculations?.retirement?.target || 0;
        const eduTarget = safeParse(sectionI.childEducation) || 0;
        const vacationTarget = safeParse(sectionI.vacation) || 0;
        const sharedGoalsText = (targetRetCorpus > 0 || eduTarget > 0 || vacationTarget > 0)
            ? `Targeting ₹${targetRetCorpus > 0 ? formatCover(targetRetCorpus) : '50L+'} for retirement, ₹${eduTarget > 0 ? formatCover(eduTarget) : '20L+'} for education, and ₹${vacationTarget > 0 ? formatCover(vacationTarget) : '2L+'} for travel.`
            : `Plan joint goals: Retirement (target: ₹5Cr+), child education (₹50L+), and family travel (₹5L+).`;

        // 8. Clubbing of Income
        const clubbingText = (hTotalInc > 0 && wTotalInc > 0)
            ? `If ${hFirstName} (earning ₹${fmt(hTotalInc)}/month) transfers assets to ${wFirstName} (earning ₹${fmt(wTotalInc)}/month), earnings are clubbed for tax.`
            : `Since only one partner earns, transferring assets to non-earning spouse clubs the income under tax laws.`;

        // 9. Will
        const hMutualFund = safeParse(sectionD.husbandMutualFund) || 0;
        const wMutualFund = safeParse(sectionD.wifeMutualFund) || 0;
        const combinedMF = hMutualFund + wMutualFund;
        const willText = (combinedMF > 0 || hlOS > 0)
            ? `Write a will to distribute your ₹${formatCover(combinedMF)} mutual funds and joint property seamlessly.`
            : `Write a joint will to specify how your property and future assets should be inherited by your heirs.`;

        // 10. Power of Attorney (POA)
        const poaText = totalEMI > 0
            ? `Allows ${hFirstName} or ${wFirstName} to manage the home loan and accounts if one partner is traveling or incapacitated.`
            : `Enables seamless management of bank accounts and investments by spouse if one partner is not available.`;

        // 4. Data Rows
        const headers = ["Term", "Meaning", "Joint Example"];
        const colWidths = [42, 50, 90]; // Total 182mm (fits exactly inside 14mm margins)

        const rows = [
            ["Dual-Income, No Kids (DINK)", "Both partners earn, no children", surplusText],
            ["Family Floater", "One health insurance policy for entire family", healthText],
            ["Nomination", "Who gets the money if you die", nominationText],
            ["Joint Tenancy", "Both own equal share of asset", jointTenancyText],
            ["Survivor Benefit", "Life insurance payout to spouse", survivorBenefitText],
            ["Household DTI", "Combined EMIs ÷ combined income", dtiText],
            ["Shared Goals", "Financial goals you plan together", sharedGoalsText],
            ["Clubbing of Income", "Transferring assets to spouse to save tax", clubbingText],
            ["Will", "Legal document for asset distribution", willText],
            ["Power of Attorney (POA)", "Spouse can act on your behalf", poaText]
        ];

        // 5. Draw the Joint Glossary Table
        const drawJointGlossaryTable = (headers, rows, colWidths, startY) => {
            const tLeft = 14;
            const tWidth = pageWidth - 28;
            const rowHeight = 9.5;
            const headerHeight = 11;

            // Calculate total table height first
            let tableHeight = headerHeight;
            rows.forEach((row) => {
                doc.setFont("Montserrat", "bold");
                const termLines = doc.splitTextToSize(row[0], colWidths[0] - 8);
                doc.setFont("Montserrat", "normal");
                const meanLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
                const exLines = doc.splitTextToSize(row[2], colWidths[2] - 8);
                const maxLines = Math.max(termLines.length, meanLines.length, exLines.length);
                const rHeight = Math.max(rowHeight, maxLines * 4.2 + 4.5);
                tableHeight += rHeight;
            });

            // Outer outline container
            doc.setDrawColor(6, 150, 104, 0.4);
            doc.setLineWidth(0.45);
            doc.roundedRect(tLeft, startY - 3, tWidth, tableHeight + 6, 4, 4, 'S');

            let cy = startY;

            // Draw header badges
            const drawHeaderBadge = (xCenter, w, label) => {
                doc.setFillColor(6, 150, 104);
                doc.roundedRect(xCenter - w / 2, cy + 2, w, 5.5, 2.75, 2.75, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("Montserrat", "bold");
                doc.setFontSize(7.5);
                doc.text(label, xCenter, cy + 5.8, { align: 'center' });
            };

            let curX = tLeft;
            headers.forEach((header, idx) => {
                const colW = colWidths[idx];
                const xCenter = curX + colW / 2;
                const badgeW = idx === 0 ? 20 : (idx === 1 ? 30 : 35);
                drawHeaderBadge(xCenter, badgeW, header);
                curX += colW;
            });

            cy += headerHeight;

            // Draw rows
            rows.forEach((row, rowIdx) => {
                // Ensure proper font weights when splitting
                doc.setFontSize(8.5);
                doc.setFont("Montserrat", "bold");
                const termLines = doc.splitTextToSize(row[0], colWidths[0] - 8);
                doc.setFont("Montserrat", "normal");
                const meanLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
                const exLines = doc.splitTextToSize(row[2], colWidths[2] - 8);
                const maxLines = Math.max(termLines.length, meanLines.length, exLines.length);
                const rHeight = Math.max(rowHeight, maxLines * 4.2 + 4.5);

                curX = tLeft;
                row.forEach((cell, idx) => {
                    doc.setFont("Montserrat", idx === 0 ? "bold" : "normal");
                    doc.setFontSize(8.5);
                    doc.setTextColor(30, 41, 59);

                    const lines = idx === 0 ? termLines : (idx === 1 ? meanLines : exLines);
                    // Vertically center lines in the row cell
                    const cellTextHeight = lines.length * 4.2;
                    const textY = cy + (rHeight - cellTextHeight) / 2 + 3.2;

                    doc.text(lines, curX + 4, textY, { lineHeightFactor: 1.15 });
                    curX += colWidths[idx];
                });

                // Dividing line between rows
                if (rowIdx < rows.length - 1) {
                    doc.setDrawColor(226, 232, 240);
                    doc.setLineWidth(0.25);
                    doc.line(tLeft, cy + rHeight, tLeft + tWidth, cy + rHeight);
                }

                cy += rHeight;
            });

            // Vertical dotted grid lines
            doc.setDrawColor(203, 213, 225);
            doc.setLineWidth(0.35);
            doc.setLineDash([0.6, 0.8], 0);
            let splitX = tLeft;
            for (let i = 0; i < colWidths.length - 1; i++) {
                splitX += colWidths[i];
                doc.line(splitX, startY + headerHeight - 1, splitX, startY + tableHeight);
            }
            doc.setLineDash([], 0); // reset
        };

        drawJointGlossaryTable(headers, rows, colWidths, y);

    } else {
        // ========== ORIGINAL SINGLE GLOSSARY LAYOUT (UNTOUCHED) ==========
        let y = 30;
        doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("FINANCIAL GLOSSARY — 25 TERMS YOU MUST KNOW", 18, y + 5.5);

        y += 12;
        doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        const pName = (name || "Suresh").toLowerCase();
        const intro = `${pName}, financial jargon can make simple concepts seem confusing. Below are 25 terms used in this report — explained in plain, simple language with real examples from your own life. Knowledge is power, and understanding these terms puts you in the driver's seat of your own finances.`;
        doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, y, { lineHeightFactor: 1.2 });

        y += 22;
        const qW = 148;
        const qX = pageWidth - qW - 14;
        doc.setFillColor(...BRAND_GREEN); doc.roundedRect(qX, y, qW, 20, 3, 3, 'F');

        try {
            try { doc.addImage('/icons/image.png', 'PNG', 14, y - 4, 28, 28); } catch (e) { }
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(10);
            doc.circle(28, y + 10, 19, 'S');
            doc.setDrawColor(229, 231, 235);
            doc.setLineWidth(0.3);
            doc.circle(28, y + 10, 14, 'S');
        } catch (e) { }

        doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5);
        const quoteTxt = "\" An investment in knowledge pays the best interest. \"";
        doc.text(quoteTxt, qX + 12, y + 10);
        doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
        doc.text("— Benjamin Franklin", qX + qW - 12, y + 15, { align: 'right' });

        y += 36;

        const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
            const boxWidth = pageWidth - 65;
            const boxX = side === 'left' ? 42 : 14;
            const iconX = side === 'left' ? 24 : pageWidth - 24;

            doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
            const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
            const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

            const boxH = Math.max(30, (meanLines.length * 3.5) + (matterLines.length * 3.5) + 18);

            doc.setTextColor(...BRAND_GREEN); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
            doc.text(title, 14, y);
            y += 4;

            doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.35);
            doc.roundedRect(boxX, y, boxWidth, boxH, 4, 4, 'S');

            const midY = y + (boxH / 2);
            doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
            if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
            else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

            doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.5);
            doc.circle(iconX, midY, 9.5, 'S');
            try { try { doc.addImage(iconPath, 'PNG', iconX - 6, midY - 6, 12, 12); } catch (e) { } } catch (e) { }

            let internalY = y + 7;
            doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
            doc.text("What it means: ", boxX + 6, internalY);
            internalY += 4;
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
            doc.text(meanLines, boxX + 6, internalY, { lineHeightFactor: 1.1 });

            internalY += (meanLines.length * 4) + 2;
            doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59); doc.setFontSize(9);
            doc.text("Why it matters for you: ", boxX + 6, internalY);
            internalY += 4;
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
            doc.text(matterLines, boxX + 6, internalY, { lineHeightFactor: 1.1 });

            y += boxH + 4.5;
            const eName = (name || "Suresh").charAt(0).toUpperCase() + (name || "Suresh").slice(1);
            doc.setTextColor(...BRAND_GREEN); doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
            doc.text(`${eName}'s example: `, boxX, y);
            const etw = doc.getTextWidth(`${eName}'s example: `);
            doc.setTextColor(30, 41, 59); doc.setFont("Montserrat", "normal");
            const exLines = doc.splitTextToSize(example, boxWidth - etw - 4);
            doc.text(exLines, boxX + etw, y, { lineHeightFactor: 1.1 });

            y += (exLines.length * 4) + 8;
        };

        const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
        const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
        const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};

        const valSIP = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
        const rawEPF = safeParse(sInc.epf) || safeParse(sInv.epf) || metrics?.epfMonthly || 0;
        const valEPF = rawEPF === 0 ? 1800 : rawEPF;
        const rawPPF = safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics?.ppfMonthly || 0;
        const valPPF = rawPPF === 0 ? 1000 : rawPPF;

        drawGlossaryBox(
            "SIP — Systematic Investment Plan (Systematic Investment Plan)",
            {
                means: "A SIP is a method of investing a fixed amount in a mutual fund every month automatically. Think of it like a recurring deposit — but instead of a bank, your money goes into equity or debt markets.",
                matters: "SIPs use 'Rupee Cost Averaging' — you buy more units when markets fall and fewer when markets rise, automatically. This reduces risk and smooths returns over time. Even small regular investments can grow significantly over 20+ years due to compounding."
            },
            valSIP > 0
                ? `Your ₹${fmt(valSIP)}/month mutual fund SIP is already running. This report recommends optimizing based on your surplus.`
                : `You have not started a SIP yet. We highly recommend starting one to build long-term wealth.`,
            '/icons/transfer.png',
            'left'
        );

        drawGlossaryBox(
            "EPF — Employees' Provident Fund (Employees' Provident Fund)",
            {
                means: "EPF is a government-mandated retirement savings scheme. Both you AND your employer contribute 12% of your basic salary every month. The money earns tax-free interest (currently 8.1% p.a.) and is accessible at retirement.",
                matters: `Your employer contributes ₹${fmt(valEPF)}/month for FREE on your behalf — this is ₹${fmt(valEPF * 12)}/year of free retirement savings. EPF is tax-free at all stages: contribution (80C), interest, and withdrawal at maturity.`
            },
            rawEPF > 0
                ? `Your EPF contribution: ₹${fmt(valEPF)}/month. Employer matches this amount. Total combined contribution working toward your retirement corpus.`
                : `We estimate a standard EPF contribution of ₹1,800/month. If you are salaried, verify with your employer to build this secure retirement base.`,
            '/icons/verified.png',
            'right'
        );

        drawGlossaryBox(
            "PPF — Public Provident Fund (Public Provident Fund)",
            {
                means: "PPF is a government savings scheme with a 15-year lock-in (extendable in 5-year blocks). It offers guaranteed returns (7.1% currently), tax deduction on investment (80C), and completely tax-free maturity proceeds.",
                matters: `PPF is the 'triple tax benefit' product: (1) Invest ₹${fmt(valPPF)}/month saves income tax now, (2) Interest earned not taxable, (3) Maturity amount not taxable. No other investment in India offers all three. Perfect for conservative retirement savings.`
            },
            rawPPF > 0
                ? `Your PPF contribution: ₹${fmt(valPPF)}/month. Tax-free retirement savings with sovereign guarantee.`
                : `You haven't started a PPF yet. We recommend starting with ₹1,000/month to secure your tax-free retirement savings with a sovereign guarantee.`,
            '/icons/security.png',
            'left'
        );
    }
}

export const renderGlossaryPage2 = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    addHeader(doc, "11", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL GLOSSARY \u2014 25 TERMS (CONTINUED)", 18, y + 5.5);

    y += 12;

    // --- GLOSSARY ITEMS ---
    const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
        const boxWidth = pageWidth - 65;
        const boxX = side === 'left' ? 42 : 14;
        const iconX = side === 'left' ? 24 : pageWidth - 24;

        // Micro-Adjusted Calculations for High-Density Fit
        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
        const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

        const meanH = meanLines.length * 3.2;
        const matterH = matterLines.length * 3.2;
        const boxContentH = 5 + meanH + 5 + matterH + 4; // Tightened gaps
        const boxH = Math.max(26, boxContentH);

        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, 14, y);
        y += 4;

        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
        doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

        const midY = y + (boxH / 2);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
        if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
        else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
        doc.circle(iconX, midY, 9, 'S');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

        let internalY = y + 6;
        doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("What it means: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        internalY += meanH + 1.8;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("Why it matters for you: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        y += boxH + 3.5;
        doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
        doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
        doc.text(`${name || "Suresh"}'s case:`, boxX + 4, y + 3.8);
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const exLines = doc.splitTextToSize(example, boxWidth - 16);
        doc.text(exLines, boxX + 4, y + 7.8, { lineHeightFactor: 1.15 });
        y += (exLines.length * 4) + 11;
    };

    drawGlossaryBox(
        "NPS \u2014 National Pension System",
        {
            means: "NPS is a voluntary retirement scheme managed by PFRDA (government). You invest monthly, the money is managed in equity/debt/government securities, and at age 60 you get 60% as lump sum + 40% as monthly pension.",
            matters: "NPS gives an additional \u20B950,000 deduction under Section 80CCD(1B) \u2014 OVER and ABOVE your regular \u20B91.5 Lakh 80C limit. For someone in the 20% tax bracket, this alone saves \u20B910,000 in tax every year."
        },
        `You currently have \u20B9${fmt(metrics?.npsActual || 0)} in NPS. Adding NPS contributions can provide extra tax deduction under 80CCD(1B) while building your pension corpus.`,
        '/icons/profit.png',
        'left'
    );

    drawGlossaryBox(
        "ELSS \u2014 Equity Linked Saving Scheme",
        {
            means: "ELSS is a type of mutual fund that invests in the stock market (equity) & qualifies for \u20B91,50,000 annual tax deduction under Section 80C. It has a mandatory 3-year lock-in period \u2014 the shortest among all 80C options.",
            matters: "ELSS combines tax saving with wealth creation. The 3-year lock-in, while seeming restrictive, builds investment discipline. Historically, ELSS funds have delivered 12-16% annual returns over 5+ year periods."
        },
        `You currently invest \u20B9${fmt(metrics?.elssMonthly || 0)} in ELSS. ELSS helps you save tax under Section 80C while creating long-term wealth through equity exposure.`,
        '/icons/investing_new.png',
        'right'
    );

    const totalEMI = (metrics?.personalLoanEMI || 0) + (metrics?.homeLoanEMI || 0);
    const dti = ((totalEMI / (m?.totalMonthlyIncome || 1)) * 100).toFixed(0);
    drawGlossaryBox(
        "EMI \u2014 Equated Monthly Instalment",
        {
            means: "An EMI is the fixed monthly payment you make to repay a loan. It includes both interest & principal components. In the early months, most of your EMI is interest. Over time, the principal portion grows.",
            matters: "Understanding your EMI split helps you decide when to prepay. In the early months, most of your EMI is interest. Over time, the principal portion grows. Prepaying reduces the principal, dramatically cutting future interest."
        },
        `Your total EMIs: \u20B9${fmt(totalEMI)}/month${metrics?.homeLoanEMI > 0 || metrics?.personalLoanEMI > 0 ? ` (\u20B9${fmt(metrics?.homeLoanEMI || 0)} home${metrics?.personalLoanEMI > 0 ? ` + \u20B9${fmt(metrics?.personalLoanEMI || 0)} personal` : ''})` : ''}. Your Debt-to-Income ratio: ${dti}% \u2014 ${dti <= 35 ? 'within the safe 35% limit' : 'attention needed: above 35%'}.`,
        '/icons/invoice.png',
        'left'
    );

    const ut80c = (metrics?.epfMonthly || 0) + (metrics?.ppfMonthly || 0) + (calculations?.homeLoan?.principal || 0);
    drawGlossaryBox(
        "80C Deduction (Section 80C of the Income Tax Act)",
        {
            means: "Section 80C allows you to reduce your taxable income by up to \u20B91,50,000 per year by investing in specified instruments: PPF, ELSS, EPF, home loan principal, life insurance premiums, NSC, tax-saving FDs, and more.",
            matters: "If you are in the 20% tax bracket, maxing out 80C saves \u20B930,000 in tax every year. This is \u20B92,500/month \u2014 money that stays in your pocket just by investing in instruments you would have invested in anyway."
        },
        `Your current 80C utilisation: \u20B9${fmt(ut80c * 12)}/year (EPF + PPF + Home Loan Principal). After optimisation: \u20B91,50,000/year (fully maxed). Extra tax saving: \u20B91,200/year.`,
        '/icons/tax_new.png',
        'right'
    );
};

export const renderGlossaryPage3 = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, rentPaid, taxSavings, age, gender, emergencyTarget, currentLifeCover }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    const p = report?.personalInfo || {};

    addHeader(doc, "11", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL GLOSSARY \u2014 25 TERMS (CONTINUED)", 18, y + 5.5);

    y += 12;

    const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
        const boxWidth = pageWidth - 65;
        const boxX = side === 'left' ? 42 : 14;
        const iconX = side === 'left' ? 24 : pageWidth - 24;

        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
        const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

        const meanH = meanLines.length * 3.2;
        const matterH = matterLines.length * 3.2;
        const boxContentH = 5 + meanH + 5 + matterH + 4;
        const boxH = Math.max(26, boxContentH);

        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, 14, y);
        y += 4;

        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
        doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

        const midY = y + (boxH / 2);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
        if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
        else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
        doc.circle(iconX, midY, 9, 'S');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

        let internalY = y + 6;
        doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("What it means: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        internalY += meanH + 1.8;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("Why it matters for you: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        y += boxH + 3.5;
        doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
        doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
        doc.text(`${name || "Suresh"}'s case:`, boxX + 4, y + 3.8);
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const exLines = doc.splitTextToSize(example, boxWidth - 16);
        doc.text(exLines, boxX + 4, y + 7.8, { lineHeightFactor: 1.15 });
        y += (exLines.length * 4) + 11;
    };

    drawGlossaryBox(
        "HRA \u2014 House Rent Allowance",
        {
            means: "HRA is a component of your salary that your employer provides to cover rent expenses. The HRA you receive (minus a formula-based amount) is exempt from income tax under specific conditions.",
            matters: "Metro city employees get 50% of basic salary as HRA exemption (vs 40% for non-metro). The exemption is calculated as the MINIMUM of: actual HRA received, 50% of basic, or actual rent paid minus 10% of basic."
        },
        `You pay \u20B9${fmt(rentPaid)}/month rent. Your estimated HRA exemption: \u20B9${fmt(taxSavings)}/year. Ensure rent receipts, rental agreement, and landlord's PAN (if rent > \u20B91L/month) are submitted to HR.`,
        '/icons/home_new.png',
        'left'
    );

    drawGlossaryBox(
        "Compounding (Compound Interest / Compound Returns)",
        {
            means: "Compounding means earning returns on your returns. Your money grows on the original amount PLUS all the interest/returns already earned. Albert Einstein famously called it 'the eighth wonder of the world.'",
            matters: `Small amounts invested regularly at 12% can grow exponentially over time. For example, \u20B91,000 invested monthly at 12% for 30 years can grow to over \u20B930 Lakhs. Time is more powerful than amount. Starting at ${age || 30} vs. ${(age || 30) + 10} makes a massive difference in final wealth.`
        },
        `Your current monthly investments of ₹${fmt(metrics.totalMonthlyInvestments)}: if kept for 20 years at 12% = ₹${fmt(metrics.totalMonthlyInvestments * 100)}. This is why starting NOW is the most important action.`,
        '/icons/profit.png',
        'right'
    );

    drawGlossaryBox(
        "Term Insurance (Pure Term Life Insurance)",
        {
            means: "Term insurance is the simplest and purest form of life insurance. You pay a monthly/annual premium, and if you pass away during the term, your nominee receives the full sum assured. If you survive, no money is paid back (unlike endowment plans).",
            matters: `Term insurance is the ONLY type of life insurance that financial experts universally recommend for wealth protection. It gives maximum cover at minimum cost. A ₹1 Crore 30-year term plan for a ${age || 30}-year-old non-smoking ${gender || 'professional'} costs only ₹700-1200/month.`
        },
        `Your current life cover: ₹${fmt(metrics.currentLifeCover || 0)}. Required: ₹1 Crore+. Monthly cost to bridge this gap: approx. ₹800-1200. This is approx. 1% of your income for 100% protection.`,
        '/icons/life-insurance.png',
        'left'
    );

    drawGlossaryBox(
        "Emergency Fund (Contingency Reserve)",
        {
            means: "An emergency fund is 3-6 months of all your essential expenses kept in a highly liquid, safe account \u2014 untouched except for genuine emergencies like job loss, medical crisis, or urgent repairs.",
            matters: "Without an emergency fund, any unexpected expense forces you to break investments (often at a loss), take high-interest personal loans, or borrow from family. An emergency fund is the foundation that makes every other financial plan possible."
        },
        `Your target: ₹${fmt(emergencyTarget)} (6 months × ₹${fmt(m.totalEssentialExpenses)} essential expenses). Current: ₹${fmt(metrics.currentEmergency)} (${((metrics.currentEmergency / (emergencyTarget || 1)) * 100).toFixed(0)}% funded).`,
        '/icons/invoice.png',
        'right'
    );
};

export const renderGlossaryPage4 = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, age }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    const yearsToRetirement = Math.max(5, 60 - (age || 30));
    const inflationFactor = Math.pow(1.06, yearsToRetirement);
    const futureExpenses = (m?.totalEssentialExpenses || 0) * inflationFactor;

    addHeader(doc, "11", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL GLOSSARY \u2014 25 TERMS (CONTINUED)", 18, y + 5.5);

    y += 12;

    const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
        const boxWidth = pageWidth - 65;
        const boxX = side === 'left' ? 42 : 14;
        const iconX = side === 'left' ? 24 : pageWidth - 24;

        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
        const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

        const meanH = meanLines.length * 3.2;
        const matterH = matterLines.length * 3.2;
        const boxContentH = 5 + meanH + 5 + matterH + 4;
        const boxH = Math.max(26, boxContentH);

        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, 14, y);
        y += 4;

        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
        doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

        const midY = y + (boxH / 2);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
        if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
        else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
        doc.circle(iconX, midY, 9, 'S');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

        let internalY = y + 6;
        doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("What it means: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        internalY += meanH + 1.8;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("Why it matters for you: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        y += boxH + 3.5;
        doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
        doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
        doc.text(`${name || "Suresh"}'s case:`, boxX + 4, y + 3.8);

        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const exLines = doc.splitTextToSize(example, boxWidth - 16);
        doc.text(exLines, boxX + 4, y + 7.8, { lineHeightFactor: 1.15 });
        y += (exLines.length * 4) + 11;
    };

    const totalEMI = (safeParse(metrics?.personalLoanEMI) || 0) + (safeParse(metrics?.homeLoanEMI) || 0);
    const income = safeParse(m?.totalMonthlyIncome) || 0;
    const dti = income > 0 ? ((totalEMI / income) * 100).toFixed(0) : 0;
    const monthlyExp = m?.totalMonthlyExpenses || 0;
    const exMonths = monthlyExp > 0 ? Math.ceil(monthlyExp) : 0;
    const efTarget = exMonths * 6;
    const efContribution = income > 0 ? Math.min(10000, Math.round(income * 0.1)) : 0; // 10% of income or max 10k
    const sipMonthly = metrics?.sipMonthly || 0;
    const healthIns = (safeParse(report?.salaryIncome?.healthInsurance) || 0) * 12;
    const taxBracket = m?.taxBracket || 20;
    const savings80D = (Math.min(healthIns, 75000) * (taxBracket / 100)).toFixed(0);

    drawGlossaryBox(
        "Debt-to-Income Ratio (DTI) (DTI Ratio)",
        {
            means: "DTI ratio is the percentage of your gross monthly income that goes toward all EMI repayments. Formula: Total Monthly EMIs \u00f7 Gross Monthly Income \u00d7 100.",
            matters: "A DTI below 35% is considered healthy by banks and financial planners. Above 50% indicates financial stress. Your DTI of ${dti}% is ${dti < 35 ? 'healthy' : 'high'} \u2014 it indicates ${dti < 35 ? 'capacity for additional wealth building' : 'potential financial strain'}."
        },
        `Your DTI: \u20B9${fmt(totalEMI)} EMIs \u00f7 \u20B9${fmt(income)} income = ${dti}%. ${metrics?.personalLoanEMI > 0 ? `After personal loan closure, your DTI will drop to ${(((safeParse(metrics?.homeLoanEMI) || 0) / income) * 100).toFixed(0)}%, giving you significant financial flexibility.` : 'Maintaining a low DTI ensures you stay eligible for the best interest rates in the future.'}`,
        '/icons/balance.png',
        'right'
    );

    drawGlossaryBox(
        "Liquid Fund (Liquid Mutual Fund)",
        {
            means: "A liquid fund is a type of debt mutual fund that invests in very short-term instruments like government treasury bills and commercial paper. Money can be withdrawn within 24 hours. Returns are typically 5-7% per year \u2014 much better than a savings account.",
            matters: "For your emergency fund, a liquid fund is the perfect vehicle. Your money earns more than a savings account, is completely safe, and can be withdrawn in 1 business day via your mobile app."
        },
        `Instead of keeping \u20B9${fmt(efContribution)}/month emergency fund savings in your bank account at 2.5%, putting it in a liquid fund at 6% target interest earns you significantly more. On your \u20B9${fmt(efTarget)} target, this difference alone earns you \u20B9${fmt(efTarget * 0.035)} extra annually.`,
        '/icons/rupee.png',
        'left'
    );

    drawGlossaryBox(
        "80D Deduction (Section 80D \u2014 Health Insurance Premium Deduction)",
        {
            means: "Section 80D allows you to claim tax deductions on health insurance premiums paid for yourself, your spouse, children, and parents. The limits are: \u20B925,000 for self/spouse/children + extra \u20B925,000-50,000 for parents.",
            matters: "Claiming your full premium saves tax. You are paying \u20B9${fmt(healthIns)}/year in premiums. In your ${taxBracket}% tax bracket, this saves you \u20B9${fmt(savings80D)} in tax every year."
        },
        `Your 80D utilisation: \u20B9${fmt(healthIns)} of a potential \u20B975,000 deduction space. ${healthIns < 75000 ? `You have \u20B9${fmt(75000 - healthIns)} of unused 80D capacity if you ever want to enhance cover for your parents.` : 'You are perfectly utilizing this deduction for maximum tax efficiency.'}`,
        '/icons/medical-support.png',
        'right'
    );

    drawGlossaryBox(
        "Rupee Cost Averaging (RCA) (Rupee Cost Averaging)",
        {
            means: "When you invest a fixed amount (like a SIP) every month, you automatically buy more mutual fund units when prices are low and fewer units when prices are high. Over time, this averages out your purchase cost and reduces market timing risk.",
            matters: "This is why SIPs are safer than lump-sum investments for most individuals. You do not need to predict market highs and lows. The system automatically buys smart."
        },
        `Your \u20B9${fmt(sipMonthly)}/month SIP already benefits from RCA. Every market correction is actually an opportunity \u2014 your SIP buys more units at lower prices, which multiplies in value when the market recovers.`,
        '/icons/graph copy.png',
        'left'
    );
};

export const renderGlossaryPage5 = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, age }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    const yearsToRetirement = Math.max(5, 60 - (age || 30));
    const inflationFactor = Math.pow(1.06, yearsToRetirement);
    const futureExpenses = (m?.totalEssentialExpenses || 0) * inflationFactor;

    addHeader(doc, "11", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL GLOSSARY \u2014 25 TERMS (CONTINUED)", 18, y + 5.5);

    y += 12;

    const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
        const boxWidth = pageWidth - 65;
        const boxX = side === 'left' ? 42 : 14;
        const iconX = side === 'left' ? 24 : pageWidth - 24;

        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
        const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

        const meanH = meanLines.length * 3.2;
        const matterH = matterLines.length * 3.2;
        const boxContentH = 5 + meanH + 5 + matterH + 4;
        const boxH = Math.max(26, boxContentH);

        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, 14, y);
        y += 4;

        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
        doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

        const midY = y + (boxH / 2);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
        if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
        else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
        doc.circle(iconX, midY, 9, 'S');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

        let internalY = y + 6;
        doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("What it means: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        internalY += meanH + 1.8;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("Why it matters for you: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        y += boxH + 3.5;
        doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
        doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
        doc.text(`${name || "User"}'s case:`, boxX + 4, y + 3.8);

        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const exLines = doc.splitTextToSize(example, boxWidth - 16);
        doc.text(exLines, boxX + 4, y + 7.8, { lineHeightFactor: 1.15 });
        y += (exLines.length * 4) + 11;
    };

    drawGlossaryBox(
        "Inflation \u2014 The Silent Wealth Killer",
        {
            means: "Inflation is the rate at which the price of goods (milk, rent, school fees) increases over time. If inflation is 6%, \u20B9100 today will only buy \u20B994 worth of goods next year.",
            matters: "Your savings must grow FASTER than inflation to create wealth. Fixed Deposits often give 7% interest, but after 6% inflation and 20% tax, your real growth is negative. Equity markets (12%+) are the only way to beat Indian inflation long-term."
        },
        `Your \u20B9${fmt(m.totalEssentialExpenses)} monthly living cost today will become approx. \u20B9${fmt(futureExpenses)}/month by the time you retire in ${yearsToRetirement} years (at 6% inflation). Your retirement goal MUST account for this future cost of living.`,
        '/icons/profit.png',
        'right'
    );

    drawGlossaryBox(
        "Asset Allocation (Don't put all eggs in one basket)",
        {
            means: "Asset Allocation is the strategy of dividing your investments among different categories like Equity (growth), Debt (safety), and Gold (protection). Different assets respond differently to market shifts.",
            matters: "It is the most important decision for long-term returns. For your age, an 80% Equity / 20% Debt split is ideal. Debt provides a safety net when the stock market falls, while Equity drives the multi-crore growth you need for retirement."
        },
        `Your current mix: Gradually shift towards a 70:30 Equity/Debt ratio to help your long-term retirement goal become a reality. Current assets: ₹${fmt(m.currentAssets)}.`,
        '/icons/graph.png',
        'left'
    );

    drawGlossaryBox(
        "Net Worth \u2014 Your Financial Scorecard",
        {
            means: "Net Worth is simply the total value of everything you OWN (Assets) minus everything you OWE (Liabilities). It is the true measure of your wealth, not your salary.",
            matters: "Tracking Net Worth helps you see if you are actually getting richer or just spending more. Increasing your assets (investments) and decreasing your liabilities (loans) is the only way to financial freedom."
        },
        `Your Assets: \u20B9${fmt(m.currentAssets)}. Your Liabilities: \u20B9${fmt(m.currentLiabilities)}. Your TRUE wealth today: \u20B9${fmt(m.netWorth)}. Maxing investments grows this gap.`,
        '/icons/life-insurance.png',
        'right'
    );

    drawGlossaryBox(
        "Tax Loss Harvesting (Tax-Free Returns)",
        {
            means: "This is a strategy of selling mutual fund units at a loss to offset the tax you owe on units sold at a profit. You can then immediately reinvest the money back into the market.",
            matters: "In India, you pay 10% tax on Equity gains above \u20B91 Lakh. Harvesting involves 'resetting' your profits to zero every March. This can save you thousands in taxes over 20+ years, allowing MORE of your money to keep compounding.",
        },
        `Your estimated portfolio tax savings using harvesting: \u20B98,000/year. Reinvested over 20 years at 12%, this simple tax trick creates an extra \u20B96.4 Lakhs for your retirement.`,
        '/icons/tax_new.png',
        'left'
    );
};

export const renderGlossaryPage6 = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    addHeader(doc, "11", pageWidth);

    let y = 30;
    doc.setFillColor(...BRAND_GREEN); doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL GLOSSARY \u2014 25 TERMS (CONTINUED)", 18, y + 5.5);

    y += 12;

    const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
        const boxWidth = pageWidth - 65;
        const boxX = side === 'left' ? 42 : 14;
        const iconX = side === 'left' ? 24 : pageWidth - 24;

        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
        const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

        const meanH = meanLines.length * 3.2;
        const matterH = matterLines.length * 3.2;
        const boxContentH = 5 + meanH + 5 + matterH + 4;
        const boxH = Math.max(26, boxContentH);

        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, 14, y);
        y += 4;

        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
        doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

        const midY = y + (boxH / 2);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
        if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
        else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
        doc.circle(iconX, midY, 9, 'S');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

        let internalY = y + 6;
        doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("What it means: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        internalY += meanH + 1.8;
        doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
        doc.text("Why it matters for you: ", boxX + 5, internalY);
        internalY += 3.5;
        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
        doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

        y += boxH + 3.5;
        doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
        doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
        doc.text(`${name || "Suresh"}'s case:`, boxX + 4, y + 3.8);

        doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
        const exLines = doc.splitTextToSize(example, boxWidth - 16);
        doc.text(exLines, boxX + 4, y + 7.8, { lineHeightFactor: 1.15 });
        y += (exLines.length * 4) + 11;
    };

    // Parameter B: Savings Rate (Robust Sync)
    const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
    const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || report?.salary || {};
    const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || report?.expenses || {};

    // Sum of all monthly investments with fallbacks
    const pf = (safeParse(sInc.ppf) || safeParse(sInc.sukanyaSamriddhi) || metrics?.ppfMonthly || 0);
    const ef = (safeParse(sInc.epf) || safeParse(sInc.vpf) || safeParse(sInc.nps_80ccd2) || metrics?.epfMonthly || metrics?.npsMonthly || 0);
    const mf = (safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || safeParse(sInv.monthlyInvestment) || metrics?.sipMonthly || 0);
    const em = (safeParse(sInv.emergencyFund) || safeParse(sInv.liquidFund) || metrics?.emergencyFundMonthly || 0);

    const totalActualSavings = metrics?.totalActualSavings || (pf + ef + mf + em);
    const income = safeParse(m?.totalMonthlyIncome) || safeParse(report?.personalInfo?.monthlySalary) || 1;
    const sRate = ((totalActualSavings / income) * 100).toFixed(1);

    const oRate = Math.max(25, parseFloat(sRate) + 5).toFixed(1);

    const taxSaving = (metrics?.taxSavingsMonthly || 0) * 12;
    const hEMI = safeParse(report?.section3?.homeLoanEMI) || safeParse(sInc?.homeLoanEMI) || safeParse(metrics?.homeLoanEMI) || 0;
    const hIntYear = hEMI * 12 * 0.65; // Estimated 65% interest in early years

    const sipMonthly = safeParse(sInv.mutualFunds) || safeParse(metrics?.sipMonthly) || 0;
    const years = calculations?.yearsToRetirement || 25;
    const annualIncome = income * 12;
    const taxBracket = m?.taxBracket || (annualIncome > 1500000 ? 30 : annualIncome > 1000000 ? 20 : annualIncome > 500000 ? 10 : 0);

    // Projections for Step-up (Approximate for illustration)
    const baseCorpus = sipMonthly * 12 * years * 3.5; // Very rough 12% CAGR multiplier
    const stepUpCorpus = baseCorpus * 2.2;

    drawGlossaryBox(
        "Step-Up SIP (Annual SIP Increase Strategy)",
        {
            means: `A step-up SIP means increasing your monthly \u20B9${fmt(sipMonthly)} SIP by a fixed percentage every year \u2014 typically 10%. As your income grows, you systematically invest more.`,
            matters: `Your \u20B9${fmt(sipMonthly)} SIP for ${years} years without step-up would create approx \u20B9${(baseCorpus / 10000000).toFixed(1)} Crore. The same SIP with a 10% annual step-up could grow to over \u20B9${(stepUpCorpus / 10000000).toFixed(1)} Crore. Step-up is the most powerful wealth-building tool.`
        },
        `Action for every April: increase your \u20B9${fmt(sipMonthly)} SIP by 10% (\u20B9${fmt(sipMonthly * 0.1)}). When you get your annual increment, automatically redirecting a small portion into SIPs will dramatically accelerate your retirement goal.`,
        '/icons/profit.png',
        'right'
    );

    drawGlossaryBox(
        "Tax Regime \u2014 Old vs New (Income Tax Regime Choice)",
        {
            means: "India has two tax regimes. Old Regime: higher rates but allows 20+ deductions (80C, HRA, home loan, etc.). New Regime: lower rates but almost no deductions.",
            matters: `Based on your salary structure and investments, ${taxSaving > 0 ? `the Old Regime is your best choice, saving you \u20B9${fmt(taxSaving)} in tax annually.` : 'the New Regime may be simpler if you have limited deductions.'} You choose this at the start of each year.`
        },
        `For FY2024-25, choosing the Old Regime saves you exactly \u20B9${fmt(taxSaving)} of your hard-earned money. Always declare this choice to your HR team before the investment declaration deadline.`,
        '/icons/balance.png',
        'left'
    );

    drawGlossaryBox(
        "Savings Rate (Personal Savings Rate)",
        {
            means: `Savings rate is the percentage of your \u20B9${fmt(income)} monthly income that you save and invest. Formula: (Total Savings + Investments) \u00f7 Gross Income \u00d7 100.`,
            matters: `Your current savings rate is ${sRate}%. Financial research shows that savings rate \u2014 not just salary \u2014 is the strongest predictor of wealth. Increasing this to ${oRate}% (as per this plan) puts you in a superior wealth-creation bracket.`
        },
        `Current savings rate: ${sRate}%. Target rate: ${oRate}%. At your current income of \u20B9${fmt(income)}, maintaining a ${oRate}% rate will reward you with massive compounding returns over the next ${years} years.`,
        '/icons/expenses.png',
        'right'
    );

    drawGlossaryBox(
        "Section 24 \u2014 Home Loan Interest Deduction (Section 24(b))",
        {
            means: "Section 24(b) allows you to claim up to \u20B92,00,000 per year as deduction on the interest component of your house property.",
            matters: `With your \u20B9${fmt(hEMI)} monthly EMI, you are paying approx \u20B9${fmt(hIntYear)} in interest this year. This makes your home loan extremely tax-efficient, effectively reducing your real interest costs.`
        },
        `Your estimated interest this year: \u20B9${fmt(hIntYear)}. With the \u20B92,00,000 deduction, you save approx \u20B9${fmt(Math.min(hIntYear, 200000) * (taxBracket / 100))} in tax in your current ${taxBracket}% bracket. This is a massive hidden benefit of your home ownership.`,
        '/icons/home copy.png',
        'left'
    );
};

export const renderSnapshotPage = (doc, { name, metrics, pageWidth, pageHeight, report, calculations, personalInfo }) => {
    addHeader(doc, "03", pageWidth);

    const GREY_TEXT = [30, 41, 59];
    const BLUE_DOT = [26, 111, 188];
    const GREEN_DOT = [22, 163, 74];
    const RED_DOT = [192, 57, 43];
    const ORANGE_DOT = [230, 126, 34];

    const isCouple = personalInfo?.familyType === 'couple';

    // Page Title
    doc.setTextColor(...G); doc.setFontSize(22); doc.setFont("Montserrat", "bold");
    doc.text("YOUR FINANCIAL", 14, 30);
    const tw = doc.getTextWidth("YOUR FINANCIAL ");
    doc.setTextColor(...GREY_TEXT); doc.setFont("Montserrat", "normal");
    doc.text("SNAPSHOT AT A GLANCE", 14 + tw, 30);

    doc.setTextColor(...SLATE); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const pName = isCouple ? name : (name || "Neha").split(' ')[0];
    const intro = `Close your eyes for a moment and imagine looking at your entire financial life laid out on a single page — every rupee in, every rupee out, every hope and every gap. ${pName}, what you are about to see is something most people never dare to look at. They go through entire careers without this clarity. You are already different. You looked.`;
    doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, 37);

    // DID YOU KNOW BOX
    const dykY = 48;
    doc.setFillColor(255, 255, 255); doc.setDrawColor(...GREEN_DOT); doc.setLineWidth(1.5);
    doc.line(14, dykY, 14, dykY + 12);

    doc.setTextColor(...GREEN_DOT); doc.setFontSize(12); doc.setFont("Montserrat", "bold");
    doc.text("DID YOU", 20, dykY + 5);
    doc.text("KNOW?", 20, dykY + 11);

    doc.setTextColor(...SLATE); doc.setFontSize(9); doc.setFont("Montserrat", "normal");
    const sRate = (metrics.savingsRate || 0).toFixed(1);
    // Projected savings rate after 5-year plan (realistic +4% improvement, minimum 20%)
    const projSR = Math.min(40, Math.max(safeParse(sRate) + 4, 20)).toFixed(0);
    const dykComparison = safeParse(sRate) > 15
        ? 'already better than most Indians'
        : safeParse(sRate) > 8 ? 'on the right track' : 'starting to build wealth';
    const dykText = `The average Indian household saves only 8-10% of income. You are currently saving ${sRate}% — ${dykComparison}. With this plan, you can reach ${projSR}%!`;
    doc.text(doc.splitTextToSize(dykText, 105), 55, dykY + 6, { lineHeightFactor: 1.15 });

    // Question Mark Icon (Positioned to the extreme right to avoid overlap)
    try { try { doc.addImage('/icons/question.png', 'PNG', 182, dykY - 4, 18, 18); } catch (e) { } } catch (e) { }

    const fmt = v => v ? Math.round(v).toLocaleString('en-IN') : '0';

    if (isCouple) {
        const jointSalary = report?.jointSalary || {};
        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};

        const capitalize = (str) => {
            if (!str) return "";
            return str.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        };

        const hName = capitalize((sectionA.husbandName || "Husband").split(' ')[0]);
        const wName = capitalize((sectionA.wifeName || "Wife").split(' ')[0]);

        // Incomes
        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;

        const totalSalary = hSalary + wSalary;
        const totalOther = hOther + wOther;
        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        const combinedMonthly = hTotal + wTotal;

        const salaryPct = combinedMonthly > 0 ? Math.round((totalSalary / combinedMonthly) * 100) : 0;
        const otherPct = combinedMonthly > 0 ? Math.round((totalOther / combinedMonthly) * 100) : 0;

        // TABLE 1: Combined Monthly Family Income
        let y = 66;
        doc.setTextColor(...G); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
        doc.text("Combined Monthly Family Income", 14, y);
        y += 4; // y is 70

        const colX1 = [14, 64, 96, 128, 160];
        const table1Start = y;

        // Header
        doc.setFillColor(...G); doc.rect(14, y, pageWidth - 28, 8, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("Source", colX1[0] + 3, y + 5.5);
        doc.text(hName, colX1[1] + 3, y + 5.5);
        doc.text(wName, colX1[2] + 3, y + 5.5);
        doc.text("Total", colX1[3] + 3, y + 5.5);
        doc.text("% of Combined", colX1[4] + 3, y + 5.5);

        y += 8;

        // Rows
        const drawRow1 = (label, v1, v2, vTotal, pctStr, isBold, rowIdx) => {
            const bg = isBold ? [241, 245, 249] : (rowIdx % 2 === 0 ? [255, 255, 255] : [248, 250, 252]);
            doc.setFillColor(...bg); doc.rect(14, y, pageWidth - 28, 8, 'F');
            doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
            doc.line(14, y + 8, pageWidth - 14, y + 8);

            doc.setTextColor(30, 41, 59); doc.setFontSize(8.5);
            doc.setFont("Montserrat", isBold ? "bold" : "normal");
            doc.text(label, colX1[0] + 3, y + 5.5);
            doc.text(`\u20B9${fmt(v1)}`, colX1[1] + 3, y + 5.5);
            doc.text(`\u20B9${fmt(v2)}`, colX1[2] + 3, y + 5.5);
            doc.text(`\u20B9${fmt(vTotal)}`, colX1[3] + 3, y + 5.5);
            doc.text(pctStr, colX1[4] + 3, y + 5.5);
            y += 8;
        };

        drawRow1("Salary (Post-Tax)", hSalary, wSalary, totalSalary, `${salaryPct}%`, false, 0);
        drawRow1("Other Income (Freelance/Rent)", hOther, wOther, totalOther, `${otherPct}%`, false, 1);
        drawRow1("TOTAL FAMILY INCOME", hTotal, wTotal, combinedMonthly, "100%", true, 2);

        // Draw Table 1 Outer Border
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.5);
        doc.rect(14, table1Start, pageWidth - 28, y - table1Start, 'S');

        // TABLE 2: Combined Household Expenses & Gaps
        y += 12; // Start Table 2 with a larger gap to balance the page
        doc.setTextColor(...G); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
        doc.text("Tracked Expenses & Investments", 14, y);
        y += 4;

        // Header
        const colX2 = [14, 64, 94, 124, 156];
        const table2Start = y;
        doc.setFillColor(...G); doc.rect(14, y, pageWidth - 28, 8, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("Category", colX2[0] + 3, y + 5.5);
        doc.text("Amount", colX2[1] + 3, y + 5.5);
        doc.text("% of Income", colX2[2] + 3, y + 5.5);
        doc.text("Health Check", colX2[3] + 3, y + 5.5);
        doc.text("Notes", colX2[4] + 3, y + 5.5);

        y += 8;

        // Categories Calculations
        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const plRate = safeParse(sectionF.personalLoanRate) || 15;
        const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
        const transport = (safeParse(sectionC.transport) || 0) + (safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0);
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;
        const insurance = (safeParse(sectionC.healthInsurance) || 0) + (safeParse(sectionE.husbandLifePremium) || 0) + (safeParse(sectionE.wifeLifePremium) || 0) + (safeParse(sectionE.husbandHealthPremium) || 0) + (safeParse(sectionE.wifeHealthPremium) || 0);
        const investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
            (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
            (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
            (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
            (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
            (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
            (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

        const totalTracked = hl + pl + groceriesUtilities + transport + dining + shopping + insurance + investments;
        const unassignedSurplus = Math.max(0, combinedMonthly - totalTracked);

        const lifeCover = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
        const invPct = combinedMonthly > 0 ? (investments / combinedMonthly) : 0;

        const table2Rows = [
            { label: "Joint Home Loan EMI", amt: hl, hc: hl > 0 ? "Productive" : "Healthy", notes: hl > 0 ? "Good debt" : "No home loan EMI" },
            { label: "Joint Personal Loan EMI", amt: pl, hc: pl > 0 ? "Expensive" : "Zero Debt", notes: pl > 0 ? `${plRate}% interest` : "No high-interest debt." },
            { label: "Groceries & Utilities", amt: groceriesUtilities, hc: "Healthy", notes: "Combined" },
            { label: "Transport", amt: transport, hc: "Manageable", notes: "Fuel + maintenance" },
            { label: "Dining & Entertainment", amt: dining, hc: "Healthy", notes: "No cuts needed" },
            { label: "Shopping", amt: shopping, hc: "Manageable", notes: "Combined" },
            { label: "Insurance Premiums", amt: insurance, hc: lifeCover === 0 ? "Inadequate" : "Smart", notes: lifeCover === 0 ? "Health only, no life" : "Adequate protection" },
            { label: "Investments (SIP + EPF)", amt: investments, hc: invPct < 0.2 ? "Too Low" : "Healthy", notes: invPct < 0.2 ? "Needs 3x increase" : "Good retirement base" }
        ];

        const getBadgeColors = (hc) => {
            const label = hc.toUpperCase();
            if (label === "HEALTHY" || label === "PRODUCTIVE" || label === "ZERO DEBT" || label === "SMART") {
                return { bg: [230, 244, 234], text: [19, 115, 51] };
            } else if (label === "EXPENSIVE" || label === "TOO LOW" || label === "CRITICAL GAP") {
                return { bg: [252, 232, 230], text: [197, 34, 31] };
            } else if (label === "INADEQUATE" || label === "WATCH OUT") {
                return { bg: [254, 247, 224], text: [176, 96, 0] };
            } else {
                return { bg: [232, 240, 254], text: [26, 115, 232] };
            }
        };

        const drawRow2 = (label, amt, pctStr, hc, hcColor, notes, isBold, isTotal, isSurplus, rowIdx) => {
            if (isTotal) {
                doc.setFillColor(26, 39, 68); doc.rect(14, y, pageWidth - 28, 8, 'F');
                doc.setTextColor(255, 255, 255);
            } else if (isSurplus) {
                doc.setFillColor(240, 253, 250); doc.rect(14, y, pageWidth - 28, 8, 'F');
                doc.setTextColor(19, 115, 51);
            } else {
                const bg = rowIdx % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
                doc.setFillColor(...bg); doc.rect(14, y, pageWidth - 28, 8, 'F');
                doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.4);
                doc.line(14, y + 8, pageWidth - 14, y + 8);
                doc.setTextColor(30, 41, 59);
            }

            doc.setFontSize(8.5);
            doc.setFont("Montserrat", isBold ? "bold" : "normal");
            doc.text(label, colX2[0] + 3, y + 5.5);
            doc.text(`\u20B9${fmt(amt)}`, colX2[1] + 3, y + 5.5);
            doc.text(pctStr, colX2[2] + 3, y + 5.5);

            if (hc) {
                const colors = getBadgeColors(hc);
                doc.setFillColor(...colors.bg);
                doc.roundedRect(colX2[3] + 3, y + 1.5, 26, 5, 2.5, 2.5, 'F');
                doc.setTextColor(...colors.text); doc.setFontSize(7.5); doc.setFont("Montserrat", "bold");
                doc.text(hc, colX2[3] + 16, y + 5.0, { align: 'center' });
            }

            if (notes) {
                if (isSurplus) {
                    doc.setTextColor(19, 115, 51);
                } else if (isTotal) {
                    doc.setTextColor(255, 255, 255);
                } else {
                    doc.setTextColor(100, 116, 139);
                }
                doc.setFontSize(8); doc.setFont("Montserrat", "normal");
                doc.text(notes, colX2[4] + 3, y + 5.5);
            }

            y += 8;
        };

        table2Rows.forEach((row, index) => {
            const pctVal = combinedMonthly > 0 ? `${Math.round((row.amt / combinedMonthly) * 100)}%` : "0%";
            drawRow2(row.label, row.amt, pctVal, row.hc, null, row.notes, false, false, false, index);
        });

        const trackedPctVal = combinedMonthly > 0 ? `${Math.round((totalTracked / combinedMonthly) * 100)}%` : "0%";
        drawRow2("TOTAL TRACKED", totalTracked, trackedPctVal, "", null, "", true, true, false, 8);

        const surplusPctVal = combinedMonthly > 0 ? `${Math.round((unassignedSurplus / combinedMonthly) * 100)}%` : "0%";
        drawRow2("UNASSIGNED SURPLUS", unassignedSurplus, surplusPctVal, "Opportunity", null, "Needs joint plan", true, false, true, 9);

        // Draw Table 2 Outer Border
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.5);
        doc.rect(14, table2Start, pageWidth - 28, y - table2Start, 'S');

        // Key Insight Box
        y += 6;
        const insightTxt = `You have ₹${fmt(unassignedSurplus)} of unassigned surplus every month. This report redirects it to build wealth, not waste it.`;
        doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        const insightLines = doc.splitTextToSize(insightTxt, pageWidth - 62); // 210 - 62 = 148mm
        const boxHeight = Math.max(12, 6 + (insightLines.length * 4));

        doc.setFillColor(248, 253, 250); doc.setDrawColor(...GREEN_DOT); doc.setLineWidth(0.6);
        doc.roundedRect(14, y, pageWidth - 28, boxHeight, 3, 3, 'FD');

        doc.setTextColor(...GREEN_DOT); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text("> Key Insight:", 18, y + 5.5 + (boxHeight - 12) / 2);

        doc.setTextColor(30, 41, 59); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
        insightLines.forEach((line, i) => {
            doc.text(line, 46, y + 5.5 + (i * 3.8) + (boxHeight - 12) / 2);
        });
        y += boxHeight;
    } else {
        // Table Header
        const tY = 65;
        const colX = { icon: 20, label: 32, pill: 74, pct: 102, dot: 116, hc: 124, desc: 166 };
        const rowH = 12.2;

        doc.setFillColor(...G); doc.rect(14, tY, pageWidth - 28, 10, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("Category", 16, tY + 6.5);
        doc.text("Monthly", colX.pill, tY + 4.2, { align: 'center' });
        doc.text("Amount", colX.pill, tY + 7.8, { align: 'center' });
        doc.text("% of", colX.pct, tY + 4.2, { align: 'center' });
        doc.text("Income", colX.pct, tY + 7.8, { align: 'center' });
        doc.text("Health Check", colX.hc + 19, tY + 6.5, { align: 'center' });
        doc.text("What This Means", colX.desc + 15, tY + 6.5, { align: 'center' });

        const m = metrics || {};
        const inc = m.totalMonthlyIncome || 0;
        const pct = v => ((v / (inc || 1)) * 100).toFixed(0);

        const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
        const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
        const sExp = (isBiz ? report?.businessExpenses : report?.salaryExpenses) || {};
        const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};
        const section3 = report?.section3 || {};

        // --- DEEP DATA AGGREGATION (Matching diagnosisEngine exactly) ---
        // 1. Housing (Rent, EMI, Maintenance, Taxes)
        const hl = safeParse(section3.homeLoanEMI) || (safeParse(sInc.homeLoanEMI) || 0);
        const rt = (safeParse(sInc.rentEMI) || 0);
        const lv = (safeParse(sInc.societyMaintenance) || 0) + (safeParse(sInc.propertyTax) || 0);

        // 2. Groceries & Essentials
        const gr = (safeParse(sExp.groceries) || 0) + (safeParse(sExp.vegetables) || 0) + (safeParse(sExp.dairyProducts) || 0) + (safeParse(sExp.householdSupplies) || 0);

        // 3. Utilities (All bills)
        const medical = (safeParse(sExp.doctorConsultations) || 0) + (safeParse(sExp.medicines) || 0) + (safeParse(sExp.diagnosticTests) || 0) + (safeParse(sExp.dentalCare) || 0) + (safeParse(sExp.eyeCare) || 0);
        const education = (safeParse(sExp.schoolFees) || 0) + (safeParse(sExp.booksStationery) || 0) + (safeParse(sExp.tuitionClasses) || 0) + (safeParse(sExp.extracurricular) || 0);
        const ut = (safeParse(sInc.electricity) || 0) + (safeParse(sInc.water) || 0) + (safeParse(sInc.gas) || 0) + (safeParse(sInc.internet) || 0) + (safeParse(sInc.broadband) || 0) + (safeParse(sInc.ottSubscriptions) || 0) + (safeParse(sInc.cable) || 0) + medical + education;

        // 4. Transport
        const tr = (safeParse(sExp.fuelDiesel) || 0) + (safeParse(sExp.publicTransport) || 0) + (safeParse(sExp.taxiCabUber) || 0) + (safeParse(sExp.vehicleMaintenance) || 0) + (safeParse(sExp.parkingCharges) || 0) + (safeParse(sExp.tollCharges) || 0);

        // 5. Debt (Non-Home Loans)
        const pl = safeParse(section3.personalLoanEMI) || (safeParse(sInc.personalLoanEMI) || 0) + (safeParse(sInc.carLoanEMI) || 0) + (safeParse(sInc.educationLoanEMI) || 0) + (safeParse(sInc.creditCardEMI) || 0) + (safeParse(sInc.otherLoanEMI) || 0);

        // 6. Insurance (Risk Protection)
        const in_ = (safeParse(sInc.lifeInsurance) || 0) + (safeParse(sInc.healthInsurance) || 0) + (safeParse(sInc.criticalIllness) || 0) + (safeParse(sInc.motorInsurance) || 0) + (safeParse(sInc.homeInsurance) || 0);

        // 7. Lifestyle (Wants)
        const di = (safeParse(sInv.diningOut) || 0) + (safeParse(sInv.entertainment) || 0) + (safeParse(sInv.moviesTheater) || 0);
        const shExpKeys = ['salonBarber', 'cosmeticsAmount', 'clothingFootwearAmount'];
        const shInvKeys = [
            'sportsHobbies', 'clubMemberships', 'shopping', 'electronics', 'furnitureDecor', 'apparelAccessories', 'giftsDonations',
            'travel', 'vacationPlanning', 'dailyOfficeCommute', 'parentingSupport', 'childrenAllowance', 'domesticHelp',
            'weddingContributions', 'festivalExpenses', 'religiousDonations', 'charityNGO', 'birthdayCelebrations', 'anniversaryCelebrations', 'familyFunctions',
            'professionalCourses', 'certificationFees', 'booksJournals', 'workFromHome', 'professionalMembership', 'officeCabTravel', 'businessMeals'
        ];
        const sh = shExpKeys.reduce((acc, k) => acc + (safeParse(sExp[k]) || 0), 0) + shInvKeys.reduce((acc, k) => acc + (safeParse(sInv[k]) || 0), 0);

        // 8. Emergency (Self-Protection)
        const em = (safeParse(sInv.emergencyFund) || 0) + (safeParse(sInv.liquidFund) || 0);

        // 9. Mandatory (Locked Savings)
        const pf = (safeParse(sInc.ppf) || 0) + (safeParse(sInc.sukanyaSamriddhi) || 0);
        const ef = (safeParse(sInc.epf) || 0) + (safeParse(sInc.vpf) || 0) + (safeParse(sInc.nps_80ccd2) || 0);

        // 10. Wealth (Investments)
        const mf = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0) + (safeParse(sInv.recurringDeposits) || 0) + (safeParse(sInv.fixedDeposits) || 0) + (safeParse(sInv.goldBonds) || 0);

        // Emergency fund monthly target = 10% of income, rounded to nearest ₹500 (min ₹1,000)
        const emMonthlyTarget = inc > 0 ? Math.max(Math.round(inc * 0.10 / 500) * 500, 1000) : 1000;
        const emStatus = em > 0 ? `is building (currently ₹${em.toLocaleString('en-IN')})` : 'needs to start now';
        // EPF employer contribution = employee EPF (they contribute equally)
        const empEPFMatch = ef > 0 ? `₹${Math.round(ef).toLocaleString('en-IN')}` : 'an equal amount';

        const rows = [
            { l: "GROSS INCOME", v: inc, dot: BLUE_DOT, hc: "Your Earning Power", d: "This is your starting point\u2014every financial decision flows from here.", ic: 'bag' },
            { l: hl > 0 ? "Home Loan EMI" : "Rent Expense", v: hl || rt, dot: (hl + rt) / inc > 0.45 ? RED_DOT : GREEN_DOT, hc: hl > 0 ? "Productive Debt" : "Essential", d: hl > 0 ? "Building equity in your own home.\nThis is 'good debt' - it creates an asset." : "Essential housing cost.\nNon-negotiable.", ic: 'home' },
            { l: "Society & Living", v: lv, dot: GREEN_DOT, hc: "Maintenance", d: "Society maintenance, property tax,\nand other housing overheads.", ic: 'home' },
            { l: "Groceries", v: gr, dot: (gr / inc) > 0.15 ? ORANGE_DOT : GREEN_DOT, hc: (gr / inc) > 0.15 ? "High Spending" : "Healthy", d: "Daily sustenance costs.\nWithin normal range for city living.", ic: 'cart' },
            { l: "Utilities/Compulsory", v: ut, dot: GREEN_DOT, hc: "Healthy", d: "Electricity, internet, education\n& medical living costs.", ic: 'bolt' },
            { l: "Transport", v: tr, dot: GREEN_DOT, hc: "Healthy", d: "Fuel/commute. Normal for working\nprofessional.", ic: 'car' },
            { l: "Loan EMI", v: pl, dot: pl > 0 ? RED_DOT : GREEN_DOT, hc: pl > 0 ? "High Interest" : "Zero Debt", d: pl > 0 ? "Multiple loans tracked. Prioritize\nclosing early - see Section 8." : "Excellent financial discipline.\nNo high-interest debt.", ic: 'loan' },
            { l: "Insurance Premiums", v: in_, dot: in_ > 0 ? GREEN_DOT : RED_DOT, hc: in_ > 0 ? "Smart" : "CRITICAL GAP", d: in_ > 0 ? "Protecting your health & assets.\nNon-negotiable spending." : "You are unprotected! Fix this first\nto secure your family.", ic: 'shield' },
            { l: "Dining Out", v: di, dot: (di / inc) > 0.12 ? RED_DOT : GREEN_DOT, hc: (di / inc) > 0.12 ? "Spend Alert" : "Manageable", d: "Within healthy 10-15% lifestyle\nband. Enjoy - no cuts needed!", ic: 'eat' },
            { l: "Shopping/Lifestyle", v: sh, dot: (sh / inc) > 0.15 ? ORANGE_DOT : GREEN_DOT, hc: (sh / inc) > 0.15 ? "Watch Out" : "Manageable", d: "Shopping, personal care, travel,\nfamily & social obligations.", ic: 'shop' },
            { l: "Emergency Fund", v: em, dot: (em / inc) < 0.05 ? RED_DOT : GREEN_DOT, hc: (em / inc) < 0.05 ? "Too Low" : "Building", d: `Target: ₹${emMonthlyTarget.toLocaleString('en-IN')}/month.\nSafety net ${emStatus}.`, ic: 'umbrella' },
            { l: "PPF", v: pf, dot: pf > 0 ? GREEN_DOT : ORANGE_DOT, hc: pf > 0 ? "Good Start" : "Not Started", d: "Tax-free, guaranteed returns.\nFoundation of retirement.", ic: 'piggy' },
            { l: "Mutual Fund SIP", v: mf, dot: mf > 0 ? GREEN_DOT : ORANGE_DOT, hc: mf > 0 ? "Good Start" : "Missing Engine", d: "Wealth creation engine. Needs to\ngrow over time.", ic: 'trend' },
            { l: "EPF (Auto-deducted)", v: ef, dot: ef > 0 ? GREEN_DOT : RED_DOT, hc: ef > 0 ? "Automatic" : "Inactive", d: ef > 0 ? `Employer also adds ${empEPFMatch}!\nGreat tax-free retirement savings.` : "Start EPF now for long-term\ntax-free retirement wealth.", ic: 'gear' }
        ];

        let ry = tY + 14;
        rows.forEach((r) => {
            const ix = colX.icon, iy = ry;
            doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.2);
            let iconPath = null;
            if (r.ic === 'bag') iconPath = '/icons/rupee.png';
            else if (r.ic === 'home') iconPath = '/icons/home copy.png';
            else if (r.ic === 'cart') iconPath = '/icons/cutlery.png';
            else if (r.ic === 'bolt') iconPath = '/icons/utilities.png';
            else if (r.ic === 'car') iconPath = '/icons/car.png';
            else if (r.ic === 'loan') iconPath = '/icons/rupees.png';
            else if (r.ic === 'shield') iconPath = '/icons/insurance.png';
            else if (r.ic === 'eat') iconPath = '/icons/cutlery.png';
            else if (r.ic === 'shop') iconPath = '/icons/shopping-cart.png';
            else if (r.ic === 'umbrella') iconPath = '/icons/question.png';
            else if (r.ic === 'piggy') iconPath = '/icons/piggy-bank.png';
            else if (r.ic === 'trend') iconPath = '/icons/rupees.png';
            else if (r.ic === 'gear') iconPath = '/icons/settings.png';

            if (iconPath) {
                try { try { doc.addImage(iconPath, 'PNG', ix - 4, iy - 4, 8, 8); } catch (e) { } } catch (e) { }
            }

            doc.setTextColor(...SLATE); doc.setFont("Montserrat", "bold");
            if (r.l.length > 16) {
                doc.setFontSize(7.5);
            } else {
                doc.setFontSize(8.5);
            }
            doc.text(r.l, colX.label, ry + 1.2);

            // Amount Pill
            doc.setDrawColor(203, 213, 225); doc.setFillColor(255, 255, 255);
            doc.roundedRect(colX.pill - 12, ry - 3.5, 24, 7, 3.5, 3.5, 'FD');
            doc.setTextColor(30, 41, 59); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
            doc.text(`\u20B9${fmt(r.v)}`, colX.pill, ry + 1.2, { align: 'center' });

            doc.setTextColor(30, 41, 59); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
            doc.text(`${pct(r.v)}%`, colX.pct, ry + 1.2, { align: 'center' });

            // Connector Map
            doc.setFillColor(...r.dot); doc.circle(colX.pct + 12, ry + 0.2, 1, 'F');
            doc.setDrawColor(...r.dot); doc.setLineWidth(0.3);
            doc.line(colX.pct + 13, ry + 0.2, colX.hc, ry + 0.2);

            // Health Check Pill
            doc.setDrawColor(...r.dot); doc.roundedRect(colX.hc, ry - 3.5, 38, 7, 3.5, 3.5, 'S');
            doc.setTextColor(...r.dot); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
            doc.text(r.hc, colX.hc + 19, ry + 1.2, { align: 'center' });

            // Description
            doc.setTextColor(...SLATE); doc.setFontSize(6.8); doc.setFont("Montserrat", "normal");
            const lines = doc.splitTextToSize(r.d, 35);
            lines.forEach((line, i) => doc.text(line, colX.desc, ry - 0.5 + (i * 3)));

            ry += rowH;
        });

        // Total Tracked Bar (Improved Design matching Goal Image)
        doc.setFillColor(...G); doc.rect(14, ry, pageWidth - 28, 14, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text("Total\nTracked", 18, ry + 6, { lineHeightFactor: 1.0 });
        const trackedVal = rows.slice(1).reduce((sum, x) => sum + x.v, 0);
        doc.text(`\u20B9${fmt(trackedVal)}`, 45, ry + 9);
        doc.text(`${pct(trackedVal)}%`, 70, ry + 9);

        // Surplus Row (Advanced Design matching ref 2241)
        ry += 22; // Increased offset to move it lower
        doc.setTextColor(30, 41, 59); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
        doc.text("UNASSIGNED\nSURPLUS", 14, ry - 1, { lineHeightFactor: 1.1 });

        const surplusVal = inc - trackedVal;
        const sPct = pct(surplusVal);

        // Shared Metric Pill (Amount + Percentage)
        const spX = 40, spW = 48, spH = 10; // Moved even closer (46 -> 40)
        doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.4); doc.setFillColor(255, 255, 255);
        doc.roundedRect(spX, ry - 5, spW, spH, 5, 5, 'S');

        doc.setTextColor(30, 41, 59); doc.setFontSize(9);
        doc.text(`\u20B9${fmt(surplusVal)}`, spX + 5, ry + 1.5);
        doc.setFontSize(8.5); doc.text(`${sPct}%`, spX + 30, ry + 1.5);

        // Inner Dot
        doc.setFillColor(...GREEN_DOT); doc.circle(spX + spW - 4, ry, 1.2, 'F');

        // Pointer Connector (Clean line with dot)
        const connX = spX + spW;
        const targetX = 102; // Adjusted to maintain gap (108 -> 102)
        doc.setDrawColor(...GREEN_DOT); doc.setLineWidth(0.5);
        doc.line(connX + 1, ry, targetX - 5, ry);

        // Opportunity Pill (Empty box with green border)
        doc.setDrawColor(...GREEN_DOT); doc.roundedRect(targetX - 5, ry - 5, 45, spH, 5, 5, 'S');

        doc.setTextColor(...G); doc.setFontSize(7); doc.setFont("Montserrat", "bold");
        const sDesc = "This money exists! Redirect it\nwith intent using this plan.";
        doc.text(sDesc, 119.5, ry - 1, { align: 'center', lineHeightFactor: 1.0 });
    }
}

export const renderFiveGapsPage = (doc, { name, m, metrics, pageWidth, pageHeight, report, calculations }) => {
    const score = calculations?.grades?.overall || report?.healthScore || 46;
    addHeader(doc, 2, pageWidth);

    // Page Title
    doc.setTextColor(6, 150, 104); doc.setFontSize(28); doc.setFont("Montserrat", "bold");
    doc.text("The Important Gaps ", 14, 28);
    const tw = doc.getTextWidth("The Important Gaps ");
    doc.setTextColor(30, 41, 59); doc.setFont("Montserrat", "normal");
    doc.text("That Need Your", 14 + tw, 28);
    doc.setTextColor(6, 150, 104); doc.setFont("Montserrat", "bold");
    doc.text("Attention", 14, 38);
    doc.setDrawColor(229, 231, 235); doc.setLineWidth(1); doc.line(14, 44, 34, 44);

    // Vertical Timeline on Right
    const tlX = 142;

    const fmt = (v) => v ? Math.round(v).toLocaleString('en-IN') : '0';
    const fmtL = (val) => {
        if (!val || val <= 0) return "₹0.0L";
        if (val < 100000) return `₹${(val / 1000).toFixed(1)}K`;
        if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
        return `₹${(val / 100000).toFixed(1)}L`;
    };

    const RED = [220, 38, 38], ORANGE = [234, 88, 12], BLUE = [30, 64, 175], GREEN = [5, 150, 105], PURPLE = [124, 58, 237];
    const RED_D = [153, 27, 27], ORANGE_D = [154, 52, 18], BLUE_D = [30, 58, 138], GREEN_D = [6, 95, 70], PURPLE_D = [91, 33, 182];

    const pInfo = report?.personalInfo || {};
    const sInc = report?.salaryIncome || {};
    const bInc = report?.businessIncome || {};
    const bDet = report?.businessDetails || {};
    const bExp = report?.businessExpenses || {};
    const sInv = report?.salaryInvestment || {};
    const bInv = report?.businessInvestment || {};
    const section3 = report?.section3 || {};
    const section4 = report?.section4 || {};

    // Helper for Severity
    const getSeverity = (ratio) => {
        if (ratio >= 0.8) return { label: "Critical", color: RED, dark: RED_D };
        if (ratio >= 0.5) return { label: "URGENT", color: ORANGE, dark: ORANGE_D };
        if (ratio >= 0.2) return { label: "HIGH", color: BLUE, dark: BLUE_D };
        if (ratio > 0) return { label: "MEDIUM", color: GREEN, dark: GREEN_D };
        return { label: "OPTIMISATION", color: PURPLE, dark: PURPLE_D };
    };

    const rawGaps = [];
    const isCouple = pInfo.familyType === 'couple';

    if (isCouple) {
        const jointSalary = report?.jointSalary || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        // 1. Life Insurance Gap (Couple)
        const lifeActual = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
        const husbandSalary = safeParse(sectionB.husbandSalary) || 0;
        const wifeSalary = safeParse(sectionB.wifeSalary) || 0;
        const husbandOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wifeOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const combinedMonthly = husbandSalary + wifeSalary + husbandOther + wifeOther;
        const combinedAnnual = combinedMonthly * 12;
        const lifeTarget = Math.max(25000000, combinedAnnual * 10);
        const lifeGapRatio = lifeTarget > 0 ? Math.max(0, (lifeTarget - lifeActual) / lifeTarget) : 0;

        if (lifeGapRatio > 0) {
            const sev = getSeverity(lifeGapRatio);
            rawGaps.push({
                priority: lifeGapRatio,
                label: lifeActual === 0 ? "Zero Life Insurance" : "Life Insurance Gap!",
                val: `${fmtL(lifeActual)} (Actual) vs. ${fmtL(lifeTarget)} (Needed)`,
                desc: lifeActual === 0
                    ? "Family is completely exposed. Fix this week via Term Insurance."
                    : `Family is dangerously exposed. Fix this week via Healthcare by Yathartha — yathartha.in/wealthcare`,
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/risk-management.png'
            });
        }

        // 2. Emergency Fund Gap (Couple)
        const emActual = safeParse(sectionG.emergencyFund) || 0;
        const jointExpenses = (safeParse(sectionC.homeLoanEMI) || 0) +
            (safeParse(sectionC.personalLoanEMI) || 0) +
            (safeParse(sectionC.carLoanEMI) || 0) +
            (safeParse(sectionC.groceries) || 0) +
            (safeParse(sectionC.transport) || 0) +
            (safeParse(sectionC.dining) || 0) +
            (safeParse(sectionC.shopping) || 0) +
            (safeParse(sectionC.healthInsurance) || 0) +
            (safeParse(sectionC.rent) || 0) +
            (safeParse(sectionC.schoolFees) || 0) +
            (safeParse(sectionC.otherExpenses) || 0);
        const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
        const emTarget = monthlyExpenses * 6;
        const emGapRatio = emTarget > 0 ? Math.max(0, (emTarget - emActual) / emTarget) : 0;

        if (emGapRatio > 0) {
            const sev = getSeverity(emGapRatio);
            rawGaps.push({
                priority: emGapRatio,
                label: emActual === 0 ? "No Emergency Fund" : "Emergency Fund!",
                val: `${fmtL(emActual)} (Actual) vs. ${fmtL(emTarget)} (Needed)`,
                desc: emActual === 0
                    ? `₹0 vs ${fmtL(emTarget)} required. One unexpected event can derail the entire plan.`
                    : "One unexpected event can derail the entire plan. Automate your emergency fund savings now.",
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/money-currency.png'
            });
        }

        // 3. Joint Personal Loan / High Interest Loan (Couple)
        const plOS = safeParse(sectionF.personalLoanOutstanding) || 0;
        const plEMI = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const plRate = safeParse(sectionF.personalLoanRate) || 15;
        const ccOS = safeParse(sectionF.creditCardOutstanding) || 0;
        const ccRate = safeParse(sectionF.creditCardRate) || 42;
        const clOS = safeParse(sectionF.carLoanOutstanding) || 0;
        const clEMI = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;
        const clRate = safeParse(sectionF.carLoanRate) || 9;

        const loans = [];
        if (plOS > 0) {
            loans.push({ name: "Joint Personal Loan", os: plOS, emi: plEMI, rate: plRate });
        }
        if (ccOS > 0) {
            loans.push({ name: "Credit Card Debt", os: ccOS, emi: 0, rate: ccRate });
        }
        if (clOS > 0) {
            loans.push({ name: "Car Loan", os: clOS, emi: clEMI, rate: clRate });
        }
        const activeLoan = loans.sort((a, b) => b.rate - a.rate)[0];

        if (activeLoan) {
            const loanGapRatio = activeLoan.rate >= 30 ? 0.9 : activeLoan.rate >= 14 ? 0.6 : 0.4;
            const sev = getSeverity(loanGapRatio);
            rawGaps.push({
                priority: loanGapRatio,
                label: `${activeLoan.name} at ${activeLoan.rate}%`,
                val: `\u20B9${(activeLoan.os / 100000).toFixed(1)}L outstanding.`,
                desc: activeLoan.emi > 0
                    ? `Paying \u20b9${fmt(activeLoan.emi)} EMI/month. High-cost loan is a wealth destroyer.`
                    : `High-cost loan of \u20B9${(activeLoan.os / 100000).toFixed(1)}L outstanding is a wealth destroyer.`,
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/loan.png'
            });
        }

        // 4. Separate Tax Filing (Couple)
        const taxSaving = Math.min(85000, Math.round(combinedAnnual * 0.05));
        if (taxSaving > 0) {
            rawGaps.push({
                priority: 0.15, // MEDIUM
                label: "Separate Tax Filing",
                val: `Potential savings: \u20B9${fmt(taxSaving)}/year.`,
                desc: `Filing individually is losing up to \u20b9${fmt(taxSaving)}/year in tax savings. Split HRA/home loan benefits.`,
                sev: "MEDIUM", color: GREEN, dark: GREEN_D, icon: '/icons/invoice.png'
            });
        }

        // 5. Surplus Cash (Couple)
        const investments = (safeParse(sectionD.husbandEPF) || 0) +
            (safeParse(sectionD.wifeEPF) || 0) +
            (safeParse(sectionD.husbandPPF) || 0) +
            (safeParse(sectionD.wifePPF) || 0) +
            (safeParse(sectionD.husbandMutualFund) || 0) +
            (safeParse(sectionD.wifeMutualFund) || 0) +
            (safeParse(sectionD.husbandELSS) || 0) +
            (safeParse(sectionD.wifeELSS) || 0) +
            (safeParse(sectionD.husbandNPS) || 0) +
            (safeParse(sectionD.wifeNPS) || 0) +
            (safeParse(sectionD.husbandFD) || 0) +
            (safeParse(sectionD.wifeFD) || 0) +
            (safeParse(sectionD.husbandOtherInvest) || 0) +
            (safeParse(sectionD.wifeOtherInvest) || 0);
        const surplusCash = combinedMonthly - jointExpenses - investments;
        if (surplusCash > 0) {
            rawGaps.push({
                priority: 0.05, // OPTIMISATION
                label: "Surplus Cash",
                val: `\u20B9${fmt(surplusCash)}/month unassigned.`,
                desc: "Needs joint goal allocation. Idle cash loses purchasing power to inflation.",
                sev: "OPTIMISATION", color: PURPLE, dark: PURPLE_D, icon: '/icons/piggy-bank.png'
            });
        }
    } else {
        // 1. Risk Protection Gap (Life or Health)
        const lifeActual = safeParse(section3.lifeInsuranceCover) || safeParse(section4.lifeInsuranceCover) || safeParse(sInc.lifeInsuranceCover) || safeParse(bInc.lifeInsuranceCover) || safeParse(bDet.lifeInsuranceCover) || safeParse(sInc.lifeInsuranceAmount) || safeParse(bExp.termInsuranceOwner) || safeParse(report?.incomeType === 'salary' ? sInc.lifeInsurance : bInv.lifeInsurance) || (calculations?.lifeInsurance?.actualCover) || 0;
        const annualSalary = (safeParse(pInfo?.monthlySalary) || safeParse(pInfo?.monthlyBusinessProfit) || safeParse(metrics?.monthlyIncome) || 0) * 12;
        const lifeTarget = (annualSalary * 10) || (calculations?.lifeInsurance?.targetCover) || 15000000;
        const lifeGapRatio = lifeTarget > 0 ? Math.max(0, (lifeTarget - lifeActual) / lifeTarget) : 0;

        const healthActual = safeParse(section3.healthInsuranceCover) || safeParse(section4.healthInsuranceCover) || safeParse(sInc.healthInsuranceCover) || safeParse(bInc.healthInsuranceCover) || safeParse(bDet.healthInsuranceCover) || safeParse(bExp.healthInsuranceCover) || safeParse(bExp.healthInsuranceOwner) || safeParse(sInc.healthInsuranceAmount) || metrics?.healthInsuranceCover || 0;
        const healthTarget = 500000; // Standard 5L target
        const healthGapRatio = healthTarget > 0 ? Math.max(0, (healthTarget - healthActual) / healthTarget) : 0;

        // 1. Life Insurance Gap
        if (lifeGapRatio > 0) {
            const sev = getSeverity(lifeGapRatio);
            rawGaps.push({
                priority: lifeGapRatio,
                label: "Life Insurance Gap!",
                val: `${fmtL(lifeActual)} (Actual) vs. ${fmtL(lifeTarget)} (Needed)`,
                desc: "Family is dangerously exposed. Fix this week via Healthcare by Yathartha — yathartha.in/wealthcare",
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/risk-management.png'
            });
        }

        // 2. Health Insurance Gap
        if (healthGapRatio > 0) {
            const sev = getSeverity(healthGapRatio);
            rawGaps.push({
                priority: healthGapRatio,
                label: "Health Insurance Gap!",
                val: `${fmtL(healthActual)} (Actual) vs. ${fmtL(healthTarget)} (Needed)`,
                desc: "Medical bills can wipe out years of savings. Get a base health cover for yourself and family.",
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/insurance.png'
            });
        }

        // 2. Emergency Fund Gap
        const emActual = m?.currentEmergency || safeParse(section4.emergencyFundAmount) || safeParse(sInv.emergencyFund) || safeParse(bInv.emergencyFund) || safeParse(sInv.liquidFund) || safeParse(bInv.liquidFund) || (calculations?.emergencyFund?.currentAmount) || 0;
        const emTarget = m?.targetEmergency || (safeParse(m?.totalEssentialExpenses || metrics?.totalEssentialExpenses || pInfo?.monthlySalary || metrics?.totalMonthlyIncome || 30000) * 6);
        const emGap = Math.max(0, emTarget - emActual);
        const emGapRatio = emTarget > 0 ? emGap / emTarget : 0;

        if (emGapRatio > 0) {
            const sev = getSeverity(emGapRatio);
            rawGaps.push({
                priority: emGapRatio,
                label: "Emergency Fund!",
                val: `${fmtL(emActual)} (Actual) vs. ${fmtL(emTarget)} (Needed)`,
                desc: "One unexpected event can derail the entire plan. Automate your emergency fund savings now.",
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/money-currency.png'
            });
        }

        // 3. Savings Rate Gap
        const currentSR = safeParse(metrics.savingsRate) || 0;
        const srTarget = 25;
        const srGapRatio = Math.max(0, (srTarget - currentSR) / srTarget);

        if (srGapRatio > 0) {
            const sev = getSeverity(srGapRatio);
            rawGaps.push({
                priority: srGapRatio,
                label: "Savings Rate:",
                val: `${currentSR.toFixed(1)}% (Actual) vs. ${srTarget}% (Needed)`,
                desc: "Insufficient savings rate reduces long-term wealth. Fixable by redirecting existing surplus.",
                sev: sev.label, color: sev.color, dark: sev.dark, icon: '/icons/piggy-bank.png'
            });
        }

        // 4. Loan Gap (Prioritize highest outstanding loan)
        const loans = [
            { name: "Personal Loan", os: safeParse(sInc.personalLoanOutstanding) || safeParse(bInc.personalLoanOutstanding) || calculations?.personalLoan?.balance || 0, emi: safeParse(section3.personalLoanEMI) || safeParse(sInc.personalLoanEMI) || safeParse(bInc.personalLoanEMI) || 0, rate: metrics?.personalLoanRate || 14 },
            { name: "Car Loan", os: safeParse(safeParse(sInc.carLoanOutstanding) || safeParse(bInc.carLoanOutstanding) || calculations?.carLoan?.balance || 0), emi: safeParse(sInc.carLoanEMI) || safeParse(bInc.carLoanEMI) || 0, rate: metrics?.carLoanRate || 9 },
            { name: "Credit Card Debt", os: safeParse(sInc.creditCardOutstanding) || safeParse(bInc.creditCardOutstanding) || 0, emi: safeParse(sInc.creditCardEMI) || safeParse(bInc.creditCardEMI) || 0, rate: 42 },
            { name: "Other Loan", os: safeParse(sInc.otherLoanOutstanding) || safeParse(bInc.otherLoanOutstanding) || 0, emi: safeParse(sInc.otherLoanEMI) || safeParse(bInc.otherLoanEMI) || 0, rate: 12 }
        ];

        const activeLoan = loans.sort((a, b) => b.os - a.os).find(l => l.os > 0);

        if (activeLoan) {
            rawGaps.push({
                priority: 0.6,
                label: `${activeLoan.name} at ${activeLoan.rate}%`,
                val: `\u20B9${(activeLoan.os / 100000).toFixed(1)}L outstanding.`,
                desc: `Pay \u20b9${fmt(activeLoan.emi)} EMI/month. Consider paying extra to close early and save interest.`,
                sev: "MEDIUM", color: GREEN, dark: GREEN_D, icon: '/icons/loan.png'
            });
        }
    }

    // 5. Tax Optimisation (Always include if not already at 5 gaps)
    /*
    const taxSaving = (metrics?.annualGrossSalary || 0) * 0.05;
    rawGaps.push({
        priority: 0.1,
        label: "Tax Optimisation",
        val: "Review tax declarations.",
        desc: `Potential savings: \u20B9${fmt(taxSaving)}/year. Maximise 80C, 80D deductions and HRA benefits.`,
        sev: "OPTIMISATION", color: PURPLE, dark: PURPLE_D, icon: '/icons/invoice.png'
    });
    */

    // SORT GAPS BY PRIORITY
    const sortedGaps = rawGaps.sort((a, b) => b.priority - a.priority).slice(0, 5);

    // Vertical Timeline on Right
    doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.6);
    const lineEndY = sortedGaps.length > 0 ? (55 + (sortedGaps.length - 1) * 34 + 17.5) : 55;
    doc.line(tlX, 55, tlX, lineEndY);

    let gy = 55;
    sortedGaps.forEach(gap => {
        const cardW = 120, cardH = 27;
        const cx = 14, cy = gy + 4;

        doc.setFillColor(...gap.color); doc.roundedRect(cx, cy, cardW, cardH, 3, 3, 'F');
        doc.setFont("Montserrat", "bold"); doc.setFontSize(9);
        const labelText = gap.label;
        const labelW = doc.getTextWidth(labelText) + 8;
        doc.setFillColor(...gap.dark); doc.roundedRect(cx + 17, gy, labelW, 6.5, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(labelText, cx + 17 + labelW / 2, gy + 4.5, { align: 'center' });

        doc.path([
            { op: 'm', c: [cx + cardW, cy + cardH / 2 - 4] },
            { op: 'l', c: [cx + cardW + 8, cy + cardH / 2] },
            { op: 'l', c: [cx + cardW, cy + cardH / 2 + 4] },
            { op: 'z' }
        ]); doc.fill();

        try { doc.addImage(gap.icon, 'PNG', cx + 4, cy + 4.5, 18, 18); } catch (e) { }

        doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
        doc.text(gap.val, cx + 24, cy + 10);
        doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        const lines = doc.splitTextToSize(gap.desc, 90);
        doc.text(lines, cx + 24, cy + 15, { lineHeightFactor: 1.1 });

        doc.setFillColor(255, 255, 255); doc.setDrawColor(156, 163, 175); doc.setLineWidth(0.8);
        doc.circle(tlX, cy + cardH / 2, 4.5, 'FD');
        doc.setFillColor(156, 163, 175); doc.circle(tlX, cy + cardH / 2, 1.8, 'F');

        const pillX = tlX + 10, pillW = 42, pillH = 8;
        doc.setFillColor(255, 255, 255); doc.setDrawColor(...gap.color); doc.setLineWidth(1.1);
        doc.roundedRect(pillX, cy + cardH / 2 - pillH / 2, pillW, pillH, 4, 4, 'FD');
        doc.setTextColor(...gap.color); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
        doc.text(gap.sev, pillX + pillW / 2, cy + cardH / 2 + 1.2, { align: 'center' });

        gy += 34;
    });

    // Big Picture
    const bpY = 230;
    doc.setDrawColor(6, 150, 104, 0.4); doc.setLineWidth(0.6);
    doc.setFillColor(248, 253, 250);
    doc.roundedRect(12, bpY, pageWidth - 24, 46, 6, 6, 'FD');

    doc.setFont("Montserrat", "bold"); doc.setFontSize(13); doc.setTextColor(6, 150, 104);
    doc.text("The Big Picture", 20, bpY + 10);
    doc.setTextColor(115, 115, 115); doc.setFont("Montserrat", "normal");
    doc.text("\u2014 Where You Are Headed", 62, bpY + 10);

    doc.setFontSize(10.5); doc.setTextColor(30, 41, 59);
    const firstName = (name || "Client").split(' ')[0];
    const pronoun3 = pInfo?.gender === 'female' ? 'her' : (pInfo?.gender === 'male' ? 'his' : 'their');
    const Pronoun3 = pInfo?.gender === 'female' ? 'Her' : (pInfo?.gender === 'male' ? 'His' : 'Their');

    const mSurplus = safeParse(metrics.monthlySurplus) || (calculations?.metrics?.monthlySurplus) || 0;
    const mIn = safeParse(pInfo?.monthlySalary) || safeParse(pInfo?.monthlyBusinessProfit) || metrics?.totalMonthlyIncome || metrics?.monthlyIncome || 1;
    const currentSR_val = (safeParse(metrics?.savingsRate) || 0).toFixed(1);
    const projectedSR = Math.min(40, safeParse(currentSR_val) + 24).toFixed(1);
    const annualIn = mIn * 12;
    const corpusCr = annualIn > 0 ? (Math.round(annualIn * 15 * 1.12 ** 20 / 10000000 * 10) / 10).toFixed(1) : '0';

    const bpText = `If ${firstName} takes the actions in this report, ${pronoun3} financial score moves from ${score} to 80+ in two years. ${Pronoun3} savings rate can grow from ${currentSR_val}% to approximately ${projectedSR}%. With consistent investing, ${pronoun3} projected retirement corpus grows significantly over time.\nNone of this requires a higher income. It requires a 5-year plan, consistent action, and the right guide walking alongside ${pronoun3}. That is exactly what the 5-Year Comprehensive Roadmap by Yathartha is built for.`;
    doc.text(doc.splitTextToSize(bpText, 175), 20, bpY + 18, { lineHeightFactor: 1.25 });
    // Footer is handled by addFooter() in pdfGenerator.js — do NOT add footer here
};


export const renderWealthLiabilitiesPage = (doc, { name: clientName, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, pageWidth, pageHeight, report }) => {
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');
    const BRAND_GREEN = [6, 150, 104];
    const isCouple = report?.personalInfo?.familyType === 'couple';

    // --- Header ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    // Section Header (Matching standard layout)
    doc.setFillColor(...BRAND_GREEN); doc.roundedRect(14, 10, 45, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 10", 18, 14);

    let y = 28;
    // Main Title Bar
    doc.setFillColor(...BRAND_GREEN); doc.roundedRect(14, y, pageWidth - 28, 10, 1.5, 1.5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("Montserrat", "bold");
    const pageTitle = isCouple ? "MASTER JOINT ACTION CHECKLIST" : "MASTER ACTION CHECKLIST";
    doc.text(pageTitle, 18, y + 6.5);

    y += 18;
    doc.setTextColor(30, 41, 59); doc.setFontSize(9.2); doc.setFont("Montserrat", "normal");
    const intro = `This page, ${clientName || "Client"}, is where your financial story officially begins. Every tick you make here is a real, irreversible step toward the life you are building. Print it. Put it where you can see it. Come back to it in 6 months and feel the quiet pride of someone who actually did what most people only talk about.`;
    doc.text(doc.splitTextToSize(intro, pageWidth - 28), 14, y, { lineHeightFactor: 1.25 });

    y += 20;

    // Helper for drawing tables in PDF
    const drawChecklistTable = (headers, rows, colWidths, startY) => {
        const tLeft = 14;
        const tWidth = pageWidth - 28;
        const rowHeight = 9.5;
        const headerHeight = 11;

        // Calculate total table height first
        let tableHeight = headerHeight;
        rows.forEach((row) => {
            const actionLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
            const rHeight = Math.max(rowHeight, actionLines.length * 4.2 + 4.5);
            tableHeight += rHeight;
        });

        // 1. Draw outer rounded card outline container (matching Section 8/9 style)
        doc.setDrawColor(6, 150, 104, 0.4);
        doc.setLineWidth(0.45);
        doc.roundedRect(tLeft, startY - 3, tWidth, tableHeight + 6, 4, 4, 'S');

        let cy = startY;

        // Helper to draw rounded capsule header badges
        const drawHeaderBadge = (xCenter, w, label) => {
            doc.setFillColor(6, 150, 104);
            doc.roundedRect(xCenter - w / 2, cy + 2, w, 5.5, 2.75, 2.75, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont("Montserrat", "bold");
            doc.setFontSize(7.5);
            doc.text(label, xCenter, cy + 5.8, { align: 'center' });
        };

        // 2. Draw header badges
        let curX = tLeft;
        headers.forEach((header, idx) => {
            const colW = colWidths[idx];
            const xCenter = curX + colW / 2;
            if (idx === 0) {
                // Checkbox header: draw a clean square checkbox without fill
                doc.setDrawColor(71, 85, 105);
                doc.setLineWidth(0.4);
                doc.rect(xCenter - 2, cy + 2.75, 4, 4, 'S');
            } else {
                const badgeW = idx === 1 ? 45 : (idx === 2 ? 22 : 25);
                drawHeaderBadge(xCenter, badgeW, header);
            }
            curX += colW;
        });

        cy += headerHeight;

        // 3. Draw rows
        rows.forEach((row, rowIdx) => {
            const actionLines = doc.splitTextToSize(row[1], colWidths[1] - 8);
            const rHeight = Math.max(rowHeight, actionLines.length * 4.2 + 4.5);

            // Draw cells
            curX = tLeft;
            row.forEach((cell, idx) => {
                doc.setFont("Montserrat", "normal");
                doc.setFontSize(8.5);
                doc.setTextColor(30, 41, 59);

                if (idx === 0) {
                    // Checkbox
                    doc.setDrawColor(71, 85, 105);
                    doc.setLineWidth(0.4);
                    doc.rect(curX + colWidths[idx] / 2 - 2, cy + rHeight / 2 - 2, 4, 4, 'S');
                } else if (idx === 1) {
                    // Action
                    doc.setFont("Montserrat", "bold");
                    doc.text(actionLines, curX + 4, cy + 5.8, { lineHeightFactor: 1.15 });
                } else if (idx === 2) {
                    // By When
                    doc.text(cell, curX + 4, cy + rHeight / 2 + 1.2);
                } else {
                    // Responsible / Impact
                    if (cell === "Celebrate!") {
                        doc.setTextColor(6, 150, 104);
                        doc.setFont("Montserrat", "bold");
                    }
                    doc.text(cell, curX + 4, cy + rHeight / 2 + 1.2);
                }
                curX += colWidths[idx];
            });

            // Draw horizontal dividing line between rows (not after last row)
            if (rowIdx < rows.length - 1) {
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.2);
                doc.line(tLeft, cy + rHeight, tLeft + tWidth, cy + rHeight);
            }

            cy += rHeight;
        });

        // 4. Draw vertical dotted grid lines (matching Section 8/9 style)
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.35);
        doc.setLineDash([0.6, 0.8], 0);
        let splitX = tLeft;
        for (let i = 0; i < colWidths.length - 1; i++) {
            splitX += colWidths[i];
            doc.line(splitX, startY + headerHeight - 1, splitX, startY + tableHeight);
        }
        doc.setLineDash([], 0); // reset line dash

        return cy;
    };

    if (isCouple) {
        // --- COUPLE CHECKLIST DATA ---
        const jointSalary = report?.jointSalary || {};
        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};
        const sectionH = jointSalary.sectionH || {};

        const fmtK = (val) => {
            if (!val) return "0";
            if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
            if (val >= 1000) return `${Math.round(val / 1000)}k`;
            return val.toString();
        };

        const formatCover = (val) => {
            if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
            return `${Math.round(val / 100000)}L`;
        };

        const hN = (sectionA.husbandName || "Rohan").trim().split(' ')[0];
        const wN = (sectionA.wifeName || "Priya").trim().split(' ')[0];
        const hFirstName = hN.charAt(0).toUpperCase() + hN.slice(1);
        const wFirstName = wN.charAt(0).toUpperCase() + wN.slice(1);

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotalInc = hSalary + hOther;
        const wTotalInc = wSalary + wOther;
        const combinedMonthly = hSalary + wSalary + hOther + wOther;

        const hCov = hTotalInc * 12 * 10;
        const wCov = wTotalInc * 12 * 10;
        const hTargCover = hCov > 0 ? Math.round(hCov / 2500000) * 2500000 : 0;
        const wTargCover = wCov > 0 ? Math.round(wCov / 2500000) * 2500000 : 0;
        const hTarget = formatCover(Math.max(10000000, hTargCover));
        const wTarget = formatCover(Math.max(10000000, wTargCover));

        const husbandLifeCover = safeParse(sectionE.husbandLifeCover) || 0;
        const wifeLifeCover = safeParse(sectionE.wifeLifeCover) || 0;

        let hTermAction = "";
        let hTermWhen = "";
        let hTermWho = "";
        if (hTotalInc === 0 && husbandLifeCover === 0) {
            hTermAction = `No life cover needed for ${hFirstName} (no active income)`;
            hTermWhen = "N/A";
            hTermWho = "N/A";
        } else if (husbandLifeCover >= Math.max(10000000, hTargCover)) {
            hTermAction = `Maintain ${hFirstName}'s term insurance of ₹${formatCover(husbandLifeCover)}`;
            hTermWhen = "Ongoing";
            hTermWho = hFirstName;
        } else {
            hTermAction = husbandLifeCover > 0
                ? `Increase ${hFirstName}'s term cover by ₹${formatCover(Math.max(10000000, hTargCover) - husbandLifeCover)} (target ₹${hTarget})`
                : `${hFirstName} buys ₹${hTarget} term plan`;
            hTermWhen = "This week";
            hTermWho = hFirstName;
        }

        let wTermAction = "";
        let wTermWhen = "";
        let wTermWho = "";
        if (wTotalInc === 0 && wifeLifeCover === 0) {
            wTermAction = `No life cover needed for ${wFirstName} (no active income)`;
            wTermWhen = "N/A";
            wTermWho = "N/A";
        } else if (wifeLifeCover >= Math.max(10000000, wTargCover)) {
            wTermAction = `Maintain ${wFirstName}'s term insurance of ₹${formatCover(wifeLifeCover)}`;
            wTermWhen = "Ongoing";
            wTermWho = wFirstName;
        } else {
            wTermAction = wifeLifeCover > 0
                ? `Increase ${wFirstName}'s term cover by ₹${formatCover(Math.max(10000000, wTargCover) - wifeLifeCover)} (target ₹${wTarget})`
                : `${wFirstName} buys ₹${wTarget} term plan`;
            wTermWhen = "This week";
            wTermWho = wFirstName;
        }

        const emActual = safeParse(sectionG.emergencyFund) || 0;
        const hLoan = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const pLoan = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cLoan = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;
        const groc = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;
        const insurance = (safeParse(sectionC.healthInsurance) || 0) + (safeParse(sectionE.husbandLifePremium) || 0) + (safeParse(sectionE.wifeLifePremium) || 0) + (safeParse(sectionE.husbandHealthPremium) || 0) + (safeParse(sectionE.wifeHealthPremium) || 0);

        const jExpenses = groc + (safeParse(sectionC.transport) || 0) + dining + shopping + insurance + hLoan + pLoan + cLoan;
        const mExpenses = jExpenses || (combinedMonthly * 0.5) || 0;
        const targetEF = mExpenses * 6;
        const eGap = Math.max(0, targetEF - emActual);
        const efMonthlyTransfer = eGap > 0 ? Math.max(1000, Math.round(eGap / 18 / 1000) * 1000) : 0;

        let efAction = "";
        let efWhen = "";
        let efWho = "";
        if (combinedMonthly === 0) {
            efAction = "No emergency fund target (No active income)";
            efWhen = "N/A";
            efWho = "N/A";
        } else if (eGap === 0) {
            efAction = `Maintain joint liquid fund buffer of ₹${formatCover(targetEF)}`;
            efWhen = "Ongoing";
            efWho = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
        } else {
            efAction = `Open joint liquid fund, start ₹${fmtK(efMonthlyTransfer)}/month auto-debit`;
            efWhen = "This week";
            efWho = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
        }

        const taxReg = sectionH.taxRegime || "old";
        let taxAction = "";
        let taxWhen = "";
        let taxWho = "";
        if (combinedMonthly === 0) {
            taxAction = "No tax regime declaration needed (No active income)";
            taxWhen = "N/A";
            taxWho = "N/A";
        } else {
            const hasSalH = hSalary > 0;
            const hasSalW = wSalary > 0;
            if (hasSalH && hasSalW) {
                taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to both HRs`;
                taxWhen = "This week";
                taxWho = "Both";
            } else if (hasSalH) {
                taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to HR for ${hFirstName}`;
                taxWhen = "This week";
                taxWho = hFirstName;
            } else if (hasSalW) {
                taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to HR for ${wFirstName}`;
                taxWhen = "This week";
                taxWho = wFirstName;
            } else {
                taxAction = `Adopt ${taxReg.toUpperCase()} Tax Regime for tax filing`;
                taxWhen = "Filing deadline";
                taxWho = "Both";
            }
        }

        const rentVal = safeParse(sectionC.rent) || 0;
        let hrAction = "";
        let hrWhen = "";
        let hrWho = "";
        if (combinedMonthly === 0) {
            hrAction = "No HR submission required (No active income)";
            hrWhen = "N/A";
            hrWho = "N/A";
        } else {
            const hasSalH = hSalary > 0;
            const hasSalW = wSalary > 0;
            if (rentVal > 0) {
                if (sectionH.claimHRA === 'husband' && hasSalH) {
                    hrAction = `Submit rent agreement + HRA declaration for ${hFirstName}`;
                    hrWhen = "This week";
                    hrWho = hFirstName;
                } else if (sectionH.claimHRA === 'wife' && hasSalW) {
                    hrAction = `Submit rent agreement + HRA declaration for ${wFirstName}`;
                    hrWhen = "This week";
                    hrWho = wFirstName;
                } else {
                    hrAction = "Submit joint rent agreement + HRA declaration";
                    hrWhen = "This week";
                    hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirstName : wFirstName);
                }
            } else if (hLoan > 0) {
                hrAction = "Submit home loan interest certificate to HR";
                hrWhen = "This week";
                hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirstName : wFirstName);
            } else {
                hrAction = "Submit PPF/ELSS investment declarations to HR";
                hrWhen = "This week";
                hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirstName : wFirstName);
            }
        }

        const hPPF = safeParse(sectionD.husbandPPF) || 0;
        const wPPF = safeParse(sectionD.wifePPF) || 0;
        let ppfAction = "";
        let ppfWhen = "";
        let ppfWho = "";
        if (combinedMonthly === 0) {
            ppfAction = "No PPF contribution recommended (No active income)";
            ppfWhen = "N/A";
            ppfWho = "N/A";
        } else {
            if (hTotalInc > 0 && wTotalInc > 0) {
                if (hPPF >= 12500 && wPPF >= 12500) {
                    ppfAction = `Maintain PPF contribution of ₹12.5k/month each`;
                    ppfWhen = "Ongoing";
                    ppfWho = "Both";
                } else {
                    const parts = [];
                    if (hPPF < 12500) parts.push(`${hFirstName}: ₹${fmtK(hPPF)}→₹12.5k`);
                    if (wPPF < 12500) parts.push(`${wFirstName}: ₹${fmtK(wPPF)}→₹12.5k`);
                    ppfAction = `Increase PPF to ₹12.5k/month (${parts.join(', ')})`;
                    ppfWhen = "This month";
                    ppfWho = "Both";
                }
            } else if (hTotalInc > 0) {
                if (hPPF >= 12500) {
                    ppfAction = `Maintain PPF contribution of ₹12.5k/month for ${hFirstName}`;
                    ppfWhen = "Ongoing";
                    ppfWho = hFirstName;
                } else {
                    ppfAction = `Increase PPF to ₹12.5k/month for ${hFirstName} (current: ₹${fmtK(hPPF)})`;
                    ppfWhen = "This month";
                    ppfWho = hFirstName;
                }
            } else {
                if (wPPF >= 12500) {
                    ppfAction = `Maintain PPF contribution of ₹12.5k/month for ${wFirstName}`;
                    ppfWhen = "Ongoing";
                    ppfWho = wFirstName;
                } else {
                    ppfAction = `Increase PPF to ₹12.5k/month for ${wFirstName} (current: ₹${fmtK(wPPF)})`;
                    ppfWhen = "This month";
                    ppfWho = wFirstName;
                }
            }
        }

        const hELSS = safeParse(sectionD.husbandELSS) || 0;
        const wELSS = safeParse(sectionD.wifeELSS) || 0;
        const hMF = safeParse(sectionD.husbandMutualFund) || 0;
        const wMF = safeParse(sectionD.wifeMutualFund) || 0;
        const currELSS = hELSS + wELSS + hMF + wMF;
        const targetELSS = combinedMonthly > 0 ? Math.max(10000, Math.round(combinedMonthly * 0.1 / 1000) * 1000) : 0;

        let elssAction = "";
        let elssWhen = "";
        let elssWho = "";
        if (combinedMonthly === 0) {
            elssAction = "No ELSS SIP recommended (No active income)";
            elssWhen = "N/A";
            elssWho = "N/A";
        } else {
            const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
            elssWho = whoRole;
            const prefix = whoRole === "Joint" ? "joint " : "";
            if (currELSS >= targetELSS) {
                elssAction = `Maintain ${prefix}ELSS SIP of ₹${fmtK(currELSS)}/month`;
                elssWhen = "Ongoing";
            } else if (currELSS > 0) {
                elssAction = `Increase ${prefix}ELSS SIP to ₹${fmtK(targetELSS)}/month (current: ₹${fmtK(currELSS)})`;
                elssWhen = "Month 2";
            } else {
                elssAction = `Start ${prefix}ELSS SIP of ₹${fmtK(targetELSS)}/month`;
                elssWhen = "Month 2";
            }
        }

        const hNPS = safeParse(sectionD.husbandNPS) || 0;
        const wNPS = safeParse(sectionD.wifeNPS) || 0;
        let npsAction = "";
        let npsWhen = "";
        let npsWho = "";
        if (combinedMonthly === 0) {
            npsAction = "No NPS contribution recommended (No active income)";
            npsWhen = "N/A";
            npsWho = "N/A";
        } else {
            if (hTotalInc > 0 && wTotalInc > 0) {
                if (hNPS >= 4166 && wNPS >= 4166) {
                    npsAction = `Maintain NPS of ₹4.2k/month each`;
                    npsWhen = "Ongoing";
                    npsWho = "Both";
                } else {
                    const parts = [];
                    if (hNPS < 4166) parts.push(`${hFirstName}: ₹${fmtK(hNPS)}→₹4.2k`);
                    if (wNPS < 4166) parts.push(`${wFirstName}: ₹${fmtK(wNPS)}→₹4.2k`);
                    npsAction = `Increase NPS to ₹4.2k/month (${parts.join(', ')})`;
                    npsWhen = "Month 2";
                    npsWho = "Both";
                }
            } else if (hTotalInc > 0) {
                if (hNPS >= 4166) {
                    npsAction = `Maintain NPS of ₹4.2k/month for ${hFirstName}`;
                    npsWhen = "Ongoing";
                    npsWho = hFirstName;
                } else {
                    npsAction = `Start NPS of ₹4.2k/month for ${hFirstName} (current: ₹${fmtK(hNPS)})`;
                    npsWhen = "Month 2";
                    npsWho = hFirstName;
                }
            } else {
                if (wNPS >= 4166) {
                    npsAction = `Maintain NPS of ₹4.2k/month for ${wFirstName}`;
                    npsWhen = "Ongoing";
                    npsWho = wFirstName;
                } else {
                    npsAction = `Start NPS of ₹4.2k/month for ${wFirstName} (current: ₹${fmtK(wNPS)})`;
                    npsWhen = "Month 2";
                    npsWho = wFirstName;
                }
            }
        }

        const ccOS = safeParse(sectionF.creditCardOutstanding) || 0;
        let plAction = "";
        let plWhen = "";
        let plWho = "";
        if (combinedMonthly === 0) {
            plAction = "No surplus routing recommended (No active income)";
            plWhen = "N/A";
            plWho = "N/A";
        } else {
            const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
            plWho = whoRole;
            if (ccOS > 0) {
                plAction = `Pay off credit card outstanding of ₹${fmt(ccOS)} immediately`;
                plWhen = "This week";
            } else if (pLoan > 0) {
                const extraPL = Math.max(10000, Math.round(pLoan * 0.5 / 1000) * 1000);
                plAction = `Pay ₹${fmtK(extraPL)} extra on personal loan monthly (current: ₹${fmtK(pLoan)})`;
                plWhen = "Month 1-10";
            } else if (cLoan > 0) {
                const extraCL = Math.max(5000, Math.round(cLoan * 0.3 / 1000) * 1000);
                plAction = `Pay ₹${fmtK(extraCL)} extra on car loan monthly (current: ₹${fmtK(cLoan)})`;
                plWhen = "Month 1-12";
            } else if (hLoan > 0) {
                const extraHL = Math.max(5000, Math.round(hLoan * 0.2 / 1000) * 1000);
                plAction = `Pay ₹${fmtK(extraHL)} extra on home loan monthly (current: ₹${fmtK(hLoan)})`;
                plWhen = "Month 1-12";
            } else {
                plAction = "Route monthly surplus to wealth creation (Equity SIPs)";
                plWhen = "Month 1";
            }
        }

        let milestoneAction = "";
        let milestoneWhen = "";
        let milestoneWho = "";
        if (combinedMonthly === 0) {
            milestoneAction = "Review goals and explore income options";
            milestoneWhen = "Ongoing";
            milestoneWho = "Both";
        } else {
            const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
            milestoneWho = whoRole;
            if (ccOS > 0) {
                milestoneAction = "Clear credit card debt and close accounts";
                milestoneWhen = "Month 2";
            } else if (pLoan > 0) {
                milestoneAction = `Close personal loan entirely`;
                milestoneWhen = "Month 10";
                milestoneWho = "Celebrate!";
            } else if (cLoan > 0) {
                milestoneAction = "Prepay car loan to reduce tenure";
                milestoneWhen = "Month 12";
            } else if (hLoan > 0) {
                milestoneAction = "Prepay 5% home loan principal annually";
                milestoneWhen = "Year 1";
            } else {
                milestoneAction = "Build a ₹5L tax-free investment corpus";
                milestoneWhen = "Month 12";
            }
        }

        const efTotal = targetEF > 0 ? `₹${formatCover(targetEF)}` : "₹6L";
        let efTargetAction = "";
        let efTargetWhen = "";
        let efTargetWho = "";
        if (combinedMonthly === 0) {
            efTargetAction = "No emergency fund target (No active income)";
            efTargetWhen = "N/A";
            efTargetWho = "N/A";
        } else {
            efTargetAction = `Emergency fund reaches ${efTotal}`;
            efTargetWhen = "Month 12";
            efTargetWho = "Redirect to SIPs";
        }

        let sipAction = "";
        let sipWhen = "";
        let sipWho = "";
        if (combinedMonthly === 0) {
            sipAction = "No SIP increase recommended (No active income)";
            sipWhen = "N/A";
            sipWho = "N/A";
        } else {
            const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirstName : wFirstName);
            sipWho = whoRole;
            const surplus = combinedMonthly - jExpenses - currELSS;
            const targetSIPVal = currELSS + (safeParse(surplus) || 0) + (pLoan || cLoan || 0);
            const futureSIPVal = targetSIPVal > 0 ? targetSIPVal : Math.max(50000, Math.round(combinedMonthly * 0.35 / 10000) * 10000);
            sipAction = `Increase SIP to ₹${fmtK(futureSIPVal)}/month`;
            sipWhen = "Year 2";
        }

        const headers = ["", "Action", "By When", "Responsible"];
        const colWidths = [12, 100, 35, 35];

        const combinedRows = [
            ["", hTermAction, hTermWhen, hTermWho],
            ["", wTermAction, wTermWhen, wTermWho],
            ["", efAction, efWhen, efWho],
            ["", taxAction, taxWhen, taxWho],
            ["", hrAction, hrWhen, hrWho],
            ["", ppfAction, ppfWhen, ppfWho],
            ["", elssAction, elssWhen, elssWho],
            ["", npsAction, npsWhen, npsWho],
            ["", plAction, plWhen, plWho],
            ["", milestoneAction, milestoneWhen, milestoneWho],
            ["", efTargetAction, efTargetWhen, efTargetWho],
            ["", sipAction, sipWhen, sipWho]
        ];

        drawChecklistTable(headers, combinedRows, colWidths, y);

    } else {
        // --- SINGLE CHECKLIST DATA ---
        const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
        const sInc = (isBiz ? report?.businessDetails : report?.salaryIncome) || {};
        const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};
        const section3 = report?.section3 || {};
        const section4 = report?.section4 || {};
        const personalInfo = report?.personalInfo || {};

        const actualLoanEMI = safeParse(section3.personalLoanEMI) || safeParse(personalLoanEMI) || safeParse(sInc.personalLoanEMI) || 0;
        const monthlyIncome = safeParse(personalInfo.monthlySalary) || metrics?.totalMonthlyIncome || 0;
        const annualGrossSalary = monthlyIncome * 12;
        const idealLifeCover = annualGrossSalary * 10;
        const currentLifeCover = safeParse(metrics?.currentLifeCover) || 0;

        const formatCover = (val) => {
            if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')} Crore`;
            return `${Math.round(val / 100000)} Lakh`;
        };

        // 1. Term Insurance
        let termAction = "";
        let termWhen = "";
        let termImpact = "";
        if (monthlyIncome === 0 && currentLifeCover === 0) {
            termAction = "No term insurance needed (No active income)";
            termWhen = "N/A";
            termImpact = "N/A";
        } else if (currentLifeCover >= idealLifeCover) {
            termAction = `Maintain Term Life Insurance of \u20B9${formatCover(currentLifeCover)}`;
            termWhen = "Ongoing";
            termImpact = "Adequate \u2014 maintains safety net";
        } else {
            termAction = currentLifeCover > 0
                ? `Increase Term Life Insurance by \u20B9${formatCover(idealLifeCover - currentLifeCover)} (target \u20B9${formatCover(idealLifeCover)})`
                : `Buy \u20B9${formatCover(idealLifeCover)} Term Life Insurance`;
            termWhen = "This Week";
            termImpact = "Critical \u2014 closes cover gap";
        }

        // 2. Emergency Fund
        const targetEmergency = metrics?.targetEmergency || (metrics?.totalEssentialExpenses * 6) || 0;
        const currentEmergency = metrics?.currentEmergency || 0;
        const eGap = Math.max(0, targetEmergency - currentEmergency);
        const efMonthly = eGap > 0 ? Math.max(1000, Math.round(eGap / 18 / 1000) * 1000) : 0;

        let efAction = "";
        let efWhen = "";
        let efImpact = "";
        if (monthlyIncome === 0) {
            efAction = "No emergency fund target (No active income)";
            efWhen = "N/A";
            efImpact = "N/A";
        } else if (eGap === 0) {
            efAction = `Maintain emergency fund reserve of \u20B9${formatCover(targetEmergency)}`;
            efWhen = "Ongoing";
            efImpact = "Adequate \u2014 safety net fully funded";
        } else {
            efAction = `Set up \u20B9${fmt(efMonthly)}/month Emergency Fund auto-transfer`;
            efWhen = "This Week";
            efImpact = "Urgent \u2014 builds safety net";
        }

        // 3. Tax Regime
        const recRegime = metrics?.tax?.recommendedRegime || report?.taxDetails?.taxRegime || "Old";
        const taxSavingsAnn = safeParse(metrics?.tax?.taxSavings) || 0;

        let taxAction = "";
        let taxWhen = "";
        let taxImpact = "";
        if (monthlyIncome === 0) {
            taxAction = "No tax regime declaration needed (No active income)";
            taxWhen = "N/A";
            taxImpact = "N/A";
        } else if (isBiz) {
            taxAction = `Adopt ${recRegime.charAt(0).toUpperCase() + recRegime.slice(1)} Tax Regime for business filing`;
            taxWhen = "Filing deadline";
            taxImpact = taxSavingsAnn > 0 ? `Saves \u20B9${fmt(taxSavingsAnn)}/yr on tax liability` : "Optimizes tax liability";
        } else {
            taxAction = `Declare ${recRegime.charAt(0).toUpperCase() + recRegime.slice(1)} Tax Regime to HR`;
            taxWhen = "This Week";
            taxImpact = taxSavingsAnn > 0 ? `Saves \u20B9${fmt(taxSavingsAnn)}/yr immediately` : "Optimizes tax liability";
        }

        // 4. PPF
        const currentPPF = safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics?.ppfMonthly || 0;
        let ppfAction = "";
        let ppfWhen = "";
        let ppfImpact = "";
        if (monthlyIncome === 0) {
            ppfAction = "No PPF contribution recommended (No active income)";
            ppfWhen = "N/A";
            ppfImpact = "N/A";
        } else if (currentPPF >= 12500) {
            ppfAction = `Maintain PPF contribution of \u20B912.5k/month`;
            ppfWhen = "Ongoing";
            ppfImpact = "Maxes 80C + retirement corpus";
        } else {
            ppfAction = currentPPF > 0
                ? `Increase PPF to \u20B912.5k/month (current: \u20B9${fmt(currentPPF)})`
                : `Start PPF contribution of \u20B912.5k/month`;
            ppfWhen = "This Month";
            ppfImpact = "Maxes 80C + retirement corpus";
        }

        // 5. SIP
        const currentSIP = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
        const targetSIP = monthlyIncome > 0 ? Math.max(10000, Math.round(monthlyIncome * 0.15 / 1000) * 1000) : 0;
        let sipAction = "";
        let sipWhen = "";
        let sipImpact = "";
        if (monthlyIncome === 0) {
            sipAction = "No ELSS SIP recommended (No active income)";
            sipWhen = "N/A";
            sipImpact = "N/A";
        } else if (currentSIP >= targetSIP) {
            sipAction = `Maintain ELSS SIP of \u20B9${fmt(currentSIP)}/month`;
            sipWhen = "Ongoing";
            sipImpact = "Tax saving + long-term wealth";
        } else {
            sipAction = currentSIP > 0
                ? `Increase ELSS SIP to \u20B9${fmt(targetSIP)}/month (current: \u20B9${fmt(currentSIP)})`
                : `Start ELSS SIP of \u20B9${fmt(targetSIP)}/month`;
            sipWhen = "Month 2";
            sipImpact = "Tax saving + long-term wealth";
        }

        // 6. NPS
        const currentNPS = safeParse(sInv.nps_80ccd1b) || safeParse(sInc.nps_80ccd1b) || metrics?.npsMonthly || 0;
        let npsAction = "";
        let npsWhen = "";
        let npsImpact = "";
        if (monthlyIncome === 0) {
            npsAction = "No NPS contribution recommended (No active income)";
            npsWhen = "N/A";
            npsImpact = "N/A";
        } else if (currentNPS >= 4166) {
            npsAction = `Maintain NPS contribution of \u20B94.2k/month`;
            npsWhen = "Ongoing";
            npsImpact = "Extra \u20B950k deduction + pension";
        } else {
            npsAction = currentNPS > 0
                ? `Increase NPS to \u20B94.2k/month (current: \u20B9${fmt(currentNPS)})`
                : `Open NPS and invest \u20B94.2k/month`;
            npsWhen = "Month 3";
            npsImpact = "Extra \u20B950k deduction + pension";
        }

        // 7. Personal Loan extra prepayment / Routing surplus
        let plAction = "";
        let plWhen = "";
        let plImpact = "";
        if (monthlyIncome === 0) {
            plAction = "No surplus to route (No active income)";
            plWhen = "N/A";
            plImpact = "N/A";
        } else if (actualLoanEMI > 0) {
            const plExtra = Math.max(5000, Math.round(actualLoanEMI * 0.5 / 1000) * 1000);
            plAction = `Start \u20B9${fmt(plExtra)} extra personal loan prepayment`;
            plWhen = "Month 1";
            plImpact = "Save interest and reduce tenure";
        } else {
            plAction = "Route surplus to equity mutual funds";
            plWhen = "Month 1";
            plImpact = "Accelerate long-term wealth creation";
        }

        // 8. Milestone
        let milestoneAction = "";
        let milestoneWhen = "";
        let milestoneImpact = "";
        if (monthlyIncome === 0) {
            milestoneAction = "Explore new income opportunities and review goals";
            milestoneWhen = "Ongoing";
            milestoneImpact = "N/A";
        } else if (actualLoanEMI > 0) {
            milestoneAction = "Close personal loan entirely";
            milestoneWhen = "Month 12";
            milestoneImpact = "Free cash flow for future goals";
        } else {
            milestoneAction = "Build a \u20B95L investment corpus";
            milestoneWhen = "Month 12";
            milestoneImpact = "Accelerate wealth creation";
        }

        // 9. HR declaration / Rent receipts
        const rentVal = safeParse(personalInfo?.rent) || 0;
        let hrAction = "";
        let hrWhen = "";
        let hrImpact = "";
        if (monthlyIncome === 0) {
            hrAction = "No HR submission required (No active income)";
            hrWhen = "N/A";
            hrImpact = "N/A";
        } else if (isBiz) {
            hrAction = rentVal > 0 ? "Keep rent receipts and invoices for business deductions" : "Document business expenses for deduction";
            hrWhen = "Filing season";
            hrImpact = "Reduce taxable business income";
        } else {
            if (rentVal > 0) {
                hrAction = "Submit rent receipts to HR (HRA)";
                hrWhen = "Before deadline";
                hrImpact = "Optimize HRA exemption";
            } else {
                hrAction = "Submit tax declarations to HR";
                hrWhen = "Before deadline";
                hrImpact = "Optimize tax deductions";
            }
        }

        // 10. Emergency Fund target reached
        const efTotal = targetEmergency > 0 ? `\u20B9${formatCover(targetEmergency)}` : "\u20B95 Lakh";
        let efTargetAction = "";
        let efTargetWhen = "";
        let efTargetImpact = "";
        if (monthlyIncome === 0) {
            efTargetAction = "No emergency fund target (No active income)";
            efTargetWhen = "N/A";
            efTargetImpact = "N/A";
        } else {
            efTargetAction = `Emergency fund reaches target (${efTotal})`;
            efTargetWhen = "Month 18";
            efTargetImpact = "Safety net fully funded";
        }

        const headers = ["", "Action", "By When", "Impact"];
        const colWidths = [12, 100, 35, 35];

        const singleRows = [
            ["", termAction, termWhen, termImpact],
            ["", efAction, efWhen, efImpact],
            ["", taxAction, taxWhen, taxImpact],
            ["", ppfAction, ppfWhen, ppfImpact],
            ["", sipAction, sipWhen, sipImpact],
            ["", npsAction, npsWhen, npsImpact],
            ["", plAction, plWhen, plImpact],
            ["", milestoneAction, milestoneWhen, milestoneImpact],
            ["", hrAction, hrWhen, hrImpact],
            ["", efTargetAction, efTargetWhen, efTargetImpact]
        ];

        drawChecklistTable(headers, singleRows, colWidths, y);
    }

    // Footer
    doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont("Montserrat", "normal");
    const footerText = ` www.myyathartha.com | Confidential - for ${(clientName || "Client").toLowerCase()} only | Financial awareness & education report.`;
    doc.text(footerText, pageWidth / 2, pageHeight - 16, { align: 'center' });

    // Page number circle
    doc.setFillColor(30, 41, 59); doc.circle(pageWidth / 2, pageHeight - 7, 5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.text("14", pageWidth / 2, pageHeight - 5, { align: 'center' });
};

export const renderParametersPage = (doc, { name, m, metrics, calculations, pageWidth, pageHeight, report }) => {
    const BRAND_GREEN = [6, 150, 104];
    const DARK_GREEN = [2, 85, 59];
    // ... (rest of the code remains the same)
    const SHADOW_GREEN = [0, 50, 35];
    const SLATE_DARK = [31, 41, 55];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { } } catch (e) { }

    let y = 26;
    doc.setTextColor(...BRAND_GREEN); doc.setFontSize(22); doc.setFont("Montserrat", "bold");
    doc.text("FINANCIAL HEALTH PARAMETERS", 14, y);
    y += 8;
    doc.setTextColor(30, 41, 59); doc.setFontSize(10.2); doc.setFont("Montserrat", "normal");
    doc.text("Understanding Your A, B, C, D Report Card \u2014 What Each Grade Means & What It Takes to Improve", 14, y);
    doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.8);
    doc.line(14, y + 4.5, 120, y + 4.5);

    y += 20;

    const drawParameterRibbon = (title, score, target, meanings, iconPath) => {
        const boxX = 38;
        const boxW = pageWidth - boxX - 14;
        const boxH = 26; // Aggressive compression

        doc.setTextColor(...SLATE_DARK); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
        doc.text(title, 50, y);
        doc.setFontSize(8.5);
        doc.text(`(Your Score: ${score} \u2192 Target: ${target}) ${score === 'A' ? '' : 'Improve'}`, 50, y + 6);

        y += 8.5;

        // 1. Draw Main Box (Brand Green)
        doc.setFillColor(...BRAND_GREEN);
        doc.roundedRect(boxX, y, boxW, boxH, 12, 12, 'F');

        // 2. Icon Badge (White Circle with Dark Border)
        const iconX = 26;
        doc.setDrawColor(...SLATE_DARK); doc.setLineWidth(0.8);
        doc.setFillColor(255, 255, 255);
        doc.circle(iconX, y + 10, 11, 'DF');
        try { try { doc.addImage(iconPath, 'PNG', iconX - 6.5, y + 3.5, 13, 13); } catch (e) { } } catch (e) { }

        // --- Content Detail Rows ---
        let internalY = y + 7;
        const textL = 48;
        const drawDetail = (lbl, val) => {
            doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
            doc.text(`${lbl}:`, textL, internalY);
            const lw = doc.getTextWidth(`${lbl}: `);
            doc.setFont("Montserrat", "normal"); doc.setFontSize(8.5);
            const lines = doc.splitTextToSize(val, boxW - (textL - boxX) - lw - 2);
            doc.text(lines, textL + lw, internalY, { lineHeightFactor: 1.08 });
            internalY += (lines.length * 3.2) + 1.0;
        };

        drawDetail("What It Measures", meanings.measures);
        drawDetail("Current Status", meanings.status);
        drawDetail("Path to A", meanings.path);

        y += boxH + 9;
    };

    const income = safeParse(m?.totalMonthlyIncome) || 1;
    const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
    const safeIncome = safeParse(m?.totalMonthlyIncome) || safeParse(report?.personalInfo?.monthlySalary) || safeParse(report?.personalInfo?.monthlyBusinessProfit) || 1;

    // --- Dynamic Scoring Logic ---
    const getScore = (val, thresholds) => {
        if (val >= thresholds[0]) return 'A';
        if (val >= thresholds[1]) return 'B';
        if (val >= thresholds[2]) return 'C';
        return 'D';
    };

    // Card A: Protection
    const eFundMonths = (metrics?.totalLiquid || 0) / (m?.totalMonthlyExpenses || 1);
    const scoreA = getScore(eFundMonths, [6, 3, 1]);
    const adviceA = {
        measures: "Whether your financial life is protected against sudden shocks.",
        status: `Emergency fund is ${eFundMonths.toFixed(1)} months of expenses. Life insurance gap is ₹${fmt(calculations?.lifeInsuranceTarget - (metrics?.lifeInsuranceActual || 0))}.`,
        path: scoreA === 'A' ? "Maintain this safety net and review insurance annually." : `Prioritize building 6 months of expenses (₹${fmt(m?.totalMonthlyExpenses * 6)}) and buy ₹1Cr term plan.`
    };

    // Card B: Savings (Robust Extraction)
    const sIncSave = (isBiz ? report?.businessIncome : report?.salaryIncome) || report?.salary || {};
    const sInvSave = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || report?.expenses || {};

    const pf_b = (safeParse(sIncSave.ppf) || safeParse(sIncSave.sukanyaSamriddhi) || metrics?.ppfMonthly || 0);
    const ef_b = (safeParse(sIncSave.epf) || safeParse(sIncSave.vpf) || safeParse(sIncSave.nps_80ccd2) || metrics?.epfMonthly || metrics?.npsMonthly || 0);
    const mf_b = (safeParse(sInvSave.mutualFunds) || safeParse(sInvSave.stocksEquity) || safeParse(sInvSave.monthlyInvestment) || metrics?.sipMonthly || 0);
    const em_b = (safeParse(sInvSave.emergencyFund) || safeParse(sInvSave.liquidFund) || metrics?.emergencyFundMonthly || 0);

    const totalActualSavingsB = metrics?.totalActualSavings || (pf_b + ef_b + mf_b + em_b);
    const sRate = ((totalActualSavingsB / safeIncome) * 100);
    const scoreB = getScore(sRate, [25, 15, 10]);
    const adviceB = {
        measures: "How much of income is systematically saved and invested.",
        status: `Current savings rate is ${sRate.toFixed(1)}% of your monthly income.`,
        path: scoreB === 'A' ? "Great job! Consider diversifying into long-term equity if not done." : `Aim for 25% savings. Automate \u20B9${fmt(Math.max(0, safeIncome * 0.25 - totalActualSavingsB))} more into SIPs/NPS.`
    };

    // Card C: Debt
    const dti = (metrics?.totalEMI || 0) / safeIncome * 100;
    const scoreC = dti <= 20 ? 'A' : dti <= 35 ? 'B' : dti <= 50 ? 'C' : 'D';
    const adviceC = {
        measures: "How efficiently you manage money owed and free cash flow.",
        status: `Debt-to-Income (DTI) ratio is ${dti.toFixed(0)}%. ${dti > 40 ? 'Debt is heavy.' : 'Debt is manageable.'}`,
        path: scoreC === 'A' ? "Debt is well-controlled. Avoid new high-interest liabilities." : `Aggressively prepay high-interest loans. Aim to bring DTI below 20%.`
    };

    // Card D: Retirement
    const monthlyExpForRetirement = m?.totalMonthlyExpenses || m?.totalEssentialExpenses || 0;
    const calculatedTarget = monthlyExpForRetirement > 0 ? monthlyExpForRetirement * 12 * 25 : 0;
    const corpusTarget = calculations?.retirementCorpusTarget || calculatedTarget;

    // --- Robust Data Extraction for Retirement (Sync with Page 16) ---
    const sInvRetire = report?.salaryInvestment || report?.expenses || {};
    const sIncRetire = report?.salaryIncome || report?.salary || {};
    const pInfoRetire = report?.personalInfo || {};

    const currentAgeRetire = parseInt(pInfoRetire.age) || 30;
    const yearsToRetireRetire = Math.max(1, 60 - currentAgeRetire);

    // Check multiple possible keys for each investment type to ensure data fetch
    const valEPF = safeParse(sIncRetire.epf) || safeParse(sInvRetire.epf) || metrics?.epfMonthly || 0;

    const rawPPF = safeParse(sInvRetire.ppf) || safeParse(sIncRetire.ppf) || safeParse(sInvRetire.ppfAmount) || metrics?.ppfMonthly || 0;
    const valPPF = rawPPF === 0 ? 1000 : rawPPF;

    const rawNPS = safeParse(sIncRetire?.nps_80ccd2) || safeParse(sInvRetire?.nps_80ccd2) || safeParse(sInvRetire?.npsTier1) || metrics?.npsMonthly || 0;
    const valNPS = rawNPS === 0 ? 1000 : rawNPS;

    const valSIP = safeParse(sInvRetire.mutualFunds) || safeParse(sInvRetire.monthlyInvestment) || safeParse(sInvRetire.sip) || safeParse(sInvRetire.sipAmount) || metrics?.sipMonthly || 0;
    const valELSS = safeParse(sInvRetire.elss) || safeParse(sInvRetire.elssAmount) || 0;

    const calculateFV = (P, r, n) => {
        if (P <= 0) return 0;
        const monthlyRate = r / 12 / 100;
        const months = n * 12;
        return P * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    };

    const corpusEPF = calculateFV(valEPF * 2, 8.1, yearsToRetireRetire);
    const corpusPPF = calculateFV(valPPF, 7.1, yearsToRetireRetire);
    const corpusNPS = calculateFV(valNPS, 10, yearsToRetireRetire);
    const corpusSIP = calculateFV(valSIP + valELSS, 12, yearsToRetireRetire);

    const projectedCorpus = corpusEPF + corpusPPF + corpusNPS + corpusSIP;

    const scoreD = getScore(projectedCorpus / (corpusTarget || 1), [1, 0.7, 0.4]);
    const adviceD = {
        measures: "How well-positioned you are for retirement at age 60.",
        status: `Projected corpus is ₹${(projectedCorpus / 10000000).toFixed(2)}Cr (Target: ₹${(corpusTarget / 10000000).toFixed(2)}Cr).`,
        path: scoreD === 'A' ? "On track for a comfortable retirement. Maintain step-ups." : `Increase monthly investments by 10% annually to hit the target.`
    };

    drawParameterRibbon("PARAMETER A \u2014 Financial Protection", scoreA, "A", adviceA, '/icons/verified.png');
    drawParameterRibbon("PARAMETER B \u2014 Savings & Wealth", scoreB, "A", adviceB, '/icons/business-growth.png');
    drawParameterRibbon("PARAMETER C \u2014 Debt Management", scoreC, "A", adviceC, '/icons/investment.png');
    drawParameterRibbon("PARAMETER D \u2014 Retirement Planning", scoreD, "A", adviceD, '/icons/graph copy.png');
};


// Page 24 - Final Word (Fully Dynamic)
export const renderFinalPage = (doc, { name, m, metrics, retirementCorpusCr, pageWidth, pageHeight, calculations, report = {} }) => {
    const BRAND_GREEN = [6, 150, 104];
    const fmt = v => (Math.round(v || 0)).toLocaleString('en-IN');

    // ========== DYNAMIC DATA ==========
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};
    const isCouple = personalInfo.familyType === 'couple';

    addHeader(doc, "12", pageWidth);

    if (isCouple) {
        const jointSalary = report?.jointSalary || {};
        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionI = jointSalary.sectionI || {};

        const hN = (sectionA.husbandName || "Rohan").trim().split(' ')[0];
        const wN = (sectionA.wifeName || "Priya").trim().split(' ')[0];
        const hFirstName = hN.charAt(0).toUpperCase() + hN.slice(1);
        const wFirstName = wN.charAt(0).toUpperCase() + wN.slice(1);

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotalInc = hSalary + hOther;
        const wTotalInc = wSalary + wOther;
        const combinedMonthly = hTotalInc + wTotalInc;

        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;
        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const totalEMI = pl + cl + hl;
        const dti = combinedMonthly > 0 ? Math.round((totalEMI / combinedMonthly) * 100) : 0;
        const dtiWithoutPL = combinedMonthly > 0 ? Math.round(((hl + cl) / combinedMonthly) * 100) : 0;

        const investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
            (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
            (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
            (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
            (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
            (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
            (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

        const srVal = combinedMonthly > 0 ? (investments / combinedMonthly) * 100 : 0;

        // Emergency Fund & Targets
        const fallbackExp = combinedMonthly * 0.4;
        const emergencyFundTarget = metrics.emergencyFundTarget || metrics.targetEmergency || Math.round((metrics.totalEssentialExpenses || fallbackExp || 30000) * 6);

        // Term target cover
        const hCov = hTotalInc * 12 * 10;
        const wCov = wTotalInc * 12 * 10;
        const hTargCover = hCov > 0 ? Math.round(hCov / 2500000) * 2500000 : 0;
        const wTargCover = wCov > 0 ? Math.round(wCov / 2500000) * 2500000 : 0;
        const hTargetVal = Math.max(10000000, hTargCover);
        const wTargetVal = Math.max(10000000, wTargCover);

        const formatCover = (val) => {
            if (!val) return "0";
            if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
            if (val >= 100000) return `${(val / 100000).toFixed(1).replace(/\.0$/, '')}L`;
            if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
            return `${Math.round(val)}`;
        };

        const formatCoverNoRs = (val) => {
            if (!val) return "0";
            if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
            if (val >= 100000) return `${(val / 100000).toFixed(0)}L`;
            return `${Math.round(val)}`;
        };

        // Grades
        const grades = calculations.grades || {};
        const score = grades.overall || 46;

        const getLowestGrade = (g1, g2) => {
            const order = { 'A': 4, 'B': 3, 'C': 2, 'D': 1, 'F': 0 };
            const o1 = order[g1] !== undefined ? order[g1] : 0;
            const o2 = order[g2] !== undefined ? order[g2] : 0;
            return o1 < o2 ? g1 : g2;
        };
        const protectionGrade = getLowestGrade(grades.insurance || 'F', grades.emergency || 'F');

        // Text formulas for rows
        const protectionText = `₹${formatCoverNoRs(hTargetVal + wTargetVal)} term insurance + ₹${formatCoverNoRs(emergencyFundTarget)} emergency fund. 12 months.`;
        const savingsText = `Savings rate from ${srVal.toFixed(1)}% -> 30%+. Fully achievable.`;
        const debtText = pl > 0
            ? `Close personal loan. DTI drops to ${dtiWithoutPL}%.`
            : "Maintain zero-debt status. Direct surplus to wealth creation.";

        const targetSIPVal = Math.max(10000, Math.round(combinedMonthly * 0.15 / 10000) * 10000);
        const yearsToRetire = Math.max(5, 60 - (parseInt(personalInfo.age) || 30));
        const retirementText = `Joint NPS + ₹${(targetSIPVal / 1000).toFixed(0)}k/month SIP. ${yearsToRetire} years of compounding.`;

        let y = 28;
        // Draw Section Title
        doc.setTextColor(0, 0, 180); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
        doc.text("SECTION 12: FAMILY HEALTH PARAMETERS – WHAT EACH GRADE MEANS", 14, y);

        const colWidths = [55, 28, 28, 71]; // sum = 182
        const rows = [
            ["A: Protection & Safety", protectionGrade, "A", protectionText],
            ["B: Savings & Wealth", grades.savings || "F", "A", savingsText],
            ["C: Debt Management", grades.debt || "A", "A", debtText],
            ["D: Retirement Goals", grades.retirement || "F", "A", retirementText]
        ];

        const gradeColor = (g) => {
            if (g === 'A') return [6, 150, 104];   // green
            if (g === 'B') return [59, 130, 246];  // blue
            if (g === 'C') return [245, 158, 11];  // amber
            return [239, 68, 68];                   // red (D/F)
        };

        // ── Pre-measure row heights ──────────────────────────────────────
        const headerHeight = 14;
        const rowHeight = 12;
        const PAD = 4;

        doc.setFontSize(8.5);
        const rowMetas = rows.map(row => {
            doc.setFont("Montserrat", "bold");
            const paramLines = doc.splitTextToSize(row[0], (colWidths[0] - PAD * 2) * 0.65);
            doc.setFont("Montserrat", "normal");
            const takesLines = doc.splitTextToSize(row[3], (colWidths[3] - PAD * 2) * 0.75);
            const maxL = Math.max(paramLines.length, takesLines.length);
            const rH = Math.max(rowHeight, maxL * 4.2 + PAD * 2);
            return { paramLines, takesLines, rH };
        });

        const totalTableH = headerHeight + rowMetas.reduce((s, r) => s + r.rH, 0);
        const tLeft = 14;
        const tWidth = pageWidth - 28;


        let cy = 34;

        // ── Outer rounded container ──────────────────────────────────────
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(6, 150, 104);
        doc.setLineWidth(0.5);
        doc.roundedRect(tLeft, cy - 2, tWidth, totalTableH + 4, 4, 4, 'FD');

        // ── Header pill badges ───────────────────────────────────────────
        const headerCenters = [];
        let hx = tLeft;
        colWidths.forEach((cw) => {
            headerCenters.push(hx + cw / 2);
            hx += cw;
        });

        const badgeWidths = [34, 24, 28, 30];
        const badgeLabels = ["Parameter", "Your Grade", "Target Grade", "What It Takes"];
        headerCenters.forEach((cx, i) => {
            const bw = badgeWidths[i];
            doc.setFillColor(6, 150, 104);
            doc.roundedRect(cx - bw / 2, cy + 3.5, bw, 6, 3, 3, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont("Montserrat", "bold");
            doc.setFontSize(6.5);
            doc.text(badgeLabels[i], cx, cy + 7.5, { align: 'center' });
        });

        cy += headerHeight;

        // ── Data rows ────────────────────────────────────────────────────
        rowMetas.forEach(({ paramLines, takesLines, rH }, rowIdx) => {
            const row = rows[rowIdx];

            // Alternating subtle row fill
            if (rowIdx % 2 === 0) {
                doc.setFillColor(248, 250, 252);
                doc.rect(tLeft + 0.5, cy, tWidth - 1, rH, 'F');
            }

            // Horizontal row divider
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.25);
            doc.line(tLeft, cy, tLeft + tWidth, cy);

            // ── Col 0: Parameter (bold, dark) ─────────────────────────
            doc.setFont("Montserrat", "bold");
            doc.setFontSize(8.5);
            doc.setTextColor(30, 41, 59);
            const p0H = paramLines.length * 4.2;
            const p0Y = cy + (rH - p0H) / 2 + 3.2;
            doc.text(paramLines, tLeft + PAD, p0Y, { lineHeightFactor: 1.15 });

            // ── Col 1 & 2: Grade circle badges ───────────────────────────
            [1, 2].forEach(ci => {
                const grade = row[ci];
                const gCol = gradeColor(grade);
                const gx = tLeft + colWidths.slice(0, ci).reduce((a, b) => a + b, 0);
                const gcx = gx + colWidths[ci] / 2;
                const gcy = cy + rH / 2;

                // Filled circle
                doc.setFillColor(...gCol);
                doc.circle(gcx, gcy, 5.5, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("Montserrat", "bold");
                doc.setFontSize(9.5);
                doc.text(grade, gcx, gcy + 1.8, { align: 'center' });
            });

            // ── Col 3: What It Takes (normal, muted slate) ─────────────
            const col3X = tLeft + colWidths[0] + colWidths[1] + colWidths[2];
            doc.setFont("Montserrat", "normal");
            doc.setFontSize(8.2);
            doc.setTextColor(71, 85, 105);
            const t3H = takesLines.length * 4.2;
            const t3Y = cy + (rH - t3H) / 2 + 3.2;
            doc.text(takesLines, col3X + PAD, t3Y, { lineHeightFactor: 1.15 });

            cy += rH;
        });

        // ── Dashed vertical column separators ───────────────────────────
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.3);
        doc.setLineDash([0.6, 0.8], 0);
        let sepX = tLeft;
        for (let i = 0; i < colWidths.length - 1; i++) {
            sepX += colWidths[i];
            doc.line(sepX, 34 + headerHeight, sepX, cy);
        }
        doc.setLineDash([], 0);

        y = cy + 12; // Start after table


        doc.setTextColor(26, 111, 188); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
        doc.text(`A Final Word for ${hFirstName} & ${wFirstName}`, 14, y);
        y += 6.5;

        doc.setTextColor(30, 41, 59); doc.setFontSize(9.2); doc.setFont("Montserrat", "bold");
        doc.text("You came as two individuals. You leave as a single financial unit with:", 14, y);
        y += 7.5;

        // Bullets
        doc.setFont("Montserrat", "normal"); doc.setFontSize(9.2);

        const husbandLifeCover = safeParse(sectionE.husbandLifeCover) || 0;
        const wifeLifeCover = safeParse(sectionE.wifeLifeCover) || 0;
        const lifeActual = husbandLifeCover + wifeLifeCover;
        const lifeTarget = hTargetVal + wTargetVal;

        const monthlySurplus = metrics?.monthlySurplus || 0;

        const bullet1 = `·  A joint score of ${score}/100 and a clear path to 80+.`;
        const bullet2 = `·  A plan to ${lifeActual === 0 ? "go from zero life insurance" : `increase life cover from ₹${formatCover(lifeActual)}`} to ₹${formatCover(lifeTarget)} protection.`;
        const bullet3 = `·  A roadmap to turn ₹${fmt(monthlySurplus)} of idle surplus into ₹${retirementCorpusCr}Cr of retirement wealth.`;

        [bullet1, bullet2, bullet3].forEach(bullet => {
            const bLines = doc.splitTextToSize(bullet, pageWidth - 28);
            doc.text(bLines, 14, y, { lineHeightFactor: 1.25 });
            y += (bLines.length * 4.5) + 3;
        });

        y += 8;
        doc.setFont("Montserrat", "bold"); doc.setFontSize(10.2); doc.setTextColor(6, 150, 104);
        doc.text("You are not behind. You are just getting started. Together.", pageWidth / 2, y, { align: 'center' });

        // ── Disclaimer box at the bottom ─────────────────────────────────
        y += 14;
        const disclaimerText = "Disclaimer: This is an educational template. All figures are examples. Please consult a SEBI-registered advisor and a tax CA for your specific joint financial situation. Market-linked returns are not guaranteed.";
        const disclaimerLines = doc.splitTextToSize(disclaimerText, pageWidth - 40);
        const disclaimerBoxH = disclaimerLines.length * 3.8 + 8;

        // Light amber background box
        doc.setFillColor(254, 252, 232);
        doc.setDrawColor(202, 138, 4);
        doc.setLineWidth(0.4);
        doc.roundedRect(14, y, pageWidth - 28, disclaimerBoxH, 3, 3, 'FD');

        // "⚠ Disclaimer" label
        doc.setFont("Montserrat", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(161, 98, 7);
        doc.text("⚠  Disclaimer", 20, y + 5.5);

        // Disclaimer body text
        doc.setFont("Montserrat", "normal");
        doc.setFontSize(7);
        doc.setTextColor(120, 80, 10);
        doc.text(disclaimerLines, 20, y + 10.5, { lineHeightFactor: 1.3 });

    } else {
        // ========== ORIGINAL SINGLE LAYOUT (UNTOUCHED) ==========
        const sInv = report?.salaryInvestment || {};
        const personalInfo = report?.personalInfo || {};

        // Dynamic retirement corpus calculation
        const monthlyIncome = safeParse(personalInfo.monthlySalary) || metrics?.totalMonthlyIncome || 0;
        const yearsToRetire = Math.max(5, 60 - (parseInt(personalInfo.age) || 30));
        const monthlyInvestments = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0) + (safeParse(sInv.ppf) || 0) + (safeParse(sInv.epf) || 0);
        const monthlyRate = 0.01; // 12% annual
        const months = yearsToRetire * 12;
        const projectedCorpus = monthlyInvestments > 0
            ? monthlyInvestments * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate)
            : (retirementCorpusCr || 5) * 10000000;

        addHeader(doc, "12", pageWidth);

        let y = 30;
        doc.setTextColor(...BRAND_GREEN); doc.setFontSize(19); doc.setFont("Montserrat", "bold");
        const title = "Financial Independence (FI) (Financial Independence)";
        const splitTitle = doc.splitTextToSize(title, pageWidth - 28);
        doc.text(splitTitle, 14, y);
        y += (splitTitle.length * 8) + 4;

        const drawGlossaryBox = (title, meanings, example, iconPath, side = 'left') => {
            const boxWidth = pageWidth - 65;
            const boxX = side === 'left' ? 42 : 14;
            const iconX = side === 'left' ? 24 : pageWidth - 24;

            doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
            const meanLines = doc.splitTextToSize(meanings.means, boxWidth - 12);
            const matterLines = doc.splitTextToSize(meanings.matters, boxWidth - 12);

            const meanH = meanLines.length * 3.2;
            const matterH = matterLines.length * 3.2;
            const boxContentH = 5 + meanH + 5 + matterH + 4;
            const boxH = Math.max(26, boxContentH);

            doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.35);
            doc.roundedRect(boxX, y, boxWidth, boxH, 5, 5, 'S');

            const midY = y + (boxH / 2);
            doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(0.7);
            if (side === 'left') doc.line(iconX + 10, midY, boxX, midY);
            else doc.line(iconX - 10, midY, boxX + boxWidth, midY);

            doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.4);
            doc.circle(iconX, midY, 9, 'S');
            try { try { doc.addImage(iconPath, 'PNG', iconX - 5.5, midY - 5.5, 11, 11); } catch (e) { } } catch (e) { }

            let internalY = y + 6;
            doc.setFontSize(9); doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
            doc.text("What it means: ", boxX + 5, internalY);
            internalY += 3.8;
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
            doc.text(meanLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

            internalY += meanH + 2.2;
            doc.setFont("Montserrat", "bold"); doc.setTextColor(30, 41, 59);
            doc.text("Why it matters for you: ", boxX + 5, internalY);
            internalY += 3.8;
            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59);
            doc.text(matterLines, boxX + 5, internalY, { lineHeightFactor: 1.05 });

            y += boxH + 4;
            doc.setFillColor(...BRAND_GREEN); doc.rect(boxX, y - 0.5, 1.2, 5.5, 'F');
            doc.setFont("Montserrat", "bold"); doc.setTextColor(...BRAND_GREEN); doc.setFontSize(9);
            doc.text(`${name || "Suresh"}'s case:`, boxX + 4, y + 3.8);

            doc.setFont("Montserrat", "normal"); doc.setTextColor(30, 41, 59); doc.setFontSize(9);
            const exLines = doc.splitTextToSize(example, boxWidth - 16);
            doc.text(exLines, boxX + 4, y + 8, { lineHeightFactor: 1.15 });
            y += (exLines.length * 4) + 12;
        };

        const annualEx = (m?.totalMonthlyExpenses || 0) * 12;
        const fiCorpus = annualEx * 25;
        const projCorpus = (safeParse(retirementCorpusCr) || 4.34);
        const monthlyDraw = (projCorpus * 10000000 * 0.04) / 12;

        drawGlossaryBox(
            "",
            {
                means: "Financial Independence means having enough invested wealth that your investment returns can cover your living expenses — so work becomes optional. The standard formula: FI Corpus = 25× Annual Expenses (the '25x Rule').",
                matters: `You do not need to stop working to be financially independent. FI means having the choice. With \u20B9${(annualEx / 100000).toFixed(1)} Lakh annual expenses, your FI corpus is \u20B9${(fiCorpus / 10000000).toFixed(2)} Crore. Your projected corpus of \u20B9${projCorpus} Crore will make you comfortably FI.`
            },
            `At your projected corpus of \u20B9${projCorpus} Crore, a 4% withdrawal (the 'Safe Withdrawal Rate') provides \u20B9${(monthlyDraw / 100000).toFixed(2)} Lakhs/month — inflation-adjusted, potentially forever.`,
            '/icons/profit.png',
            'right'
        );

        y += 4;
        doc.setFillColor(...BRAND_GREEN);
        doc.roundedRect(14, y, pageWidth - 28, 120, 10, 10, 'F');

        let cardY = y + 12;
        doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont("Montserrat", "bold");
        doc.text(`${name || "Suresh"} — A Final Word from Yathartha`, 22, cardY);
        cardY += 10;

        const sRate = ((safeParse(metrics?.currentSavings || 0) / (safeParse(m?.totalMonthlyIncome) || 1)) * 100).toFixed(1);
        const oRate = ((safeParse(metrics?.totalActualSavings || 0) / (safeParse(m?.totalMonthlyIncome) || 1)) * 100).toFixed(1);
        const gapCost = (metrics?.lifeInsurancePremium || 0) + (metrics?.emergencyFundTarget || 0); // Est monthly cost to fix gaps

        const finalBullets = [
            "You came into this report as someone who was curious enough to look. You are leaving as someone with a complete roadmap, a clear score, and 25 financial terms that no one can use to confuse you again.",
            `You learned that your two most urgent gaps — life insurance and emergency fund — can both be fixed this week for less than \u20B9${fmt(gapCost)} per month that you already have in surplus.`,
            `You learned that your savings rate can nearly triple — from ${sRate}% to ${oRate}% — without cutting a single restaurant meal or shopping trip.`,
            "You learned 25 financial terms, which means no banker, advisor, or colleague can ever speak over your head about money again.",
            `Most importantly — you learned that your financial future, at \u20B9${projCorpus} Crore projected corpus, is brighter and more achievable than you may have believed when you started reading this.`,
            "Knowledge → Clarity → Action → Freedom.",
            "The journey of a thousand miles begins with a single step. Take yours today."
        ];

        doc.setFontSize(10.2); doc.setFont("Montserrat", "normal");
        finalBullets.forEach((bullet, idx) => {
            const textLines = doc.splitTextToSize(bullet, pageWidth - 44);
            const isBold = idx >= finalBullets.length - 2;
            if (isBold) doc.setFont("Montserrat", "bold");
            doc.text(textLines, 22, cardY, { lineHeightFactor: 1.4 });
            cardY += (textLines.length * 5) + 3;
        });
    }
};

export const renderDisclaimerPage = (doc, { pageWidth, pageHeight, isCouple }) => {
    const RED_BG = [220, 38, 38];
    const YELLOW_CIRCLE = [253, 224, 71];
    const WARNING_RED = [239, 68, 68];

    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8); } catch (e) { } } catch (e) { }

    const cardW = pageWidth - 40;
    const cardH = 188;
    const cardX = 20;
    const cardY = 65;

    // --- Main Red Card ---
    doc.setFillColor(...RED_BG);
    doc.roundedRect(cardX, cardY, cardW, cardH, 15, 15, 'F');

    // --- Warning Icon (Yellow Circle + Triangle) ---
    const iconY = cardY;
    doc.setFillColor(...YELLOW_CIRCLE);
    doc.circle(pageWidth / 2, iconY, 28, 'F');

    const triSize = 16;
    const centerX = pageWidth / 2;
    const centerY = iconY + 2;
    doc.setFillColor(...WARNING_RED);
    doc.path([
        { op: 'm', c: [centerX, centerY - triSize] },
        { op: 'l', c: [centerX - triSize, centerY + triSize - 2] },
        { op: 'l', c: [centerX + triSize, centerY + triSize - 2] },
        { op: 'z' }
    ]).fill();

    doc.setTextColor(255, 255, 255); doc.setFontSize(26); doc.setFont("Montserrat", "bold");
    doc.text("!", centerX, centerY + 8.5, { align: 'center' });

    // --- Content ---
    let textY = cardY + 45;
    doc.setTextColor(255, 255, 255); doc.setFontSize(16.5); doc.setFont("Montserrat", "bold");
    doc.text("Important Disclaimer – Please Read", centerX, textY, { align: 'center' });

    textY += 14;
    doc.setFontSize(11.2); doc.setFont("Montserrat", "normal");
    const margin = cardX + 15;
    const textW = cardW - 30;

    const disclaimers = [
        "This report is generated by Yathartha App for financial awareness and education purposes only.",
        "All figures are based on data provided by the user and standard financial planning benchmarks.",
        "Investment and insurance decisions should be made with a SEBI-registered Investment Adviser (RIA) or Certified Financial Planner (CFP).",
        "Mutual fund and market-linked returns are not guaranteed. Past performance is not indicative of future results.",
        "Tax calculations are based on FY2024-25 rules and are subject to change with annual Budget announcements.",
        "Emergency fund targets, insurance rules of thumb, and savings benchmarks are guidelines, not guarantees."
    ];

    disclaimers.forEach(msg => {
        const lines = doc.splitTextToSize(msg, textW);
        doc.text(lines, margin, textY, { lineHeightFactor: 1.35 });
        textY += (lines.length * 5) + 6.5;
    });

    // Footer
    const pageNum = isCouple ? "18" : "26";
    doc.setFillColor(30, 41, 59); doc.circle(pageWidth / 2, pageHeight - 10, 5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    doc.text(pageNum, pageWidth / 2, pageHeight - 8, { align: 'center' });
};

// Page 27 - 5-Year Roadmap Summary (Fully Dynamic)
export const renderRoadmapSummaryPage = (doc, { name, pageWidth, pageHeight, calculations, metrics, report = {} }) => {
    const BRAND_GREEN = [6, 150, 104];
    const DARK_GREEN = [2, 85, 59];
    const DARK_SLATE = [31, 41, 55];

    // ========== DYNAMIC DATA ==========
    const sInc = report?.salaryIncome || {};
    const sInv = report?.salaryInvestment || {};
    const personalInfo = report?.personalInfo || {};

    // Dynamic calculations
    const monthlyIncome = safeParse(personalInfo.monthlySalary) || metrics?.totalMonthlyIncome || 0;
    const monthlyExpenses = metrics?.totalEssentialExpenses || 0;
    const monthlySurplus = metrics?.monthlySurplus || (monthlyIncome - monthlyExpenses);
    const currentSIP = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0);
    const targetSIP = monthlySurplus > 0 ? Math.max(1000, Math.round(monthlySurplus * 0.5 / 1000) * 1000) : 0;
    const yearlyStepUp = 1.10; // 10% annual increase
    const score = calculations?.grades?.overall || 62;
    const LIGHT_SLATE = [30, 41, 59];
    const uName = (name || "Neha").charAt(0).toUpperCase() + (name || "Neha").slice(1).toLowerCase();

    // --- Dynamic Logic Preparation ---
    const corpus = calculations?.retirementCorpusCr || "4.34";
    const calculatedTargetSIP = Math.round((currentSIP * 1.25) / 1000) * 1000;
    const hasInsuranceGap = (calculations?.insuranceGap || 0) > 0;
    const hasEFGap = (calculations?.emergencyFundMonths || 0) < 6;
    const isHeavyDebt = (calculations?.debtToIncomeRatio || 0) > 0.35;

    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');
    try { try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth / 2 - 15, 8, 30, 8); } catch (e) { } } catch (e) { }

    let y = 30;
    doc.setTextColor(...BRAND_GREEN); doc.setFontSize(21); doc.setFont("Montserrat", "bold");
    doc.text("YOUR 5-YEAR COMPREHENSIVE ROADMAP", pageWidth / 2, y, { align: 'center' });

    y += 7;
    doc.setTextColor(...LIGHT_SLATE); doc.setFontSize(8.5); doc.setFont("Montserrat", "normal");
    doc.text("Exclusively by Yathartha \u2014 Your Personal Finance Companion for Real Indians", pageWidth / 2, y, { align: 'center' });

    y += 5;
    doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1.2);
    doc.line(pageWidth / 2 - 35, y, pageWidth / 2 + 35, y);

    y += 12;
    doc.setTextColor(...DARK_SLATE); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
    doc.text("Why This Report Is Not Enough \u2014 And Why You Need a 5-Year Roadmap", pageWidth / 2, y, { align: 'center' });

    y += 6;
    doc.setFontSize(8.6); doc.setFont("Montserrat", "normal");
    const intro = `${uName}, this diagnosis report is your financial X-ray. It has shown you exactly what is broken and what is healthy. But an X-ray alone does not heal the fracture \u2014 a treatment plan does. A doctor does not stop at the diagnosis; they walk alongside you to make sure you actually get better. That is exactly what the 5-Year Comprehensive Roadmap by Yathartha does for your financial life.`;
    const introLines = doc.splitTextToSize(intro, pageWidth - 32);
    doc.text(introLines, pageWidth / 2, y, { align: 'center', lineHeightFactor: 1.15 });

    y += (introLines.length * 4) + 10;

    const timelineX = 40;
    const drawYear = (num, title, content) => {
        doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.8);
        if (num < 5) doc.line(timelineX, y + 10, timelineX, y + 36);
        doc.setDrawColor(...BRAND_GREEN); doc.setLineWidth(1);
        doc.line(timelineX - 20, y + 10, timelineX - 10, y + 10);
        doc.setFillColor(...BRAND_GREEN); doc.circle(timelineX - 20, y + 10, 1.2, 'F');
        doc.setFillColor(...BRAND_GREEN); doc.circle(timelineX, y + 10, 10, 'F');
        doc.setDrawColor(255, 255, 255); doc.setLineWidth(1.2); doc.circle(timelineX, y + 10, 8.5, 'S');
        doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("Montserrat", "bold");
        doc.text(num.toString(), timelineX, y + 11.5, { align: 'center' });
        doc.setTextColor(...DARK_SLATE); doc.setFontSize(10.2); doc.setFont("Montserrat", "bold");
        doc.text(title, timelineX + 18, y + 8.5);
        doc.setFontSize(9); doc.setFont("Montserrat", "normal");
        const cLines = doc.splitTextToSize(content, pageWidth - timelineX - 35);
        doc.text(cLines, timelineX + 18, y + 14.5, { lineHeightFactor: 1.12 });
        y += 26;
    };

    const y1Text = `${hasInsuranceGap ? "Term insurance secured. " : "Protection plan optimized. "}${hasEFGap ? "Emergency fund building. " : "Liquidity maintained. "}${isHeavyDebt ? "High-interest debt on fast track to closure. " : "Debt-free status maintained. "}Score: ${score} \u2192 ${score + 8}.`;
    drawYear(1, "Year 1 \u2014 Foundation:", y1Text);

    const y2Text = `${hasEFGap ? "Emergency fund complete. " : "Buffer reserves optimized. "}SIPs growing to \u20B9${(targetSIP).toLocaleString('en-IN')}/month. NPS contributing for tax efficiency. Score: ${score + 8} \u2192 ${score + 15}.`;
    drawYear(2, "Year 2 \u2014 Momentum:", y2Text);

    drawYear(3, "Year 3 \u2014 Acceleration:", "Tax efficiency at peak. A-grade achieved in 3 out of 4 parameters. System-driven wealth creation in full swing.");
    drawYear(4, "Year 4 \u2014 Compounding:", "Wealth visibly growing. Future goal funding strategies initiated. Zero 'leaky' expenses. A-grade in all parameters.");
    drawYear(5, "Year 5 \u2014 Freedom:", `Financial score 90+. Projected corpus on track to exceed \u20B9${corpus}Cr target. Financial independence within clear sight. Truly in control of your money & your future.`);

    const footerH = 82;
    doc.setFillColor(...DARK_GREEN); doc.rect(0, pageHeight - footerH, pageWidth, footerH, 'F');
    let fy = pageHeight - footerH + 11;
    doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text("Yathartha Walks the Journey With You.", 16, fy);
    fy += 9;
    doc.setFontSize(9.2); doc.setFont("Montserrat", "normal");
    const fDesc = "The 5-Year Comprehensive Roadmap is not just a product. It is a commitment \u2014 from Yathartha to you that you will never have to figure your financial future out alone. Every review, update, and question is celebrated. Every setback is navigated together.";
    const fDescLines = doc.splitTextToSize(fDesc, pageWidth - 32);
    doc.text(fDescLines, 16, fy, { lineHeightFactor: 1.12 });
    fy += (fDescLines.length * 4) + 5;
    doc.text("\u2022 Begin Your 5-Year Journey Today: yathartha.in/5year-roadmap", 16, fy);
    fy += 5.2;
    doc.text("\u2022 Wealthcare Products & Tools: yathartha.in/wealthcare", 16, fy);
    fy += 8;
    doc.setFont("Montserrat", "bold"); doc.setFontSize(8.5);
    doc.text("\u201C The best time to plant a tree was 20 years ago. The second best time is today. \u201D", 16, fy);
    fy += 9;
    doc.setFontSize(11.5);
    const lastLine = `${uName}, your tree was planted the moment you read this report. Now let’s watch it grow.`;
    doc.text(doc.splitTextToSize(lastLine, pageWidth - 32), 16, fy, { lineHeightFactor: 1.12 });
    doc.setFillColor(30, 41, 59); doc.circle(pageWidth / 2, pageHeight - 10, 5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8.5); doc.setFont("Montserrat", "bold");
    const isCouple = report?.personalInfo?.familyType === 'couple';
    const pageNum = isCouple ? "19" : "27";
    doc.text(pageNum, pageWidth / 2, pageHeight - 8, { align: 'center' });
};


